// ─────────────────────────────────────────────────────────────
// TERROR NPC ENGINE — REDESIGNED (sequential, one-card-per-write)
//
// Beb      — always acts, random (but LEGAL) decisions, no hesitation
// Champion — plays correctly, attacks every turn, manages Khao,
//            trains Impera, resurrects when its board is empty
// Lord     — chess-level: reads board state, calculates kill
//            turns, pivots strategy, neutralizes threats, and now
//            wields the FULL arsenal: searches, deviancy, training,
//            U'hlti/Affinity, soul activations, rules-true revival
//
// CARD GAME RULE: One card placed/activated/played at a time.
// Every visible action = its own Firebase write + its own pause.
// This makes the NPC readable for the player and lets them
// respond with L3 Tomes, plan blocks, etc.
//
// ═══════════════ NPC AWAKENING WAVE (July 2026) ═══════════════
// The "make them feel alive" milestone. New capabilities, all
// mirroring the PLAYER-side Shatterfield handlers exactly:
//
//   • DEVIANCY — legal Deviant Transcends (school match, ilk differ,
//     free) and Deviant Ascends (any next-level being, cost =
//     rules.deviantFare(sharedTraits, targetLevel) — ONE table shared
//     with Shatterfield.handleAscend, R101). Deviant status is sticky.
//   • LEGALITY FIX — placeOneEscendant used to allow ANY L1 into a
//     slot (R16 violation: school must match the Escling, both paths).
//     Now only legal placements are considered.
//   • GRIMOIRE SEARCH — mirror of handleGrimoireSearch: pay 2 (L1) /
//     4 (L2+) Khao-else-Anima to tutor a being of a chosen level.
//     CONSUMES the turn's draw, exactly like the player.
//   • LUXMOIRE SEARCH — mirror of handleLuxmoireSearch: 5 Khao to
//     tutor any Impera. Does NOT consume the draw.
//   • TRAINING — trainOneImpera mirrors handleAttachImpera:
//     school+ilk match, Impera level <= being level, one Impera per
//     level (R71), free attach (the khaoCost on an Impera is its
//     ACTIVATION fare, paid in activateOneSoulCard).
//   • U'HLTI + AFFINITY — attachOneSoulCard mirrors
//     handleAttachSoulCard: U'hlti owner-bound, Natural L3 only, one
//     each, Affinity requires the U'hlti first. The NPC can now
//     assemble a Supreme Being.
//   • SOUL ACTIVATIONS — activateOneSoulCard: a compact mirror of
//     Shatterfield.handleActivateSoulCard covering every Impera /
//     U'hlti / Affinity in the 12 starter decks, with per-effect
//     "is this useful right now" gates so Khao isn't wasted. One
//     activation per soul card per turn (self-imposed guard — the
//     player side has no cooldown gate yet; the NPC stays modest).
//   • RESURRECTION, RULES-TRUE — resurrectOne fully mirrors
//     handleResurrect + findResurrectSlot: R44 once per card instance
//     per Solrend (resurrectedCards, append-only), R45/R46 order
//     (L1 → empty slot; L2/L3 only over its same-chain lower level,
//     souling the outgoing being — the CHAIN FIX), R47 cost 2/4 paid
//     Khao-else-Anima, R48 souls preserved. The old version could
//     drop an L3 straight onto an Escling — illegal — and could
//     revive the same card forever.
//   • DEFENSIVE-STATE PARITY — doAttacks now respects every state
//     the player can set: being-level mustBeRuinedFirst (Quasar —
//     getRequiredTargetNpc mirrors getRequiredTarget: escling flag
//     FIRST, then the D'uhmdumn tome), intangible (attack passes
//     through, flag consumed), nullifyNextAttack (consumed),
//     reflectNextAttack (Fate Maker — reflects the attacker's Lyt
//     onto the NPC's OWN Anima; the NPC can LOSE to it), and Sweet
//     Dreams (u_xulan_lord on a player being — the NPC dims its own
//     Anima by 1 per Lyt Attack, exactly like the human does).
//   • EFFECTIVE STATS — effLytPure / effLyfPure mirror Shatterfield's
//     getEffectiveLytPure / getEffectiveLyfPure (Gauntlets bond,
//     Diamond Skin, Cosmic Casbah team aura, Premier Dragon auras,
//     Apocalypse Now). The NPC attacks with its buffs and respects
//     the player's.
//   • OFFENSIVE SOUL FLAGS — the NPC's own attacks now consume /
//     honor extraAttack, extraAttackAnimaOnly, dimAnimaOnAttack,
//     spectralClaws, invFangsBuff, canDirectAnima, animaRedirectAll.
//   • PROPHECY TRACKING PARITY — new Anima dims write
//     buildAnimaLossUpdate / buildAnimaGainUpdate trackers like the
//     player side does.
// ─────────────────────────────────────────────────────────────

// ⚠ gameManager's raw `endMatch` IS DELIBERATELY NOT IMPORTED HERE any
// more. See npcFinish: seven sites in this file used to call it, and a
// raw endMatch writes `status: finished` on the WHOLE TABLE and asks
// R98 nothing.
import { updatePlayer as updatePlayerRaw, updateMatch, getMatch } from "./gameManager";
// ── R32'S SEAL (2026-08-25) ─────────────────────────────────────────
// The NPC seat gains Anima at five sites and every one of them wrote
// its own `Math.min(MAX_ANIMA, …)`. R-UNIVERSAL means the brain and
// the player are checked by the same gate, so the brain asks it too.
// Same number out, one rule asked instead of five clamps remembered.
// ── THE GATE ─────────────────────────────────────────────────────────
// `enforce` is the chokepoint the renderer routes every card-playing
// handler through. The brain asked it for NOTHING until 2026-09-14 —
// `sealAnimaGain` was the whole of this import — which is why every
// rule in this file is a second implementation. ACTIVATE_SOUL is the
// first one routed; see the call in activateOneSoulCard.
import { enforce, sealAnimaGain } from "./ruleGate";
// R08 — THE OPENING CYCLE. rules.js owns the window; the brain asks it
// rather than hand-rolling a `turn >= 2` that would drift the first time
// a mode seats more than two players.
// isEffectSearch — R08's 2026-08-26 narrowing: the search bar covers the
// seat's own search ACTION, and a card effect that searches is exempt.
// Same file, same reason: the distinction is declared once.
import { isOpeningTurnIndex, isEffectSearch, SEARCH_BY_PLAYER,
         // R110 — the battle line is FOUR rifts wide since 2026-09-07.
         // Every literal 3 in this file that meant the battle line is now
         // this; the literal 3s that meant the three TOME rifts are not,
         // and were checked one at a time rather than swept.
         ESCLING_SLOTS,
         // R119/R121 — the Mind rift never walls Anima, cannot be swung at
         // while the Body stands, and is silenced by a full trio.
         MIND_RIFT, wardenOpenToLytAttack, animaOpenToLytAttack,
         mindIsSilenced, mindMayAct, routeExileOnWrite,
         // R115-R118 — the brain performs the Arts as of 2026-09-13.
         // The three rift indices it can reason about, the sleep, and the
         // two numbers its judgement is built on. Every one of them is
         // READ from rules.js: npcRoleArts decides WHETHER, never WHAT.
         SOL_RIFT, BODY_RIFT, HEART_RIFT, rolesAwake,
         TERROR_ART_OVERKILL, TANK_LYT_REDUCTION,
         // R125 — three shipped Treasures print intangibility and the
         // brain's ruin path asked about none of them.
         tomeIsIntangible,
         // R30 + R124 — "how many swings does this being have", asked of
         // the ONE predicate the board asks. The brain used to answer it
         // with `hasExtra ? 2 : 1`, which knows about the GRANTED flags
         // and nothing about King of the Anthill's passive add.
         lytAttacksAllowed, CURIO_GIFTS } from "./rules";
import { requestWindow } from "./ResponseBridge";
import { resolveTomeAsBrain } from "./npcResolverBridge";   // ⭐ ONE RESOLVER (2026-09-15)
import { trueCounterOptions } from "./ChainLaw";
// ── R33/R98 · THE PROPHECY, IN-TURN ─────────────────────────────────
// `prophecyConditionMet` is the ONE pure predicate (lifted out of
// checkProphecies' own switch for R98), and `checkProphecies` is the ONE
// resolver — it charges the fare, writes the spend stamp, applies R97's
// Toll outside 1v1 and ends the duel. The brain asks the cheap predicate
// and, only when it is true, hands the expensive resolver the live board.
// No second predicate and no second resolver: the board already grew one
// twin of this in a useMemo and that is trap 4.5.
import { buildAnimaLossUpdate, buildSelfDimUpdate, buildAnimaGainUpdate,
         prophecyConditionMet, checkProphecies } from "./prophecyEngine";
import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS, getEscendantLine } from "./cards";
// ── R44 TALLIES (Rulez July 25, 2026 wave) — rules.js is the one truth.
// Beb 3× / Champion 2× / Lord 1× per Solrend, per card instance; the NPC
// runs the identical arithmetic the player's gate runs (R-UNIVERSAL).
import { RESURRECT_LIMIT, PERSISTENT_TOME_TAGS, GRAVESHIFT_COST, tomeWaitHalfTurns,
         cooldownHalfTurns, deviantFare,
         HAND_LIMIT, STARTING_ANIMA, MAX_ANIMA, STARTING_KHAO, MAX_KHAO_ABS,
         seatIsBrain,   // R10 — a brain seat settles its own hand cap (2026-09-15)
         LUX_SEARCH_COST, grimSearchFare, PRIMERA_COST,
         canPayVoluntary, RESURRECT_PER_TURN, PRIMERAS,
         // ── THE 2026-08-26 CANON PASS — R-UNIVERSAL, so the brain
         // asks the SAME functions the renderer and the gate ask.
         // activationFare      R105 — the printed fare, EVERY activation.
         // farePoolsFor        R107 — one pool per fare, never a split.
         // isLuxSearchable     R106 — a Lux search finds Impera only.
         // durationExpiryTurn  R103 — "2 turns" is 4 half-turns.
         // PHASE / phaseOf     R108 — the five phases and the gate.
         activationFare, printedFare, canPayPrintedFare, farePoolsFor, isLuxSearchable, durationExpiryTurn,
         // durationStillRunning R103 — the EXCLUSIVE window. Read the
         // helper, never the comparison (caP'n, 2026-09-14).
         durationStillRunning,
         // R129 — the two fed Treasures. The currency, the per-Turn
         // ALLOWANCE and the ceiling live in exactly one table; the
         // brain reads it rather than re-deriving it off the book row.
         ATV_FEED, isFedTreasure, feedAllowanceLeft,
         // R128 — a NATURAL Impera Training pays the card's printed fare
         // (caP'n, 2026-09-14). `trainOneImpera` touched no pool at all.
         trainingFare,
         // tomeWardScope/Blocks  R123 — Shower of Lyt wards BEINGS, Phoenix
         //                       Aura wards CARDS, and the brain read neither.
         tomeWardScope, tomeWardBlocks,
         // ── R122 · THE CADENCE TAGS BIND THE SOUL TOO ──────────────
         // caP'n, 2026-09-13: "every card, acronyms like OPT OP3 etc are
         // real, they are not just there to look cool." The board asks
         // these two through gateSoulActivation; the brain asks them
         // directly, so BOTH drivers read one definition of "has this
         // card already acted this cadence" and cannot drift.
         soulCardOffCooldown, stampSoulUsed,
         PHASE, phaseOf } from "./rules";
// ── THE HALF-TURN HAND-OFF, SHARED WITH THE RENDERER (August 2026) ──
// callOnClock used to be imported here and asked by hand in
// npcHandOffTurn. It now lives one level up, inside handOffHalfTurn,
// together with the ledger rotation and the Prophecy check that this
// driver never had. See src/turnEngine.js.
import { handOffHalfTurn } from "./turnEngine";
import { getEffectiveAnima } from "./matchSeats";   // R127 — the pooled read, for the R33 check after a resolved Tome
// ⚠ THE SHARED RESOLVER, NOT A TWIN. See npcRoleArts' banner.
import { performRoleArt, performExiliation, artsRailFor } from "./roleArts";
// THE ONE ANSWER to "seat W just won against seat L — does the MATCH
// end, or does one side get knocked out?" — and the one place R98's
// co-winner question is asked. See npcFinish below for what the seven
// raw endMatch calls it replaced could not see.
import { finishOrEliminateSeat } from "./matchOutcome";
// ── THE NECRO SEAL (July 27, 2026) — R-UNIVERSAL parity ──────
// The NPC obeys the identical per-copy tally and Graveshift generation
// the player does. Rationale lives in necroSeal.js.
import { baseId, sealPayload, sealGraveshift,
         resTally as resTallySealed, canPassiveResurrect } from "./necroSeal";
// ── THE TOME BOOK (August 2026) — one row per Tome, asserted complete
// against cards.js at import. This module REPLACES the four
// hand-maintained id lists that used to live in this file and drift
// apart from each other; see the banner over npcTomeIsWorthPaying and
// the one at the top of npcTomeBook.js. If a Tome is added to cards.js
// without a row, THIS IMPORT THROWS — which is the whole point.
import { tomeWant, tomeRow, tomeMode, tomeIsStanding, tomeIsPlaceable,
         buildView, matchesFilter } from "./npcTomeBook";

// Per-COPY tally (sealed ids count individually; unsealed fall back to
// name matching so an in-flight match is never broken).
const resTallyNpc = resTallySealed;

const ALL_CARDS = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];
// Base-aware — a sealed being (taez_beb~0~7) resolves to its catalog card.
const getCard = (id) => ALL_CARDS.find(c => c.id === baseId(id)) || null;
const isEscendantId = (id) => getCard(id)?.type === "Escendant";

// ── THE WRITE OVERSEER ────────────────────────────────────────
// Shadows the imported writer so every NPC ruin path seals on the way in,
// exactly like Shatterfield's. The live read only fires when the payload
// actually carries a Necropolis (ruin writes) — every other write goes
// straight through with no extra round trip.
// ⚠ `opts` RIDES THROUGH UNTOUCHED, and R08 is why. This overseer has
// never had an R08 clause in it — the backstop lives one level down, in
// gameManager, where EVERY writer in the project meets (see THE R08
// SEAL there). All this shadow has to do is not swallow the writer's
// declaration on its way past.
const updatePlayer = async (code, playerIdx, updates, opts = {}) => {
  if (!updates || !Array.isArray(updates.necropolis)) {
    return updatePlayerRaw(code, playerIdx, updates, opts);
  }
  let live = null;
  try { live = (await getMatch(code))?.players?.[playerIdx] || null; } catch (_) { live = null; }
  // R113 — THE EXILE ROUTER, the brain's twin of the renderer's. Same
  // order for the same reason: seal first so a being HAS its per-copy
  // identity, then route so the Mark is matched against that identity
  // rather than against a bare name. Without this line the brain would
  // send a Marked card back to the Necropolis and the two drivers would
  // be playing different games — which is exactly the fault that makes
  // self-play train against a board the human never sees.
  return updatePlayerRaw(code, playerIdx, routeExileOnWrite(sealPayload(updates, live, isEscendantId), live, isEscendantId), opts);
};
// A stable name for the function above, so a nested scope that shadows
// `updatePlayer` (see applyNpcTomeFireEffects' R123 ward) can still
// reach it without tripping its own temporal dead zone.
const _npcUpdateOuter = updatePlayer;


// ─────────────────────────────────────────────────────────────
// BUG #1 FIX (must-ruin-first) — universal-rules parity.
//
// The player-side enforcement (Shatterfield.getRequiredTarget) already
// blocks the human from attacking past a face-up Khao D'uhmdumn. The NPC
// had NO equivalent awareness, so when the PLAYER dropped a D'uhmdumn the
// NPC attacked straight past it — reading in-game as "the rule isn't
// blocking." This mirrors Shatterfield's self-deriving design EXACTLY:
// the requirement is intrinsic to card identity, never a stored flag.
//
// Khao D'uhmdumn is a TOME with Lyf (0/1/1) — a TAUNT WALL. Canon per
// caP'n (July 2026): "every single attack must first be directed at the
// D'uhmdumn, whether Lyt attack, Tome, etc." So the NPC does NOT freeze
// its attack phase anymore — it DIRECTS its Lyt attacks at the tome:
//   • Lyt >= tome's current Lyf → the tome is ruined (to necropolis)
//   • Lyt <  current Lyf        → chip the tome's Lyf
//   • Lord's nullifyFirstRuin   → the FIRST hit (of any kind) is soaked;
//     the flag is consumed and the attack does nothing.
// Once the tome is gone, remaining attackers resume normal targeting
// within the SAME attack phase. See attackRuinFirstTome below.
const TOME_RUIN_FIRST_IDS = new Set([
  "tome_khao_duhmdumn_beb",
  "tome_khao_duhmdumn_champion",
  "tome_khao_duhmdumn_lord",
]);

// ── riftArray — Firebase array-hole normalizer (July 2026) ──
// RTDB deletes null entries and returns sparse integer-keyed OBJECTS
// for arrays like tomes [null, x, null] → {1:x}. Any `.findIndex`/
// `.some`/`.forEach` on that object crashes (or silently misses empty
// slots — a length-1 array from {0:x} hides slots 1 and 2). Coerce any
// shape into a fixed-length array with explicit nulls.
const riftArray = (v, len) => {
  const out = new Array(len).fill(null);
  if (Array.isArray(v)) {
    for (let i = 0; i < len; i++) out[i] = v[i] ?? null;
  } else if (v && typeof v === "object") {
    Object.keys(v).forEach(k => {
      const n = parseInt(k, 10);
      if (n >= 0 && n < len) out[n] = v[k];
    });
  }
  return out;
};

// Returns the first face-up priority-target tome on a field, or null.
// Face-down tomes are hidden and impose no block. Derived from card
// identity only — no reliance on a persisted mustBeRuinedFirst flag.
function getRuinFirstTome(field) {
  const tomes = riftArray(field?.tomes, 3);
  const idx = tomes.findIndex(
    t => t?.id && !t?.faceDown && TOME_RUIN_FIRST_IDS.has(t.id)
  );
  if (idx < 0) return null;
  const t = tomes[idx];
  return { slotIdx: idx, id: t.id, name: getCard(t.id)?.name || "this Tome" };
}

// ── AWAKENING: full mirror of Shatterfield.getRequiredTarget ──
// The player side checks the ESCLING-slot flag FIRST (Quasar-style —
// "Choose 1 of your beings, it must be ruined before your adversary can
// attack another being" — an activated state, legitimately flag-based),
// THEN the intrinsic D'uhmdumn tome requirement. The NPC only ever knew
// about the tome — a player Quasar taunt was silently ignored.
// Returns { kind: "escling"|"tome", slotIdx, id, name } or null.
function getRequiredTargetNpc(field) {
  if (!field) return null;
  const esclings = field.esclings || [];
  const esclingMatch = esclings.findIndex(s => s?.escendant && s?.mustBeRuinedFirst);
  if (esclingMatch >= 0) {
    const s = esclings[esclingMatch];
    return { kind: "escling", slotIdx: esclingMatch, id: s.escendant, name: getCard(s.escendant)?.name || "this being" };
  }
  const tome = getRuinFirstTome(field);
  if (tome) return { kind: "tome", ...tome };
  return null;
}

// ─────────────────────────────────────────────────────────────
// UNIVERSAL RUIN SEMANTICS — descendPlayerSlot (July 2026)
//
// THE fix for "soul entirely destroyed instead of descending."
// The NPC's three ruin sites used to write `escendant:null, soul:[]`
// — obliterating the player's being AND deleting its soul cards from
// the game (they never even reached the Necropolis). This helper
// mirrors Shatterfield.descendBeing EXACTLY, so a ruin caused by the
// NPC behaves identically to one caused by a human:
//
//   • L2/L3 with its lower level in the soul → DESCENDS one level.
//     Only the top escendant goes to Necropolis. EVERY Impera stays,
//     at every level (caP'n, 13 Sept 2026 — the July "Impera belong
//     to their level counterpart" reading is overturned), along with
//     lower Escendants, U'hltis and Affinities.
//     Descended slot is Fallen for one turn (fallenTurn = match turn).
//     One-shot flags (mustBeRuinedFirst / nullifyNextAttack /
//     reflectNextAttack) clear — they belonged to the ruined level.
//   • L1 (or no lower level in soul) → FULL RUIN: escendant + entire
//     soul go to Necropolis, slot resets to bare Escling, and any
//     Treasure bonded to the slot (wieldedBy) cascades to Necropolis.
//
// MUTATES liveField + liveNec in place (the doAttacks pattern);
// caller writes both via its own updatePlayer. Returns "descended"
// | "ruined" | null so callers can log/pace accurately.
// ─────────────────────────────────────────────────────────────
function descendPlayerSlot(liveField, liveNec, slotIdx, matchTurn) {
  const slot = liveField?.esclings?.[slotIdx];
  if (!slot?.escendant) return null;
  const topCard = getCard(slot.escendant);
  if (!topCard) return null;

  liveNec.push(slot.escendant);
  const soul = slot.soul || [];
  const prevLevelId = soul.find(id => {
    const c = getCard(id);
    return c?.type === "Escendant" && c?.level === (topCard.level - 1);
  });

  // 🔥 R68 — THE HERETICAL TOME LEAVES WITH THE LEVEL IT WAS BONDED TO,
  // in BOTH branches, exactly as tomeResolver's descendBeing does. It is
  // NOT in `soul`, so nothing above this line touched it: on the descent
  // branch `{...slot}` would carry it down onto a Champion, where it is
  // illegal, and leave it there permanently; on the full-ruin branch it
  // would be nulled with no destination, which is a card leaving the game
  // (trap 4.3). One push, above both branches, because both need it.
  if (slot.heretical) liveNec.push(slot.heretical);

  if (prevLevelId && topCard.level > 1) {
    // ── DESCENT ──
    const keptSoul = soul.filter(id => {
      const c = getCard(id);
      if (!c) return false;
      if (c.type === "Escendant") return true;
      // ALL Impera stay (caP'n, 13 Sept 2026 — overturns the July
      // "level-bound" reading). RULEZ_V2 :541/:563. Mirrors
      // tomeResolver.descendBeing exactly; if one moves, both move.
      if (c.type === "Impera") return true;
      if (c.type === "U'hlti" || c.type === "Affinity") return true;
      return false;
    });
    soul.filter(id => !keptSoul.includes(id)).forEach(id => liveNec.push(id));
    const prevCard = getCard(prevLevelId);
    liveField.esclings[slotIdx] = {
      ...slot,
      escendant: prevLevelId,
      heretical: null,          // 🔥 R68 — pushed above; never rides down
      currentLyf: prevCard?.lyf ?? null,
      soul: keptSoul.filter(id => id !== prevLevelId),
      attackedThisTurn: false,
      intangible: false,
      mustBeRuinedFirst: false,
      nullifyNextAttack: false,
      reflectNextAttack: false,
      fallen: true,
      fallenTurn: matchTurn || 0,
      // Nullification rides with the surviving souls; departed entries drop.
      nullifiedSouls: (slot.nullifiedSouls || []).filter(id => keptSoul.includes(id)),
    };
    return "descended";
  }

  // ── FULL RUIN — soul to Necropolis, Treasures cascade ──
  soul.forEach(id => liveNec.push(id));
  liveField.esclings[slotIdx] = {
    ...slot, escendant: null, currentLyf: null, soul: [], soulUsed: {}, heretical: null,  // 🔥 R68
    fallen: false, fallenTurn: null, attackedThisTurn: false, intangible: false,
    mustBeRuinedFirst: false, nullifyNextAttack: false, reflectNextAttack: false,
    nullifiedSouls: [],
  };
  liveField.tomes = riftArray(liveField.tomes, 3);
  liveField.tomes.forEach((t, i) => {
    if (t?.id && t?.wieldedBy === slotIdx) {
      liveNec.push(t.id);
      liveField.tomes[i] = null;
    }
  });
  // ── NECKLACE OF AKULA'S LEDGER ──────────────────────────────
  // "You gain +1 Anima whenever a being is ruined." Player-side that is
  // fireAkulaListenerOnRuin, awaited from four handlers. This function
  // is a SYNCHRONOUS mutator with seven call sites and no await in it,
  // so the ruin is BANKED here and paid at the next await-safe point
  // (flushAkula, called at the end of every attack phase and every tome
  // fire). Without this the card is face-up theatre on the NPC's side
  // of the board while it works perfectly on the human's.
  bankAkula(1);
  return "ruined";
}

export const NPC_IDX = 1;
export const NPC_DIFFICULTIES = {
  BEB:      "Beb",
  CHAMPION: "Champion",
  LORD:     "Lord",
};

// ═════════════════════════════════════════════════════════════
// DEPTHS BRAIN (July 2026) — skill + temperament
// ═════════════════════════════════════════════════════════════
// Scaling is DECISION QUALITY ONLY. The NPC and the player follow the
// same rulebook with no exceptions (canon) — no stat cheats, no free
// resources, no illegal moves. A higher-skill NPC simply makes better
// choices inside the same rules; a temperament biases WHICH good
// choices it prefers, so two opponents at the same ELO play with
// different souls.
//
//   skill        0..1 scalar. Depths opponents derive it from ELO
//                (EndlessDepths.skillFromElo); gatekeepers carry an
//                authored value; everything outside the Depths falls
//                back to TIER_BASELINE_SKILL so Plaza / Story / Dungeon
//                behavior is unchanged by this wave (Beb 0.5 and
//                Champion 0.35 reproduce the pre-wave chance gates;
//                Lord 0.7 turns the prophecy brain ON everywhere —
//                that upgrade is deliberate).
//   temperament  "Reckless" | "Patient" | "Greedy" | "Cold" | absent.
//                Absent = Neutral (pre-wave behavior).
//
// SKILL GATES — where each unlock lives on the ladder:
//   ≥ 0.38  Champion: ascension-line Grimoire Search (top of the
//           Champion ELO band + all gatekeeper Champions)
//   ≥ 0.40  Champion: third soul activation per turn
//   ≥ 0.70  Lord: PROPHECY brain (race its own win condition)
//   ≥ 0.82  Lord: LETHAL FOCUS (a clear, lethal Anima lane is never
//           traded for a being kill — Aenarchist band)
//   ≥ 0.95  Lord: PROPHECY DENIAL (hunts the being that completes the
//           PLAYER's prophecy — Solrend band + Rük)
const SKILL_CHAMP_LINE_SEARCH = 0.38;
const SKILL_CHAMP_SOUL3       = 0.40;
const SKILL_PROPHECY          = 0.70;
const SKILL_LETHAL_FOCUS      = 0.82;
const SKILL_DENIAL            = 0.95;

export const NPC_TEMPERAMENTS = ["Reckless", "Patient", "Greedy", "Cold"];

// Temperament knobs — every field is WIRED, nothing decorative:
//   chaos       flat adjustment to every random-appetite gate
//   luxBias     extra pull toward Luxmoire draws (Impera hunger)
//   animaHunger Champion prefers redirecting to the Anima over chip
//               damage when trained for it
//   zeroRandom  collapses randomness — any appetite ≥ 0.5 becomes
//               certain, below never fires. The Cold ones don't dice.
const TEMPERAMENT_PROFILES = {
  Reckless: { chaos: +0.12, luxBias: 0,     animaHunger: true,  zeroRandom: false },
  Patient:  { chaos: -0.10, luxBias: 0,     animaHunger: false, zeroRandom: false },
  Greedy:   { chaos: 0,     luxBias: +0.25, animaHunger: false, zeroRandom: false },
  Cold:     { chaos: 0,     luxBias: 0,     animaHunger: true,  zeroRandom: true  },
  Neutral:  { chaos: 0,     luxBias: 0,     animaHunger: false, zeroRandom: false },
};

const TIER_BASELINE_SKILL = { Beb: 0.5, Champion: 0.35, Lord: 0.7 };

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));
const lerp = (a, b, k) => a + (b - a) * clamp01(k);
// One gate for every random appetite. Temperament chaos shifts the
// probability; zeroRandom temperaments make it deterministic.
const chance = (p, brain) => {
  const adj = clamp01(p + (brain?.t?.chaos || 0));
  if (brain?.t?.zeroRandom) return adj >= 0.5;
  return Math.random() < adj;
};

// ── One-shot brain handoff ──
// The launch chain is EndlessDepths.handleFight → Plaza onFight →
// App.handleEnterNPC → buildNPCPlayerData, and only the FIRST link
// knows the opponent's ELO/temperament. Rather than threading a new
// argument through three files, the launcher parks the brain here and
// buildNPCPlayerData consumes it (consume-once — a stale brain from an
// aborted launch can at worst mis-tune one match, never leak forever).
// Launchers that never call this get tier baselines: Plaza ranked/free,
// Story, Dungeon bosses, Inner Minerium all play exactly as before.
let _pendingNpcBrain = null;
export function setNextNpcBrain(brain) { _pendingNpcBrain = brain || null; }
function consumePendingNpcBrain() {
  const b = _pendingNpcBrain;
  _pendingNpcBrain = null;
  return b;
}

// Resolve the working brain from the seat-1 object (stamped at build
// time). Reading it off the match means runNPCTurn's signature — and
// App.js — never had to change.
function resolveBrain(npc, difficulty) {
  const skill = clamp01(
    (typeof npc?.npcSkill === "number") ? npc.npcSkill
      : (TIER_BASELINE_SKILL[difficulty] ?? 0.5)
  );
  const temperament = NPC_TEMPERAMENTS.includes(npc?.temperament)
    ? npc.temperament : "Neutral";
  return { skill, temperament, t: TEMPERAMENT_PROFILES[temperament] };
}

// ─────────────────────────────────────────────────────────────
// PACING — single source of truth for every visible delay.
// Tune here if anything feels too fast/slow.
// ─────────────────────────────────────────────────────────────
const ACTION_DELAY    = 750;   // every visible card placement / activation
const POST_RUIN_DELAY = 1200;  // after destroying player being (descent visibility)
const TOME_INTERRUPT  = 1100;  // after activating a tome (interrupt window for L3 response)
const RESURRECT_DELAY = 1300;  // dramatic resurrect from necropolis
const KILL_SHOT_DELAY = 800;   // direct anima attacks
const SEARCH_DELAY    = 950;   // Grimoire/Luxmoire searches (deliberate, readable)
const THINK_TIME = { Beb: 1400, Champion: 2000, Lord: 2800 };

// ─────────────────────────────────────────────────────────────
// COST GATE — canonical helpers (universal rules, mirrors player)
// ─────────────────────────────────────────────────────────────
// Both player and NPC follow the SAME rulebook. Player-side cost
// gating lives in Shatterfield.spendKhao (line 2803). These helpers
// mirror that pattern exactly:
//   1. Read fresh from Firebase (no local state mirror to desync)
//   2. Gate atomically — return false if insufficient, no write
//   3. Deduct via single updatePlayer write
//
// USE THESE for every NPC cost path. Never inline `state.khao -= cost`
// or read a stale snapshot value as the gate. The previous pattern
// (passing a `state` object through helpers) caused Bug F — NPC fired
// Solomon Godfry's Manslaughter at 5 Khao when it cost 8, because the
// local mirror had drifted from the Firebase truth across a long
// sequential turn.
// ═════════════════════════════════════════════════════════════
// THE REBASE LAW (C12 — the chain session, August 2026)
// ═════════════════════════════════════════════════════════════
// The reactive brain moves the NPC's OWN cards from OUTSIDE the turn
// runner — and once the chain was lifted it can do so DURING the NPC's
// own turn (an NPC rebuttal to the player's counter fires inside a
// window the runner is parked on). Every draft the runner took before
// that moment is stale the instant it lands, and the end-of-turn write
// would resurrect the very card the reactor just spent. That hazard is
// the entire reason Wave 2's chain ran one direction only.
//
// The law, in one line: A REACTOR CARD MOVE MARKS THE TURN DIRTY, AND
// EVERY DRAFT RE-BASES FROM FIREBASE BEFORE IT IS WRITTEN AGAIN.
//
// Why a flag and not a re-read everywhere: the runner persists after
// every mutation it makes, so fresh Firebase and a clean draft are the
// same thing — re-reading unconditionally would cost a round trip per
// step to fix a case that occurs only when the brain actually answers.
// The flag makes it exact: zero cost when nothing happened, correct
// when something did. FIELD drafts already re-base at every responded
// window (Wave 4); this covers the HAND, which is held across the whole
// attack phase and written once at the end of the turn.
let npcReactorDirty = false;
const markNpcReactorMove = () => { npcReactorDirty = true; };
// ── THE SAME LAW, FOR THE LUXMOIRE ──────────────────────────────────
// Two Tomes (Shadow Blanket, Light Casting Shadow) tutor an Impera out
// of the LUXMOIRE, and the runners write `luxmoire:` from a turn-start
// local at end of turn — so the tutored Impera walked straight back
// into the Luxmoire while the hand kept a copy. Exactly the hazard the
// REBASE LAW above exists for, on the one pile the tome executor can
// touch and the runners hold a stale draft of.
let npcLuxDirty = false;
async function syncNpcLuxmoire(code, lux) {
  if (!npcLuxDirty) return lux;
  npcLuxDirty = false;
  const fresh = (await getMatch(code))?.players?.[NPC_IDX]?.luxmoire;
  return (fresh && typeof fresh === "object") ? { ...fresh } : lux;
}
async function syncNpcHand(code, hand) {
  if (!npcReactorDirty) return hand;
  npcReactorDirty = false;
  const fresh = (await getMatch(code))?.players?.[NPC_IDX]?.hand;
  return Array.isArray(fresh) ? [...fresh] : hand;
}

// ══════════════════════════════════════════════════════════════
// 🚦 R108 · THE PHASE GATE, IN THE BRAIN (caP'n, 2026-08-26)
// ══════════════════════════════════════════════════════════════
//   *"After main phase is over — playing all your cards, ascending,
//   resurrection, everything — now you declare attack phase, you
//   cannot play any more cards."*
//
// R-UNIVERSAL: the NPC obeys the same phase the player does, out of the
// same rules.js predicate, read through the same enforcer.
//
// ⚠ WHY THIS IS A DECLARATION AND NOT A RESTRICTION HERE. All three
// tier runners were ALREADY ordered Main-then-Attack — every draw,
// Transcend, Ascend, Training, Resurrection, Tome flip, stamp and soul
// activation happens in a numbered step ABOVE the ATTACK step, and
// nothing plays a card after it. So declaring the phase at the top of
// the ATTACK step costs the brain NOTHING it was doing: it writes down
// a commitment it was already keeping. That is the point. Before this,
// the ordering was a CONVENTION of three hand-written runners; now it
// is a fact on the seat that the gate can refuse to let them break.
//
// The declaration is a plain seat write rather than a gate call because
// the brain has nothing to be refused about — it has not acted out of
// order — and a failed write must never abort a turn mid-attack.
async function npcDeclareAttackPhase(code) {
  await updatePlayer(code, NPC_IDX, { phase: PHASE.ATTACK });
}

/** A new Turn opens in the Main phase. Mirrors the renderer's reset. */
async function npcOpenMainPhase(code) {
  await updatePlayer(code, NPC_IDX, { phase: PHASE.MAIN });
}

// ══════════════════════════════════════════════════════════════
// 🜂 THE ROLE ARTS AND EXILIATION, ON THE BRAIN'S SIDE (R111-R119)
// ══════════════════════════════════════════════════════════════
// caP'n ruled the Exile Wave playable at release on 2026-09-13, and the
// player got six doors the same day. This is the other side of them.
//
// ⚠ THE RESOLVER IS SHARED, NOT MIRRORED. Every one of these calls goes
// into `src/roleArts.js` — the same function the radial wedges call,
// through the same gate, charging the same fare and stamping the same
// cadence. Terror has paid for two resolvers before (npcTomeBook vs
// tomeResolver, and six asymmetries found in one audit); a third pair
// was never going to be written.
//
// ⚠ WHAT IS THE BRAIN'S OWN IS ONLY THE JUDGEMENT, and it is narrow on
// purpose. An Art costs 5 of a pool that tops out at 10, so a brain
// that buys one whenever it is legal simply stops playing Terror: it
// would arrive at its attack step with nothing left to Train, Ascend or
// activate. Each rule below is a CONDITION under which the Art pays for
// itself this half-turn, and nothing buys on speculation.
//
//   Sol    — only when a swing this Turn would actually convert. Armed
//            with no attacker that can overkill, 5 Khao buys nothing.
//   Body   — only when the player has a being whose Lyt is exactly the
//            Lyf of one of ours: one point off it is the whole
//            difference between a Fracture and a survivor.
//   Heart  — only when the Necropolis holds a being and the board has
//            somewhere to put it. The Art slips the tally, so it is
//            worth most when the tally is spent — which is exactly when
//            an ordinary Resurrection is refused.
//   Mind   — the Warden nullifies are NOT bought. They arm a standing
//            answer that nothing consumes yet (declared in
//            roleArts.selftest §9), and a brain that pays 5 Khao for an
//            effect with no consumer is a brain losing on purpose. The
//            day the window reads them, this is the line that changes.
//
// Difficulty gates the whole function: a Beb does not know it has Arts.
// ⚠ IT TAKES AND RETURNS THE RUNNER'S LOCALS, AND THAT IS THE REBASE
// LAW, NOT A CONVENIENCE. Each brain holds a turn-start DRAFT of its
// hand, Grimoire and Luxmoire in local variables and writes them back at
// the end of the turn. Two of these Arts put a card in the hand and one
// moves a card out of a pile — straight to Firebase, through the shared
// resolver — so a runner that kept its draft would write the pre-Art
// state back over them.
//
// The first cut of this function returned nothing, and the balancer said
// so in the loudest way it has: 1,411 × H02 (hand over the cap of 8) and
// 16 × G06 (a Grimoire over a card's deck limit) across one batch, where
// the run before it had ZERO of either. Those are not Art bugs — they
// are the stale draft being written back on top of a live board, which
// is exactly the hazard syncNpcHand and syncNpcLuxmoire were written
// for. Returning the rebased locals is the fix, and the hand cap is
// enforced here rather than at the bottom of the turn because R10 is
// draw-THEN-discard and an Art is a draw the runner did not make.
async function npcRoleArts(code, turn, seatCount, difficulty, locals = {}) {
  const same = { hand: locals.hand, grimoire: locals.grimoire, luxmoire: locals.luxmoire };
  if (difficulty === "Beb") return same;
  const m = await getMatch(code);
  const npc = m?.players?.[NPC_IDX];
  const player = m?.players?.[0];
  if (!npc || !player) return same;
  if (!rolesAwake(turn, seatCount)) return same;   // R115 — three full Turns asleep
  let acted = false;

  const ctx = {
    code, playerIdx: NPC_IDX, oppIdx: 0,
    match: m, matchRef: { current: m },
    me: npc, opponent: player,
    updatePlayer,
    // The brain has no toasts. A no-op rather than an absent key, so the
    // resolver never has to ask whether it is talking to a screen.
    showToast: () => {},
    setTargetPicker: () => {},
  };
  const rail = artsRailFor(npc, player, turn, seatCount);
  const legal = (i) => (rail.find((r) => r.rift === i) || {}).legal;
  const mine = npc.field?.esclings || [];
  const theirs = player.field?.esclings || [];

  // ── SOL · only if a swing this Turn would convert a Fracture into a ruin
  if (legal(SOL_RIFT)) {
    const sol = mine[SOL_RIFT];
    const lyt = getCard(sol?.escendant)?.lyt || 0;
    const converts = theirs.some((s) => {
      if (!s?.escendant || s.fallen) return false;
      const lyf = s.currentLyf != null ? s.currentLyf : (getCard(s.escendant)?.lyf || 0);
      return lyf > 0 && TERROR_ART_OVERKILL(lyt, lyf);
    });
    if (converts && !sol?.attackedThisTurn) {
      const r = await performRoleArt(SOL_RIFT, ctx, {});
      if (r?.ok) { acted = true; tomeProbe("roleArt", "sol"); }
    }
  }

  // ── BODY · only if one Lyt off a swing saves one of ours
  if (legal(BODY_RIFT)) {
    const saved = theirs.map((s, i) => ({ s, i })).find(({ s }) => {
      if (!s?.escendant) return false;
      const lyt = getCard(s.escendant)?.lyt || 0;
      return mine.some((mySlot) => {
        if (!mySlot?.escendant) return false;
        const lyf = mySlot.currentLyf != null ? mySlot.currentLyf : (getCard(mySlot.escendant)?.lyf || 0);
        return lyf > 0 && lyt >= lyf && lyt - TANK_LYT_REDUCTION < lyf;
      });
    });
    if (saved) {
      const r = await performRoleArt(BODY_RIFT, ctx, { targetSlotIdx: saved.i });
      if (r?.ok) { acted = true; tomeProbe("roleArt", "body"); }
    }
  }

  // ── HEART · only with a being to return and a reason to return it
  if (legal(HEART_RIFT)) {
    const back = (npc.necropolis || []).find((id) => getCard(id)?.type === "Escendant");
    const room = mine.some((s) => !s?.escendant);
    if (back && room) {
      const r = await performRoleArt(HEART_RIFT, ctx, { cardId: back, level: getCard(back)?.level || 1 });
      if (r?.ok) { acted = true; tomeProbe("roleArt", "heart"); }
    }
  }

  // ── EXILIATION · only when it converts a dead card into a live one.
  // The Necropolis→Necropolis option is the one the brain can always
  // reason about: give up something it will never Resurrect, take back
  // a being of the same level that it can. Anything cleverer is a
  // judgement about a hand it has not drawn yet.
  {
    const nec = npc.necropolis || [];
    const beings = nec.filter((id) => getCard(id)?.type === "Escendant");
    if (beings.length >= 2) {
      const give = nec.find((id) => getCard(id)?.type !== "Escendant") || beings[beings.length - 1];
      const lvl = getCard(give)?.level || 1;
      const take = beings.find((id) => id !== give && (getCard(id)?.level || 1) === lvl);
      if (take) {
        const r = await performExiliation(ctx, {
          optionId: "necro_being", exiledCardId: give, foundCardId: take,
        });
        if (r?.ok) { acted = true; tomeProbe("exiliation", give); }
      }
    }
  }

  // ── THE REBASE, AND THE CAP ──────────────────────────────────
  // Nothing fired → the runner's draft is still the truth and is handed
  // straight back, so a Turn with no Art costs no extra read.
  if (!acted) return same;
  const fresh = (await getMatch(code))?.players?.[NPC_IDX] || {};
  let hand = Array.isArray(fresh.hand) ? [...fresh.hand] : (locals.hand || []);
  hand = await npcEnforceHandCap(code, hand);      // R10 — draw, THEN discard
  return {
    hand,
    grimoire: (fresh.grimoire && typeof fresh.grimoire === "object") ? { ...fresh.grimoire } : locals.grimoire,
    luxmoire: (fresh.luxmoire && typeof fresh.luxmoire === "object") ? { ...fresh.luxmoire } : locals.luxmoire,
  };
}

async function npcSpendKhao(code, amount) {
  if (amount <= 0) return true; // free actions always succeed
  const m = await getMatch(code);
  const cur = m?.players?.[NPC_IDX]?.khao ?? 0;
  if (cur < amount) return false;
  await updatePlayer(code, NPC_IDX, {
    khao: cur - amount,
    khaoSpentThisCycle: true,
  });
  return true;
}

// ⭐ R33 AT THE TILL — found by tools/balance.mjs, 2026-08-20.
//
// The old guard was `if (cur < amount) return false`. Which means that
// when cur EQUALS amount the payment goes through, the NPC's Anima
// lands on exactly 0, and NOTHING ends the Solrend -- because every
// endMatch in the game hangs off an ATTACK resolving, and nobody
// attacked. R33 says "Anima at 0 = loss". The Rulebook says "when your
// Anima reaches 0, you have lost." The till said nothing at all.
//
// The result is a duel with a corpse still taking turns. It does not
// hang -- the next Anima strike floors at 0, sees `<= 0` and ends it --
// so the seat can sit there dead for as long as it keeps a being
// standing to block. The balancer caught 23 of these in 120 games. It
// is not rare and it is not theoretical: GRAVESHIFT COSTS EXACTLY 10
// ANIMA (R41), so any seat on exactly 10 that empties its Grimoire
// walks straight into it, by the rules, doing the correct thing.
//
// ⚠ THE SAME HOLE IS IN Shatterfield.spendAnima AND IS FIXED THERE TOO.
// It was never an NPC bug; it was a bug the NPC happened to hit first
// because it plays ten thousand games an hour.
//
// TWO GUARDS, AND THEY ARE DIFFERENT THINGS:
//   · npcWouldDiePaying — a BRAIN guard. Paying your last Anima for a
//     Primera activation is not a decision, it is an accident, so the
//     brain simply cannot afford it. This is the ONLY reason a brain
//     should ever decline a legal move it can pay for.
//   · the endMatch below — an ENGINE guard, and the load-bearing one.
//     It fires for every OTHER route to zero (a card effect, a self-cost
//     the brain does not route through here, a future call site nobody
//     has written yet), so the rule holds even when the guard above is
//     bypassed. Never delete this one on the grounds that the first one
//     makes it unreachable.
// ════════════════════════════════════════════════════════════════════
// 🏁 npcFinish — THE BRAIN ENDS A SOLREND THE WAY EVERY OTHER SEAT DOES
// ════════════════════════════════════════════════════════════════════
// SEVEN sites in this file called gameManager's raw `endMatch(code, W)`
// directly — the two tills, the two Anima dims, and the kill-shot exit
// of all three turn runners. A raw endMatch does exactly two things:
// writes `status: "finished"` and a `winner`. It does NOT ask:
//
//   • R98 · WHO ELSE WON. caP'n: *"prophecies do not prevent wins
//     merely grant them, the other player can still win naturally or by
//     their own prophecy"*. A seat sitting on a fulfilled, unspent,
//     affordable Prophecy at the instant the duel closed won it too.
//     Every OTHER ending path in the project asks. These seven did not,
//     so whether a co-winner was recorded depended on whether the
//     killing blow happened to land in the brain or in the renderer.
//   • THE FFA / 2v2 KNOCKOUT. `finishOrEliminateSeat` is what knows
//     that a seat hitting 0 KNOCKS THAT SEAT OUT and only ends the
//     table when one side is left. The raw call ends the game for seats
//     that were never involved. The NPC only ever takes a 2-seat chair
//     today — so this is coherence, not a live bug — but "the brain
//     ends duels differently from the renderer" is precisely the school
//     of divergence matchOutcome.js was written to abolish, and it is
//     the reason that file exists at all.
//
// `loserIdx` is ALWAYS passed explicitly. finishOrEliminateSeat's
// default is `getTargetIdx(match, winnerIdx)`, i.e. who the winner is
// currently TARGETING — a different question from who just hit 0, and
// every one of these seven sites knows that answer exactly.
//
// The board is re-read rather than threaded: these calls fire at most
// once per duel, and a runner's `match` is its ENTRY snapshot, taken
// before the very damage that is ending the Solrend.
async function npcFinish(code, winnerIdx, loserIdx) {
  const live = await getMatch(code);
  if (!live) return true;
  if (live.status === "finished") return true;   // somebody already closed the book
  try { await finishOrEliminateSeat(code, live, winnerIdx, loserIdx); }
  catch (e) { console.warn("[NPC] finish failed:", e && e.message); }
  return true;
}

async function npcSpendAnima(code, amount) {
  if (amount <= 0) return true;
  const m = await getMatch(code);
  const cur = m?.players?.[NPC_IDX]?.anima ?? 0;
  if (cur < amount) return false;
  const next = cur - amount;
  // R100 — PARITY WITH THE PLAYER'S TILL. Shatterfield's spendAnima
  // writes the SELF ledger on every fare; this one wrote no ledger at
  // all, which was accidentally right about the Prophecy question and
  // wrong about honesty — the NPC's "what have I bled myself" counter
  // simply did not exist. It does now, and it is the self one, so it
  // still cannot feed a Prophecy.
  await updatePlayer(code, NPC_IDX, { anima: next, ...buildSelfDimUpdate(m?.players?.[NPC_IDX] || {}, amount) });
  // R33 — THE ENGINE GUARD, and it is IDENTICAL to Shatterfield's.
  // ⚠ THE BRAIN GUARD USED TO SIT ON THE LINE ABOVE THIS ONE AND THAT
  // WAS TWO BUGS AT ONCE (both found by the self-review pass, both
  // introduced earlier the same night):
  //   · It made this endMatch UNREACHABLE — a refusal above it meant
  //     `next <= 0` could never be true — while the banner claimed the
  //     opposite and told the next reader never to delete it.
  //   · It BROKE R-UNIVERSAL in the direction that matters. A human on
  //     exactly 10 Anima who Graveshifts loses on the spot; the NPC
  //     refused to pay, kept its Anima, and drew nothing for the rest
  //     of the duel. The fix for "the NPC shouldn't kill itself"
  //     accidentally gave it an immortality the player does not have.
  //   · And it opened a HALF-PAID window: the callers pre-check Anima
  //     with `<` and pay Khao FIRST, so at anima === cost every
  //     pre-check passed, Khao was deducted, and then this refused.
  //     Khao gone, nothing gained, repeat next turn.
  // The brain guard now lives in the CALLERS' affordability tests,
  // which is where "should I" belongs — this is "may I", and the rule
  // says yes and then you lose.
  if (next <= 0) return npcFinish(code, 0, NPC_IDX);
  return true;
}

// "Can I afford this without it killing me?" — the BRAIN guard, used by
// the callers that decide whether to spend, never by the tills that do
// the spending. See the banner on npcSpendAnima for why that split
// exists and what it cost to learn.
// eslint-disable-next-line no-unused-vars -- read by the pre-checks as
// a concept; kept as a named function so the day caP'n rules a being
// SHOULD be allowed to spend its last breath, it is one line and one
// place rather than six inline comparisons.
function npcWouldDiePaying(current, amount) { return current - amount <= 0; }

// ── AWAKENING: Khao-else-Anima payment (mirror of handleResurrect) ──
// The player's Resurrection and Grimoire Search costs are payable in
// either currency; the player-side pattern is spendKhao first, and on
// failure deduct Anima (if it covers the cost). Same order here.
async function npcPayKhaoElseAnima(code, amount) {
  if (amount <= 0) return true;
  if (await npcSpendKhao(code, amount)) return true;
  const m = await getMatch(code);
  const cur = m?.players?.[NPC_IDX]?.anima ?? 0;
  // R96 — THE TILL LAW, and it is asked with the SHARED predicate on
  // purpose. `cur - amount < 0` admitted the exact-zero payment the
  // player's till (Shatterfield.spendAnima -> canPayVoluntary) refuses,
  // so an NPC on exactly 4 Anima could search its Grimoire and die of a
  // fare. Two seats, one predicate.
  if (!canPayVoluntary(cur, amount)) return false;
  // R33 AT THE TILL — the second door onto the same hole. `cur - amount
  // < 0` let an exact-zero payment through here too, and this is the
  // path Resurrection and Grimoire Search take, which are the two most
  // common Anima spends in the game.
  // ⚠ The BRAIN guard that used to sit here moved to the callers for
  // the three reasons on npcSpendAnima. What is left is the ENGINE
  // guard, and it is what the player's till does.
  const next = cur - amount;
  // R100 — a Resurrection / Grimoire-search fare is voluntary. SELF.
  await updatePlayer(code, NPC_IDX, { anima: next, ...buildSelfDimUpdate(m?.players?.[NPC_IDX] || {}, amount) });
  if (next <= 0) return npcFinish(code, 0, NPC_IDX);
  return true;
}

// ── R79 + R107 · THE STAMP FARE — ONE POOL, NEVER BOTH ──────
// Mirror of Shatterfield.payPrimeraCost. caP'n, 2026-08-26:
//
//   *"no mixing for any reason at all unless the card tells you, like
//   U'hltis etc."*
//
// ⚠ THIS FUNCTION EXISTED ONLY TO MIX, AND ITS OWN BANNER SAID SO: "the
// Primera fare is the ONLY cost in the game that may be split across
// both pools, so it gets its own till rather than riding
// npcPayKhaoElseAnima (which is either/or and would decline a fare the
// rules say is payable)". The rules no longer say it is payable. The
// either/or helper was right all along.
//
// It is KEPT rather than deleted and re-pointed at npcPayKhaoElseAnima,
// for two reasons: the call site reads as a stamp fare and should keep
// saying so, and `farePoolsFor` is asked here explicitly so the brain's
// affordability answer comes out of the same function the renderer and
// the gate use. Order of preference is Khao, because Anima is a life
// total and R96 says a voluntary payment may never take it to zero.
//
// R96 is carried INSIDE farePoolsFor on the Anima leg, and the whole
// decision is made BEFORE a single coin leaves the till — the half-paid
// window that cost this file two bugs (see npcSpendAnima) cannot open
// when only one pool is ever touched.
async function npcPayPrimeraFare(code, amount) {
  if (amount <= 0) return true;
  const m = await getMatch(code);
  const live = m?.players?.[NPC_IDX] || {};
  const pools = farePoolsFor(amount, { khao: live.khao ?? 0, anima: live.anima ?? 0 });
  if (pools.length === 0) return false;               // R107 — neither pool alone
  if (pools.includes("khao")) return npcSpendKhao(code, amount);
  return npcSpendAnima(code, amount);
}

// ═════════════════════════════════════════════════════════════
// ⭐ CURIOS (R75) — THE FREE VALUE THE BRAIN WAS THROWING AWAY
// ═════════════════════════════════════════════════════════════
// Mirror of Shatterfield's activateCuriosForPlayer (~4703). Two Curios
// are PRE-PLACED on every seat before the duel begins (R05) and every
// one of the nine is a flat, unconditional, once-per-duel gift: up to
// +3 Anima, or +1 Max Khao. The player's client fires them from an
// onValue listener — which means they only exist where a Shatterfield
// screen is mounted. NPC.js had no path to them at all, so a brain
// driven by anything else (the harness, a future headless mode) simply
// forfeited them, and the audit's "free value, every duel" was literal.
//
// `curiosActivated` is the same idempotence flag the player side sets,
// so the two implementations can both run against the same match
// without double-firing. That is deliberate: this is a SECOND door onto
// one effect, not a competing copy.
// The table itself moved to rules.CURIO_GIFTS on 2026-09-15 — it was the
// second of two hand-typed copies (Shatterfield held a switch), and two
// copies of one number is one drift waiting. Imported below with the rest
// of the catalogue; the door here is unchanged.

async function activateNpcCurios(code) {
  const m = await getMatch(code);
  const live = m?.players?.[NPC_IDX];
  if (!live || live.curiosActivated) return false;
  const curios = (live.field?.curios || []).filter(Boolean);
  let animaBonus = 0, khaoBonus = 0;
  curios.forEach(id => {
    const gift = CURIO_GIFTS[id];
    if (!gift) return;
    animaBonus += gift.anima || 0;
    khaoBonus  += gift.khao  || 0;
  });
  const updates = { curiosActivated: true };
  if (animaBonus > 0) {
    updates.anima = sealAnimaGain((live.anima || STARTING_ANIMA), animaBonus);
    Object.assign(updates, buildAnimaGainUpdate(live, animaBonus));
  }
  if (khaoBonus > 0) {
    // R13 — cards may push the ceiling to MAX_KHAO_ABS; the natural
    // climb's own stop at 10 is the end-of-turn line, not this one.
    updates.maxKhao = Math.min(MAX_KHAO_ABS, (live.maxKhao || STARTING_KHAO) + khaoBonus);
    updates.khao    = Math.min(MAX_KHAO_ABS, (live.khao    || STARTING_KHAO) + khaoBonus);
  }
  await updatePlayer(code, NPC_IDX, updates);
  return animaBonus > 0 || khaoBonus > 0;
}

// Mirrors Shatterfield.isTomePersistent (line 5322). Persistent tomes
// stay on the field and can be re-activated; non-persistent ruin to
// necropolis after firing once.
const FIELD_PERSISTENT_TOMES = new Set([
  "tome_khao_duhmdumn_beb", "tome_khao_duhmdumn_champion", "tome_khao_duhmdumn_lord",
  "tome_lightning_blade", "tome_gauntlets_azka",
  "tome_necklace_akula",
  "tome_necklace_death", "tome_necklace_courage",
  "tome_restraining_ordeal", "tome_magma_carta", "tome_king_of_the_castle",
]);
function isTomePersistent(card) {
  if (!card) return false;
  const tag = card.effectTag;
  if (tag === "OPT" || tag === "OP2" || tag === "OP3") return true;
  if (tag === "ATV") return true;
  if (FIELD_PERSISTENT_TOMES.has(card.id)) return true;
  return false;
}

// Push a non-persistent tome to necropolis after it fires (Bug A fix).
// Mirrors player-side handlePlayTome lines 5372-5373.
//
// ⚠ THIS IS THE LIFECYCLE EXIT, NOT A RUIN, and the distinction is the
// whole reason `ruinNpcTomeSlot` below exists beside it. A one-shot Tome
// leaving the field because it has finished resolving is not being
// RUINED by anything, so it asks about no shield and no intangibility —
// correctly. Use it only for that. Anything that RUINS one of the
// brain's own Tomes — Seize and Disease's symmetric price, a future
// self-cost — must go through the twin.
async function ruinNpcTome(code, slotIdx, cardId) {
  await updatePlayer(code, NPC_IDX, { [`field/tomes/${slotIdx}`]: null });
  const m = await getMatch(code);
  const nec = [...((m?.players?.[NPC_IDX]?.necropolis) || []), cardId];
  await updatePlayer(code, NPC_IDX, { necropolis: nec });
}

// (function applySolomonGodfryEffect removed 2026-09-15 — the brain's second copy of resolver logic; the ONE resolver in tomeResolver.js owns it now)

// ─────────────────────────────────────────────────────────────
// CORE HELPERS
// ─────────────────────────────────────────────────────────────
function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function drawFromPool(pool) {
  const cards = [];
  Object.entries(pool || {}).forEach(([id, qty]) => { for (let i = 0; i < qty; i++) cards.push(id); });
  return cards.length ? pickRandom(cards) : null;
}

function drawAndDeplete(pool) {
  const card = drawFromPool(pool);
  if (!card) return { card: null, newPool: pool };
  const newPool = { ...pool };
  if (newPool[card] > 1) newPool[card]--; else delete newPool[card];
  return { card, newPool };
}

function canAscendNatural(currentCard, nextCard) {
  if (!currentCard || !nextCard) return false;
  return nextCard.type === "Escendant" &&
    nextCard.level === currentCard.level + 1 &&
    nextCard.school === currentCard.school &&
    nextCard.ilk === currentCard.ilk;
}

// ── AWAKENING: shared-trait mirrors (Shatterfield lines 260-278) ──
// sharedTraits counts School + Ilk matches (0, 1, or 2).
//
// ⚠ WITH TODAY'S CARDS IT CAN ONLY RETURN 0 OR 2 — NEVER 1. src/cards.js
// pairs Ilk and School one-to-one across the whole shipped set (every
// Humanoid is Space, every Beast is Phantom, every Machine is Time,
// every Dragon is Dragon), so a School match implies an Ilk match and
// vice versa. The "1 trait shared" column of the Deviant fare, and
// Deviant Transcending itself (School matches, Ilk does not), are both
// unreachable until a card crosses the pairing. Encoded anyway — it is
// the designer's schedule and it costs nothing to carry — but nobody
// should read this and believe the cheap column is doing work.
function sharedTraits(cardA, cardB) {
  if (!cardA || !cardB) return 0;
  let traits = 0;
  if (cardA.school === cardB.school) traits++;
  if (cardA.ilk === cardB.ilk) traits++;
  return traits;
}

// ── R101 · DELETED: the third copy of the Deviant fare. It was
// `deviantAscendCost(traits, level)`, a hand-kept mirror of a
// Shatterfield helper that has also been deleted. rules.deviantFare is
// the one table now, and a mirror that cannot drift is better than a
// mirror with a comment asking you not to let it.
//
// What is left here is the PAYER, because the fare has two numbers:
// Rulez v2 ch.15's L3 / 0-trait cell is 5 KHAO OR 4 ANIMA, so
// npcPayKhaoElseAnima's single `amount` could not spend it correctly —
// it would have charged 5 Anima on the fallback leg. Khao first, Anima
// for the remainder, same order as every other either/or fare, and R96
// still guards the Anima leg.
async function npcPayDeviantFare(code, fare) {
  if (!fare || (fare.khao <= 0 && fare.anima <= 0)) return true;
  if (await npcSpendKhao(code, fare.khao)) return true;
  const live = (await getMatch(code))?.players?.[NPC_IDX] || {};
  if (!canPayVoluntary(live.anima ?? STARTING_ANIMA, fare.anima)) return false;  // R96
  return npcSpendAnima(code, fare.anima);
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: EFFECTIVE STAT MIRRORS
// Full mirrors of Shatterfield.getEffectiveLytPure (line 198) and
// getEffectiveLyfPure (line 235). Pure functions of (slot, fields) —
// no React, no Firebase. The one intentional difference: the player
// version resolves the slot index by object identity (s === slot),
// which breaks after spreads; the NPC version takes slotIdx explicitly.
// If a new passive aura lands in Shatterfield, mirror it HERE too.
// ─────────────────────────────────────────────────────────────
// Counts beings by SCHOOL (Space / Phantom / Dragon / Time). Named for
// the Ilk axis until 2026-08-25; every caller has always passed a
// Family-B word, and `card.school` is now where those words live.
function countBeingsOfSchool(field, cls) {
  return (field?.esclings || []).filter(s => { const c = getCard(s?.escendant); return c && c.school === cls; }).length;
}

function effLytPure(slot, slotIdx, myField, oppField = null) {
  const card = getCard(slot?.escendant);
  if (!card) return 0;
  let lyt = card.lyt || 0;
  // Gauntlets of A'zka bonded to this slot (BUG #2 FIX, preserved)
  const hasGauntletsBonded = riftArray(myField?.tomes, 3).some(t =>
    t?.id === "tome_gauntlets_azka" && t?.wieldedBy === slotIdx && !t?.faceDown
  );
  if (hasGauntletsBonded) lyt += 2;
  // Legacy soul-slot Gauntlets (pre-Wave-1 fallback, parity with Shatterfield)
  if ((slot?.soul || []).some(id => id === "tome_gauntlets_azka")) lyt += 2;
  // Premier Dragon Beb — +1 Lyt per Dragon Being (own field only)
  if (card.id === "premier_dragon_beb") {
    if ((slot?.soul || []).some(id => id === "p_premier_dragon_1")) lyt += countBeingsOfSchool(myField, "Dragon");
  }
  // Premier Dragon Champion — +1 Lyt per Dragon Being (BOTH fields)
  if (card.id === "premier_dragon_champion") {
    if ((slot?.soul || []).some(id => id === "p_premier_dragon_2")) {
      lyt += countBeingsOfSchool(myField, "Dragon");
      lyt += countBeingsOfSchool(oppField, "Dragon");
    }
  }
  // Z4Z Lord U'hlti Apocalypse Now — +1 Lyt per Time Being (BOTH fields)
  if ((slot?.soul || []).some(id => id === "u_z4z_lord")) {
    lyt += countBeingsOfSchool(myField, "Time");
    lyt += countBeingsOfSchool(oppField, "Time");
  }
  // ⭐ THE PRIMERA STAMP — same omission as effLyfPure below, same
  // one-line fix, same source line in Shatterfield.getEffectiveLytPure.
  lyt += slot?.primeraLytBuff || 0;
  return lyt;
}

function effLyfPure(slot, myField) {
  const card = getCard(slot?.escendant);
  if (!card) return slot?.currentLyf ?? 0;
  let lyf = slot?.currentLyf !== null && slot?.currentLyf !== undefined ? slot.currentLyf : (card.lyf ?? 0);
  // Xulan Wanderer Diamond Skin — +2 Lyf passive
  if ((slot?.soul || []).some(id => id === "p_xulan_1")) lyf += 2;
  // Cosmic Casbah — TEAM AURA: every friendly being gains +1 Lyf per Astro Being
  const anyCosmicCasbah = (myField?.esclings || []).some(s =>
    (s?.soul || []).some(id => id === "p_h09_1")
  );
  if (anyCosmicCasbah) lyf += countBeingsOfSchool(myField, "Space");
  // Premier Dragon Beb — +1 Lyf per Dragon Being (when impera attached)
  if (card.id === "premier_dragon_beb") {
    if ((slot?.soul || []).some(id => id === "p_premier_dragon_1")) lyf += countBeingsOfSchool(myField, "Dragon");
  }
  // ⭐ THE PRIMERA STAMP. Shatterfield.getEffectiveLyfPure ends with
  // exactly this line and this mirror stopped one line short, so
  // `primeraLyfBuff` appeared ZERO times in a 222KB file whose whole
  // job is to mirror that one. A player who paid Khao or Anima for the
  // School-Dragon stamp got +1 Lyf that the NPC's targeting could not
  // see: it read the being as weaker than it is, attacked into it, and
  // the board disagreed with the brain. R-UNIVERSAL, and the rule that
  // should have caught it is PRIME's own "grep the NPC for its mirror".
  lyf += slot?.primeraLyfBuff || 0;
  return lyf;
}

// Effective vulnerability — mirrors the player-side gate: a being is
// vulnerable (ruinable) only when its EFFECTIVE Lyf is 0, not its raw
// currentLyf. A Diamond Skin being at currentLyf 0 still shows Lyf 2.
function isVulnerable(slot, field) { return effLyfPure(slot, field) === 0; }

// ⚠ THIS USED TO BE A PRIVATE FOURTH COPY OF THE WALL RULE, under a
// banner two hundred lines below forbidding exactly that (doAttacks, "a
// private fourth copy here is exactly the compensation-instead-of-a-fix
// trap this repo has written down"). It predated R119, so it counted a
// Warden as a wall: the Lord declined legal lethal pushes and the stamp
// chooser mispriced every board whose only survivor stood in the Mind
// rift. One predicate, asked by everyone.
function playerAnimaClear(playerField, turn, seatCount = 2) {
  return animaOpenToLytAttack(playerField?.esclings || [], turn, seatCount);
}

function standingBeings(field) {
  return (field?.esclings || [])
    .map((s, idx) => ({ s, idx }))
    .filter(({ s }) => s?.escendant && !s?.fallen);
}

// ── AWAKENING: effective total Lyt — fields, not raw arrays ──
// Signature change: (field, oppField) instead of (esclings), so the
// Gauntlets bond / cross-field auras are counted in kill math.
function totalAvailableLyt(field, oppField = null) {
  return (field?.esclings || []).reduce((sum, slot, idx) => {
    if (!slot?.escendant || slot.attackedThisTurn || slot.fallen) return sum;
    return sum + effLytPure(slot, idx, field, oppField);
  }, 0);
}

// ── BUG #2 FIX: effective Lyt for an NPC being, including Gauntlets bond ──
// Now a thin wrapper over the full effLytPure mirror (AWAKENING).
// `field` is the NPC's own field; `slotIdx` is the attacker's escling
// slot; `oppField` (optional) enables the cross-field auras.
function getNpcEffectiveLyt(field, slotIdx, oppField = null) {
  const slot = field?.esclings?.[slotIdx];
  if (!slot?.escendant) return 0;
  return effLytPure(slot, slotIdx, field, oppField);
}

function highestLevel(esclings) {
  return esclings.reduce((max, s) => {
    const lv = getCard(s?.escendant)?.level || 0;
    return lv > max ? lv : max;
  }, 0);
}

function canAscendFromPool(slot, pool) {
  const cur = getCard(slot?.escendant);
  if (!cur || cur.level >= 3) return false;
  return Object.keys(pool || {}).some(id => {
    const c = getCard(id);
    return c && canAscendNatural(cur, c);
  });
}

function threatScore(player, playerField) {
  let score = 0;
  const beings = standingBeings(playerField);
  beings.forEach(({ s, idx }) => {
    const c = getCard(s.escendant);
    if (!c) return;
    score += effLytPure(s, idx, playerField) * 2;
    score += (c.level || 1) * 5;
    if (c.level === 3) score += 15;
    if ((s.soul || []).length >= 4) score += 20;
  });
  score += Math.max(0, 30 - (player.anima || 30));
  return score;
}

// ⭐ R31 LIVES HERE NOW. This summed the PLAYER'S ENTIRE Lyt against
// the NPC's Anima with no check that any of it could actually reach
// that Anima — but "Anima is attackable only if ALL opposing Escendants
// are gone or Fallen" means a single standing being of the NPC's own
// makes the whole sum unreachable. The Lord read a rules-IMPOSSIBLE
// threat, flipped to SURVIVAL (baseMode, turnsToMyDeath <= 1), and
// SURVIVAL is what switches off the ascension-line search, the Luxmoire
// search and the aggressive tome chooser. Panic, on a board where
// nothing could touch it.
//
// The exceptions are real and both are modelled: a being carrying
// canDirectAnima / animaRedirectAll strikes past the wall, and a
// Fallen wall is no wall at all (R26).
function turnsUntilDeath(npcAnima, playerField, npcField = null, turn, seatCount = 2) {
  // R119 — same predicate as everywhere else. A lone standing Warden of
  // the NPC's own is not a wall once the Arts wake, so reading it as one
  // let the Lord think itself safe and skip SURVIVAL when it was not.
  const wallStands = !animaOpenToLytAttack(npcField?.esclings || [], turn, seatCount);
  const reaching = (playerField?.esclings || []).reduce((sum, slot, idx) => {
    if (!slot?.escendant || slot.attackedThisTurn || slot.fallen) return sum;
    // Behind a standing wall only a redirect-trained being reaches Anima.
    if (wallStands && !slot.canDirectAnima && !slot.animaRedirectAll) return sum;
    return sum + effLytPure(slot, idx, playerField, npcField);
  }, 0);
  if (reaching <= 0) return 99;
  return Math.ceil(npcAnima / reaching);
}

// ─────────────────────────────────────────────────────────────
// TOME CATEGORIES — NOW A LOOKUP, NOT FOUR HAND-KEPT LISTS
// ─────────────────────────────────────────────────────────────
// ⚰ WHAT USED TO BE HERE, and why it is gone:
//
//   GOOD_TOMES, AGGRESSIVE_TOMES, SSG_TOMES, NPC_KNOWN_TOME_EFFECTS
//   and the two-entry NPC_CANNOT_USE_TOMES blacklist — five disjoint
//   arrays of card ids, each maintained by hand, none of them aware of
//   the other four or of the dispatcher they claimed to describe. The
//   banner over NPC_KNOWN_TOME_EFFECTS said, in capitals, "THIS SET IS
//   HAND-MAINTAINED AND MUST GROW WITH THE DISPATCHER." It did not.
//   AGGRESSIVE_TOMES listed "tome_omnis_corruption_jet" — an id that
//   has never existed in cards.js — so Omnis' Corruption Jet, the one
//   Tome that tears an Impera or a U'hlti off a Supreme Being, was
//   unreachable on the aggressive lane for the entire life of the file.
//   Nothing caught it because nothing was ever asked to.
//
// src/npcTomeBook.js now holds ONE row per Tome and ASSERTS AT LOAD
// that its row set and cards.js's Tome set are the same set, in both
// directions. A typo'd id cannot survive an import. That is the fix for
// the drift; these four wrappers are all that is left of the lists.
//
// ⚠ DO NOT ADD A FIFTH LIST HERE. If the brain needs to know something
// new about a Tome, it is a FIELD ON THE ROW, and then it is exhaustive
// by construction instead of exhaustive by somebody remembering.
const npcTomeIsSSG   = (card) => tomeMode(card?.id) === "ssg" || !!card?.ssg;
const npcTomeIsGood  = (card) => tomeMode(card?.id) === "good";
const npcTomeIsAggro = (card) => tomeMode(card?.id) === "aggro";
// "Utility" is the searches, tutors and draws: real plays, but plays a
// Beb does not make. Champion and Lord do.
const npcTomeIsUtility = (card) => tomeMode(card?.id) === "utility";

// ─────────────────────────────────────────────────────────────
// ⭐ NEVER PAY FOR NOTHING — THE ONE GATE, AND NOW IT IS HONEST
// ─────────────────────────────────────────────────────────────
// The old gate said a Tome was worth its fare if the dispatcher knew
// it OR it carried a persistent tag, "because a persistent field piece
// whose value is simply STANDING there is real even when the fire step
// is a no-op." That is true of a taunt wall. It is FLATLY FALSE of the
// nine Ritual Tomes, which are pure activated searches with an OPT/OP2
// tag and no standing value whatsoever — and of Weeping Dagger, Boots
// of Wing Walking, Chief Calidum, Sword of Shadows, Magma Carta and
// King of the Castle. Fifteen cards. The brain paid 9 Khao for
// Mausoleum of Omnis every two turns, forever, against an EMPTY
// Necropolis, and this file's own law two hundred lines up says it must
// never do that.
//
// The gate is now the row's own want(): a number when firing changes
// the board in the NPC's favour, null when it does not. An empty
// Necropolis makes every Ritual Tome null. A bare adversary board makes
// every ruin Tome null. Solomon Godfry with nothing to kill on the
// other side is null, because it is a forced sacrifice of my OWN being
// for no return — the brain used to fire that one on an empty board.
//
// `V` is the board view (npcTomeBook.buildView). Callers that already
// hold a fresh match read pass one in; the rest is a round trip.
function npcTomeWant(card, V) {
  if (!card?.id || !V) return null;
  return tomeWant(card.id, V);
}
function npcTomeIsWorthPaying(card, V) {
  return npcTomeWant(card, V) !== null;
}

// Build the board view a want() reads. ONE match read, no writes.
async function buildTomeView(code, turn, slotIdx = -1, tomeEntry = null) {
  const m = await getMatch(code);
  return buildTomeViewFrom(m, turn, slotIdx, tomeEntry);
}
function buildTomeViewFrom(m, turn, slotIdx = -1, tomeEntry = null) {
  return buildView({
    turn: turn ?? (m?.turn || 0),
    me: m?.players?.[NPC_IDX] || {},
    opp: m?.players?.[0] || {},
    slotIdx,
    tome: tomeEntry,
    // R103 — a duration written in FULL turns needs the table width to
    // become half-turns. Carried here rather than looked up inside the
    // step, so the view is the single thing a step consults.
    seatCount: m?.seatCount || 2,
  });
}

// ── THE PER-TOME PROBE ──────────────────────────────────────────────
// tools/balance.mjs asks a question this file could not previously
// answer: of the 72 Tomes, how many did the brain PLAY, how many
// actually CHANGED STATE when they fired, and how much Khao went into
// fires that changed nothing. The harness instruments function names
// mechanically; a card id is not a function name, so the book reports
// itself through this one hook. In production `globalThis.__TOME_PROBE__`
// is undefined and this is a dead branch — the shipped brain is
// unaffected, exactly like the harness's function probe.
/* eslint-disable no-undef -- globalThis is a real ES2020 global; this
   project's eslint-config-react-app preset predates it in its globals
   list. Feature-detected below on purpose (see comment above), not a
   typo for `window`. */
const tomeProbe = (event, id, extra) => {
  if (typeof globalThis !== "undefined" && globalThis.__TOME_PROBE__) {
    try { globalThis.__TOME_PROBE__(event, id, extra); } catch (_) { /* never break a duel to measure it */ }
  }
};
/* eslint-enable no-undef */

// ─────────────────────────────────────────────────────────────
// AWAKENING: SHARED EFFECT UTILITIES (soul dispatcher backbone)
// ─────────────────────────────────────────────────────────────

// Dim the PLAYER's Anima by `amount`, with prophecy-tracking parity
// (buildAnimaLossUpdate — the player side always writes these trackers;
// the NPC's bare writes never did). Returns true if the player LOST
// (anima hit 0) — in which case the match has been ended already.
//
// R100 — ADVERSARIAL by definition: this is the brain reaching across
// the table. It feeds the Prophecy ledger and it should.
async function dimPlayerAnima(code, amount) {
  if (amount <= 0) return false;
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const next = Math.max(0, (p.anima ?? 30) - amount);
  await updatePlayer(code, 0, { anima: next, ...buildAnimaLossUpdate(p, amount) });
  if (next <= 0) {
    if (LOCK) {
      const r = LOCK.get(code);
      const esc = m?.players?.[NPC_IDX]?.field?.esclings || [];
      (r.pressed || []).filter(x => x.turn === (m?.turn || 0)).forEach(x => {
        const raw = esc[x.slot];
        if (raw?.escendant) { r.p04ok = (r.p04ok || 0) + 1; if (String(raw.escendant).includes("~")) r.p04sealed = (r.p04sealed || 0) + 1; }
        else r.p04real = (r.p04real || 0) + 1;
      });
    }
    return npcFinish(code, NPC_IDX, 0);
  }
  return false;
}

// Dim the NPC's OWN Anima. Returns true if the NPC LOST.
//
// ⚠ THIS ONE HELPER SERVES TWO KINDS OF DAMAGE AND R100 SPLITS THEM.
// Most callers are SELF-COSTS — Eternal Nightmare, Relentless,
// Apocalypse, Law and Order, Restless, Equivalent Exchange, Pay Day, a
// Treasure feed, a Devotion, a tome-book selfDim step. But two callers
// are the Sweet Dreams / Fate Maker parity paths, where the PLAYER's
// card is doing the dimming and the loss is genuinely adversarial —
// Fate Maker can kill the NPC, same as it can kill a human.
//
// So the caller says which, and it says it in words. The default is
// SELF because the overwhelming majority of the call sites are costs,
// and because over-counting the NPC's Prophecy ledger is the failure
// this rule exists to stop.
async function dimNpcAnima(code, amount, kind = "self") {
  if (amount <= 0) return false;
  const m = await getMatch(code);
  const p = m?.players?.[NPC_IDX] || {};
  const next = Math.max(0, (p.anima ?? 30) - amount);
  const ledger = kind === "adversary"
    ? buildAnimaLossUpdate(p, amount)   // R100 — their card, their damage
    : buildSelfDimUpdate(p, amount);    // R100 — the NPC's own hand
  await updatePlayer(code, NPC_IDX, { anima: next, ...ledger });
  if (next <= 0) return npcFinish(code, 0, NPC_IDX);
  return false;
}

// ── R96 · THE TILL LAW, ON THE BRAIN'S SIDE OF THE TABLE ──────
// caP'n, 2026-08-26: *"It is a payment and yes you cannot end yourself
// that way."* R-UNIVERSAL says the NPC plays the game the player
// plays, so a card whose printed cost is a self-dim must be REFUSED to
// the brain on exactly the terms it is refused to a human. The
// tome-book's `selfDim` step, execFeed and the Devotion loop already
// asked canPayVoluntary; the seven soul-dispatcher self-costs below
// did not, which meant the NPC could activate Apocalypse at 8 Anima
// and quietly sit on 0. Mirror of Shatterfield's tillRefusesSelfDim.
async function npcTillRefusesSelfDim(code, amount) {
  if (amount <= 0) return false;
  const live = (await getMatch(code))?.players?.[NPC_IDX] || {};
  return !canPayVoluntary(live.anima ?? STARTING_ANIMA, amount);
}

// Mirror of Shatterfield.dimAllOppLyf — dims raw currentLyf on every
// player CARD (floors at 0). One write, one pause at call site.
//
// ⚠ IT DIMMED BEINGS ONLY, AND "CARDS" IS A DIFFERENT WORD. Lightning
// Blade prints *"when the wielder Lyt Attacks, dim the Lyf of your
// adversary's cards by 1"*, and R123 is caP'n settling that exact noun:
// *"Cards is general and Beings is specific, written and chosen
// specifically for those purposes."* So the sweep reaches the Tome rifts
// too — Treasures, the Khao D'uhmdumns, anything with printed Lyf — and
// the board's dimAllOppLyf has always done both. The brain was the
// narrow side: the same Blade, on the same board, dimmed less when the
// NPC swung than when the player did.
//
// ⚠ AND IT REPORTED A NO-OP AS A STATE CHANGE. `changed = true` fired
// for a being already sitting on 0 Lyf, so a sweep over a board of
// Fractured beings wrote the field and told its caller something moved.
// The caller pauses and narrates on that boolean. `cur > 0` is the
// board's own guard, and the Tome leg needs it twice over: a Tome with
// no printed Lyf (most of them) has nothing to dim at all.
async function dimAllPlayerLyf(code, amount) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  let changed = false;
  (f.esclings || []).forEach((s, i) => {
    if (!s?.escendant) return;
    const card = getCard(s.escendant);
    const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (card?.lyf ?? 0);
    if (cur > 0) {
      f.esclings[i] = { ...s, currentLyf: Math.max(0, cur - amount) };
      changed = true;
    }
  });
  // The Tome rifts — R123's "cards", mirroring Shatterfield.dimAllOppLyf.
  f.tomes = riftArray(f.tomes, 3);
  (f.tomes || []).forEach((t, i) => {
    if (!t?.id) return;
    const c = getCard(t.id);
    const baseLyf = c?.lyf ?? 0;
    if (baseLyf <= 0) return;   // nothing printed to dim
    const cur = t.currentLyf !== null && t.currentLyf !== undefined ? t.currentLyf : baseLyf;
    if (cur > 0) {
      f.tomes[i] = { ...t, currentLyf: Math.max(0, cur - amount) };
      changed = true;
    }
  });
  if (changed) await updatePlayer(code, 0, { field: f });
  return changed;
}

// Ruin one of the PLAYER's face-up tomes by slot index, honoring the
// D'uhmdumn Lord's nullifyFirstRuin soak (the same soak section 10's
// offensive tome destruction respects). MUTATES nothing — reads fresh,
// writes fresh. Returns "ruined" | "soaked" | null.
async function ruinPlayerTomeSlot(code, slotIdx) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  f.tomes = riftArray(f.tomes, 3);
  const t = f.tomes[slotIdx];
  if (!t?.id) return null;
  // R125 — an Intangible Treasure cannot be ruined. Asked FIRST, before
  // the one-shot shield, because a window must not eat a shield. Mirrors
  // tomeResolver.attemptRuinOppTome exactly; if one moves, both move.
  if (tomeIsIntangible(t, m?.turn ?? 0, m?.seatCount ?? 2)) return "intangible";
  if (t.nullifyFirstRuin) {
    f.tomes[slotIdx] = { ...t, nullifyFirstRuin: false };
    await updatePlayer(code, 0, { field: f });
    return "soaked";
  }
  const id = t.id;
  f.tomes[slotIdx] = null;
  await updatePlayer(code, 0, { field: f, necropolis: [...(p.necropolis || []), id] });
  return "ruined";
}

// ═════════════════════════════════════════════════════════════
// SEQUENTIAL ACTION HELPERS — each places/activates ONE card,
// writes to Firebase, sleeps, returns true/false (did something).
// Designed to be called in `while(...)` loops by the turn runners.
// ═════════════════════════════════════════════════════════════

// ── Place ONE Lvl 1 escendant into ONE empty slot. ──
// Returns updated { hand, field, placed: true/false }.
//
// AWAKENING LEGALITY FIX (R16): Transcend requires the L1's SCHOOL to
// match the Escling's school on BOTH paths — Shatterfield.handleTranscend:
// "A pure school mismatch is illegal entirely." Natural additionally
// requires the Ilk to match (free); Deviant Transcend (school match, ilk
// differ) is also free but marks the being permanently Deviant. The old
// code placed ANY L1 into the slot — an illegal move the player could
// never make. Beb now picks randomly among LEGAL options only.
async function placeOneEscendant(code, hand, field, preferNatural) {
  const esclings = riftArray(field.esclings, ESCLING_SLOTS);   // R110

  // Find first empty slot
  const slotIdx = esclings.findIndex(s => !s?.escendant);
  if (slotIdx === -1) return { hand, field, placed: false };

  const esclingCard = getCard(esclings[slotIdx]?.escling);

  // Find LEGAL Lvl 1s in hand (school must match the Escling — R16)
  const allOpts = hand.map((id, idx) => ({ card: getCard(id), idx }))
    .filter(({ card }) => card?.type === "Escendant" && card?.level === 1)
    .filter(({ card }) => !esclingCard || card.school === esclingCard.school);
  if (allOpts.length === 0) return { hand, field, placed: false };

  const naturalOpts = allOpts.filter(({ card }) =>
    esclingCard && card.school === esclingCard.school && card.ilk === esclingCard.ilk);

  let chosen;
  if (preferNatural) {
    chosen = naturalOpts.length > 0
      ? naturalOpts.sort((a, b) => (b.card.lyt || 0) - (a.card.lyt || 0))[0]
      : allOpts.sort((a, b) => (b.card.lyt || 0) - (a.card.lyt || 0))[0];
  } else {
    chosen = pickRandom(allOpts); // Beb: chaotic, but legal
  }

  const isNatural = esclingCard
    && chosen.card.school === esclingCard.school
    && chosen.card.ilk === esclingCard.ilk;

  const newEsclings = [...esclings];
  newEsclings[slotIdx] = {
    ...newEsclings[slotIdx],
    escendant: hand[chosen.idx],
    currentLyf: chosen.card.lyf ?? null,
    deviant: !isNatural,
    natural: isNatural,
    fallen: false,
    attackedThisTurn: false,
  };
  const newHand = [...hand];
  newHand.splice(chosen.idx, 1);
  const newField = { ...field, esclings: newEsclings };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, placed: true };
}

// ── Ascend ONE being (best target). ──
// Universal rule (Rulebook line 643): only ONE Ascension per turn,
// regardless of Natural or Deviant, regardless of which being. Enforced
// by callers calling this function exactly once per turn — not by a
// per-slot flag. No leaping levels (canAscendNatural enforces L+1 only).
//
// AWAKENING — DEVIANT ASCEND (R21, mirror of Shatterfield.handleAscend):
// When `allowDeviant` is set and NO free natural line exists, the NPC
// considers ANY next-level Escendant in hand. Cost = rules.deviantFare(
// sharedTraits(current, target), target.level) — R101's ONE table, read
// by this file, by Shatterfield.handleAscend, by RulesEnforcer and by
// the Rulebook's printed page. `target.level` is the DESTINATION, which
// is the reading the 2026-08-26 ruling settled. Paid Khao-first via
// npcPayDeviantFare, mirroring the player exactly.
// Deviant status is permanent and sticky:
//   deviant: slot.deviant || !isNatural, natural: isNatural && !slot.deviant
// A Deviant being may still Ascend naturally for free later — but it
// never sheds the mark (Rulebook ch.15: "Once Deviant, always Deviant").
async function ascendOne(code, hand, field, prioritizeHighest, allowDeviant = false) {
  const esclings = riftArray(field.esclings, ESCLING_SLOTS);   // R110

  // ── Natural candidates (free — R19/R20) ──
  const naturals = [];
  for (let i = 0; i < esclings.length; i++) {
    const slot = esclings[i];
    if (!slot?.escendant || slot.fallen) continue;
    const cur = getCard(slot.escendant);
    if (!cur || cur.level >= 3) continue;
    const opts = hand.map((id, idx) => ({ card: getCard(id), idx }))
      .filter(({ card }) => canAscendNatural(cur, card));
    if (opts.length > 0) naturals.push({ slotIdx: i, opt: opts[0], curLevel: cur.level, cost: 0, natural: true });
  }

  let best = null;
  if (naturals.length > 0) {
    best = prioritizeHighest
      ? naturals.sort((a, b) => b.curLevel - a.curLevel)[0]
      : naturals[0];
  } else if (allowDeviant) {
    // ── Deviant candidates — cheapest fare first, then strongest ──
    const deviants = [];
    for (let i = 0; i < esclings.length; i++) {
      const slot = esclings[i];
      if (!slot?.escendant || slot.fallen) continue;
      const cur = getCard(slot.escendant);
      if (!cur || cur.level >= 3) continue;
      hand.forEach((id, idx) => {
        const card = getCard(id);
        if (card?.type !== "Escendant" || card?.level !== cur.level + 1) return;
        // R101 — the DESTINATION level (`card.level`), for a Deviant
        // Ascend exactly as for a Training. The brain sorts on the Khao
        // leg because Khao is what it prefers to spend; the fare object
        // is carried through so the payment can take the Anima leg at
        // its own (sometimes cheaper) price.
        const fare = deviantFare(sharedTraits(cur, card), card.level);
        const cost = fare.khao;
        if (cost <= 0) return; // natural would have caught it
        deviants.push({ slotIdx: i, opt: { card, idx }, curLevel: cur.level, cost, fare, natural: false });
      });
    }
    deviants.sort((a, b) => a.cost - b.cost || (b.opt.card.lyt || 0) - (a.opt.card.lyt || 0));
    best = deviants[0] || null;
  }
  if (!best) return { hand, field, ascended: false };

  // Deviant fare — fresh Firebase gate (Bug F discipline). If the NPC
  // can't afford it right now, the ascension simply doesn't happen.
  // CANON (caP'n ruling, July 2026): Deviant fares are payable in Khao
  // OR Anima — Khao first, Anima fallback (same order as Resurrection).
  // The player-side handleAscend gets the identical fix this deploy.
  if (!best.natural) {
    const paid = await npcPayDeviantFare(code, best.fare);
    if (!paid) return { hand, field, ascended: false };
  }

  const card = getCard(hand[best.opt.idx]);

  const newEsclings = [...esclings];
  // ── DESCENT FIX (July 2026, mirrors Shatterfield.handleAscend) ──
  // Push the outgoing lower-level Escendant into the soul so that when
  // this being is later ruined, descendBeing/descendPlayerSlot can find
  // it and the being DESCENDS instead of fully ruining back to Escling.
  // The NPC never did this — which is why NPC beings could never descend.
  const ascSlot = newEsclings[best.slotIdx];
  const prevSoul = ascSlot.soul || [];
  const newSoul = prevSoul.includes(ascSlot.escendant) ? prevSoul : [...prevSoul, ascSlot.escendant];
  newEsclings[best.slotIdx] = {
    ...ascSlot,
    escendant: hand[best.opt.idx],
    currentLyf: card?.lyf ?? null,
    soul: newSoul,
    deviant: ascSlot.deviant || !best.natural,
    natural: best.natural && !ascSlot.deviant,
  };
  const newHand = [...hand];
  newHand.splice(best.opt.idx, 1);
  const newField = { ...field, esclings: newEsclings };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, ascended: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: TRAINING — trainOneImpera
// Mirror of Shatterfield.handleAttachImpera: school+ilk must both
// match the being, the Impera's level must be <= the being's level,
// and only one Impera per level may sit in a soul (R71).
// CANON (caP'n ruling, July 2026): this is NATURAL Training — free
// (Rulebook ch.8: "Natural Training — Free. Unlimited per turn").
// An Impera's printed khaoCost is its ACTIVATION fare, paid in
// activateOneSoulCard. DEVIANT Training (attaching a mismatched
// Impera at the shared-traits cost table) is a separate, expensive
// mechanic not yet implemented on EITHER side — backlog, both sides
// together, so the NPC never gains a move the player lacks.
// Attaches ONE per call (one action = one write = one pause); loop it.
// Priority: build the strongest being first (highest being level),
// then the highest Impera level — the road to a Supreme Being (R69)
// runs through the full L1/L2/L3 set.
// ─────────────────────────────────────────────────────────────
async function trainOneImpera(code, hand, field) {
  const esclings = field.esclings || [];
  const pairs = [];
  hand.forEach((id, idx) => {
    const card = getCard(id);
    if (card?.type !== "Impera") return;
    esclings.forEach((slot, slotIdx) => {
      if (!slot?.escendant) return;
      const being = getCard(slot.escendant);
      if (!being) return;
      if (card.school !== being.school || card.ilk !== being.ilk) return;
      if ((card.level || 1) > (being.level || 1)) return;
      const soul = slot.soul || [];
      const alreadyHasLevel = soul.some(sid => {
        const sc = getCard(sid);
        return sc?.type === "Impera" && sc?.level === card.level;
      });
      if (alreadyHasLevel) return;
      pairs.push({ idx, slotIdx, card, beingLevel: being.level || 1 });
    });
  });
  if (pairs.length === 0) return { hand, field, trained: false };

  // ══════════════════════════════════════════════════════════
  // 💰 R128 · A TRAINING PAYS, AND THIS ONE PAID NOTHING
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-09-14: *"To keep consistency, we are going to make the
  // user pay to use an Impera if it has OPT, OP3, etc type acronyms. If
  // it doesn't have them, then you only pay once, if it does, you must
  // pay to activate again."*
  //
  // This function spliced the card out of hand onto the Soul and touched
  // NO POOL — not Khao, not Anima, nothing. So the brain built full
  // Impera sets for free, and every UNTAGGED Impera it trained was free
  // forever (a passive is never activated, so the soul-card till never
  // sees it either). The board had the identical hole in the other half
  // of one `if`; both are closed now, out of the same `trainingFare`.
  //
  // ⚠ AFFORDABILITY IS ASKED BEFORE THE SPLICE, AND IT IS THE RULE HERE
  // RATHER THAN THE LAYOUT. A Training that took the card out of hand
  // and then failed the charge would be a brand-new pay-for-nothing —
  // the exact bug class the Tome ledger fought, in reverse. So the
  // candidates are FILTERED by what the seat can actually pay, off a
  // FRESH read, and the pick is the best AFFORDABLE pair.
  //
  // ⚠ AND AN UNAFFORDABLE PAIR IS SKIPPED, NOT A BLOCKER. The sort puts
  // the highest being and the highest Impera first, so a 10-Khao King of
  // the Anthill at the front of the list would otherwise stall every
  // cheaper Training behind it for the rest of the duel. The brain's job
  // is the JUDGEMENT; the rule is that it pays for what it takes.
  const fareOf = (card) => trainingFare(card);
  {
    const live = (await getMatch(code))?.players?.[NPC_IDX] || {};
    const khaoNow  = live.khao  ?? 0;
    const animaNow = live.anima ?? STARTING_ANIMA;
    const affordable = pairs.filter(({ card }) => {
      const f = fareOf(card);
      return f.free || canPayPrintedFare(card, { khao: khaoNow, anima: animaNow });
    });
    if (affordable.length === 0) return { hand, field, trained: false };
    pairs.length = 0;
    pairs.push(...affordable);
  }

  pairs.sort((a, b) => b.beingLevel - a.beingLevel || (b.card.level || 0) - (a.card.level || 0));
  const pick = pairs[0];

  // PAY FIRST, AND THROUGH THE GUARDED TILLS, so R96's voluntary-payment
  // law binds the Anima leg exactly as it binds the player's. Both legs
  // of a printed fare, in the board's order — Khao, then Anima — because
  // Anima is a life total and must never be the leg spent on a fare the
  // other leg cannot complete.
  {
    const f = fareOf(pick.card);
    if (!f.free) {
      if (f.khao > 0 && !(await npcSpendKhao(code, f.khao))) return { hand, field, trained: false };
      if (f.anima > 0 && !(await npcSpendAnima(code, f.anima))) return { hand, field, trained: false };
    }
  }

  const newEsclings = [...esclings];
  const slot = newEsclings[pick.slotIdx];
  newEsclings[pick.slotIdx] = { ...slot, soul: [...(slot.soul || []), hand[pick.idx]] };
  const newHand = [...hand];
  newHand.splice(pick.idx, 1);
  const newField = { ...field, esclings: newEsclings };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, trained: true };
}

// ═════════════════════════════════════════════════════════════
// ⭐ THE PRIMERA STAMPS (R78–R84) — THE BRAIN CAN PRESS THEM NOW
// ═════════════════════════════════════════════════════════════
// Mirror of Shatterfield.handlePressPrimera / payPrimeraCost /
// markPrimeraSpent (~6396-6800). Until this block existed the NPC
// bore two innate powers on every being it controlled and could press
// neither — eight legal actions, zero code paths, in a 5000-line
// brain. The audit called it the headline finding and it is also the
// answer to the mutual board-lock:
//
//   R31 — Anima is unreachable while any adversary being stands.
//   R28 — Lyf refreshes for the ACTING seat at the end of its OWN Turn,
//         so chip damage is real INSIDE a Turn and worth nothing ACROSS
//         one. ⚠ THIS LINE USED TO READ "Lyf resets every Turn, so chip
//         damage never accumulates", which is the every-seat-every-
//         half-turn reading caP'n's own ruling killed: a being chipped
//         or Fractured on an adversary's board STAYS that way until its
//         own Turn comes round. So two of your beings CAN combine on one
//         defender within a half-turn and the second swing lands on the
//         damage the first one did — which is precisely why the lane
//         plan below is worth computing at all.
//   R44 — ruined beings come back.
// Three correct rules that, together, make a stalled board STABLE.
// The PHANTOM stamp is the designed way out: "Dim the adversary's
// Anima directly by 1, BYPASSING ALL BEINGS." One point a Turn is not
// much — it is the difference between a duel that ends and one that
// does not.
//
// This helper — and the belt-and-braces second lookup it used to
// carry — existed to compensate for an axis inversion in cards.js: the
// Family-A word (Humanoid/Beast/Machine/Dragon) was stored under `ilk`
// and the Family-B word (Space/Phantom/Dragon/Time) under `school`,
// exactly opposite to the PRIMERAS tables that consume them. Fixed in
// the data on 2026-08-25. The axis now simply names the field, and the
// only thing left to do is confirm the word is a real stamp.
const stampWordFor = (card, axis) => {
  const word = card?.[axis];
  return word && PRIMERAS?.[axis]?.[word] ? word : null;
};

// R81 + R82 + R83 bookkeeping. One object per NPC Turn, created by the
// runner and thrown away at end of turn — which IS "all limits reset at
// the start of your next Turn" (R83), with no state to leak between
// duels. `words` is keyed by the WORD, so Dragon-as-Ilk and
// Dragon-as-School share one key: "Dragon is ONE word on both axes"
// (R82) falls out of the data shape instead of a special case.
const newStampTurn = () => ({ words: {}, slots: {}, beastArmed: {} });

// ═════════════════════════════════════════════════════════════
// ⭐ THE TWO LEDGERS — filling a contract the referee was reading
// ═════════════════════════════════════════════════════════════
// GameStateValidator's P01–P10 watch R79/R80/R81/R82/R83/R89 off two
// records on the seat. Shatterfield persists both; NPC.js persisted
// NEITHER, so in an NPC-vs-NPC run those ten checks could not fire and
// their silence meant "nobody reported", not "nothing was wrong".
//
// ⭐ THE TURN IS STAMPED, NOT CLEARED — the same law both readers obey.
// A ledger is binding only for the half-turn named in its own `turn`;
// an older one is STALE and reads as empty, but it is still THERE, so
// "pressed nothing this Turn" and "never wrote a ledger" stay
// distinguishable. Clearing on turn change destroys that evidence.
//
// ⚠ `slots` is keyed by slot INDEX, so RTDB stores {"0":true} as an
// ARRAY. The contract is kept verbatim anyway — the validator's reader
// (indexedEntries / `slots?.[i]`) is already correct against both.
// beastArmed stays out: it is a rider's armed flag, not an R81/R82
// allowance, and does not belong in a shared contract.
const primeraLedgerOf = (stampTurn, turn) => ({
  primeraLedger: { turn: turn || 0, words: { ...stampTurn.words }, slots: { ...stampTurn.slots } },
});

// R89 — one PAID Resurrection per Turn. Card-effect returns (Tomes,
// Impera, the Time Primera) are EXEMPT from this clock — being exempt
// is what those cards buy — though R77 still tallies them per copy.
// So this reads and writes the clock for the paid rite ONLY, and the
// count is aged by its own stamp: a ledger from an older half-turn
// reads as zero.
const resClockOf = (live, turn) => {
  const led = live?.resurrectedThisTurn;
  if (led && typeof led === "object" && led.turn === turn) return Number(led.count) || 0;
  return 0;
};

// Everything the chooser needs to score a stamp, gathered once.
// `fracturable` answers the question the board-lock turns on: can ANY
// of my un-swung attackers actually break something this Turn? If not,
// the attack phase is about to produce nothing and Phantom is the only
// move on the board that changes a number.
function stampBoardRead(field, playerField, turn, seatCount = 2) {
  // R121 — THE MIND'S SILENCE REACHES THE STAMP CHOOSER TWICE, and this
  // is the first of the two. `attackers` is what `canRuin` and
  // `fracturable` are computed from, and those two decide whether the
  // board is LOCKED — the state in which Phantom scores 95 and the
  // brain spends its Turn on a stamp instead of a swing. Counting a
  // silenced Warden as an attacker makes a locked board look breakable,
  // so the brain declines the one move that could still change a number
  // and then swings with nothing. The silence is rules.js's to
  // determine; this file only asks where the answer applies.
  const mine = field?.esclings || [];
  const attackers = mine
    .map((s, i) => ({ s, i }))
    .filter(({ s, i }) => s?.escendant && !s.attackedThisTurn && !s.fallen
      && (i !== MIND_RIFT || mindMayAct(mine, "ATTACK", turn, seatCount)));
  const oppStanding = (playerField?.esclings || [])
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s?.escendant && !s.fallen);
  const walled = oppStanding.length > 0;
  const anyVuln = oppStanding.some(({ s: o }) => effLyfPure(o, playerField) === 0);
  const canFracture = attackers.some(({ s, i }) =>
    oppStanding.some(({ s: o }) => effLytPure(s, i, field, playerField) >= effLyfPure(o, playerField)));
  // ⚠ FRACTURE IS NOT PROGRESS. R28 resets Lyf at the end of every
  // Turn, so a being fractured and not FINISHED is whole again before
  // it matters. The question that decides whether the attack phase can
  // move the game is "can I RUIN something": a being already at 0 Lyf
  // with an attacker to spare, or two attackers to fracture-then-ruin.
  const canRuin = (anyVuln && attackers.length >= 1)
    || (canFracture && attackers.length >= 2);
  return { attackers, oppStanding, walled, fracturable: canFracture || anyVuln, canRuin };
}

// ── THE PRESS. One stamp, one write-sequence, fully gated. ──
// Returns { field, hand, grimoire, luxmoire, pressed }. Callers loop
// until `pressed` is false; the R81/R82 marks make that loop finite
// (at most one press per being and one per word).
//
// R80 is structural: this is only ever called from a Turn runner, and
// never from npcReact — the reactive brain has no path to it.
async function pressOnePrimera(code, hand, field, grimoire, luxmoire, stampTurn, ctx) {
  const nothing = { field, hand, grimoire, luxmoire, pressed: false };
  const esclings = field?.esclings || [];
  const playerField = ctx?.playerField || {};
  const m = await getMatch(code);
  const live = m?.players?.[NPC_IDX] || {};
  const oppLive = m?.players?.[0] || {};
  const turnNow = m?.turn || 0;
  const seatCountNow = m?.seatCount || 2;
  // ⚠ READ AFTER THE MATCH, NOT BEFORE IT. The board read asks whether
  // this seat's Mind is silenced, and R115 sleeps that silence for the
  // first three full Turns — so it needs the clock, and the clock comes
  // off the match. It used to run one line earlier, before `m` existed.
  const board = stampBoardRead(field, playerField, turnNow, seatCountNow);
  const khao = live.khao ?? 0;
  const anima = live.anima ?? 0;
  const necropolis = live.necropolis || [];
  const revived = live.resurrectedCards || [];
  // The card-effect record. NOT a limit — R89 exempts card-effect
  // returns from the paid rite's clock — just the evidence that the
  // return happened, kept off `resurrectedThisTurn` on purpose.
  const cardResClock = (() => {
    const led = live?.cardResurrectedThisTurn;
    return (led && typeof led === "object" && led.turn === turnNow) ? (Number(led.count) || 0) : 0;
  })();

  // ── CANDIDATE SCAN ──
  // Every legal (being, axis) pair, scored. Score 0 means "legal but
  // pointless" — this file's own law is never pay for nothing, and a
  // stamp fare is Anima as often as it is Khao.
  const cands = [];
  for (let i = 0; i < esclings.length; i++) {
    const slot = esclings[i];
    const card = getCard(slot?.escendant);
    if (!card || card.type !== "Escendant") continue;
    if (stampTurn.slots[i]) continue;                      // R81 — one per being
    // ── R121 · A SILENCED MIND PRESSES NOTHING ────────────────────
    // caP'n, 2026-09-07: with Body, Sol and Heart all holding
    // Escendants, the being in the Mind rift may perform *"no attacks
    // of any kind etc except the Warden Primera"* — and a Primera stamp
    // of its own Ilk or School is exactly the "etc". Only the Warden
    // Art of the rift it stands in survives, and that Art is not one of
    // the eight innate stamps this scan enumerates.
    //
    // ⚠ FILTERED AT THE SCAN, NOT AT THE RESOLVE, for the same reason
    // the R08 attack ban above is: a candidate that reaches the resolve
    // has already been scored, chosen and had its fare committed, and
    // the gate then refuses it — a press that never lands and a fare
    // the brain believes it spent. Barred here, it is never chosen.
    //
    // ⚠ AND GROWTH IS NOT SILENCED. caP'n: *"you can keep ascending etc
    // and attaching cards to it etc like your ace in the hole."* This
    // bars only what the BEING performs; ascending into the Mind rift,
    // training Impera onto it and bonding U'hltis stay legal, so none
    // of those paths in this file are touched.
    if (!mindMayAct(esclings, "PRIMERA", turnNow, seatCountNow) && i === MIND_RIFT) continue;
    const level = card.level || 1;
    const cost = PRIMERA_COST[`L${level}`] || level;
    // ── R79 + R107 · AFFORDABILITY IS ONE POOL OR THE OTHER ──────
    // This scan used to ask `khao + anima >= cost` and then check R96
    // on the remainder — i.e. it scored presses the till could only
    // have paid for by SPLITTING, which R107 now forbids. One call,
    // one predicate, shared with the renderer and the gate; R96 rides
    // inside it on the Anima leg.
    if (farePoolsFor(cost, { khao, anima }).length === 0) continue;

    for (const axis of ["ilk", "school"]) {
      const word = stampWordFor(card, axis);
      if (!word) continue;
      if (stampTurn.words[word]) continue;                 // R82 — one per word, field-wide
      // ── R08 · THE TWO STAMPS THAT ATTACK ──────────────────────
      // caP'n, 2026-08-25: *"neither can Attack at all first turn"*,
      // and Attack is the BROAD word. The Phantom School stamp dims the
      // adversary's Anima directly and the Beast Ilk stamp amplifies a
      // dim; both are Attacks and neither may be pressed on the opening
      // Cycle. Every OTHER stamp stays legal there, because R09 says
      // activation on Turn 1 is explicitly allowed.
      //
      // ⚠ FILTERED AT THE SCAN, NOT AT THE RESOLVE, so the brain never
      // scores a press it cannot make and never pays a fare for it.
      // The referee caught this one in the wild: X10 fired on
      // "Anima dimmed 34 → 33 on the OPENING CYCLE (turn 1)", which is
      // this exact stamp, at 1 Anima, exactly as the card reads.
      //
      // ⚠ AND THE SEARCH BAN IS A DIFFERENT BAN — caP'n, 2026-08-26:
      // *"brilliant distinction yes, cards are exempt from the first
      // turn search rule"*. MACHINE digs through the Grimoire and
      // HUMANOID digs through the Luxmoire, and both are EXEMPT: a
      // stamp is a card's printed power, not the seat's search action,
      // so neither is filtered here and neither goes near
      // grimoireSearchOne / luxmoireSearchOne (which pay the R88 fare
      // and ARE the seat's action). The filter below is the ATTACK ban
      // and only ever the Attack ban. A stamp can be caught by one and
      // not the other, and these four are the proof: two searchers
      // pass, two attackers do not.
      if (isOpeningTurnIndex(turnNow, m?.seatCount || 2)
          && ((axis === "school" && word === "Phantom") || (axis === "ilk" && word === "Beast"))) continue;
      let score = 0;
      const plan = { word, axis, slotIdx: i, cost, level, card };

      if (word === "Phantom") {
        // THE LOCK-BREAKER. Highest score in the file when the board is
        // walled and the attack phase is about to do nothing; still
        // worth a fare when it is simply lethal.
        const oppAnima = oppLive.anima ?? 30;
        if (oppAnima <= 1) score = 100;                    // lethal, bypassing everything
        else if (board.walled && !board.canRuin) score = 95; // THE LOCK — nothing else moves a number
        else if (board.walled) score = 75;                   // still the only unblockable damage
        else score = 8;                                      // the lane is open; attacks do more
      } else if (word === "Machine") {
        const pool = Object.keys(grimoire || {}).filter(id => {
          const c = getCard(id); return c?.type === "Escendant" && c?.level === 1 && (grimoire[id] || 0) > 0;
        });
        const emptySlot = esclings.some(s => s?.escling && !s?.escendant);
        const l1InHand = hand.some(id => { const c = getCard(id); return c?.type === "Escendant" && c.level === 1; });
        // R10 — the cap is 8 and the rule is draw-then-discard, but a
        // free tutor that immediately forces a discard has bought
        // nothing. Every other draw path in this file declines at the
        // cap for the same reason, and the referee's H02 caught this
        // one the first time it ran.
        if (pool.length === 0 || hand.length >= NPC_HAND_LIMIT) score = 0;
        else if (emptySlot && !l1InHand) score = 70;       // a free body for an empty rift
        else score = 18;                                   // free card, no forced discard
        plan.pool = pool;
      } else if (word === "Space") {
        const l1Slots = esclings.map((s, k) => ({ s, k }))
          .filter(({ s }) => { const c = getCard(s?.escendant); return c?.type === "Escendant" && c?.level === 1; });
        const pool = Object.keys(grimoire || {}).filter(id => {
          const c = getCard(id);
          if (c?.type !== "Escendant" || c?.level !== 2 || (grimoire[id] || 0) <= 0) return false;
          return l1Slots.some(({ s }) => sharedTraits(c, getCard(s.escendant)) === 2);
        });
        score = pool.length > 0 ? 65 : 0;                  // a free Ascend that does not spend the Turn's one
        plan.pool = pool; plan.l1Slots = l1Slots;
      } else if (word === "Time") {
        // R44/R77 — a card-effect resurrect still marks the tally.
        // R85 EXEMPT (Shatterfield says so in as many words): this is
        // a card effect, not the paid rite, so canPassiveResurrect is
        // deliberately NOT consulted here.
        const best = new Map();
        necropolis.forEach(id => {
          const c = getCard(id);
          if (c?.type !== "Escendant" || c?.level !== 1) return;
          const used = resTallyNpc(revived, id);
          if (used >= (RESURRECT_LIMIT.L1 ?? 3)) return;
          const b = baseId(id);
          const prev = best.get(b);
          if (!prev || used < prev.used) best.set(b, { id, used });
        });
        const emptyIdx = esclings.findIndex(s => s?.escling && !s?.escendant);
        const cands2 = [...best.values()].map(v => v.id);
        // ⭐ R89 DOES NOT REACH THIS PRESS. The clock counts PAID rites;
        // a card-effect return is exempt, and that exemption is the
        // whole thing the card buys. R77 still applies and is honoured
        // in the write below — the tally is per copy, the clock is per
        // Turn, and they are different ledgers for different rules.
        // (The first cut gated this on the clock, which cost 1.4 points
        // of clock-call rate for a rule that was never asking.)
        score = (cands2.length > 0 && emptyIdx >= 0) ? 60 : 0;
        plan.pool = cands2; plan.destIdx = emptyIdx;
      } else if (word === "Humanoid") {
        const soul = slot.soul || [];
        const hasL1 = soul.some(id => { const sc = getCard(id); return sc?.type === "Impera" && sc?.level === 1; });
        const pool = hasL1 ? [] : Object.keys(luxmoire || {}).filter(id => {
          const c = getCard(id);
          return c?.type === "Impera" && c?.level === 1 && (luxmoire[id] || 0) > 0
            && c?.school === card.school && c?.ilk === card.ilk;
        });
        score = pool.length > 0 ? 45 : 0;                  // free Impera, straight onto the Supreme road
        plan.pool = pool;
      } else if (word === "Beast") {
        // "If the user dims the adversary's Anima this Turn, dim it by
        // 1 more." The brain has no retroactive tracker, so it arms the
        // rider — and only when the lane is genuinely open, otherwise
        // the rider never fires and the fare bought nothing.
        // Any route by which this being will dim Anima this Turn arms
        // the rider — the open lane, a trained redirect, OR a
        // dim-on-attack soul card that reaches past the wall.
        // ⚠ MEASURED AND NARROWED. Arming Beast on any dim-on-attack
        // rider (so it fires through the wall) reads correct and costs
        // FOUR POINTS of clock-call rate: R81 is one Primera per BEING
        // per Turn, so a 40-point Beast press spends a being that a
        // higher-value stamp wanted, and the +1 it buys does not repay
        // the stamp it displaced. The open lane only.
        const willDim = playerAnimaClear(playerField, turnNow, seatCountNow)
          || !!slot.canDirectAnima || !!slot.animaRedirectAll;
        const canSwing = !slot.attackedThisTurn && !slot.fallen && (card.lyt || 0) > 0;
        score = (willDim && canSwing && !slot.beastArmed) ? 40 : 0;
        plan.arm = true;
      } else if (word === "Dragon" && axis === "ilk") {
        // "-1 Lyf, +1 Lyt for the Turn." Worth it ONLY when the extra
        // point converts a swing that does nothing into a fracture, and
        // only when the Lyf it costs does not make the user vulnerable.
        const myLyt = effLytPure(slot, i, field, playerField);
        const myLyf = effLyfPure(slot, field);
        const converts = board.oppStanding.some(({ s: o }) => {
          const oLyf = effLyfPure(o, playerField);
          return myLyt < oLyf && myLyt + 1 >= oLyf;
        });
        score = (converts && myLyf > 1 && !slot.attackedThisTurn && !slot.fallen) ? 55 : 0;
      } else if (word === "Dragon" && axis === "school") {
        // "-1 Lyt, +1 Lyf for the Turn." Purely defensive, and it COSTS
        // offence — so it fires only when the Lyt it spends was going to
        // buy nothing anyway (this being cannot fracture anything on the
        // board, even with the point it still has) and the Lyf it buys
        // is the exact point that survives the adversary's best swing.
        //
        // ⚠ The first cut gated on `slot.attackedThisTurn`, which is
        // ALWAYS false here — the stamps are pressed BEFORE the attack
        // phase — so this was the one word of the eight that could
        // never fire. Caught by counting presses per word, not by
        // reading the code.
        const myLyf = effLyfPure(slot, field);
        const myLyt = effLytPure(slot, i, field, playerField);
        const buysNothing = !board.oppStanding.some(({ s: o }) =>
          myLyt - 1 >= effLyfPure(o, playerField));
        const worstIncoming = (playerField?.esclings || []).reduce((mx, o, k) =>
          Math.max(mx, o?.escendant && !o.fallen ? effLytPure(o, k, playerField, field) : 0), 0);
        const saves = worstIncoming >= myLyf && worstIncoming < myLyf + 1;
        score = (buysNothing && saves && myLyt >= 1) ? 35 : 0;
      }

      if (score > 0) cands.push({ ...plan, score });
    }
  }
  // ── THE DIAL. A stamp press is a judgement, so skill decides HOW
  // MUCH value it takes to be worth one. At the bottom of the ladder
  // only the board-breaking presses (Phantom on a locked board, a free
  // body into an empty rift, lethal) clear the bar; at the top the
  // brain takes every point of value on the table. Temperament rides
  // along through the same knob everything else uses.
  const minScore = lerp(62, 0, clamp01((ctx?.brain?.skill ?? 0.5) + (ctx?.brain?.t?.chaos || 0)));
  const worth = cands.filter(c => c.score >= minScore);
  if (worth.length === 0) return nothing;
  cands.length = 0; cands.push(...worth);

  // Highest value first; cheapest fare breaks a tie (the pool has other
  // work to do this Turn), then the lower slot for determinism.
  cands.sort((a, b) => b.score - a.score || a.cost - b.cost || a.slotIdx - b.slotIdx);
  const pick = cands[0];

  if (LOCK) {
    const r = LOCK.get(code); const k = pick.axis + ":" + pick.word;
    r.stamps[k] = (r.stamps[k] || 0) + 1;
    r.pressed = r.pressed || []; r.pressed.push({ turn: turnNow, slot: pick.slotIdx });
  }
  // ── PAY (R79 · R107 one pool · R96-guarded) ──
  if (!(await npcPayPrimeraFare(code, pick.cost))) return nothing;

  // ── MARK (R81 being, R82 word) — before the effect, so a resolve
  // that ends the match still leaves the bookkeeping honest. ──
  stampTurn.words[pick.word] = true;
  stampTurn.slots[pick.slotIdx] = true;
  // ── AND ONTO THE RECORD, AT THE PRESS (see primeraLedgerOf). The
  // ledger has to land in the same beat as the mark, not at end of
  // turn: P08 reads it to prove the presser was the ACTIVE seat, and
  // by end of turn the seat is no longer active. Folded into each
  // resolve branch's own updatePlayer wherever there is one — only
  // Phantom (which writes to the adversary) needs a write of its own.
  const ledger = primeraLedgerOf(stampTurn, turnNow);

  // ── RESOLVE ──
  let newField = field;
  let newHand = hand;
  let newGrim = grimoire;
  let newLux = luxmoire;

  if (pick.word === "Phantom") {
    // The only branch whose effect lands on the ADVERSARY's node, so it
    // is the only one with no write of its own to ride. Ledger first:
    // dimPlayerAnima can end the duel, and a press that won the game
    // still has to be on the record that the referee reads.
    await updatePlayer(code, NPC_IDX, ledger);
    const died = await dimPlayerAnima(code, 1);
    await sleep(ACTION_DELAY);
    return { field, hand, grimoire, luxmoire, pressed: true, matchOver: died };
  }

  if (pick.word === "Machine") {
    const chosen = pick.pool.sort((a, b) => (getCard(b)?.lyt || 0) - (getCard(a)?.lyt || 0))[0];
    newGrim = { ...grimoire };
    if (newGrim[chosen] > 1) newGrim[chosen]--; else delete newGrim[chosen];
    newHand = [...hand, chosen];
    // Canon dial (Shatterfield says so at the call site): a free search
    // does NOT consume the Turn's draw. The runner is not told.
    await updatePlayer(code, NPC_IDX, { hand: newHand, grimoire: newGrim, ...ledger });
  } else if (pick.word === "Humanoid") {
    const chosen = pick.pool[0];
    newLux = { ...luxmoire };
    if (newLux[chosen] > 1) newLux[chosen]--; else delete newLux[chosen];
    const esc = [...esclings];
    const s0 = esc[pick.slotIdx] || {};
    esc[pick.slotIdx] = { ...s0, soul: [...(s0.soul || []), chosen] };
    newField = { ...field, esclings: esc };
    await updatePlayer(code, NPC_IDX, { field: newField, luxmoire: newLux, ...ledger });
  } else if (pick.word === "Time") {
    const chosen = pick.pool[0];
    const target = getCard(chosen);
    const esc = [...esclings];
    esc[pick.destIdx] = {
      ...(esc[pick.destIdx] || {}),
      escendant: chosen, soul: [], soulUsed: {}, fallen: false, fallenTurn: null,
      currentLyf: target?.lyf ?? null, attackedThisTurn: false, intangible: false,
    };
    newField = { ...field, esclings: esc };
    const nec = [...necropolis];
    const ni = nec.indexOf(chosen);
    if (ni >= 0) nec.splice(ni, 1);
    await updatePlayer(code, NPC_IDX, {
      field: newField, necropolis: nec,
      resurrectedCards: [...revived, chosen],   // R44/R77 — every method marks the TALLY
      // ⚠ NOT `resurrectedThisTurn`. That field IS the R89 clock and
      // GameStateValidator's auditResurrectionClock reads it as such —
      // writing a card-effect return into it would make an exempt
      // return look like a second paid rite and fire P10 on a legal
      // board. The press still goes on the record, in its own field,
      // where nothing mistakes it for the clock. Same shape as the
      // clock so a future reader needs no second idiom.
      cardResurrectedThisTurn: { turn: turnNow, count: cardResClock + 1 },
      ...ledger,
    });
  } else if (pick.word === "Space") {
    const chosen = pick.pool.sort((a, b) => (getCard(b)?.lyt || 0) - (getCard(a)?.lyt || 0))[0];
    const l2 = getCard(chosen);
    const dest = pick.l1Slots.find(({ s }) => sharedTraits(l2, getCard(s.escendant)) === 2);
    if (!dest) {                                    // fare paid, fizzles
      await updatePlayer(code, NPC_IDX, ledger);    // the press still happened
      return { field, hand, grimoire, luxmoire, pressed: true };
    }
    const esc = [...esclings];
    const s0 = esc[dest.k] || {};
    const prevSoul = s0.soul || [];
    esc[dest.k] = {
      ...s0, escendant: chosen,
      soul: s0.escendant && !prevSoul.includes(s0.escendant) ? [...prevSoul, s0.escendant] : prevSoul,
      currentLyf: l2?.lyf ?? null,
      deviant: s0.deviant || false, natural: !s0.deviant,
    };
    newField = { ...field, esclings: esc };
    newGrim = { ...grimoire };
    if (newGrim[chosen] > 1) newGrim[chosen]--; else delete newGrim[chosen];
    await updatePlayer(code, NPC_IDX, { field: newField, grimoire: newGrim, ...ledger });
  } else if (pick.word === "Beast") {
    stampTurn.beastArmed[pick.slotIdx] = true;
    const esc = [...esclings];
    esc[pick.slotIdx] = { ...(esc[pick.slotIdx] || {}), beastArmed: true };
    newField = { ...field, esclings: esc };
    await updatePlayer(code, NPC_IDX, { field: newField, ...ledger });
  } else if (pick.word === "Dragon") {
    const esc = [...esclings];
    const s0 = esc[pick.slotIdx] || {};
    if (pick.axis === "ilk") {
      const curLyf = (s0.currentLyf !== null && s0.currentLyf !== undefined) ? s0.currentLyf : (pick.card.lyf ?? 0);
      esc[pick.slotIdx] = { ...s0, currentLyf: Math.max(0, curLyf - 1), primeraLytBuff: (s0.primeraLytBuff || 0) + 1 };
    } else {
      esc[pick.slotIdx] = { ...s0, primeraLytBuff: (s0.primeraLytBuff || 0) - 1, primeraLyfBuff: (s0.primeraLyfBuff || 0) + 1 };
    }
    newField = { ...field, esclings: esc };
    await updatePlayer(code, NPC_IDX, { field: newField, ...ledger });
  }

  await sleep(ACTION_DELAY);
  return { field: newField, hand: newHand, grimoire: newGrim, luxmoire: newLux, pressed: true };
}

// The runner-facing step: press every stamp worth pressing this Turn.
// Bounded by R81/R82 (at most one per being, one per word) and by a
// hard loop cap so a future scoring bug can never hang a turn.
async function pressPrimeras(code, hand, field, grimoire, luxmoire, stampTurn, ctx) {
  let h = hand, f = field, g = grimoire, l = luxmoire, over = false;
  for (let n = 0; n < 6; n++) {
    const r = await pressOnePrimera(code, h, f, g, l, stampTurn, ctx);
    h = r.hand; f = r.field; g = r.grimoire; l = r.luxmoire;
    if (r.matchOver) { over = true; break; }
    if (!r.pressed) break;
  }
  return { hand: h, field: f, grimoire: g, luxmoire: l, matchOver: over };
}

// ── R83 — the stamp buffs are FOR THE TURN. resetEndOfTurn clears
// them alongside attackedThisTurn; see the note there.

// ─────────────────────────────────────────────────────────────
// AWAKENING: U'HLTI + AFFINITY — attachOneSoulCard
// Mirror of Shatterfield.handleAttachSoulCard:
//   U'hlti  — owner-bound (card.owner === the exact escendant id),
//             Natural beings only (R64), Level 3 only, one per being
//             (R72). Free to attach.
//   Affinity — Natural L3 only, REQUIRES the U'hlti already attached
//             (R70), one per being (R73). Free to attach.
// This is the final third of the Supreme Being checklist (R69):
// Natural L3 + full Impera set + U'hlti + Affinity.
// ─────────────────────────────────────────────────────────────
async function attachOneSoulCard(code, hand, field) {
  const esclings = field.esclings || [];
  for (let idx = 0; idx < hand.length; idx++) {
    const card = getCard(hand[idx]);
    if (!card || (card.type !== "U'hlti" && card.type !== "Affinity")) continue;
    for (let slotIdx = 0; slotIdx < esclings.length; slotIdx++) {
      const slot = esclings[slotIdx];
      if (!slot?.escendant) continue;
      const being = getCard(slot.escendant);
      if (!being || (being.level || 1) < 3) continue;
      if (slot.deviant || slot.natural === false) continue; // R64 — Deviants never wear these
      const soul = slot.soul || [];
      const hasUhlti = soul.some(sid => getCard(sid)?.type === "U'hlti");
      const hasAffinity = soul.some(sid => getCard(sid)?.type === "Affinity");
      if (card.type === "U'hlti") {
        if (card.owner !== baseId(slot.escendant)) continue; // belongs to a different being
        if (hasUhlti) continue;                       // R72
        // ⚠ RULING 7 / R72 — A HERETICAL TOME FILLS THE U'HLTI SLOT AND
        // NOTHING MAY FOLLOW IT IN. `hasUhlti` above reads `slot.soul`,
        // and the Heretical Tome is NOT in the soul — it has its own
        // field (see R68) — so this check was invisible here. The board
        // refuses it twice over (handleAttachSoulCard's own line and
        // RulesEnforcer.validateSoulAttachment's `hasUhlti(being) ||
        // hasHereticalTome(being)`), and the brain reaches neither.
        // Unreachable today only because the brain has no Heretical
        // Training path of its own — which is a missing feature, not a
        // guard.
        if (slot.heretical) continue;                 // R72 / Ruling 7
      } else {
        if (!hasUhlti) continue;    // R70 — U'hlti first
        if (hasAffinity) continue;  // R73
      }
      const newEsclings = [...esclings];
      newEsclings[slotIdx] = { ...slot, soul: [...soul, hand[idx]] };
      const newHand = [...hand];
      newHand.splice(idx, 1);
      const newField = { ...field, esclings: newEsclings };
      await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
      await sleep(ACTION_DELAY);
      return { hand: newHand, field: newField, attached: true };
    }
  }
  return { hand, field, attached: false };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: SOUL CARD ACTIVATION — compact mirror of
// Shatterfield.handleActivateSoulCard for every Impera / U'hlti /
// Affinity shipped in the 12 starter decks.
//
// Contract, per activation:
//   1. Skip passives and anything not implemented (never pay for
//      nothing — that's the exact bug class the tome ledger fought).
//   2. "Useful right now" gate — soulEffectUseful. No redundant flags,
//      no suicidal self-dims, no dead tutors.
//   3. Pay fares fresh, through the SHARED printedFare/canPayPrintedFare
//      (R105 — the printed fare, both halves, every activation), and
//      Khao BEFORE Anima. Two things were wrong here and both were
//      fixed on the board first: the Anima half was gated on
//      `type === "U'hlti"`, so an Impera or Affinity printing an Anima
//      number activated free; and Anima was spent before the Khao half
//      was known to be payable, which took the Anima for nothing.
//   4. R122 — THE CADENCE TAG IS REAL. `soulCardOffCooldown` /
//      `stampSoulUsed` off `slot.soulUsed`, the same pair
//      gateSoulActivation asks for the player, counted in the same
//      half-turns. ⚠ THIS USED TO BE A PER-TURN `Set` AND THAT WAS THE
//      BRAIN CHEATING IN PLAIN SIGHT: essentially every Impera and ten
//      of the twelve U'hltis carry OP2 or OP3, so Super Nova (dim 4)
//      and Show Stomper (dim 10) fired EVERY Turn for the NPC while the
//      player's own copies greyed out for two and three full Turns.
//      The `used` Set stays on top as the brain's modesty dial — one
//      firing per card per Turn even when the cadence would allow it —
//      but it is no longer the rule, it is a preference.
//   5. Effects that mutate the NPC's OWN field operate on the CALLER's
//      field draft (never a fresh read) so the runner's end-of-turn
//      full-field write cannot clobber them.
//
// Returns { hand, field, grimoire, activated, matchOver }. matchOver
// means the match ENDED inside the effect (either side hit 0 Anima) —
// the runner must stop immediately.
// ─────────────────────────────────────────────────────────────
const SOUL_PASSIVE_IDS = new Set([
  // True passives — their effect lives in effLytPure/effLyfPure or in
  // attack riders; "activating" them player-side just shows an info toast.
  "p_h09_1", "p_xulan_1", "p_dg0_1", "p_antdroid_3",
  "p_premier_dragon_1", "p_premier_dragon_2", "p_chaos_dragon_3",
  "u_xulan_lord", "u_z4z_lord",
  // Deliberately skipped (needs state the NPC doesn't track, or
  // canon-sensitive interactions) — never paid for, never fired:
  "p_dg0_3", "p_premier_dragon_3", "p_chaos_dragon_2",
]);

const CHAMPION_SOUL_IDS = new Set([
  "p_h01_1", "p_shadow_fox_2", "p_shadow_fox_3",   // Intangible
  "p_h09_3", "aff_holy",                            // Nullify
  "p_xulan_2", "p_inverted_serpent_2",              // Dim-on-attack
  "p_spectral_tiger_3",                             // Dim all Lyf
  "u_h01_lord", "u_shadow_fox_lord", "u_spectral_tiger_lord", "u_h09_lord",
  "p_dg0_2",                                        // Cheap draw
]);

function soulEffectUseful(id, slot, slotIdx, ctx) {
  const { field, playerField, playerLive, npcAnima, turn, hand, grimoire,
          playerStanding, npcStanding, myHighLevel, difficulty } = ctx;
  // ── NULLIFICATION CANON (caP'n, July 10 2026): a nullified soul card
  // stays attached but can do NOTHING — the brain never pays for it.
  if (((slot?.nullifiedSouls) || []).includes(id)) return false;
  const beingLevel = getCard(slot.escendant)?.level || 1;

  if (["p_h01_1", "p_shadow_fox_2", "p_shadow_fox_3"].includes(id)) return !slot.intangible;
  if (id === "p_h01_2") return !slot.mustBeRuinedFirst && npcStanding >= 2 && beingLevel < myHighLevel;
  if (id === "p_h09_3" || id === "aff_holy") return !slot.nullifyNextAttack;
  if (id === "p_lucky_dragon_3") return !slot.reflectNextAttack;
  if (["p_xulan_2", "p_inverted_serpent_2"].includes(id)) return !slot.dimAnimaOnAttack;
  if (["p_shadow_fox_1", "p_spectral_tiger_1", "p_inverted_serpent_1"].includes(id)) return !slot.canDirectAnima;
  if (id === "p_spectral_tiger_2") return !slot.spectralClaws;
  if (id === "u_inverted_serpent_lord") return !slot.invFangsBuff;
  if (id === "p_xulan_3") return !slot.nullifyNextAttack && npcAnima > 12;
  if (id === "p_z4z_1") return turn >= 2 && npcAnima > 15 && !slot.extraAttack;
  if (id === "p_z4z_2") {
    if (slot.extraAttack || turn < 2) return false;
    return (field.esclings || []).some((s, i) => {
      if (i === slotIdx) return false;
      const c = getCard(s?.escendant);
      return c && c.school === "Time" && c.level === 2;
    });
  }
  if (id === "p_z4z_3") return turn >= 2 && npcAnima > 20 && playerAnimaClear(playerField, turn, ctx?.seatCount || 2);
  if (id === "p_antdroid_1") return turn >= 2 && npcAnima > 15 && playerAnimaClear(playerField, turn, ctx?.seatCount || 2) && !slot.extraAttackAnimaOnly;
  if (id === "p_antdroid_2") return npcAnima > 12 && hand.length < 6 && Object.keys(grimoire || {}).length > 0;
  if (id === "p_dg0_2") return npcAnima > 6 && hand.length < 7 && Object.keys(grimoire || {}).length > 0;
  if (["p_lucky_dragon_1", "p_lucky_dragon_2"].includes(id)) return hand.length < 7;
  if (id === "p_h01_3") {
    return hand.length < 7 && Object.keys(grimoire || {}).some(gid => {
      const c = getCard(gid); return c?.school === "Space" && c?.type === "Escendant";
    });
  }
  if (id === "p_h09_2") {
    return hand.length < 6 && Object.keys(grimoire || {}).some(gid => {
      const c = getCard(gid); return c?.school === "Space" && c?.type === "Escendant" && (c.level || 1) <= 2;
    });
  }
  if (id === "aff_jynxi") return hand.length < 7;
  if (["u_h01_lord", "u_premier_dragon_lord", "u_shadow_fox_lord",
       "u_spectral_tiger_lord", "u_h09_lord"].includes(id)) return turn >= 2;
  if (id === "p_spectral_tiger_3") return playerStanding >= 1;
  if (id === "p_inverted_serpent_3") return ((playerField?.esclings) || []).some(s => s?.intangible);
  if (id === "u_antdroid_lord") {
    if (getRequiredTargetNpc(playerField)) return false;
    const lethal = totalAvailableLyt(field, playerField) >= (playerLive.anima ?? 30);
    return lethal || (playerLive.anima ?? 30) < 10;
  }
  if (id === "u_chaos_dragon_lord") return playerStanding > npcStanding && npcStanding >= 1;
  if (id === "u_dg0_lord") return true; // always has the draw fallback
  if (id === "p_chaos_dragon_1") {
    const tomes = riftArray(playerField?.tomes, 3);
    return tomes.some(t => t?.id && !t.faceDown) || (playerField?.curios || []).some(c => c);
  }
  if (id === "aff_evil") {
    if (getRequiredTargetNpc(playerField)) return false; // mirror: X'aol refuses under MRF
    return playerStanding > 0
      || riftArray(playerField?.tomes, 3).some(t => t?.id && !t.faceDown)
      || (playerField?.curios || []).some(c => c);
  }
  if (id === "aff_awoken") {
    // Only Affinities that still have their power are worth nullifying.
    return ((playerField?.esclings) || []).some(s => (s?.soul || []).some(sid =>
      getCard(sid)?.type === "Affinity" && !((s?.nullifiedSouls) || []).includes(sid)));
  }
  if (id === "u_lucky_dragon_lord") return difficulty === "Beb"; // only the chaotic one gambles
  return false; // unknown / unimplemented — never pay for nothing
}

async function applyNpcSoulEffect(code, id, slotIdx, ctx) {
  let { hand, field, grimoire } = ctx;
  const playerLive = ctx.playerLive || {};
  const turn = ctx.turn || 0;
  let matchOver = false;

  // Set flags on the CALLER's field draft + write (never a fresh read —
  // the runner's end-of-turn full-field write must not clobber these).
  const setSlotFlags = async (flags) => {
    const newEsclings = [...(field.esclings || [])];
    newEsclings[slotIdx] = { ...newEsclings[slotIdx], ...flags };
    field = { ...field, esclings: newEsclings };
    await updatePlayer(code, NPC_IDX, { field });
  };

  // ⭐ R10 LIVES HERE NOW, not only at the end of the turn.
  //
  // This used to write whatever it drew and leave the cap to
  // npcEnforceHandCap, which runs ONCE, at the bottom of each turn
  // runner. That is fine for a draw taken during the NPC's own turn.
  // It is not fine for a draw taken from the REACTION LANE -- a soul
  // card fired in a Response Window during the ADVERSARY's turn never
  // reaches a turn runner's bottom, so the NPC sat on nine or ten cards
  // for the whole of the opponent's half-turn and got to play them.
  // tools/balance.mjs caught it as H02 on the seat that was not even
  // taking its turn.
  //
  // R10 is DRAW-THEN-DISCARD: "drawing at the cap still resolves, then
  // discard 1 to the Necropolis." The discard is attached to the DRAW,
  // not to the turn. Shatterfield already reads it that way -- it
  // raises the forced-discard modal immediately after every draw, not
  // at end of turn -- so this is parity, not a new rule.
  //
  // npcEnforceHandCap at the bottom of each runner STAYS. It is now a
  // no-op in the ordinary case, which is exactly what a backstop should
  // be: the day somebody adds a fifth way to gain a card and forgets
  // this line, the turn still ends legally.
  const drawFromGrim = async (count) => {
    const newHand = [...hand];
    const newGrim = { ...grimoire };
    for (let d = 0; d < count; d++) {
      const pool = Object.keys(newGrim).filter(gid => newGrim[gid] > 0);
      if (pool.length === 0) break;
      const drawn = pickRandom(pool);
      newHand.push(drawn);
      if (newGrim[drawn] > 1) newGrim[drawn]--; else delete newGrim[drawn];
    }
    hand = newHand; grimoire = newGrim;
    await updatePlayer(code, NPC_IDX, { hand, grimoire });
    hand = await npcEnforceHandCap(code, hand);   // R10 — draw, THEN discard
    await updatePlayer(code, NPC_IDX, { hand });
  };

  // Tutor from the Grimoire — prefers cards that complete an ascension
  // line for a standing being, else takes the highest level available.
  // NOTE: the NPC DEPLETES its Grimoire on a tutor (rules-true). The
  // player-side searchAndAdd currently does NOT — flagged as a
  // player-side bug in the Awakening tally, not mirrored here.
  const tutorFromGrim = async (filter, count) => {
    const wantIds = [];
    (field.esclings || []).forEach(s => {
      const cur = getCard(s?.escendant);
      if (!cur || cur.level >= 3 || s?.fallen) return;
      Object.keys(grimoire || {}).forEach(gid => {
        const c = getCard(gid);
        if (c && canAscendNatural(cur, c) && filter(c)) wantIds.push(gid);
      });
    });
    const newHand = [...hand];
    const newGrim = { ...grimoire };
    for (let d = 0; d < count; d++) {
      const pool = Object.keys(newGrim).filter(gid => newGrim[gid] > 0 && filter(getCard(gid)));
      if (pool.length === 0) break;
      const chosen = pool.find(gid => wantIds.includes(gid))
        || pool.sort((a, b) => (getCard(b)?.level || 0) - (getCard(a)?.level || 0))[0];
      newHand.push(chosen);
      if (newGrim[chosen] > 1) newGrim[chosen]--; else delete newGrim[chosen];
    }
    hand = newHand; grimoire = newGrim;
    await updatePlayer(code, NPC_IDX, { hand, grimoire });
  };

  const done = () => ({ hand, field, grimoire, matchOver });

  // ── Self-flag setters ──
  if (["p_h01_1", "p_shadow_fox_2", "p_shadow_fox_3"].includes(id)) {
    await setSlotFlags({ intangible: true }); return done();
  }
  if (id === "p_h01_2") { await setSlotFlags({ mustBeRuinedFirst: true }); return done(); }
  if (id === "p_h09_3" || id === "aff_holy") { await setSlotFlags({ nullifyNextAttack: true }); return done(); }
  if (id === "p_lucky_dragon_3") { await setSlotFlags({ reflectNextAttack: true }); return done(); }
  if (["p_xulan_2", "p_inverted_serpent_2"].includes(id)) { await setSlotFlags({ dimAnimaOnAttack: 1 }); return done(); }
  if (["p_shadow_fox_1", "p_spectral_tiger_1", "p_inverted_serpent_1"].includes(id)) {
    await setSlotFlags({ canDirectAnima: true }); return done();
  }
  if (id === "p_spectral_tiger_2") { await setSlotFlags({ spectralClaws: true }); return done(); }
  if (id === "u_inverted_serpent_lord") { await setSlotFlags({ invFangsBuff: true }); return done(); }

  // ── Self-Anima costs + flags ──
  // R96 (2026-08-26): every one of these is a PAYMENT and is refused
  // outright when it would take the brain to 0 — see
  // npcTillRefusesSelfDim. The `dimNpcAnima` return branches below are
  // therefore unreachable on these seven sites; they are kept because
  // they are the correct handling and because deleting an unreachable
  // guard is how the hole came back the last two times.
  if (id === "p_xulan_3") {
    if (await npcTillRefusesSelfDim(code, 4)) return done();          // R96
    if (await dimNpcAnima(code, 4)) { matchOver = true; return done(); }
    await setSlotFlags({ nullifyNextAttack: true }); return done();
  }
  if (id === "p_z4z_1") {
    if (await npcTillRefusesSelfDim(code, 5)) return done();          // R96
    if (await dimNpcAnima(code, 5)) { matchOver = true; return done(); }
    await setSlotFlags({ extraAttack: true }); return done();
  }
  if (id === "p_z4z_2") { await setSlotFlags({ extraAttack: true }); return done(); }
  if (id === "p_z4z_3") {
    if (await npcTillRefusesSelfDim(code, 8)) return done();          // R96
    if (await dimNpcAnima(code, 8)) { matchOver = true; return done(); }
    const newEsclings = (field.esclings || []).map(s => s?.escendant ? { ...s, extraAttack: true } : s);
    field = { ...field, esclings: newEsclings };
    await updatePlayer(code, NPC_IDX, { field });
    return done();
  }
  if (id === "p_antdroid_1") {
    if (await npcTillRefusesSelfDim(code, 5)) return done();          // R96
    if (await dimNpcAnima(code, 5)) { matchOver = true; return done(); }
    await setSlotFlags({ extraAttackAnimaOnly: true }); return done();
  }

  // ── Draws / tutors ──
  if (id === "p_dg0_2") {
    if (await npcTillRefusesSelfDim(code, 1)) return done();          // R96
    if (await dimNpcAnima(code, 1)) { matchOver = true; return done(); }
    await drawFromGrim(1); return done();
  }
  if (id === "p_antdroid_2") {
    if (await npcTillRefusesSelfDim(code, 4)) return done();          // R96
    if (await dimNpcAnima(code, 4)) { matchOver = true; return done(); }
    await drawFromGrim(2); return done();
  }
  if (id === "p_h01_3") {
    await tutorFromGrim(c => c?.school === "Space" && c?.type === "Escendant", 1); return done();
  }
  if (id === "p_h09_2") {
    await tutorFromGrim(c => c?.school === "Space" && c?.type === "Escendant" && (c?.level || 1) <= 2, 2); return done();
  }
  if (id === "aff_jynxi") {
    // Fiend or Faux — add 1 Shadow Gambling from Grimoire or Necropolis.
    const isSSGCard = (gid) => tomeMode(gid) === "ssg" || getCard(gid)?.ssg;
    const inGrim = Object.keys(grimoire || {}).find(gid => isSSGCard(gid) && (grimoire[gid] || 0) > 0);
    if (inGrim) {
      const newGrim = { ...grimoire };
      if (newGrim[inGrim] > 1) newGrim[inGrim]--; else delete newGrim[inGrim];
      hand = [...hand, inGrim]; grimoire = newGrim;
      await updatePlayer(code, NPC_IDX, { hand, grimoire });
      return done();
    }
    const mFresh = await getMatch(code);
    const nec = [...((mFresh?.players?.[NPC_IDX]?.necropolis) || [])];
    const necIdx = nec.findIndex(nid => isSSGCard(nid));
    if (necIdx >= 0) {
      const picked = nec.splice(necIdx, 1)[0];
      hand = [...hand, picked];
      await updatePlayer(code, NPC_IDX, { hand, necropolis: nec });
    }
    return done();
  }
  // ⭐ PAY DAY / BLACK CAT'S BEST FRIEND — rewritten 2026-08-20 after
  // tools/balance.mjs caught the hand of a seat that was not even
  // taking its turn climbing to ten cards. Four defects in eight lines,
  // and every one of them is a divergence from the PLAYER'S copy of the
  // same card in Shatterfield (p_lucky_dragon_1 / _2), which is right.
  //
  // THE CARD, verbatim from cards.js:
  //   Pay Day — "Dim your own Anima by 1. Flip a coin and choose your
  //   side; if it's your side, draw 1 card; if not, your adversary
  //   draws 1 AS WELL."
  //
  // "AS WELL" is the whole card. The owner draws EITHER WAY. Losing the
  // flip does not cost you the draw, it gives your adversary one too --
  // which is what makes it a gamble worth taking instead of a coin that
  // sometimes just helps the other guy.
  //
  //   1 · THE OWNER LOST ITS DRAW ON TAILS. The old branch read
  //       "heads: NPC draws / tails: PLAYER draws", so the NPC was
  //       playing a strictly worse card than the human holding the
  //       same one, half the time, forever.
  //   2 · PAY DAY'S ANIMA COST WAS NEVER PAID. "Dim your own Anima by
  //       1" — the player pays it (and can die to it, which is checked
  //       there). The NPC played it free.
  //   3 · THE PLAYER'S GRIMOIRE WAS NEVER DEPLETED, so a card drawn
  //       this way was COPIED, not moved: the same card in the hand and
  //       still in the deck. The player side had this exact bug and
  //       fixed it in July ("GRIM/LUX FIX"); the comment that used to
  //       sit here cited the OLD behaviour as its justification, which
  //       is how a stale comment becomes a live duplication bug.
  //   4 · NO HAND LIMIT. Shatterfield refuses to overfill the ADVERSARY
  //       (`if (whoIdx === oppIdx && hand.length >= HAND_LIMIT) return`)
  //       precisely because the adversary cannot be shown a forced
  //       discard modal by the card's owner. R10's draw-then-discard is
  //       something you do to YOUR OWN hand. The NPC had no such guard,
  //       so it could push a human to ten cards with no way to correct
  //       it until their next draw — and in NPC vs NPC it simply stayed
  //       there.
  //
  // The helper below is the mirror of Shatterfield's pdDrawOne/
  // bcDrawTwo, deliberately shaped the same way so the two can be
  // diffed by eye.
  if (id === "p_lucky_dragon_1" || id === "p_lucky_dragon_2") {
    const count = id === "p_lucky_dragon_1" ? 1 : 2;

    // Pay Day's cost — same as the player's copy, and since R96 was
    // widened on 2026-08-26 that means REFUSED rather than fatal.
    if (id === "p_lucky_dragon_1") {
      if (await npcTillRefusesSelfDim(code, 1)) return done();        // R96
      if (await dimNpcAnima(code, 1)) { matchOver = true; return done(); }
    }

    // Draw for the ADVERSARY (seat 0), depleting their Grimoire and
    // never exceeding their hand limit. The owner's own draw goes
    // through drawFromGrim, which already deplete-and-writes correctly.
    const drawForPlayer = async (n) => {
      const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
      const newG = { ...(pFresh.grimoire || {}) };
      const pool = Object.entries(newG).flatMap(([gid, qty]) => Array(qty).fill(gid));
      if (!pool.length) return;
      const room = Math.max(0, NPC_HAND_LIMIT - (pFresh.hand || []).length);
      const take = pool.sort(() => Math.random() - 0.5).slice(0, Math.min(n, room));
      if (!take.length) return;
      take.forEach((gid) => { if (newG[gid] > 1) newG[gid]--; else delete newG[gid]; });
      await updatePlayer(code, 0, { hand: [...(pFresh.hand || []), ...take], grimoire: newG });
    };

    // The owner draws either way. On a loss, so does the adversary.
    await drawFromGrim(count);
    if (Math.random() >= 0.5) await drawForPlayer(count);
    return done();
  }

  // ── Player-facing dims (win checks live inside dimPlayerAnima) ──
  if (id === "u_h01_lord") { matchOver = await dimPlayerAnima(code, 4); return done(); }
  if (id === "u_premier_dragon_lord") { matchOver = await dimPlayerAnima(code, 10); return done(); }
  if (id === "u_shadow_fox_lord") {
    await dimAllPlayerLyf(code, 2);
    matchOver = await dimPlayerAnima(code, 1); return done();
  }
  if (id === "u_spectral_tiger_lord") {
    await dimAllPlayerLyf(code, 1);
    matchOver = await dimPlayerAnima(code, 2); return done();
  }
  if (id === "u_h09_lord") {
    // Radiant Shower — the choice: wide Lyf pressure vs direct damage.
    if (standingBeings(playerLive.field || {}).length >= 2) await dimAllPlayerLyf(code, 1);
    else matchOver = await dimPlayerAnima(code, 2);
    return done();
  }
  if (id === "p_spectral_tiger_3") { await dimAllPlayerLyf(code, 2); return done(); }
  if (id === "p_inverted_serpent_3") {
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const cnt = ((pFresh.field?.esclings) || []).filter(s => s?.intangible).length;
    if (cnt > 0) matchOver = await dimPlayerAnima(code, cnt * 3);
    return done();
  }

  // ── Board manipulation ──
  if (id === "u_antdroid_lord") {
    const newEsclings = (field.esclings || []).map(s =>
      s?.escendant && !s.fallen ? { ...s, animaRedirectAll: true } : s);
    field = { ...field, esclings: newEsclings };
    await updatePlayer(code, NPC_IDX, { field });
    return done();
  }
  if (id === "u_chaos_dragon_lord") {
    // I or None — both Warlocks ruin beings until 1 remains. Universal
    // ruin semantics on BOTH sides via descendPlayerSlot. The NPC's own
    // descents mutate the CALLER's field draft (sync discipline).
    const mFresh = await getMatch(code);
    const npcNec = [...((mFresh?.players?.[NPC_IDX]?.necropolis) || [])];
    const myAlive = (field.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
    for (const { i } of myAlive.slice(0, -1)) {
      descendPlayerSlot(field, npcNec, i, turn);
      await updatePlayer(code, NPC_IDX, { field, necropolis: npcNec });
      await sleep(POST_RUIN_DELAY);
    }
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pField = JSON.parse(JSON.stringify(pFresh.field || {}));
    pField.tomes = riftArray(pField.tomes, 3);
    const pNec = [...(pFresh.necropolis || [])];
    const pAlive = (pField.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
    for (const { i } of pAlive.slice(0, -1)) {
      descendPlayerSlot(pField, pNec, i, turn);
      await updatePlayer(code, 0, { field: pField, necropolis: pNec });
      await sleep(POST_RUIN_DELAY);
    }
    return done();
  }
  if (id === "u_dg0_lord") {
    // Justice Reigns — ruin 1 opp Non-Being, else draw 1. The NPC never
    // targets a Prophecy (Ruling 5: prophecies are structural).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pTomes = riftArray(pFresh.field?.tomes, 3);
    const mrf = getRuinFirstTome(pFresh.field);
    const tomeIdx = mrf ? mrf.slotIdx : pTomes.findIndex(t => t?.id && !t.faceDown);
    if (tomeIdx >= 0) {
      await ruinPlayerTomeSlot(code, tomeIdx);
      await sleep(ACTION_DELAY);
      return done();
    }
    const curios = pFresh.field?.curios || [];
    const curioIdx = curios.findIndex(c => c);
    if (curioIdx >= 0) {
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      const cid = pf.curios[curioIdx];
      pf.curios[curioIdx] = null;
      await updatePlayer(code, 0, { field: pf, necropolis: [...(pFresh.necropolis || []), cid] });
      await sleep(ACTION_DELAY);
      return done();
    }
    const soulTargets = (pFresh.field?.esclings || []).flatMap((s, si) =>
      (s?.soul || []).filter(sid => {
        const c = getCard(sid);
        return c?.type === "Impera" || c?.type === "U'hlti";
      }).map(sid => ({ sid, si })));
    if (soulTargets.length > 0) {
      const pick = soulTargets.sort((a, b) => (getCard(b.sid)?.level || 0) - (getCard(a.sid)?.level || 0))[0];
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      // R70 — see stripSoulCard. THIS is the site the balancer's A06/F08
      // actually came from: tearing a U'hlti off an adversary's Supreme
      // left its Affinity worn by a being that no longer qualifies. The
      // Affinity leaves with it.
      const stripP = stripSoulCard(pf.esclings[pick.si].soul, pick.sid);
      pf.esclings[pick.si] = { ...pf.esclings[pick.si], soul: stripP.soul };
      await updatePlayer(code, 0, { field: pf, necropolis: [...(pFresh.necropolis || []), ...stripP.gone] });
      await sleep(ACTION_DELAY);
      return done();
    }
    await drawFromGrim(1);
    return done();
  }
  if (id === "p_chaos_dragon_1") {
    // Natural Disaster — ruin 1 opp Non-Being (Tome or Curio). MRF tome
    // eats the effect first (mirrors section 10's priority).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pTomes = riftArray(pFresh.field?.tomes, 3);
    const mrf = getRuinFirstTome(pFresh.field);
    const tomeIdx = mrf ? mrf.slotIdx : pTomes.findIndex(t => t?.id && !t.faceDown);
    if (tomeIdx >= 0) {
      await ruinPlayerTomeSlot(code, tomeIdx);
      await sleep(ACTION_DELAY);
      return done();
    }
    const curios = pFresh.field?.curios || [];
    const curioIdx = curios.findIndex(c => c);
    if (curioIdx >= 0) {
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      const cid = pf.curios[curioIdx];
      pf.curios[curioIdx] = null;
      await updatePlayer(code, 0, { field: pf, necropolis: [...(pFresh.necropolis || []), cid] });
      await sleep(ACTION_DELAY);
    }
    return done();
  }
  if (id === "aff_evil") {
    // X'aol — send 1 player card to Necropolis (never an Affinity).
    // Priority: strongest standing being (universal ruin semantics) →
    // face-up tome (soak-aware) → curio. Refused under any MRF (mirror).
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const pField = JSON.parse(JSON.stringify(pFresh.field || {}));
    pField.tomes = riftArray(pField.tomes, 3);
    if (getRequiredTargetNpc(pField)) return done();
    const pNec = [...(pFresh.necropolis || [])];
    const beings = (pField.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant && !s.fallen);
    if (beings.length > 0) {
      const target = beings.sort((a, b) =>
        (getCard(b.s.escendant)?.level || 0) - (getCard(a.s.escendant)?.level || 0))[0];
      descendPlayerSlot(pField, pNec, target.i, turn);
      await updatePlayer(code, 0, { field: pField, necropolis: pNec });
      await sleep(POST_RUIN_DELAY);
      return done();
    }
    const tomeIdx = pField.tomes.findIndex(t => t?.id && !t.faceDown);
    if (tomeIdx >= 0) {
      await ruinPlayerTomeSlot(code, tomeIdx);
      await sleep(ACTION_DELAY);
      return done();
    }
    const curioIdx = (pField.curios || []).findIndex(c => c);
    if (curioIdx >= 0) {
      const cid = pField.curios[curioIdx];
      pField.curios[curioIdx] = null;
      await updatePlayer(code, 0, { field: pField, necropolis: [...pNec, cid] });
      await sleep(ACTION_DELAY);
    }
    return done();
  }
  if (id === "aff_awoken") {
    // Enlightenment — nullify ONE player Affinity.
    // ── NULLIFICATION CANON (caP'n ruling, July 10 2026): nullified
    // means USELESS, not removed. The Affinity STAYS attached to the
    // soul — a dead sigil — and both activation gates refuse it. The
    // old "removed from the soul" mirror is superseded by this ruling.
    const pFresh = (await getMatch(code))?.players?.[0] || playerLive;
    const affTargets = (pFresh.field?.esclings || []).flatMap((s, si) =>
      (s?.soul || []).filter(sid => getCard(sid)?.type === "Affinity"
        && !((s?.nullifiedSouls) || []).includes(sid)).map(sid => ({ sid, si })));
    if (affTargets.length > 0) {
      const pick = affTargets[0];
      const pf = JSON.parse(JSON.stringify(pFresh.field || {}));
      const slN = pf.esclings[pick.si];
      pf.esclings[pick.si] = {
        ...slN,
        nullifiedSouls: [...new Set([...(slN.nullifiedSouls || []), pick.sid])],
      };
      await updatePlayer(code, 0, { field: pf });
      await sleep(ACTION_DELAY);
    }
    return done();
  }
  if (id === "u_lucky_dragon_lord") {
    // Dragon Eyes — roll 2 dice; sum 2 = +2 Shadow Gambling streak +
    // dragonEyesBonus; anything else breaks the streak. Beb-only gamble.
    const mFresh = await getMatch(code);
    const npcLive = mFresh?.players?.[NPC_IDX] || {};
    const d1 = Math.floor(Math.random() * 6) + 1;
    const d2 = Math.floor(Math.random() * 6) + 1;
    if (d1 + d2 === 2) {
      field = { ...field, dragonEyesBonus: true };
      await updatePlayer(code, NPC_IDX, {
        shadowGamblingStreak: (npcLive.shadowGamblingStreak || 0) + 2,
        field,
      });
    } else {
      await updatePlayer(code, NPC_IDX, { shadowGamblingStreak: 0 });
    }
    return done();
  }

  return done(); // unknown id — nothing applied (gate should prevent this)
}

// ⭐ R70 ON THE WAY OUT — "an Affinity is only attachable when the
// Natural L3 has its U'hlti." Attaching honours that (attachOneSoulCard);
// nothing honoured it when a card was TORN OFF. An Omnia counter aimed
// at a U'hlti spliced exactly that one id out of the soul and left the
// Affinity sitting on a being that no longer qualifies to wear it —
// GameStateValidator calls that state impossible (A06 / F08) and it is
// right. The Affinity rides out with its U'hlti, into the Necropolis
// where the rest of a torn soul goes.
//
// Found by the balancer, not by reading: it only became reachable once
// the Beb started dressing its L3s, which put U'hltis on three times as
// many boards. The reverse order is safe — tearing off an Affinity
// leaves a U'hlti that is legal on its own.
function stripSoulCard(soul, soulId) {
  const card = getCard(soulId);
  const gone = [soulId];
  if (card?.type === "U'hlti") {
    (soul || []).forEach(id => { if (getCard(id)?.type === "Affinity") gone.push(id); });
  }
  return { soul: (soul || []).filter(id => !gone.includes(id)), gone };
}

async function activateOneSoulCard(code, hand, field, grimoire, turn, used, difficulty) {
  const m = await getMatch(code);
  const seatCount = m?.seatCount || 2;
  const npcLive = m?.players?.[NPC_IDX] || {};
  const playerLive = m?.players?.[0] || {};
  const playerField = playerLive.field || {};
  const npcAnima = npcLive.anima ?? 30;
  const esclings = field.esclings || [];
  const ctxGate = {
    field, playerField, playerLive, npcAnima, turn, hand, grimoire,
    seatCount,   // R119 — the wall predicate needs it; see playerAnimaClear
    playerStanding: standingBeings(playerField).length,
    npcStanding: standingBeings(field).length,
    myHighLevel: highestLevel(esclings),
    difficulty,
  };

  for (let slotIdx = 0; slotIdx < esclings.length; slotIdx++) {
    const slot = esclings[slotIdx];
    if (!slot?.escendant) continue;
    const soul = slot.soul || [];
    for (const soulId of soul) {
      const key = slotIdx + ":" + soulId;
      if (used.has(key)) continue;                 // the modesty dial
      if (SOUL_PASSIVE_IDS.has(soulId)) continue;
      const card = getCard(soulId);
      if (!card) continue;
      // ── THE GATE, ASKED BY THE BRAIN ─────────────────────────────
      // ruleGate's own banner says "Every rule-bearing action in
      // Shatterfield — player OR NPC — passes through enforce()... The
      // NPC and the player run the identical check." That was a CLAIM:
      // NPC.js imported exactly one thing from ruleGate (sealAnimaGain)
      // and asked `enforce` nowhere, so every rule the brain obeyed was a
      // re-implementation that could drift — and R122 is the drift that
      // had already happened.
      //
      // This is the first brain action routed through the real gate. It
      // carries R108's phase door and R49 ("that card is not attached to
      // that being") as well as R122, out of the same validator the
      // player's gateSoulActivation calls.
      //
      // ⚠ MEASURED BEFORE ROUTING, because the gate FAILS CLOSED and a
      // gate that refuses what the brain used to allow would silence its
      // soul cards in the field. Compared against the brain's own guard
      // over 1,440 shapes — six cards × their real effectTags × eight
      // last-fired stamps × five half-turns × three phase values ×
      // attached/not: 0 disagreements, 0 gate errors. Then the same
      // balancer seed, both sides. If either number ever moves, this line
      // is the suspect.
      //
      // The cadence read stays on the next line as the audit trail: it is
      // the clause this call is FOR, so the refusal names it even when
      // the gate's reason is generic.
      {
        const v = enforce("ACTIVATE_SOUL", { me: npcLive, opponent: playerLive, turn, seatCount },
                          { being: slot, soulCard: soulId });
        if (!v.valid) continue;   // R108 / R49 / R122 — see ruleGate
      }
      if (!soulCardOffCooldown(slot, soulId, card.effectTag, turn, seatCount)) continue;   // R122
      if (difficulty === "Champion" && !CHAMPION_SOUL_IDS.has(soulId)) continue;

      if (!soulEffectUseful(soulId, slot, slotIdx, ctxGate)) continue;

      // ── R105 · THE PRINTED FARE, BOTH HALVES, EVERY ACTIVATION ──
      // ⚠ THIS READ `card.type === "U'hlti" ? (card.animaCost || 0) : 0`,
      // so an Impera or Affinity printing an Anima number activated with
      // the Anima half free. The board carried the identical bug and lost
      // it; `printedFare` is the shared answer and it asks the card, never
      // the type. (Latent on today's data — every animaCost in cards.js
      // belongs to a U'hlti — and fixed anyway, because "latent" is not a
      // rule.)
      const { khao: cost, anima: animaCost } = printedFare(card);
      // ⚠ AND THE KHAO IS READ FRESH. `npcLive` was taken once at the top
      // of this function, and the runner calls it in a loop — so after an
      // earlier firing spent Khao this pre-check was answering about a pool
      // that no longer existed. Combined with paying ANIMA FIRST (below),
      // that took the Anima and then failed the Khao half: a fare paid for
      // nothing, which is the exact bug class the Tome ledger fought.
      const khaoNow = (await getMatch(code))?.players?.[NPC_IDX]?.khao ?? 0;
      if (!canPayPrintedFare(card, { khao: khaoNow, anima: npcAnima })) continue;
      if (animaCost > 0 && npcAnima <= animaCost + 4) continue; // never suicide on a fare
      // (The brain guard for this site is the `npcAnima <= animaCost + 4`
      // line directly above — a WIDER margin than the till's, on purpose:
      // a soul fare is discretionary and the brain keeps a cushion. See
      // npcSpendAnima for why the till itself no longer refuses.)
      // KHAO BEFORE ANIMA — the board's order (Shatterfield ·
      // handleActivateSoulCard). Anima is a life total; it must never be
      // the leg that is spent on a fare the other leg cannot complete.
      if (cost > 0 && !(await npcSpendKhao(code, cost))) continue;
      if (animaCost > 0 && !(await npcSpendAnima(code, animaCost))) continue;

      used.add(key);
      // ── R122 · THE STAMP LANDS WITH THE FARE, NOT WITH THE EFFECT ──
      // The board's own words: "a firing that is nullified downstream
      // still spent the Turn's use, exactly as a countered Tome's
      // cooldown anchor does." So it is written HERE — after the fare,
      // before the response window that can void the firing.
      // ⚠ IT GOES ONTO THE CALLER'S DRAFT AS WELL AS TO FIREBASE. THE
      // REBASE LAW: the runner writes this whole `field` again at end of
      // turn, so a stamp that existed only in Firebase would be erased by
      // the draft on its way out — which is how an OP3 would come back
      // off cooldown a Turn early.
      {
        const stampEsc = [...(field.esclings || [])];
        stampEsc[slotIdx] = { ...slot, soulUsed: stampSoulUsed(slot, soulId, turn) };
        field = { ...field, esclings: stampEsc };
        await updatePlayer(code, NPC_IDX, { field });
      }
      // ── RESPONSE WINDOW — a soul-card effect is a card activation
      // (canon: "any card effect in use" — Sword & Shield semantics).
      // Nihil voids this firing, fares stay spent. Omnia ruins the
      // soul card OFF the being: spliced from the Soul, sent to the
      // NPC's Necropolis, effect never resolves.
      // COUNTER HIERARCHY (caP'n ruling): a LATE counter (L1/L2 vs a
      // L3+ soul card) cannot pre-empt — the effect resolves first,
      // then the soul card must leave the field all the same.
      let lateCounter = false;
      let windowResponded = false;
      {
        const rw = await requestWindow({ kind: "ACTIVATION", cardId: soulId });
        windowResponded = !!rw?.responded;
        if (rw?.counter && rw?.late) {
          lateCounter = true; // effect proceeds below; splice after
        } else if (rw?.counter) {
          if (rw.counter === "omnia") {
            const slotNow = field.esclings?.[slotIdx];
            if (slotNow) {
              const newEsc = [...(field.esclings || [])];
              const strip1 = stripSoulCard(slotNow.soul, soulId);   // R70
              newEsc[slotIdx] = { ...slotNow, soul: strip1.soul };
              field = { ...field, esclings: newEsc };
              const mNec = await getMatch(code);
              const npcNec = [...(mNec?.players?.[NPC_IDX]?.necropolis || []), ...strip1.gone];
              await updatePlayer(code, NPC_IDX, { field, necropolis: npcNec });
            }
          }
          await sleep(TOME_INTERRUPT);
          return { hand, field, grimoire, activated: true, matchOver: false };
        }
      }
      // ── Wave 4 (C12 a): re-base after a responded window — a generic
      // interrupt may have rewritten either side mid-window. If the
      // being (or this very soul card) vanished, the firing fizzles
      // with its fare paid — same law as every other funnel.
      let playerLiveW = playerLive;
      if (windowResponded) {
        hand = await syncNpcHand(code, hand); // THE REBASE LAW (see npcReactorDirty)
        const mW = await getMatch(code);
        const npcW = mW?.players?.[NPC_IDX];
        if (npcW?.field) field = JSON.parse(JSON.stringify(npcW.field));
        playerLiveW = mW?.players?.[0] || playerLive;
        const slotW = field.esclings?.[slotIdx];
        if (!slotW?.escendant || !(slotW.soul || []).includes(soulId)) {
          await sleep(TOME_INTERRUPT);
          return { hand, field, grimoire, activated: true, matchOver: false };
        }
      }
      const r = await applyNpcSoulEffect(code, soulId, slotIdx, { hand, field, grimoire, playerLive: playerLiveW, turn });
      if (lateCounter && !r.matchOver) {
        // Effect has resolved — now the countered soul card leaves.
        const fld = r.field || field;
        const slotNow = fld.esclings?.[slotIdx];
        if (slotNow && (slotNow.soul || []).includes(soulId)) {
          const newEsc = [...(fld.esclings || [])];
          const strip2 = stripSoulCard(slotNow.soul, soulId);       // R70
          newEsc[slotIdx] = { ...slotNow, soul: strip2.soul };
          r.field = { ...fld, esclings: newEsc };
          const mNec = await getMatch(code);
          const npcNec = [...(mNec?.players?.[NPC_IDX]?.necropolis || []), ...strip2.gone];
          await updatePlayer(code, NPC_IDX, { field: r.field, necropolis: npcNec });
        }
      }
      await sleep(TOME_INTERRUPT);
      return { hand: r.hand, field: r.field, grimoire: r.grimoire, activated: true, matchOver: r.matchOver || false };
    }
  }
  return { hand, field, grimoire, activated: false, matchOver: false };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: GRIMOIRE SEARCH — mirror of handleGrimoireSearch.
// Pay 2 (L1) / 4 (L2+) Khao-else-Anima to tutor a Being of the chosen
// level from the Grimoire into hand. CONSUMES the turn's draw — the
// caller must treat a successful search as its draw for the turn.
// The player-side pay-then-refund-on-empty dance is avoided by
// pre-checking the pool BEFORE paying (same net rules, no dead writes).
// `wantIds` lets the caller steer the pick toward a specific ascension
// line; otherwise the highest-Lyt candidate is taken.
// ─────────────────────────────────────────────────────────────
// ⚠ `turn` IS R08's GATE AND IT IS NOT OPTIONAL IN SPIRIT (2026-08-25).
// caP'n: *"they are also not allowed to search their Luxmoire that
// first turn, nor Grimoire."* Both search helpers are the NPC's ONLY
// route to either Book, so the ban lives here rather than at the five
// call sites — a rule enforced at one chokepoint cannot be forgotten at
// the sixth. `seatCount` used to default to 2 and NO CALLER PASSED IT,
// on the reasoning that every brain in this file drives a two-seat
// board. That reasoning is an assumption about the table, written into
// a rule predicate — and R08's window is `openingCycleLength(seatCount)`
// half-turns long, so on a four-seat table the assumption is simply
// wrong. Every caller passes the real number now; the default survives
// only as a fallback for a caller that has no match in hand.
//
// ── AND THE BAR IS THE PLAYER'S SEARCH ONLY (caP'n, 2026-08-26) ──
// caP'n, verbatim: *"brilliant distinction yes, cards are exempt from
// the first turn search rule"*. R08 bars the SEAT'S OWN search action —
// which is precisely what this helper is: it pays the R88 fare and it
// spends the Turn's draw. A CARD EFFECT that searches is exempt, and
// the brain's card-effect tutors do not come through here at all (see
// execTutor, the fare-free twin, which is the exemption's living
// example and has never asked this question). `effectSearch` is
// threaded anyway, explicitly, because the two searches are identical
// from the payload and the only place that can tell them apart is the
// caller — so the caller says so rather than this helper guessing from
// the fare or the Book. It is the exact twin of the draw exemption in
// the same ruling ("card effects excluded", `effectDrewThisTurn`).
//
// ⚠ AND IT IS THE SEARCH BAN ONLY. The Attack ban is a different ban,
// enforced at the stamp scan above (Phantom, Beast) and by the R08 seal
// at gameManager. A card can be caught by one and not the other.
async function grimoireSearchOne(code, hand, grimoire, level, wantIds = null, turn = null, seatCount = 2, effectSearch = false) {
  if (!isEffectSearch(effectSearch) && turn != null && isOpeningTurnIndex(turn, seatCount)) return { hand, grimoire, searched: false };
  const pool = Object.keys(grimoire || {}).filter(id => {
    const c = getCard(id);
    return c?.type === "Escendant" && c?.level === level && (grimoire[id] || 0) > 0;
  });
  if (pool.length === 0) return { hand, grimoire, searched: false };
  // R88 — the fare is BY LEVEL, and rules.js owns the numbers now.
  // (The first cut of R88 wrote a flat 4 into the catalogue and this
  // line followed it, which would have quietly doubled the L1 fare the
  // shipping board has always charged. The catalogue was the thing
  // that was wrong.)
  const cost = grimSearchFare(level);
  if (!(await npcPayKhaoElseAnima(code, cost))) return { hand, grimoire, searched: false };

  let chosen = null;
  if (wantIds && wantIds.length) chosen = pool.find(id => wantIds.includes(id)) || null;
  if (!chosen) chosen = pool.sort((a, b) => (getCard(b)?.lyt || 0) - (getCard(a)?.lyt || 0))[0];

  const newGrimoire = { ...grimoire };
  if (newGrimoire[chosen] > 1) newGrimoire[chosen]--; else delete newGrimoire[chosen];
  const newHand = [...hand, chosen];
  await updatePlayer(code, NPC_IDX, { hand: newHand, grimoire: newGrimoire });
  await sleep(SEARCH_DELAY);
  return { hand: newHand, grimoire: newGrimoire, searched: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: LUXMOIRE SEARCH — mirror of handleLuxmoireSearch.
// 5 Khao to tutor ANY Impera from the Luxmoire into hand.
// CANON (caP'n ruling, July 4 2026): the search CONSUMES the turn's
// draw, exactly like Grimoire Search — the live player modal sets
// drawnThisTurn and that is the rule. (Rulebook ch.8's "does not
// consume your draw" line is the stale text; caP'n is amending it.)
// Callers must treat a successful search as the turn's draw.
// The NPC only ever searches for a card it can USE right now — a
// Impera that is trainable this instant (legal target standing, no
// duplicate-level conflict), or (DEPTHS BRAIN, prophecy push) a missing
// Supreme piece passed in via extraWantIds. No dead tutors.
// ── R106 · IMPERA ONLY, AND THIS IS NOW THE RULE (caP'n, 2026-08-26) ──
//   *"And just to make sure — you can still only search the Lux for
//   Impera, not the U/A's."*
//
// ⚠ THE PARITY NOTE THAT STOOD HERE WAS BACKWARDS AND IT COST A HOLE.
// It read: "the player's Luxmoire Search modal offers EVERY Luxmoire
// card (Impera, U'hltis, Affinities) — the old Impera-only restriction
// here was NPC conservatism, not a rule." The brain's caution was
// RIGHT and the player's modal was WRONG; both are Impera-only now.
// The hole this leaves behind is the `extras` lane below, which was
// built to tutor MISSING SUPREME PIECES for a prophecy push — and a
// Supreme piece is an U'hlti or an Affinity, which is precisely what
// the ruling bars. See the filter there.
// The fare is Khao-ELSE-Anima, exactly as on the player side — one
// pool or the other, never a split (R107).
// ─────────────────────────────────────────────────────────────
// R08 — see grimoireSearchOne's banner. Same law, other Book, and the
// same 2026-08-26 narrowing: this is the brain's own fare-paying search
// action, so the opening-Cycle bar applies to it in full; a card effect
// that searches the Luxmoire is exempt and passes `effectSearch`.
async function luxmoireSearchOne(code, hand, field, luxmoire, extraWantIds = null, turn = null, seatCount = 2, effectSearch = false) {
  if (!isEffectSearch(effectSearch) && turn != null && isOpeningTurnIndex(turn, seatCount)) return { hand, luxmoire, searched: false };
  const esclings = field.esclings || [];
  const trainable = Object.keys(luxmoire || {}).filter(id => {
    if ((luxmoire[id] || 0) <= 0) return false;
    const card = getCard(id);
    if (card?.type !== "Impera") return false;
    return esclings.some(slot => {
      if (!slot?.escendant) return false;
      const being = getCard(slot.escendant);
      if (!being) return false;
      if (card.school !== being.school || card.ilk !== being.ilk) return false;
      if ((card.level || 1) > (being.level || 1)) return false;
      const soul = slot.soul || [];
      return !soul.some(sid => {
        const sc = getCard(sid);
        return sc?.type === "Impera" && sc?.level === card.level;
      });
    });
  });
  // ── R106 · THE SUPREME-PIECE LANE IS BARRED TOO ──────────────
  // `extraWantIds` is the DEPTHS BRAIN's prophecy push: "I am one
  // U'hlti short of a Supreme Being, go and find it." That is exactly
  // the tutor caP'n closed — *"you can still only search the Lux for
  // Impera, not the U/A's"* — so the want-list is filtered by the same
  // predicate as the trainable list rather than being trusted because
  // a brain asked for it. An Impera named in extraWantIds still comes
  // through, which is the half of this lane that was always legal.
  const extras = (extraWantIds || []).filter(id =>
    (luxmoire?.[id] || 0) > 0 && isLuxSearchable(getCard(id)));
  if (trainable.length === 0 && extras.length === 0) return { hand, luxmoire, searched: false };
  if (!(await npcPayKhaoElseAnima(code, LUX_SEARCH_COST))) return { hand, luxmoire, searched: false }; // R88

  // Supreme pieces first (they only arrive during a prophecy push),
  // then the highest-level trainable Impera.
  const chosen = extras[0]
    || trainable.sort((a, b) => (getCard(b)?.level || 0) - (getCard(a)?.level || 0))[0];
  const newLuxmoire = { ...luxmoire };
  if (newLuxmoire[chosen] > 1) newLuxmoire[chosen]--; else delete newLuxmoire[chosen];
  const newHand = [...hand, chosen];
  await updatePlayer(code, NPC_IDX, { hand: newHand, luxmoire: newLuxmoire });
  await sleep(SEARCH_DELAY);
  return { hand: newHand, luxmoire: newLuxmoire, searched: true };
}

// ─────────────────────────────────────────────────────────────
// AWAKENING: RULES-TRUE RESURRECTION — mirror of handleResurrect +
// findResurrectSlot (Shatterfield ~7095-7160, ~7401-7420).
//   R44 — once per CARD INSTANCE per Solrend (resurrectedCards,
//         append-only; never overwritten).
//   R45/R46 — resurrect in order: an L1 lands on an empty Escling
//         slot; an L2/L3 lands ONLY on a slot whose current escendant
//         is the (level-1) of the SAME natural chain (sharedTraits
//         === 2) — never straight onto a bare Escling.
//   R47 — cost 2 (L1) / 4 (L2/L3), paid Khao-else-Anima.
//   R48 — souls preserved; CHAIN FIX — the outgoing lower level is
//         souled so later descents can find it (July 2026 parity).
// The OLD NPC resurrect could drop the highest-level Necropolis being
// straight onto a bare Escling (illegal) and could revive the same
// card every turn forever (no R44 record). Both closed here.
// Reads necropolis/resurrectedCards FRESH — never a turn-start snapshot.
// ─────────────────────────────────────────────────────────────
async function resurrectOne(code, field, matchTurn) {
  const m = await getMatch(code);
  const npcLive = m?.players?.[NPC_IDX] || {};
  const necropolis = [...(npcLive.necropolis || [])];
  const revived = npcLive.resurrectedCards || [];
  const esclings = field.esclings || [];

  // ⭐ R89 — ONE PAID RESURRECTION PER TURN, regardless of level. This
  // is the rite the clock governs; card-effect returns (the Time
  // Primera, Tomes, Impera) are exempt and are recorded elsewhere, so
  // they neither block this nor are blocked by it. Read the clock
  // BEFORE the candidate scan so a Turn already spent costs nothing
  // to discover.
  const resTurn = matchTurn || 0;
  const resClock = resClockOf(npcLive, resTurn);
  if (resClock >= RESURRECT_PER_TURN) return { field, resurrected: false };

  const findResurrectSlot = (card) => {
    if (card.level === 1) {
      return esclings.findIndex(s => !s?.escendant);
    }
    return esclings.findIndex(s => {
      if (!s?.escendant) return false;
      const current = getCard(s.escendant);
      if (!current) return false;
      if (current.level !== card.level - 1) return false;
      return sharedTraits(current, card) === 2;
    });
  };

  // R44 TALLY (per COPY, July 27 2026) + R85 (Graveshift generation).
  // resurrectOne IS the NPC's passive rite, so R85 applies to it exactly
  // as it applies to the player's Necropolis Resurrect — the NPC's own
  // Tome effects reach Graveshifted beings and are not filtered here.
  // Dedupe by BASE, keeping the copy with the most returns left.
  const bestByBase = new Map();
  necropolis.forEach(id => {
    const c = getCard(id);
    if (c?.type !== "Escendant") return;
    if (!canPassiveResurrect(id)) return;                        // R85
    const used = resTallyNpc(revived, id);
    if (used >= (RESURRECT_LIMIT[`L${c.level}`] ?? 1)) return;   // R44
    const slotIdx = findResurrectSlot(c);
    if (slotIdx < 0) return;
    const b = baseId(id);
    const prev = bestByBase.get(b);
    if (!prev || used < prev.used) bestByBase.set(b, { id, card: c, slotIdx, used });
  });
  const candidates = [...bestByBase.values()];
  if (candidates.length === 0) return { field, resurrected: false };

  // Highest level first — an L3 revived over its standing L2 (full
  // soul intact) is the biggest tempo swing available to the NPC.
  candidates.sort((a, b) => (b.card.level || 0) - (a.card.level || 0));
  const pick = candidates[0];

  const cost = pick.card.level === 1 ? 2 : 4;
  if (!(await npcPayKhaoElseAnima(code, cost))) return { field, resurrected: false };

  const newEsclings = [...esclings];
  const resSlot = newEsclings[pick.slotIdx] || {};
  // CHAIN FIX — soul the outgoing lower level so descents can find it
  const resPrevSoul = resSlot.soul || [];
  const resSoul = resSlot.escendant && !resPrevSoul.includes(resSlot.escendant)
    ? [...resPrevSoul, resSlot.escendant]
    : resPrevSoul;
  newEsclings[pick.slotIdx] = {
    ...resSlot,
    escendant: pick.id,
    soul: resSoul,
    fallen: false, fallenTurn: null,
    currentLyf: pick.card.lyf ?? null,
    attackedThisTurn: false,
    intangible: false,
  };
  const necIdx = necropolis.indexOf(pick.id);
  if (necIdx !== -1) necropolis.splice(necIdx, 1);

  const newField = { ...field, esclings: newEsclings };
  await updatePlayer(code, NPC_IDX, {
    field: newField,
    necropolis,
    resurrectedCards: [...revived, pick.id], // R44 record — append-only
    // R89 clock — stamped, never cleared, so a Turn that took two is
    // still legible afterwards (see primeraLedgerOf's banner).
    resurrectedThisTurn: { turn: resTurn, count: resClock + 1 },
  });
  await sleep(RESURRECT_DELAY);
  return { field: newField, resurrected: true };
}

// ═══════════════════════════════════════════════════════════════════
// THE HEADLESS TOME EXECUTOR
// ═══════════════════════════════════════════════════════════════════
// WHAT THIS REPLACES: a flat if-chain of eight `card.id ===` branches
// plus a ninth catch-all that collapsed all nine Shadow Gambling Tomes
// into ONE `Math.random() > 0.5` blind draw. Seventeen of seventy-two.
// The other fifty-five split three unlovely ways: twenty-one were
// flipped, the fare paid, and NOTHING HAPPENED; thirty-two were placed
// face-down and never flipped, squatting one of three rifts for the
// rest of the duel; two were never placed at all.
//
// ── WHY THIS IS TWO JOBS AND NOT ONE ────────────────────────────────
// Shatterfield's applyTomeEffect covers 66 of the 72 in 1,473 lines and
// CANNOT BE CALLED FROM HERE. It is a React closure declared inside the
// component, over setTargetPicker (48 calls), showToast (106),
// updatePlayer (79), matchRef.current, playerIdx and oppIdx.
// Shatterfield.js has exactly one export and it is the component. There
// is no seam and there is no shim that would not be a rewrite.
//
// So this file needs two things the player side gets for free:
//   1. an EXECUTOR — the twelve primitives below, most of which already
//      existed here in a hardcoded form and needed generalising;
//   2. a TARGET-CHOICE BRAIN — the thing setTargetPicker outsources to
//      a human. "Ruin 1 Impera" is, player-side, a modal. Here it has
//      to be a decision AND a write. The pickers below are that
//      decision, and they are deliberately simple and stated:
//      MRF-forced first (the rule), then the biggest thing on offer.
//
// ── THE CONTRACT ────────────────────────────────────────────────────
//   • The row's `steps` are plain data (src/npcTomeBook.js). This
//     function is the ONLY place that turns a step into a write.
//   • SYNC DISCIPLINE, unchanged and load-bearing: draws and tutors
//     mutate the CALLER's hand/grimoire locals and return them. An
//     inline fresh-read draw gets clobbered by the runner's end-of-turn
//     write and the card silently walks back into the Grimoire. That
//     bug has been fixed here twice; do not reintroduce it.
//   • OWN-FIELD writes mutate the caller's `newField` draft IN PLACE
//     and then persist it, for exactly the same reason.
//   • Every step reports whether it CHANGED anything. The aggregate is
//     returned as `changed` and probed as `fire`/`noop`, because the
//     deliverable of this work is a measured number — "Khao spent on
//     Tome fires that produced no state change" — and a number nobody
//     measures is a number nobody fixed.
// ═══════════════════════════════════════════════════════════════════

// ── Akula's ledger ──────────────────────────────────────────────────
// "You gain +1 Anima whenever a being is ruined." Player-side this is
// fireAkulaListenerOnRuin, called from four handlers. The NPC's ruin
// paths are sync mutators (descendPlayerSlot) with no await in them, so
// the count is banked here and flushed at the next await-safe point.
// Both seats are paid — the card does not care whose being died.
let _akulaPending = 0;
const bankAkula = (n = 1) => { _akulaPending += n; };
async function flushAkula(code) {
  if (_akulaPending <= 0) return false;
  const n = _akulaPending; _akulaPending = 0;
  const m = await getMatch(code);
  let paid = false;
  for (const idx of [0, NPC_IDX]) {
    const p = m?.players?.[idx];
    if (!p) continue;
    const has = riftArray(p.field?.tomes, 3).some(t => t?.id === "tome_necklace_akula" && !t.faceDown);
    if (!has) continue;
    // ⚠ THE LAST HAND-ROLLED CEILING IN THE FILE (sealed 2026-08-25).
    // This line was `Math.min(n, MAX_ANIMA - anima)` — the same
    // arithmetic R32's seal exists to abolish, kept here because the
    // LEDGER needs the amount that actually landed, not the amount
    // asked for. It is derived from the sealed write now instead of
    // computed beside it, so there is exactly one ceiling in the
    // expression and `sealAnimaGain` owns it. R32 CAPS, it does not
    // forbid: a seat on 48 asked for 5 lands on 50 and banks 2.
    const before = p.anima ?? STARTING_ANIMA;
    const after = sealAnimaGain(before, n, "NPC.flushAkula");
    const gain = after - before;
    if (gain <= 0) continue;
    await updatePlayer(code, idx, {
      anima: after,
      ...buildAnimaGainUpdate(p, gain),
    });
    paid = true;
  }
  return paid;
}

// ── Full ruin of a PLAYER being, soul and all ───────────────────────
// Distinct from descendPlayerSlot: "ruin 1 being AND its Soul" does not
// descend, it deletes the whole stack. Mirrors Shatterfield's Weeping
// Dagger / Necklace of Congealed Death path, cascade included.
async function ruinPlayerBeingFull(code, slotIdx) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  const slot = f.esclings?.[slotIdx];
  if (!slot?.escendant) return false;
  // 🔥 R68 — AND THE HERETICAL TOME. Nulling `heretical` with no
  // destination write is a card leaving the game (trap 4.3), and this
  // path nulls it. descendBeing on the player side has always pushed it.
  const nec = [...(p.necropolis || []), slot.escendant, ...(slot.soul || []),
               ...(slot.heretical ? [slot.heretical] : [])];
  f.esclings[slotIdx] = {
    ...slot, escendant: null, currentLyf: null, soul: [], soulUsed: {}, heretical: null,
    fallen: false, fallenTurn: null, attackedThisTurn: false, intangible: false,
    mustBeRuinedFirst: false, nullifyNextAttack: false, reflectNextAttack: false,
    nullifiedSouls: [],
  };
  f.tomes = riftArray(f.tomes, 3);
  f.tomes.forEach((t, i) => {
    if (t?.id && t?.wieldedBy === slotIdx) { nec.push(t.id); f.tomes[i] = null; }
  });
  await updatePlayer(code, 0, { field: f, necropolis: nec });
  bankAkula(1);
  await sleep(POST_RUIN_DELAY);
  return true;
}

// (function ruinNpcBeingFull removed 2026-09-15 — the brain's second copy of resolver logic; the ONE resolver in tomeResolver.js owns it now)

// ── Strip one soul card off a PLAYER being (R70-aware) ──────────────
async function ruinPlayerSoulCard(code, slotIdx, soulId) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  const slot = f.esclings?.[slotIdx];
  if (!slot || !(slot.soul || []).includes(soulId)) return false;
  // R70 — a ruined U'hlti takes the Affinity it carried with it.
  const strip = stripSoulCard(slot.soul, soulId);
  f.esclings[slotIdx] = { ...slot, soul: strip.soul };
  await updatePlayer(code, 0, { field: f, necropolis: [...(p.necropolis || []), ...strip.gone] });
  await sleep(ACTION_DELAY);
  return true;
}

async function ruinPlayerCurio(code, curioIdx) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  const cid = (f.curios || [])[curioIdx];
  if (!cid) return false;
  f.curios[curioIdx] = null;
  await updatePlayer(code, 0, { field: f, necropolis: [...(p.necropolis || []), cid] });
  await sleep(ACTION_DELAY);
  return true;
}

// ── THE TARGET-CHOICE BRAIN ─────────────────────────────────────────
// One picker per ruin category. Two rules, in this order:
//   1. THE MUST-RUIN-FIRST RULE IS A RULE, NOT A PREFERENCE. A taunt
//      wall standing on the adversary's board FORCES a Tome/Curio ruin
//      onto itself and FORBIDS a being-ruin or a soul-strip outright —
//      exactly what Shatterfield does at every one of its ~10 ruin
//      branches. Getting this wrong is an illegal transition, not a bad
//      play, so it is checked before anything else.
//   2. Otherwise take the biggest thing on offer. `threatScore` already
//      encodes what "biggest" means for a being; level does for the
//      rest.
// Returns a descriptor or null. Null means "this ruin has no legal
// target", and the caller must treat that as a no-op, not as a fire.
function pickRuinTarget(V, kinds) {
  const mrf = V.oppMrf;
  const wantsTome = kinds.includes("tome") || kinds.includes("tomeFaceUp");
  // MRF: a Tome-shaped requirement eats any Tome/Curio ruin first.
  if (mrf?.kind === "tome" && wantsTome) return { kind: "tome", slotIdx: mrf.slotIdx, id: mrf.id, forced: true };
  // MRF of ANY shape blocks being-ruins and soul-strips (player parity).
  const blocked = !!mrf;

  if (kinds.includes("being") && !blocked) {
    const best = V.oppStanding.length ? V.oppStanding : V.oppBeings;
    if (best.length) {
      const pick = [...best].sort((a, b) => {
        const ca = getCard(a.s.escendant), cb = getCard(b.s.escendant);
        const sa = (ca?.level || 1) * 10 + (a.s.soul || []).length + (ca?.lyt || 0);
        const sb = (cb?.level || 1) * 10 + (b.s.soul || []).length + (cb?.lyt || 0);
        return sb - sa;
      })[0];
      return { kind: "being", slotIdx: pick.i, id: pick.s.escendant };
    }
  }
  const soulKinds = [];
  if (kinds.includes("impera")) soulKinds.push("Impera");
  if (kinds.includes("uhlti")) soulKinds.push("U'hlti");
  if (kinds.includes("affinity")) soulKinds.push("Affinity");
  if (soulKinds.length && !blocked) {
    const opts = V.oppSoul.filter(x => soulKinds.includes(x.type));
    if (opts.length) {
      const pick = [...opts].sort((a, b) => (getCard(b.sid)?.level || 0) - (getCard(a.sid)?.level || 0))[0];
      return { kind: "soul", slotIdx: pick.slotIdx, soulId: pick.sid, id: pick.sid };
    }
  }
  if (wantsTome) {
    const faceUpOnly = kinds.includes("tomeFaceUp") && !kinds.includes("tome");
    const opts = V.oppTomes.map((t, i) => ({ t, i }))
      .filter(({ t }) => t?.id && (!faceUpOnly || !t.faceDown));
    if (opts.length) {
      // A face-up Tome is doing something right now; a face-down one is
      // only a threat. Take the live one, then the highest level.
      const pick = [...opts].sort((a, b) => {
        const fa = a.t.faceDown ? 0 : 1, fb = b.t.faceDown ? 0 : 1;
        if (fa !== fb) return fb - fa;
        return (getCard(b.t.id)?.level || 0) - (getCard(a.t.id)?.level || 0);
      })[0];
      return { kind: "tome", slotIdx: pick.i, id: pick.t.id };
    }
  }
  if (kinds.includes("curio") && V.oppCurios.length) {
    return { kind: "curio", slotIdx: V.oppCurios[0].i, id: V.oppCurios[0].id };
  }
  return null;
}

async function execRuin(code, V, kinds, opts = {}) {
  const t = pickRuinTarget(V, kinds);
  if (!t) return false;
  // ── C13 · THE WARD SOAKS A TARGETED RUIN, ON BOTH SIDES OF THE BOARD.
  // "Pick a being, ruin it" IS an attack on that being, and the player's
  // resolver has always said so (tomeResolver, the Lyt Hearted soak).
  // This file read the flag nowhere and CLEARED it in ruinPlayerBeingFull,
  // so a ward the NPC stamps for itself worked and the identical ward a
  // player paid 6 Khao for did not. Asked once, here, for every row.
  // ⚠ BEINGS ONLY, because that is what the player's side soaks. The C13
  // ward reads "nullify 1 attack being made to 1 of your CARDS", and
  // tomeResolver honours it for a targeted being and not for a card sitting
  // in a Soul. Widening it here made `nullifyNextAttack` strictly stronger
  // against the brain than against the player — in a fix whose entire
  // purpose was symmetry. If the Soul case is right it belongs on BOTH
  // sides, and that is a ruling, not a tidy-up.
  if (t.kind === "being") {
    const mw = await getMatch(code);
    const tgt = mw?.players?.[0]?.field?.esclings?.[t.slotIdx];
    if (tgt?.nullifyNextAttack) {
      const fw = JSON.parse(JSON.stringify(mw?.players?.[0]?.field || {}));
      fw.esclings[t.slotIdx] = { ...fw.esclings[t.slotIdx], nullifyNextAttack: false };
      await updatePlayer(code, 0, { field: fw });
      return true;   // the ruin happened and was soaked; the row still fired
    }
  }
  if (t.kind === "tome")  return (await ruinPlayerTomeSlot(code, t.slotIdx)) !== null;
  if (t.kind === "curio") return await ruinPlayerCurio(code, t.slotIdx);
  if (t.kind === "soul")  return await ruinPlayerSoulCard(code, t.slotIdx, t.soulId);
  if (t.kind === "being") {
    // ── "RUIN 1 CARD" IS A DESCENT. Only a row that says `andSoul` takes
    // the whole stack, and only two do. Everything else descends, exactly
    // as the player's Lyt Hearted does.
    if (opts.andSoul) return await ruinPlayerBeingFull(code, t.slotIdx);
    return await descendOnePlayerBeing(code, t.slotIdx);
  }
  return false;
}

// The descent twin of ruinPlayerBeingFull: reads the live board, descends
// one player being through the shared helper, and writes it back. The
// helper itself is pure (it edits a draft), so this is the one place the
// brain turns it into a Firebase write.
async function descendOnePlayerBeing(code, slotIdx) {
  const m = await getMatch(code);
  const p = m?.players?.[0] || {};
  const f = JSON.parse(JSON.stringify(p.field || {}));
  const nec = [...(p.necropolis || [])];
  if (!f?.esclings?.[slotIdx]?.escendant) return false;
  if (descendPlayerSlot(f, nec, slotIdx, m?.turn || 0) === null) return false;
  await updatePlayer(code, 0, { field: f, necropolis: nec });
  bankAkula(1);
  await sleep(POST_RUIN_DELAY);
  return true;
}

// ── PILE MOVES (P1/P2/P3) ───────────────────────────────────────────
// grimoireSearchOne and luxmoireSearchOne already existed but were
// welded to `type === "Escendant"` / Impera and, more importantly, they
// CHARGE THE SEARCH FARE (R88). A Tome that tutors has already paid its
// own Khao at activation; charging R88 on top would be double billing.
// These are the fare-free, filter-driven twins.
//
// ⚠ AND THAT IS EXACTLY WHY THEY ARE R08-EXEMPT (caP'n, 2026-08-26):
// *"brilliant distinction yes, cards are exempt from the first turn
// search rule"*. The fare is the tell. A search that pays R88 is the
// SEAT'S action and is barred on the opening Cycle; a search that pays
// nothing because the CARD already paid is the card's printed power and
// resolves normally. execTutor has never asked the opening-Cycle
// question and, under this ruling, never should — it is the exemption's
// living example rather than a gap. The bar lives in the fare-paying
// helpers above, where the seat's own search action lives.
function poolFrom(V, from) {
  if (from === "grimoire") return { kind: "map", pool: V.grim };
  if (from === "luxmoire") return { kind: "map", pool: V.lux };
  if (from === "necropolis") return { kind: "array", pool: V.nec };
  return { kind: "both", pool: null };
}
// Rank a tutor candidate: the brain takes the highest level it can use,
// which is a crude proxy for value but an honest one and the same one
// grimoireSearchOne has always used.
const tutorRank = (id) => {
  const c = getCard(id);
  if (!c) return -1;
  // ⚠ A CURIO'S LEVEL IS THE STRING "xxx". Three of the nine Curios carry
  // `level: "xxx"` in cards.js — they are unranked by design — and the
  // old line was `(c.level || 1) * 10`, which is NaN for every one of
  // them. NaN loses every comparison it is ever in, including
  // `NaN > -Infinity`, so a Curio could never become the best candidate
  // and a tutor whose ONLY legal target was a Curio silently found
  // nothing at all. That mattered the moment the Omnis Rituals grew
  // their printed "or 1 Curios" limb: want() correctly said "there is a
  // Curio in the Necropolis, fire", the executor ranked it to NaN, and
  // the row paid its Khao and moved nothing — the exact defect section 3
  // of tools/tomebook.selftest.mjs exists to catch. An unranked Curio
  // sorts as a level 1, which is honest: it is the floor, so any real
  // Tome the filter also admits still outranks it.
  const lvl = Number(c.level);
  return (Number.isFinite(lvl) ? lvl : 1) * 10 + (c.lyt || 0);
};

// (function execTutor removed 2026-09-15 — the brain's second copy of resolver logic; the ONE resolver in tomeResolver.js owns it now)

// (function execWard removed 2026-09-15 — the brain's second copy of resolver logic; the ONE resolver in tomeResolver.js owns it now)

// ── ATV FEEDING ─────────────────────────────────────────────────────
// The two Necklaces were this file's ENTIRE "cannot use" blacklist, on
// the grounds that feeding was a player-side modal and NPC.js contained
// no `fed`, no `fedThisTurn` and no feed step at all. Feeding is not a
// modal. It is an addition with a threshold on it, and it writes the
// same two keys the player's ATVFeedModal writes, so a duel that
// changes hands mid-Necklace reads correctly from either side.
// R96 (THE TILL LAW) applies: an Anima feed is a VOLUNTARY payment and
// may never take the payer to 0.
// ── R129 · THE FED TREASURES, THE SAME WAY THE BOARD FEEDS THEM ─────
// Two changes, both of them the board's shape rather than a new one
// (resolver handoffs, 2026-09-15). The board's till is the reference
// implementation and it is deliberately not re-derived here.
//
// ⚠ 1 · "UP TO n PER TURN" IS AN ALLOWANCE, NOT ONE FEED. This read
// `fedThisTurn === V.turn` as "already fed, locked" — the identical bug
// the board's till had. Joyous Courage needs 6 and allows 3 a Turn, so
// the card is two Turns and the old lane was four: a brain that fed 1
// Khao could not feed the other 2. `rules.feedAllowanceLeft` already
// folds the allowance, what this Turn has spent and the store's ceiling
// into one number, and `fedAmountThisTurn` is what makes that sum
// possible — so both drivers now read ONE allowance.
//
// ⚠ 2 · THE RECHARGE WAS MISSING ON THIS SIDE. caP'n: *"yes, it can be
// fed after use, it needs to be ruined for it to be taken off the
// shatterfield."* The board clears the store at both payouts; this
// function never did, so after one payout the brain's Necklace sat at
// its threshold FOREVER — `feedAllowanceLeft`'s `room` is 0 at the
// ceiling — while the human's recharged indefinitely. The store clears
// on payout and `fedThisTurn`/`fedAmountThisTurn` are left standing,
// because the Turn's allowance is spent whether or not it paid out:
// emptying the card must not hand the seat a fresh allowance.
//
// ⚠ AND THE CURRENCY COMES FROM THE CARD, NOT THE STEP. R129: "read it
// here; never from a caller." `step.currency`/`step.per`/`step.cap` are
// the book's copy of numbers that now live in `ATV_FEED`. They are kept
// only as the fallback for a `feed` row the rules table does not know
// about — which would otherwise feed NOTHING silently, because
// `feedAllowanceLeft` returns 0 for an unknown card.
async function execFeed(code, V, step, newField, tomes, slotIdx) {
  const t = tomes[slotIdx];
  if (!t?.id) return { changed: false, full: false };
  const card = getCard(t.id);
  const spec = ATV_FEED[card?.id];
  const known = !!card && isFedTreasure(card);
  if (!known) tomeProbe("feedRowNotInATVFeed", t.id, step?.currency);

  const currency = known ? spec.currency : step.currency;
  const threshold = known ? spec.threshold : step.cap;
  const stored = t.fed || 0;
  const spentThisTurn = (t.fedThisTurn === V.turn) ? (t.fedAmountThisTurn || 0) : 0;
  // THE ALLOWANCE. For a known Treasure this is R129's one derivation;
  // the fallback reproduces it off the row so an unknown row still eats.
  const room = known
    ? feedAllowanceLeft(card, t, V.turn)
    : Math.max(0, Math.min((step.per || 0) - spentThisTurn, (step.cap || 0) - stored));
  if (room <= 0) return { changed: false, full: false };
  const give = room;

  if (currency === "anima") {
    // R96 — the till law. A voluntary payment may never take the seat to
    // 0, and R100 files it on the SELF ledger, so it feeds no Prophecy.
    if (!canPayVoluntary(V.anima, give)) return { changed: false, full: false };
    if (await dimNpcAnima(code, give)) return { changed: true, full: false, died: true };
  } else {
    if (!(await npcSpendKhao(code, give))) return { changed: false, full: false };
  }

  const fed = stored + give;
  const full = fed >= threshold;
  tomes[slotIdx] = {
    ...t,
    // THE RECHARGE: at the threshold the store CLEARS and the Treasure
    // stands. The food is never refunded — not on payout, not on ruin.
    fed: full ? 0 : fed,
    // `fedThisTurn` keeps its bare-turn shape because npcTomeBook.js
    // (:377, :393) and this file both read `fedThisTurn === turn`; the
    // per-Turn amount rides beside it and is NOT cleared by a payout.
    fedThisTurn: V.turn,
    fedAmountThisTurn: spentThisTurn + give,
  };
  newField.tomes = tomes;
  await updatePlayer(code, NPC_IDX, { field: newField });
  await sleep(ACTION_DELAY);
  return { changed: true, full };
}

// (function recordNpcSG removed 2026-09-15 — the brain's second copy of resolver logic; the ONE resolver in tomeResolver.js owns it now)
const rollDie = () => Math.floor(Math.random() * 6) + 1;

// ── THE CONTRACT OBLIGATION (King of the Castle / Magma Carta) ──────
// Two Contract Tomes put their body on the ADVERSARY: "your adversary
// must devote to you at least 2 of their Anima", and the contract ruins
// itself once the total reaches 8 (King) or 4 (Magma). Player-side that
// obligation is collected by a modal at the top of the adversary's turn
// (setDevotionModal). NPC.js had no equivalent, in EITHER direction:
//
//   • A King of the Castle the NPC owns collected nothing, because the
//     adversary's client is what pays it — and headless, the adversary
//     IS another brain with no devote step. So the card was a 10-Khao
//     no-op in every NPC-vs-NPC duel and a real card only against a
//     human, which is not what "the NPC plays this card" should mean.
//   • A King of the Castle the PLAYER owns was simply ignored. The NPC
//     walked past a rules obligation the human is prompted for, every
//     turn, forever. That is an R-UNIVERSAL break in the direction that
//     favours the machine.
//
// This closes both. Called once at the top of the NPC's turn, it pays
// what the adversary's standing contracts demand and completes them
// when the total lands. R96 (THE TILL LAW) applies — devotion is a
// voluntary payment and may never take the payer to zero, so a brain
// that cannot afford the contract simply does not pay it this turn.
async function npcHonourDevotions(code) {
  const m = await getMatch(code);
  const me = m?.players?.[NPC_IDX] || {};
  const opp = m?.players?.[0] || {};
  const oppTomes = riftArray(opp.field?.tomes, 3);
  let paidAny = false;
  for (let i = 0; i < oppTomes.length; i++) {
    const t = oppTomes[i];
    if (!t?.id || t.faceDown) continue;
    if (t.id !== "tome_king_of_the_castle" && t.id !== "tome_magma_carta") continue;
    const cap = t.id === "tome_king_of_the_castle" ? 8 : 4;
    const amount = 2;
    const live = (await getMatch(code))?.players?.[NPC_IDX] || me;
    if (!canPayVoluntary(live.anima ?? STARTING_ANIMA, amount)) continue;   // R96
    if (await dimNpcAnima(code, amount)) return true;                       // R33 — it can kill
    const oppLive = (await getMatch(code))?.players?.[0] || opp;
    await updatePlayer(code, 0, {
      anima: sealAnimaGain((oppLive.anima ?? STARTING_ANIMA), amount),
      ...buildAnimaGainUpdate(oppLive, amount),
    });
    const oppFresh = (await getMatch(code))?.players?.[0] || opp;
    const f = JSON.parse(JSON.stringify(oppFresh.field || {}));
    f.tomes = riftArray(f.tomes, 3);
    if (f.tomes[i]?.id !== t.id) continue;
    const devoted = (f.tomes[i].devoted || 0) + amount;
    if (devoted >= cap) {
      // The completed contract rides to its OWNER's Necropolis, and
      // King of the Castle's "draw 1 when sent to Necropolis" clause
      // fires there — the owner's grant, not the payer's.
      f.tomes[i] = null;
      const nec = [...(oppFresh.necropolis || []), t.id];
      const upd = { field: f, necropolis: nec };
      if (t.id === "tome_king_of_the_castle") {
        const g = { ...(oppFresh.grimoire || {}) };
        const drawn = drawAndDeplete(g);
        if (drawn.card) {
          let oh = [...(oppFresh.hand || []), drawn.card];
          while (oh.length > NPC_HAND_LIMIT) { const [gone] = oh.splice(npcPickDiscardIdx(oh), 1); nec.push(gone); }
          upd.grimoire = drawn.newPool; upd.hand = oh; upd.necropolis = nec;
        }
      }
      // R08 — DECLARED. The payload is byte-identical to a ruin (their
      // Necropolis grows, their Tome leaves its rift) and the meaning is
      // a contract being HONOURED, at their own card's instruction. No
      // predicate can tell those apart, so the writer says which it is.
      // See THE R08 SEAL in gameManager.js.
      await updatePlayer(code, 0, upd,
        { notAnAttack: "R08 · a completed Devotion contract rides to its OWNER's Necropolis" });
    } else {
      f.tomes[i] = { ...f.tomes[i], devoted };
      await updatePlayer(code, 0, { field: f });
    }
    paidAny = true;
    await sleep(ACTION_DELAY);
  }
  return paidAny;
}

// ─────────────────────────────────────────────────────────────
// THE EXECUTOR PROPER
// ─────────────────────────────────────────────────────────────
async function applyNpcTomeFireEffects(code, card, newField, tomes, slotIdx, hand, grimoire) {
  // ══════════════════════════════════════════════════════════════════
  // ⭐ ONE RESOLVER (2026-09-15). This function was a ~650-line step
  // executor — the brain's OWN reading of every Tome, kept in agreement
  // with tomeResolver.js by hand and by test and never by construction.
  // It is a DOOR now: the brain resolves the card through the very same
  // `applyTomeEffect` the human's board calls, via npcResolverBridge,
  // which answers the resolver's pickers with a scorer and rolls its
  // gambles the way the modal rolls them. Every standing interrupt
  // (All or Nothing, Sword and Shield, the Nihil shields), Divine Justice,
  // R123's ward, R96's till, R105's fare-on-every-firing and R135's
  // duration anchoring are therefore the SAME lines of code for both
  // seats, and a card can no longer read one way in a hand and play
  // another in a brain.
  //
  // The signature and the result shape are unchanged so both callers
  // (flipOneTome, activateOneTome) and tools/tomebook.selftest.mjs — which
  // drives all 72 Tomes through this door — did not have to move. What
  // moved is WHERE the truth lives: the result's hand / grimoire / field
  // are re-read from Firebase after the resolver's writes, and the caller
  // rebases its drafts on them.
  //
  // ⚠ ONE LANE STAYS THE BRAIN'S: feeding a Treasure (R129). A feed is not
  // a Tome effect on either side (the player feeds through ATVFeedModal),
  // so feed rows keep NPC.execFeed and the two printed payouts. Their
  // numbers live once, in rules.ATV_FEED.
  // ══════════════════════════════════════════════════════════════════
  const rowSpec = tomeRow(card?.id);
  if (!rowSpec) return { hand, grimoire, changed: false };
  tomeProbe("fire", card.id);

  const m0 = await getMatch(code);
  let changed = false;
  let matchOver = false;

  // ── THE FEED LANE (R129) ──────────────────────────────────────────
  const feedStep = (rowSpec.steps || []).find((st) => st && st.k === "feed");
  if (feedStep) {
    const V = buildTomeViewFrom(m0, m0?.turn || 0, slotIdx, tomes[slotIdx] || null);
    const r = await execFeed(code, V, feedStep, newField, tomes, slotIdx);
    if (r.died) matchOver = true;
    if (r.changed) changed = true;
    if (r.full && !matchOver) {
      for (const st of feedStep.onFull || []) {
        if (st.k === "animaSelf") {
          const live = (await getMatch(code))?.players?.[NPC_IDX] || {};
          const cur = live.anima ?? STARTING_ANIMA;
          const next = sealAnimaGain(cur, st.n);
          if (next !== cur) { await updatePlayer(code, NPC_IDX, { anima: next, ...buildAnimaGainUpdate(live, next - cur) }); changed = true; }
        } else if (st.k === "ruin") {
          const fresh = buildTomeViewFrom(await getMatch(code), V.turn, slotIdx, tomes[slotIdx]);
          if (await execRuin(code, fresh, st.kinds, { andSoul: !!st.andSoul })) changed = true;
        } else {
          tomeProbe("badstep", card.id, st.k);
        }
      }
    }
    await flushAkula(code);
    tomeProbe(changed ? "changed" : "noop", card.id, card.khaoCost || 0);
    const liveF = (await getMatch(code))?.players?.[NPC_IDX] || {};
    return { hand: liveF.hand || hand, grimoire: liveF.grimoire || grimoire, changed, matchOver };
  }

  // ── EVERY OTHER TOME: THE ONE RESOLVER ────────────────────────────
  // The caller's hand / grimoire drafts may be ahead of Firebase (the
  // brain carries drafts across its Turn); the resolver reads LIVE, so
  // the drafts land first. The field draft was written by the caller
  // when it flipped the card.
  const liveB = m0?.players?.[NPC_IDX] || {};
  const draft = {};
  if (JSON.stringify(liveB.hand || []) !== JSON.stringify(hand || [])) draft.hand = hand;
  if (JSON.stringify(liveB.grimoire || {}) !== JSON.stringify(grimoire || {})) draft.grimoire = grimoire;
  if (Object.keys(draft).length) await updatePlayer(code, NPC_IDX, draft);

  const before = JSON.stringify((await getMatch(code))?.players || {});
  const r = await resolveTomeAsBrain(code, card, { seat: NPC_IDX, oppSeat: 0 });
  let after = r.match?.players || {};
  changed = JSON.stringify(after) !== before;
  const aonRuined = r.sentinel === "AON_RUINED";
  // R10 — a Tome that draws FOR the adversary (Devil's Advocate) leaves
  // the draw resolved at the cap; the HOLDER discards. A human holder's
  // client raises the modal; a brain holder has no client, so it is
  // settled here, with the brain's own chooser (see npcSettleHandCapFor).
  if (seatIsBrain(after[0]) && (after[0]?.hand || []).length > NPC_HAND_LIMIT) {
    await npcSettleHandCapFor(code, 0);
    after = (await getMatch(code))?.players || after;
  }

  // R33 — the resolver never ends a Solrend (on the board a listener does);
  // here the brain asks the till the moment the effect has landed. The
  // brain's own Anima cannot reach 0 by its own hand (R96 refuses the
  // payment inside the resolver), so a 0 on this side is adversarial.
  const pAnima = getEffectiveAnima(r.match, 0);
  const nAnima = getEffectiveAnima(r.match, NPC_IDX);
  if (pAnima <= 0) matchOver = await npcFinish(code, NPC_IDX, 0);
  else if (nAnima <= 0) matchOver = await npcFinish(code, 0, NPC_IDX);
  else if (r.match?.status === "finished") matchOver = true;   // ended inside the resolver's own writes — the runner must stop, not play on

  await flushAkula(code);
  // A standing row is never a no-op — turning face-up IS the effect (a
  // taunt wall, Akula's listener, the drips, the contracts). The old
  // executor said so per step; the lane says it once.
  if (!changed && rowSpec.standing) changed = true;
  const gambled = card.ilk === "Shadow Gambling";
  tomeProbe(changed ? "changed" : (gambled ? "gamble" : "noop"), card.id, card.khaoCost || 0);
  const liveN = after[NPC_IDX] || {};
  const field = liveN.field ? { ...liveN.field, tomes: riftArray(liveN.field.tomes, 3) } : null;
  return { hand: liveN.hand || [], grimoire: liveN.grimoire || {}, field, changed, matchOver, aonRuined, toasts: r.toasts };
}

// ── THE TEST HOOK ───────────────────────────────────────────────────
// tools/tomebook.selftest.mjs drives every one of the 72 rows through
// the executor directly, against a hand-built board, because the twelve
// starter decks between them contain only 47 of the 72 Tomes — so a
// balancer batch, however long, can never touch the other 25. Coverage
// that stops at "what the decks happen to hold" is coverage that lets a
// broken row sit undiscovered until somebody builds a deck with it.
//
// This exports NOTHING the game uses. It is a named handle on two
// internals so a test can reach them without this file growing a
// public API. Nothing in src/ imports it.
export const __tomeBookTestHooks = {
  applyNpcTomeFireEffects, buildTomeViewFrom, npcTomeWant, npcHonourDevotions,
};

// ── Flip ONE waiting tome face-up. ──
// THIS IS THE TOME ACTIVATION MOMENT — khao (and anima) is paid here.
//
// Rulebook (Rulebook.js line 300): "Place Tomes face-down on your 3
// Tome Rifts. Always free. Activation cost paid only when used."
// Flipping face-up == using == cost is paid.
//
// Universal rules: same pattern as player-side handlePlayTome
// (Shatterfield line 5354) — spendKhao/spendAnima at activation.
//
// Policy (caP'n's ruling): if a tome can't afford its cost this turn,
// SKIP and try the next face-down tome. Don't block the whole turn,
// don't fire it for free. The skipped tome stays face-down and can
// try again next turn.
//
// Bug A fix: non-persistent tomes ruin to necropolis after firing
// (mirrors player-side handlePlayTome line 5372-5373).
//
// AWAKENING: the tome's simple effects now fire ON THE FLIP via
// applyNpcTomeFireEffects (see above) — hand/grimoire thread through.
async function flipOneTome(code, field, turn, hand, grimoire) {
  const tomes = riftArray(field.tomes, 3); // Firebase hole guard
  // R35's wait is measured in half-turns and one FULL Turn is
  // `seatCount` of them, so the table size has to be carried here the
  // same way the cooldown lane carries it. Read once, off the live
  // match, because this loop can flip more than one Tome.
  const seatsHere = (await getMatch(code))?.seatCount || 2;

  for (let i = 0; i < tomes.length; i++) {
    const t = tomes[i];
    if (!t?.id || !t.faceDown) continue;
    const card = getCard(t.id);
    if (!card) continue;
    // R35/R36/R37 — the wait is a RULE with a function, not a literal.
    // `waited < 1` once idled every L2 and L3 Tome a full extra
    // half-turn that no rule asks for.
    //
    // ⚠ THE NUMBER CHANGED ON 2026-09-13 AND THIS LANE IS WHERE IT
    // BITES. caP'n: *"if you place it face down, you must wait 1 full
    // turn to activate it."* A full Turn is TWO half-turns, and the old
    // TOME_WAIT.L1 of 1 was being compared against a half-turn delta —
    // so the brain's set L1 Tomes woke on the PLAYER's half-turn, one
    // whole seat early. `faceDown: true` is not a guess here: this
    // function only ever looks at face-down Tomes (see the `continue`
    // four lines up).
    const waited = turn - (t.placedTurn || 0);
    if (waited < tomeWaitHalfTurns(card.level, { faceDown: true, seatCount: seatsHere })) continue;

    // ⭐ NEVER PAY FOR NOTHING — and now the gate can actually tell.
    // This loop used to flip any affordable face-down tome that had
    // waited, with no check that firing it would DO anything, so the
    // brain burned Khao and Anima on 55 of the game's 72 Tomes for no
    // effect whatsoever — every turn, forever, while the player watched
    // it spend itself poor. The old gate's replacement was no better
    // for fifteen of them: it waved through anything with a persistent
    // TAG, which is every Ritual Tome, and a Ritual Tome over an empty
    // Necropolis buys precisely nothing.
    //
    // The row's want() asks the real question against the real board.
    // Null means the fare buys nothing THIS turn — the tome stays face
    // down, free, and asks again next turn.
    const cost = card.khaoCost || 0;
    const animaCost = card.animaCost || 0;

    // Pre-check both costs against fresh Firebase before spending either.
    // Prevents the half-paid-state edge case where khao deducts but anima
    // gate fails afterward. ONE read serves the affordability check and
    // the want() view.
    const m = await getMatch(code);
    const npcLive = m?.players?.[NPC_IDX] || {};
    if (!npcTomeIsWorthPaying(card, buildTomeViewFrom(m, turn, i, t))) {
      tomeProbe("declined", card.id);
      continue;
    }
    if ((npcLive.khao  ?? 0) < cost)      continue;
    // ⚠ `<=`, NOT `<` — SEE npcSpendAnima. This is the BRAIN guard and
    // it belongs here: "can I afford this without it killing me" is a
    // decision, and it has to be made BEFORE the Khao is spent, because
    // Khao is paid first. With `<`, an exact-affordable Anima cost
    // passed this check, took the Khao, and then the till refused.
    if (animaCost > 0 && (npcLive.anima ?? 0) <= animaCost) continue;
    // ⭐ was `cost > maxKhao` — which permanently blacklists a tome the
    // brain can afford RIGHT NOW, because R13 lets cards push the live
    // pool above the natural ceiling (Khao Arcadia does exactly that).
    // The question is only ever "can I pay it", and the line above
    // already answered it.
    if (cost > (npcLive.khao ?? 0))       continue;

    // Pay
    if (!(await npcSpendKhao(code, cost)))      continue;
    if (!(await npcSpendAnima(code, animaCost))) continue;

    // Flip face-up + mark cooldown anchor
    let newTomes = [...tomes];
    newTomes[i] = { ...t, faceDown: false, lastUsedTurn: turn };
    let newField = { ...field, tomes: newTomes };
    await updatePlayer(code, NPC_IDX, { field: newField });

    // ── RESPONSE WINDOW — the activation is DECLARED before its
    // effect resolves. Nihil voids the effect; Omnia ruins the tome
    // outright. The fare is NOT REFUNDED either way (this line once
    // said 'canon: paid once', which now collides with R105 — it was
    // always about refunds, never about re-charging). A
    // nullified card's normal lifecycle continues — it was activated,
    // its effect was voided, non-persistent tomes still ruin after use.
    // COUNTER HIERARCHY (caP'n ruling): a LATE counter (L1/L2 vs this
    // L3+ activation) cannot pre-empt — the effect resolves first,
    // then the card must leave the field, persistent or not.
    let lateCounter = false;
    let windowResponded = false;
    {
      const rw = await requestWindow({ kind: "ACTIVATION", cardId: t.id });
      windowResponded = !!rw?.responded;
      if (rw?.counter && rw?.late) {
        lateCounter = true; // effect proceeds below; eviction after
      } else if (rw?.counter === "omnia") {
        await ruinNpcTome(code, i, card.id);
        newField.tomes[i] = null;
        await sleep(TOME_INTERRUPT);
        return { field: newField, flipped: true, hand, grimoire };
      } else if (rw?.counter === "nihil") {
        if (!isTomePersistent(card)) {
          await ruinNpcTome(code, i, card.id);
          newField.tomes[i] = null;
        }
        await sleep(TOME_INTERRUPT);
        return { field: newField, flipped: true, hand, grimoire };
      }
    }

    // ── Wave 4 (C12 a): re-base after a responded window — a generic
    // interrupt may have rewritten the NPC's field, this very tome
    // included. If the flipped tome is gone, its fare stays paid and
    // the firing fizzles (same law as the player side); otherwise the
    // draft continues from post-window truth so no later write can
    // resurrect what the response removed.
    if (windowResponded) {
      hand = await syncNpcHand(code, hand); // THE REBASE LAW — the brain may have answered from this very hand
      const mW = await getMatch(code);
      const fW = mW?.players?.[NPC_IDX]?.field;
      if (fW) {
        newField = JSON.parse(JSON.stringify(fW));
        newField.tomes = riftArray(newField.tomes, 3);
        newTomes = newField.tomes;
        if (newTomes[i]?.id !== t.id) {
          await sleep(TOME_INTERRUPT);
          return { field: newField, flipped: true, hand, grimoire };
        }
      }
    }

    // AWAKENING: fire the tome's effects at the activation moment.
    // (Solomon fires here and ONLY here — it has no effectTag and is
    // not FIELD_PERSISTENT, so activateOneTome never sees it.)
    const fx = await applyNpcTomeFireEffects(code, card, newField, newTomes, i, hand, grimoire);
    hand = fx.hand; grimoire = fx.grimoire;
    // ⭐ ONE RESOLVER — the shared resolver wrote to Firebase directly, so the
    // brain's field draft is REBASED on the live board before anything else
    // writes it back (a draft written over a resolver's write is the clobber
    // that would resurrect what the card just ruined).
    if (fx.field) { newField = fx.field; newTomes = newField.tomes; }
    // The executor may end the duel (a self-dim that reaches zero is
    // still R33). Stop here rather than writing over a finished match.
    if (fx.matchOver) return { field: newField, flipped: true, hand, grimoire, matchOver: true };

    // ── ALL OR NOTHING ALREADY RUINED IT (see applyNpcTomeFireEffects).
    // The rift is already null in Firebase and the card is already in the
    // Necropolis; the only thing left is to stop `newField` writing the
    // destroyed Tome back onto the board.
    if (fx.aonRuined) {
      newField.tomes[i] = null;
      await sleep(TOME_INTERRUPT);
      return { field: newField, flipped: true, hand, grimoire };
    }
    // Bug A fix: non-persistent tomes ruin to necropolis after firing.
    // Counter Hierarchy: a late-countered tome ALWAYS leaves the field.
    if (!isTomePersistent(card) || lateCounter) {
      await ruinNpcTome(code, i, card.id);
      newField.tomes[i] = null;
    }

    await sleep(TOME_INTERRUPT);
    return { field: newField, flipped: true, hand, grimoire };
  }

  return { field, flipped: false, hand, grimoire };
}

// ── Place ONE tome face-down into one empty tome slot. ──
// `chooserMode`: "Beb" / "Champion" / "Lord-aggro" / "Lord-survival" / "Lord-balance"
async function placeOneTome(code, hand, field, turn, chooserMode) {
  if (turn === 0) return { hand, field, placed: false }; // never on turn 0

  // riftArray also fixes a latent miss: a Firebase-shortened array from
  // {0:tome} is length 1, so `findIndex(t => t === null)` returned -1
  // and the NPC could never place a second tome.
  const tomes = riftArray(field.tomes, 3);
  const slotIdx = tomes.findIndex(t => t === null);
  if (slotIdx === -1) return { hand, field, placed: false };

  // ⭐ NEVER SQUAT YOUR OWN RIFT WITH A CARD YOU CANNOT USE.
  //
  // ⚰ WHAT WAS HERE: a two-entry blacklist naming the ATV Necklaces,
  // because feeding was a player-side modal and NPC.js contained no
  // `fed`, no `fedThisTurn` and no feed step at all. The banner ended
  // "⚠ DELETE THIS FILTER the day feeding exists." Feeding exists now —
  // it is a `feed` step in the tome executor, writing the same two keys
  // the player's ATVFeedModal writes — so the blacklist is gone and the
  // Necklaces are ordinary cards again.
  //
  // What replaces it is BIGGER and closes the real leak. The blacklist
  // filtered TWO ids; thirty-two Tomes were being placed face-down and
  // then never flipped, because the flip gate refused a fare the place
  // step had never asked about. A tome rift is one of three and a
  // persistent tome never leaves it, so those thirty-two were a board-
  // economy leak entirely separate from the effect gap. The rift now
  // asks the same book the flip does:
  //   • lane "human-only"  — the brain genuinely cannot resolve it, the
  //     row says why, and it is never placed. A declared refusal beats
  //     a rift held hostage for the rest of the duel.
  //   • lane "reactive"    — the card IS a counter and is reached from
  //     the HAND by ChainLaw. Burying it in a rift face-down is legal
  //     and still counterable, so it stays placeable; the flip gate is
  //     what keeps it from being spent proactively.
  const tomesInHand = hand.map((id, idx) => ({ card: getCard(id), idx }))
    .filter(({ card }) => card?.type === "Tome" && tomeIsPlaceable(card.id));
  if (tomesInHand.length === 0) return { hand, field, placed: false };

  let chosen;
  if (chooserMode === "Lord-survival") {
    chosen = tomesInHand.find(({ card }) => card?.ilk === "Construction")
      || tomesInHand.find(({ card }) => card?.ilk === "Nihil")
      || tomesInHand[0];
  } else if (chooserMode === "Lord-aggro") {
    chosen = tomesInHand.find(({ card }) => card?.ilk === "Omnia")
      || tomesInHand.find(({ card }) => card?.ilk === "Treasure")
      || tomesInHand[0];
  } else if (chooserMode === "Lord-balance") {
    chosen = tomesInHand.find(({ card }) => card?.ilk === "Construction")
      || tomesInHand.sort((a, b) => (b.card?.level || 0) - (a.card?.level || 0))[0];
  } else if (chooserMode === "Champion") {
    // Champion: prefer highest level
    chosen = tomesInHand.sort((a, b) => (b.card?.level || 0) - (a.card?.level || 0))[0];
  } else {
    // Beb: random
    chosen = pickRandom(tomesInHand);
  }
  if (!chosen) return { hand, field, placed: false };

  const newTomes = [...tomes];
  newTomes[slotIdx] = { id: hand[chosen.idx], faceDown: true, placedTurn: turn };
  const newHand = [...hand];
  newHand.splice(chosen.idx, 1);
  const newField = { ...field, tomes: newTomes };

  await updatePlayer(code, NPC_IDX, { hand: newHand, field: newField });
  await sleep(ACTION_DELAY);
  return { hand: newHand, field: newField, placed: true };
}

// ── Activate ONE eligible tome (RE-ACTIVATION of already-face-up tome). ──
// First-time activations happen in flipOneTome (above) — that's where
// face-down → face-up + initial cost payment lives. THIS function handles
// re-activation of persistent tomes (OPT/OP2/OP3/ATV) that have already
// been flipped face-up on a previous turn.
//
// Universal rules: gate via fresh Firebase read (npcSpendKhao), no local
// state mirrors. Mirrors player-side "Activate Again" path (Shatterfield
// line 6132-6133).
//
// Returns { field, activated, hand, grimoire }. Loops in turn runner:
// keeps calling until no eligible tome remains.
async function activateOneTome(code, field, turn, difficulty, hand, grimoire) {
  const tomes = riftArray(field.tomes, 3); // Firebase hole guard (spread on a sparse OBJECT throws)
  // R103 — the cooldown is measured in FULL turns and a full turn is
  // seatCount half-turns, so the gate below needs to know how wide the
  // table is. Read ONCE, here: the loop already re-reads the board for
  // its own fresh-Firebase discipline, but the seat count cannot change
  // mid-duel and re-reading it per tome would be three round trips for
  // a constant. The NPC only ever takes a 2-seat chair today; this is
  // written correctly anyway so the day it takes a 4p seat the brain
  // and the renderer still agree.
  const seatCountLive = (await getMatch(code))?.seatCount || 2;

  for (let i = 0; i < tomes.length; i++) {
    const tome = tomes[i];
    if (!tome?.id || tome.faceDown) continue;
    if (tome._activatedThisCall) continue; // guard against re-activation in same turn
    const card = getCard(tome.id);
    if (!card) continue;

    // ⭐ A STANDING TOME IS NEVER RE-CHARGED.
    // Exactly EIGHT rows sit on the book's standing lane, and the lane
    // means one thing: the whole effect was bought when the card turned
    // face-up. A taunt wall taunts because it is there. A bonded
    // Gauntlet buffs because it is bonded. Akula listens because it is
    // listening. Charging any of those a second fare on a cooldown for
    // a fire step that is a no-op by construction IS the pay-for-
    // nothing bug — just wearing the persistent tag as a disguise, which
    // is exactly how it hid on fifteen cards for so long.
    //
    // ⚠ THIS LINE IS ALSO WHERE A MIS-DECLARED ROW BECOMES A DEAD CARD,
    // and it did, twice. tome_necklace_death and tome_necklace_courage
    // declared `lane: "activated"` AND `standing: true`; this test runs
    // first, so both were fed exactly once — at the flip, by
    // flipOneTome — and never again. Congealed Death could never reach
    // 8 Anima and never ruined a being; the Necklace of Courage could
    // never reach 6 Khao and never granted its +6, for their entire
    // shipping life. The book cannot say both things any more (see the
    // banner over `row` in npcTomeBook.js): standing is DERIVED from
    // the lane, and a row that hand-declares it fails the import.
    if (tomeIsStanding(card.id)) continue;

    // ── R103 · COOLDOWNS ARE IN FULL TURNS, AND A FULL TURN IS TWO
    // HALF-TURNS (caP'n, 2026-08-26 — quoted in full above
    // COOLDOWN_FULL_TURNS in rules.js). `turn` and `lastUsedTurn` are
    // HALF-turn counters. The ternary that stood here read the tag
    // number as a half-turn delta, which made OPT and OP2 the same
    // card at this seat's own cadence. rules.cooldownHalfTurns is the
    // single definition; the renderer's isTomeOffCooldown calls the
    // identical function, which is the whole point — the brain and the
    // board must never disagree about when a Tome may fire again.
    const cooldown = cooldownHalfTurns(card.effectTag, seatCountLive);
    const lastUsed = tome.lastUsedTurn || 0;
    if (turn - lastUsed < cooldown) continue;

    // ── R105 · THE ACTIVATION FARE IS PAID EVERY TIME ────────────
    // caP'n, 2026-09-13, OVERTURNING the 2026-08-26 "paid once" reading
    // that used to be quoted here: *"you must pay its fare again, thats
    // the cost of it being able to stay on the field 'indefinitely'."*
    //
    // THIS FUNCTION IS THE RE-ACTIVATION LANE — every card reaching it
    // is already face-up, which under the old reading made it free.
    // It is not free. It is the printed fare, in both pools, every
    // time, and that price is what the OPT/OP2/OP3/ATV tags buy.
    //
    // ⚠ THE FARE IS RESOLVED HERE AND NOT AT THE TILL because
    // npcTomeIsWorthPaying below needs it: the brain declines a fare it
    // would waste on a no-op, and under the old reading that guard was
    // deciding about a cost of zero — i.e. deciding nothing. It has
    // real money to protect again.
    // ⚠ EXCEPT ON THE FEED LANE — THE FOOD IS NOT A SECOND FARE.
    // (caP'n's ATV ruling, 2026-09-14; resolver handoff 2.)
    // A `feed` step moves currency ONTO the card `per` at a time until
    // it reaches `cap` — that payment IS the card's mechanic. The
    // printed `khaoCost` is what it costs to PLAY the Treasure, and
    // every card reaching this loop has already paid it once (this is
    // the RE-activation lane; face-down cards never get here). Charging
    // it again per feed billed the brain 2-3 Khao per mouthful that the
    // human's board never charges — the same card at two prices, which
    // is the board/brain divergence this audit exists to close.
    //
    // ⭐ DERIVED FROM THE STEP, NOT FROM A NEW FLAG. The handoff proposed
    // a `fareFreeStep` marker on the row. A marker is a second place to
    // remember something: a future fed Treasure that omits it silently
    // gets billed again, and nothing fails. The row ALREADY says what it
    // is — it carries a `feed` step — so the lane is read off the
    // mechanic and a new fed card is free by construction.
    const rowHere   = tomeRow(tome.id);
    const isFeedRow = ((rowHere?.steps) || []).some(st => st && st.k === "feed");
    const fare      = isFeedRow ? { khao: 0, anima: 0 } : activationFare(card);
    const cost      = fare.khao;
    const animaCost = fare.anima;

    // Pre-check costs against fresh Firebase
    const m = await getMatch(code);
    const npcLive = m?.players?.[NPC_IDX] || {};
    if ((npcLive.khao  ?? 0) < cost)      continue;
    // ⚠ `<=`, NOT `<` — SEE npcSpendAnima. This is the BRAIN guard and
    // it belongs here: "can I afford this without it killing me" is a
    // decision, and it has to be made BEFORE the Khao is spent, because
    // Khao is paid first. With `<`, an exact-affordable Anima cost
    // passed this check, took the Khao, and then the till refused.
    if (animaCost > 0 && (npcLive.anima ?? 0) <= animaCost) continue;
    // ⭐ was `cost > maxKhao` — which permanently blacklists a tome the
    // brain can afford RIGHT NOW, because R13 lets cards push the live
    // pool above the natural ceiling (Khao Arcadia does exactly that).
    // The question is only ever "can I pay it", and the line above
    // already answered it.
    if (cost > (npcLive.khao ?? 0))       continue;

    // ── THE APPETITE LADDER, now read off the row's `mode` ──────
    // Same three tiers as the old GOOD / AGGRESSIVE / SSG lists, with
    // one honest addition: "utility" — the searches, tutors and draws.
    // Those were in NONE of the three lists, so no tier ever re-
    // activated a Ritual Tome or an Ancient Sorcery even when the
    // Necropolis was full. Champion and Lord tutor; a Beb does not.
    const isGood       = npcTomeIsGood(card);
    const isAggressive = npcTomeIsAggro(card);
    const isUtility    = npcTomeIsUtility(card);
    const isSSG        = npcTomeIsSSG(card);

    let shouldActivate = false;
    if (difficulty === "Beb")      shouldActivate = isGood;
    if (difficulty === "Champion") shouldActivate = isGood || isAggressive || isUtility;
    if (difficulty === "Lord")     shouldActivate = isGood || isAggressive || isUtility || isSSG;
    if (!shouldActivate) continue;

    // ⭐ NEVER PAY FOR NOTHING — on the re-activation lane too, which is
    // where the leak actually lived. A flip happens once; a cooldown
    // fires every turn or two for the rest of the duel. Mausoleum of
    // Omnis at 9 Khao every second turn against an empty Necropolis was
    // the single most expensive thing the brain did, and it did it in
    // THIS loop.
    if (!npcTomeIsWorthPaying(card, buildTomeViewFrom(m, turn, i, tome))) {
      tomeProbe("declined", card.id);
      continue;
    }

    // Pay
    if (!(await npcSpendKhao(code, cost)))      continue;
    if (!(await npcSpendAnima(code, animaCost))) continue;

    // Mark cooldown anchor
    tomes[i] = { ...tome, lastUsedTurn: turn };
    let newField = { ...field, tomes };
    let liveTomes = tomes;
    await updatePlayer(code, NPC_IDX, { field: newField });

    // ── RESPONSE WINDOW — re-activation is still an activation, still
    // declared. Omnia ruins even a persistent tome mid-cooldown; Nihil
    // voids this firing (cooldown anchor already stamped — the turn's
    // use is spent, exactly as if the effect fizzled).
    let lateCounter = false;
    let windowResponded = false;
    {
      const rw = await requestWindow({ kind: "ACTIVATION", cardId: tome.id });
      windowResponded = !!rw?.responded;
      if (rw?.counter && rw?.late) {
        // COUNTER HIERARCHY (caP'n ruling): L1/L2 counter vs this L3+
        // activation is LATE — effect resolves first, then eviction.
        lateCounter = true;
      } else if (rw?.counter === "omnia") {
        await ruinNpcTome(code, i, card.id);
        newField.tomes[i] = null;
        await sleep(TOME_INTERRUPT);
        return { field: newField, activated: true, hand, grimoire };
      } else if (rw?.counter === "nihil") {
        await sleep(TOME_INTERRUPT);
        return { field: newField, activated: true, hand, grimoire };
      }
    }

    // ── Wave 4 (C12 a): re-base after a responded window (see
    // flipOneTome). Gone tome → fare stays paid, firing fizzles.
    if (windowResponded) {
      hand = await syncNpcHand(code, hand); // THE REBASE LAW — the brain may have answered from this very hand
      const mW = await getMatch(code);
      const fW = mW?.players?.[NPC_IDX]?.field;
      if (fW) {
        newField = JSON.parse(JSON.stringify(fW));
        newField.tomes = riftArray(newField.tomes, 3);
        liveTomes = newField.tomes;
        if (liveTomes[i]?.id !== tome.id) {
          await sleep(TOME_INTERRUPT);
          return { field: newField, activated: true, hand, grimoire };
        }
      }
    }

    // AWAKENING: apply effects via the shared fire-effects helper
    // (each write individually visible; hand/grimoire kept in sync
    // with the caller's locals — the old inline SSG draw desynced).
    const fx = await applyNpcTomeFireEffects(code, card, newField, liveTomes, i, hand, grimoire);
    hand = fx.hand; grimoire = fx.grimoire;
    if (fx.field) { newField = fx.field; liveTomes = newField.tomes; }   // ⭐ ONE RESOLVER — rebase on the live board
    if (fx.matchOver) return { field: newField, activated: true, hand, grimoire, matchOver: true };

    // ── ALL OR NOTHING ALREADY RUINED IT — see the twin of this block in
    // flipOneTome, and the sentinel's own note in applyNpcTomeFireEffects.
    if (fx.aonRuined) {
      newField.tomes[i] = null;
      await sleep(TOME_INTERRUPT);
      return { field: newField, activated: true, hand, grimoire };
    }
    // Bug A fix: non-persistent tomes ruin to necropolis after firing.
    // (Persistent tomes — OPT/OP2/OP3/ATV/specific-ids — stay on field
    // for re-activation.) Counter Hierarchy: a late-countered tome
    // ALWAYS leaves the field, persistence be damned.
    if (!isTomePersistent(card) || lateCounter) {
      await ruinNpcTome(code, i, card.id);
      newField.tomes[i] = null;
    }

    await sleep(TOME_INTERRUPT);
    return { field: newField, activated: true, hand, grimoire };
  }

  return { field, activated: false, hand, grimoire };
}

// ═════════════════════════════════════════════════════════════
// ATTACKS — per-attack, defensive-state-aware (AWAKENING).
// Adds POST_RUIN_DELAY when player being is destroyed (descent visibility).
//
// What changed in the Awakening wave:
//   • getRequiredTargetNpc — the PLAYER's being-level mustBeRuinedFirst
//     (Quasar) is honored FIRST, then the D'uhmdumn tome (canon order,
//     mirrors Shatterfield.getRequiredTarget). Attacks are DIRECTED at
//     the required target, never skipped.
//   • strikeBeing — every strike resolves the target's defensive states
//     in the player-side order: fallen (impervious) → intangible
//     (pass-through, flag consumed) → nullifyNextAttack (consumed) →
//     reflectNextAttack (Fate Maker: the attacker's Lyt dims the NPC's
//     OWN Anima — the NPC can genuinely lose mid-attack-phase) →
//     effective-Lyf combat math (ruin / fracture / chip).
//   • EFFECTIVE stats everywhere — the NPC attacks with its trained
//     buffs (Gauntlets, Apocalypse Now, Premier auras) and respects the
//     player's (Diamond Skin, Cosmic Casbah).
//   • Offensive soul flags — extraAttack (second swing), extra-
//     AttackAnimaOnly (second swing, Anima only), canDirectAnima /
//     animaRedirectAll (Lyt Attack the Anima past standing beings —
//     still blocked by any must-ruin-first requirement, mirroring the
//     player-side anima-attack gate), dimAnimaOnAttack, spectralClaws,
//     invFangsBuff (consumed on the anima-dimming swing).
//   • Sweet Dreams parity — u_xulan_lord on any player being makes the
//     NPC dim its own Anima by 1 per Lyt Attack, same as a human.
//   • Prophecy parity — player Anima dims write totalAnimaLost /
//     animaLostThisTurn trackers (buildAnimaLossUpdate semantics).
//
// Returns { liveField, liveAnima, killed, npcDied }. npcDied means the
// match ENDED with the player winning (reflect / Sweet Dreams) — the
// runner must stop immediately, no end-turn writes.
// ═════════════════════════════════════════════════════════════
// DEPTHS BRAIN additions (all decision-quality, all inside the same
// rulebook):
//   • LETHAL FOCUS (Lord, skill ≥ 0.82) — canKillNow is computed at
//     TURN START, before ascends/training/souls raise the Lyt. If the
//     lane is clear and the AVAILABLE Lyt is lethal at attack time,
//     the plan is discarded and every attacker goes face. A guaranteed
//     kill is never traded for a being.
//   • PROPHECY DENIAL (Lord, skill ≥ 0.95) — the player being closest
//     to completing THEIR prophecy is planned against first.
//   • Temperament — Champion animaHunger prefers a trained redirect
//     over chip damage; Beb's throat-lust runs through chance().
async function doAttacks(code, esclings, player, difficulty, npcOwnField = null, matchTurnParam = null, brain = null) {
  let liveField = JSON.parse(JSON.stringify(player.field || {}));
  liveField.tomes = riftArray(liveField.tomes, 3); // Firebase hole guard — attackRuinFirstTome indexes this
  let liveAnima = player.anima || 30;
  let liveNec   = [...(player.necropolis || [])];
  let killed    = false;
  let npcDied   = false;

  // ── BUG #2 FIX support: read the NPC's OWN field once, for Gauntlets ──
  // doAttacks receives the NPC's esclings array but not its tome rift, so
  // fetch the NPC's live field to resolve the Gauntlets bond when scoring
  // attacker Lyt. Built once per attack phase (cheap) and reused below.
  const _npcMatch = await getMatch(code);
  const _npcField = _npcMatch?.players?.[NPC_IDX]?.field || { esclings, tomes: [] };
  const effLyt = (slotIdx) => effLytPure(esclings[slotIdx], slotIdx, _npcField, liveField);
  const matchTurn = matchTurnParam ?? (_npcMatch?.turn || 0);

  // ── R121 · THE MIND'S SILENCE, ON THE BRAIN'S OWN SIDE ────────────
  // caP'n, 2026-09-07, volunteering the piece he had left out of the
  // Role wave: *"Whatever being is in the Warden Rift cannot perform
  // any arts, attacks, etc of any kind at alllll except for the Warden
  // Rift Primera, if the other 3 rifts are occupied ... with Ascendants
  // not Esclings."* So when the NPC's own Body, Sol and Heart rifts all
  // hold ESCENDANTS, the being in its Mind rift may do NOTHING — no Lyt
  // Attack, no Primera stamp, no Soul power — except its own Warden
  // Art. This is the price R119's shield is bought with.
  //
  // Asked as a FUNCTION rather than captured as a boolean because the
  // line moves inside a single attack phase: a trio member ruined by a
  // response window (or by None More Than I) wakes the Mind mid-turn,
  // and a value read at phase start would keep it asleep for the rest
  // of it. `esclings` is the live draft array, so every call reads the
  // board as it stands right now.
  //
  // ⚠ WITHOUT THIS THE BRAIN QUEUES SWINGS THE GATE WILL REFUSE. A
  // silenced Mind offered as an attacker is not a misplay that costs a
  // few points — the swing is declared, refused, and re-declared, which
  // reads as a turn that never ends. Every enumeration of the NPC's own
  // attackers below asks it, so the silence is decided in one place
  // against one rule instead of being remembered four times.
  const npcMaySwing = (i) => i !== MIND_RIFT
    || mindMayAct(esclings, "ATTACK", matchTurn, _npcMatch?.seatCount || 2);

  // ── Sweet Dreams parity (u_xulan_lord on ANY player being) ──
  const playerHasSweetDreams = (liveField.esclings || []).some(s =>
    (s?.soul || []).some(id => id === "u_xulan_lord"));

  // ── WAVE 4 (C12 a): NPC-side draft re-base after a responded window ──
  // In an NPC match the player can act ONLY through response windows, so
  // re-basing the drafts at every responded window IS the matchRef-style
  // migration the roadmap gates on: every later write — including the
  // caller's end-of-turn full-field write — carries the interrupt's
  // changes forward instead of resurrecting what it ruined ("the spent
  // counter would DUPE" hazard, handleWindowFire's documented cousin).
  // The shared `esclings` ARRAY refreshes IN PLACE and npcOwnField keeps
  // that same array identity — the callers' sync discipline (their
  // draft object IS this array) survives untouched.
  const rebaseNpcDrafts = (freshM) => {
    const fnpc = freshM?.players?.[NPC_IDX];
    if (!fnpc?.field) return;
    const fresh = JSON.parse(JSON.stringify(fnpc.field));
    const freshEsc = Array.isArray(fresh.esclings) ? fresh.esclings : [];
    esclings.length = 0;
    esclings.push(...freshEsc);
    if (npcOwnField) {
      Object.keys(npcOwnField).forEach(k => { if (k !== "esclings") delete npcOwnField[k]; });
      Object.assign(npcOwnField, fresh, { esclings });
    }
  };

  // ── Player Anima strikes — with prophecy-tracker parity ──
  const lostBaseTotal = player.totalAnimaLost || 0;
  const lostBaseTurn  = player.animaLostThisTurn || 0;
  let   lostAccum     = 0;
  const strikePlayerAnima = async (amt, allowWindow = true) => {
    if (amt <= 0) return;
    // ── RESPONSE WINDOW — a direct Anima strike is declared. Riders
    // (dimAnimaOnAttack / spectralClaws / invFangs) pass allowWindow
    // false: they are consequences of an already-windowed attack, not
    // fresh declarations — one attack, one window, never two prompts.
    if (allowWindow) {
      const rw = await requestWindow({ kind: "ANIMA_ATTACK", lyt: amt });
      if (rw?.responded) {
        const freshM = await getMatch(code);
        const fp = freshM?.players?.[0];
        if (fp) {
          liveAnima = fp.anima ?? liveAnima;
          liveField = JSON.parse(JSON.stringify(fp.field || liveField));
          liveField.tomes = riftArray(liveField.tomes, 3);
          liveNec = [...(fp.necropolis || liveNec)];
        }
        rebaseNpcDrafts(freshM); // Wave 4 — the NPC's own side re-bases too
      }
    }
    lostAccum += amt;
    liveAnima = Math.max(0, liveAnima - amt);
    // R100 — the brain striking the human. ADVERSARIAL, and this raw
    // pair IS the adversarial ledger (it open-codes buildAnimaLossUpdate
    // so it can accumulate across a multi-swing turn from one base
    // read). It must never touch the self-dim counters.
    await updatePlayer(code, 0, {
      anima: liveAnima,
      totalAnimaLost: lostBaseTotal + lostAccum,
      animaLostThisTurn: lostBaseTurn + lostAccum,
    });
    await sleep(KILL_SHOT_DELAY);
    if (liveAnima <= 0) killed = true;
  };

  const liveTargets = () => {
    const slots = liveField.esclings || [];
    // ── R119 · THE WARDEN SHIELD, ASKED ONCE FOR THE WHOLE LINE ──────
    // caP'n, 2026-09-06: *"Warden: tank must be ruined and either left
    // in a fallen state, or only esclings left on board to attack the
    // Warden with Lyt attacks. All other ways are always viable."* A
    // being standing in the MIND rift cannot be SWUNG AT while that
    // seat's BODY rift still holds a standing, un-Fallen Escendant.
    // Which of the two doors is open — the Tank ruined AND left Fallen,
    // or only Esclings left on that side — and the derived third case
    // (no Tank ever stood, so no shield at all) are rules.js's to
    // decide and not this file's, so the whole question is ONE call to
    // the shared predicate and the answer is reused for every rift.
    //
    // ⚠ WITHOUT THIS FILTER THE TURN DOES NOT MISPLAY — IT STALLS, AND
    // THAT IS WHY IT IS BOUND HERE. This is the ONE enumerator of
    // attackable adversary beings in the file: the Escendant swing loop
    // and the Escling swing loop below both pick their targets out of
    // what it returns. Leave a shielded Warden in the list and the
    // brain picks it, the gate refuses the swing, and the brain picks
    // it again — a hang, not a bad move. Binding the law at the
    // enumerator also means neither consumer has to remember it, which
    // is the difference between one rule and three copies of it.
    //
    // ⚠ AND IT IS THE SWING ONLY. "All other ways are always viable" —
    // a Tome, an Impera, an U'hlti, a Curio, a stamp or an Affinity
    // reaches a Warden through the shield on any Turn, so nothing but
    // this target list is narrowed.
    // R115 — with the clock: the shield is a Role passive and sleeps for
    // the first three full Turns, exactly as the gate now reads it.
    const wardenOpen = wardenOpenToLytAttack(slots, matchTurn, _npcMatch?.seatCount || 2);
    // R25: Fallen beings are impervious to Lyt attacks — never targets.
    const attackable = slots.map((s, i) => ({ s, i }))
      .filter(({ s, i }) => s?.escendant && !s.fallen && (i !== MIND_RIFT || wardenOpen));
    const vuln     = attackable.filter(({ s }) => isVulnerable(s, liveField));
    const standing = attackable.filter(({ s }) => !isVulnerable(s, liveField));
    return { vuln, standing };
  };

  // ── R26 (canon, July 2026): Fallen beings are BYPASSABLE immediately ──
  // A being that descended — even this very turn — does NOT block Anima
  // attacks. Ruin the L3, walk past the L2 it left behind, dim Anima.
  // Only standing (non-fallen) beings block.
  //
  // ⚠ AND R119 ADDED A SECOND BYPASS ON 2026-09-07: THE MIND RIFT.
  // caP'n: *"You dont need to ruin the warden or leave it in a fallen
  // state to dim Anima, only the other 3"*, and on how to say it,
  // *"pretend as though the Warden isn't there is a good way to explain
  // that."* A being in the Mind rift NEVER walls the Anima — three
  // standing beings hold the wall and the fourth does not. That is the
  // price the Warden pays for being unswingable, and it means the
  // question is no longer a flat `.some` over states: whether a being
  // blocks now depends on WHERE it stands as well as on what it is.
  //
  // ⚠ THIS ASKS THE EXPORTED PREDICATE ON PURPOSE. The wall rule
  // already has three readers — the gate (validateAnimaAttack), the
  // renderer and this brain — and a private fourth copy here is exactly
  // the compensation-instead-of-a-fix trap this repo has written down.
  // If one reader learns R119 and another does not, the brain plans a
  // dim the gate refuses, which is a stalled turn; asking rules.js is
  // what keeps all of them moving together.
  const animaClear = () => animaOpenToLytAttack(liveField.esclings || [], matchTurn, _npcMatch?.seatCount || 2);

  // ── Taunt attack (must-ruin-first, July 2026 canon) ──
  // While the PLAYER has a face-up Khao D'uhmdumn, every NPC Lyt attack
  // is DIRECTED AT THE TOME (not skipped). Resolution mirrors the player
  // side: Lord's nullifyFirstRuin soaks the first hit of any kind;
  // Lyt >= current Lyf ruins the tome to Necropolis; Lyt < Lyf chips.
  // Returns true if the attacker spent its attack on the tome.
  const attackRuinFirstTome = async (attackerLyt) => {
    // ── RESPONSE WINDOW — the tome-directed strike is declared. The
    // re-read happens BEFORE deriving the required target: a tome
    // flipped or state changed mid-window feeds the derivation fresh.
    {
      const rw = await requestWindow({ kind: "LYT_ATTACK_TOME", lyt: attackerLyt });
      if (rw?.responded) {
        const freshM = await getMatch(code);
        const fp = freshM?.players?.[0];
        if (fp) {
          liveField = JSON.parse(JSON.stringify(fp.field || liveField));
          liveField.tomes = riftArray(liveField.tomes, 3);
          liveNec = [...(fp.necropolis || liveNec)];
        }
        rebaseNpcDrafts(freshM); // Wave 4 — the NPC's own side re-bases too
      }
    }
    const req = getRuinFirstTome(liveField);
    if (!req) return false;
    const t = liveField.tomes[req.slotIdx];
    if (t?.nullifyFirstRuin) {
      // Lord soaks the first hit — flag consumed, tome untouched.
      liveField.tomes[req.slotIdx] = { ...t, nullifyFirstRuin: false };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return true;
    }
    const card = getCard(req.id);
    const tomeLyf = (t?.currentLyf !== null && t?.currentLyf !== undefined) ? t.currentLyf : (card?.lyf ?? 0);
    if (attackerLyt >= tomeLyf) {
      liveField.tomes[req.slotIdx] = null;
      liveNec.push(req.id);
      await updatePlayer(code, 0, { field: liveField, necropolis: liveNec });
      await sleep(POST_RUIN_DELAY);
    } else {
      liveField.tomes[req.slotIdx] = { ...t, currentLyf: Math.max(0, tomeLyf - attackerLyt) };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
    }
    return true;
  };

  // ── AWAKENING: defensive-state-aware strike on a player being ──
  // Player-side resolution order mirrored exactly (Shatterfield ~6740+):
  // fallen → intangible → nullify → reflect → effective-Lyf math.
  // Returns "none" | "spent" | "ruined" | "fractured" | "chipped" | "npcDied".
  const strikeBeing = async (targetIdx, aLyt) => {
    // ── RESPONSE WINDOW (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27) ──────────
    // The strike is DECLARED, not yet resolved. The defender may fire
    // a legal interrupt; if they do, re-read their live state so
    // freshly-set defensive flags (intangible / nullify / reflect /
    // buffs) count against THIS strike. Every prior mid-phase write
    // is already persisted, so Firebase is the one truth to re-read.
    {
      const rw = await requestWindow({ kind: "LYT_ATTACK", targetIdx, lyt: aLyt });
      if (rw?.responded) {
        const freshM = await getMatch(code);
        const fp = freshM?.players?.[0];
        if (fp) {
          liveField = JSON.parse(JSON.stringify(fp.field || liveField));
          liveField.tomes = riftArray(liveField.tomes, 3);
          liveNec = [...(fp.necropolis || liveNec)];
          liveAnima = fp.anima ?? liveAnima;
        }
        rebaseNpcDrafts(freshM); // Wave 4 — the NPC's own side re-bases too
      }
    }
    const t = liveField.esclings[targetIdx];
    if (!t?.escendant) return "none";
    if (t.fallen) return "none"; // R25 — impervious to Lyt
    if (t.intangible) {
      // Attack passes through — being unharmed, flag consumed, attack spent.
      liveField.esclings[targetIdx] = { ...t, intangible: false };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return "spent";
    }
    if (t.nullifyNextAttack) {
      liveField.esclings[targetIdx] = { ...t, nullifyNextAttack: false };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return "spent";
    }
    if (t.reflectNextAttack) {
      // Fate Maker — reflected onto the NPC's OWN Anima. Flag consumed.
      liveField.esclings[targetIdx] = { ...t, reflectNextAttack: false };
      await updatePlayer(code, 0, { field: liveField });
      // R100 — ADVERSARIAL: Fate Maker is the PLAYER's Impera bouncing
      // the brain's own swing back at it. Their card, their damage.
      const died = await dimNpcAnima(code, aLyt, "adversary");
      await sleep(ACTION_DELAY);
      if (died) { npcDied = true; return "npcDied"; }
      return "spent";
    }
    const defLyf = effLyfPure(t, liveField); // EFFECTIVE Lyf — player buffs count
    if (defLyf === 0) {
      // Universal ruin semantics — descend or full-ruin via helper.
      descendPlayerSlot(liveField, liveNec, targetIdx, matchTurn);
      await updatePlayer(code, 0, { field: liveField, necropolis: liveNec });
      await sleep(POST_RUIN_DELAY);
      return "ruined";
    }
    if (aLyt >= defLyf) {
      liveField.esclings[targetIdx] = { ...t, currentLyf: 0 };
      await updatePlayer(code, 0, { field: liveField });
      await sleep(ACTION_DELAY);
      return "fractured";
    }
    // Chip — mirrors the player-side write (effective Lyf minus Lyt).
    liveField.esclings[targetIdx] = { ...t, currentLyf: Math.max(0, defLyf - aLyt) };
    await updatePlayer(code, 0, { field: liveField });
    await sleep(ACTION_DELAY);
    return "chipped";
  };

  // ── Post-attack riders for the attacker that just swung ──
  // dimAnimaOnAttack fires on every Lyt Attack; spectralClaws and
  // invFangsBuff fire only when the attack itself dimmed Anima
  // (invFangsBuff is consumed). Sweet Dreams self-dim always applies.
  const afterAttackRiders = async (attackerIdx, struckAnima) => {
    if (npcDied) return;
    const s = esclings[attackerIdx];
    let dim = 0;
    if (s?.dimAnimaOnAttack) dim += s.dimAnimaOnAttack;
    if (struckAnima && s?.spectralClaws) dim += 1;
    if (struckAnima && s?.invFangsBuff) {
      dim += 2;
      esclings[attackerIdx] = { ...esclings[attackerIdx], invFangsBuff: false };
    }
    // ── R78 BEAST STAMP — "if the user dims the adversary's Anima this
    // Turn, dim it by 1 more." Armed by pressOnePrimera before the
    // attack phase; consumed by the first Anima dim this being lands.
    //
    // ⚠ THE CARD SAYS "DIMS", NOT "STRIKES". The first cut required
    // `struckAnima` — a direct Lyt Attack on the Anima — which behind a
    // standing wall is exactly the thing R31 forbids, so the one stamp
    // that adds unblockable damage could only fire when the damage was
    // already unblocked. A being wearing dimAnimaOnAttack / spectral
    // claws / Inverted Fangs dims the adversary's Anima while swinging
    // INTO the wall, and that is a dim, and Beast rides it.
    if (s?.beastArmed && (struckAnima || dim > 0)) {
      dim += 1;
      esclings[attackerIdx] = { ...esclings[attackerIdx], beastArmed: false };
    }
    if (dim > 0 && !killed) await strikePlayerAnima(dim, false); // rider — already windowed

    // ── LIGHTNING BLADE (Treasure L1) ───────────────────────────
    // "This Treasure belongs to 1 being; when the wielder Lyt Attacks,
    // dim the Lyf of your adversary's cards by 1."
    //
    // ⚰ WHY THIS NEVER FIRED: the bonding branch in the tome dispatcher
    // was hardcoded to `tome_gauntlets_azka`, so the Blade never got a
    // `wieldedBy` and this hook had nothing to hang off — a card whose
    // whole text is a rider on an attack, with no rider anywhere in the
    // attack phase. Bonding is generalised now (execBond), and this is
    // the other half. dimAllPlayerLyf has existed here, uncalled, the
    // whole time: the balancer has been printing it as a legal action
    // the NPC NEVER TOOK, in every run, for months.
    const bladeIdx = riftArray(npcOwnField?.tomes, 3)
      .findIndex(t => t?.id === "tome_lightning_blade" && !t.faceDown && t.wieldedBy === attackerIdx);
    if (bladeIdx >= 0 && !killed && !npcDied) await dimAllPlayerLyf(code, 1);

    if (playerHasSweetDreams) {
      // R100 — ADVERSARIAL: Sweet Dreams belongs to the player.
      const died = await dimNpcAnima(code, 1, "adversary");
      if (died) npcDied = true;
    }
  };

  // ═══════════════════════════════════════════════════════════
  // ⭐ THE LETHAL RE-MEASURE — EVERY TIER, EVERY TURN
  // ═══════════════════════════════════════════════════════════
  // `canKillNow` is computed in runLordTurn at TURN START, before this
  // turn's ascends, trainings, stamps and soul effects raise the Lyt —
  // and it was consulted by exactly one brain (Lord) above exactly one
  // skill threshold (0.82). Every other brain in the game could hold a
  // winning board and wander off to fracture something.
  //
  // Declining lethal is the most expensive single mistake available,
  // and it is free to check: sum the Lyt that has not swung yet, ask
  // whether the lane is clear (R31/R26 — animaClear() encodes both),
  // and compare to the live Anima. If it wins, nothing else matters.
  // SKILL_LETHAL_FOCUS survives as the gate on the *harder* judgement
  // below it (abandoning a built combo plan); taking a kill that is
  // simply on the table is not a skill, it is the game.
  //
  // ⚠ R121 — A SILENCED MIND'S LYT IS NOT AVAILABLE LYT, and neither is
  // its redirect. This sum is the whole basis of the lethal call: count
  // a silenced Warden's Lyt in it and the brain declares a kill it
  // cannot land, abandons its lane plan for it (see LETHAL FOCUS
  // below), and finishes the Turn having swung with everything EXCEPT
  // the being that made the number lethal.
  const _availLyt = esclings.reduce((sum, s, i) =>
    (s?.escendant && !s.attackedThisTurn && !s.fallen && npcMaySwing(i)) ? sum + (effLyt(i) || 0) : sum, 0);
  const _redirectOpen = esclings.some((s, i) =>
    s?.escendant && !s.attackedThisTurn && !s.fallen && npcMaySwing(i)
    && (s.canDirectAnima || s.animaRedirectAll));
  const lethalNow = (animaClear() || _redirectOpen)
    && _availLyt >= liveAnima
    && liveAnima > 0
    && !getRequiredTargetNpc(liveField);   // a taunt eats the swings first

  // ═══════════════════════════════════════════════════════════
  // ⭐ THE LANE PLAN — BLOCKER MATH, EVERY TIER
  // ═══════════════════════════════════════════════════════════
  // What the lock probe actually found, and it is not what the audit
  // guessed. In the duels that reach the R93 clock:
  //   · 78% of turn-100 boards COULD ruin something — the wall was
  //     never unbreakable, so "the brain cannot break it" was wrong.
  //   · 1,193 beings were ruined and 924 came straight back. Three
  //     quarters of every kill was undone by a Resurrection.
  //   · Anima at turn 100: 22.7 vs 20.1. A hundred turns of full-tempo
  //     combat moved seven points, because R31 only opens the lane
  //     when EVERY adversary being is gone or Fallen, and the brain
  //     was killing beings one at a time with no idea what the lane
  //     needed.
  //
  // Killing the treadmill is not the answer — both seats ride it and
  // neither can stop unilaterally. OPENING THE LANE is. And R26 makes
  // that far cheaper than it looks: a ruined L2/L3 DESCENDS and lands
  // Fallen, and a Fallen being does not block. So the lane does not
  // need the board empty, it needs every slot empty-or-Fallen — which
  // one turn of correctly SEQUENCED attacks can produce.
  //
  // So: before a single swing, count the cost.
  //   · a being at 0 effective Lyf costs ONE swing (any Lyt ruins it)
  //   · anything else costs TWO — a fracturer with Lyt >= its Lyf,
  //     then any second attacker to ruin it (R22 → R23/R24)
  //   · a being nobody can fracture costs Infinity, and the plan dies
  // If the total fits in the attackers on hand, execute exactly that
  // and send every leftover swing at the face. If it does not fit, the
  // plan is abandoned whole — a half-cleared lane is the chip damage
  // R28 erases, and that is the trade the brain kept making.
  //
  // Sequencing is the half that a per-being planner cannot do: the
  // fracturer must swing BEFORE its ruiner, and the attack loop walks
  // slots in index order, so the fracturer is assigned the lowest free
  // index and the ruiner a higher one. A pair that cannot be ordered
  // is not booked.
  let lanePlan = null;
  {
    // R121 — a silenced Mind is not an attacker, so it is not a swing
    // the lane plan may spend. Booking one makes the plan read FEASIBLE
    // on a line it cannot actually clear: the fracturer or the ruiner
    // never swings, the lane stays walled, and the Turn spends its
    // whole attack phase producing the chip damage R28 erases.
    const avail = esclings
      .map((s, i) => ({ s, i, lyt: effLyt(i) }))
      .filter(({ s, i, lyt }) => s?.escendant && !s.attackedThisTurn && !s.fallen && lyt > 0
        && npcMaySwing(i))
      .sort((a, b) => a.i - b.i);
    const blockers = (liveField.esclings || [])
      .map((s, i) => ({ s, i, lyf: effLyfPure(s, liveField) }))
      .filter(({ s }) => s?.escendant && !s.fallen);

    if (blockers.length > 0 && avail.length > 0 && !getRequiredTargetNpc(liveField)) {
      const used = new Set();
      const orders = [];   // { attackerIdx, targetIdx, role }
      let feasible = true;
      // Cheapest blockers first: the ones already at 0 Lyf, then the
      // ones the weakest attacker can still fracture.
      for (const b of [...blockers].sort((x, y) => x.lyf - y.lyf)) {
        if (b.lyf === 0) {
          const a = avail.find(x => !used.has(x.i));
          if (!a) { feasible = false; break; }
          used.add(a.i);
          orders.push({ attackerIdx: a.i, targetIdx: b.i, role: "ruin" });
          continue;
        }
        // Smallest sufficient fracturer — never spend a big swing where
        // a small one lands, the big ones are the face damage.
        const frac = [...avail].filter(x => !used.has(x.i) && x.lyt >= b.lyf)
          .sort((x, y) => x.lyt - y.lyt)[0];
        if (!frac) { feasible = false; break; }
        const ruin = avail.find(x => !used.has(x.i) && x.i !== frac.i && x.i > frac.i);
        if (!ruin) { feasible = false; break; }
        used.add(frac.i); used.add(ruin.i);
        orders.push({ attackerIdx: frac.i, targetIdx: b.i, role: "fracture" });
        orders.push({ attackerIdx: ruin.i, targetIdx: b.i, role: "ruin" });
      }
      if (feasible) {
        const spare = avail.filter(x => !used.has(x.i));
        // NOTE — a spare-less plan is deliberately still booked. It
        // reads wasteful (the lane opens with nothing left to walk
        // through it) but the board it leaves behind is three Fallen
        // beings, and MEASURED it is worth 1.7 points of clock-call
        // rate over refusing. Do not "fix" it without re-running.
        lanePlan = {
          byAttacker: new Map(orders.map(o => [o.attackerIdx, o])),
          spare: new Set(spare.map(x => x.i)),
        };
      }
    }
  }

  // LORD: pre-compute attack plan (AWAKENING: effective values, no
  // planning against Fallen beings — R25 makes those attacks dead).
  let lordPlan = null;
  if (difficulty === "Lord") {
    // R121 — the same silence, on the list the Lord plans WITH. Every
    // soloKill and comboPlan pair is drawn from here, and a pair whose
    // fracturer or ruiner is a silenced Mind is a target booked and
    // then never struck: the being survives at 0 Lyf, R28 restores it
    // at end of Turn, and the plan bought nothing.
    const myAttackers = esclings
      .map((s, i) => ({ s, i, lyt: effLyt(i) }))
      .filter(({ s, i }) => s?.escendant && !s.attackedThisTurn && !s.fallen && npcMaySwing(i));

    // SKILL_LETHAL_FOCUS still means something, and it is the HARDER
    // half of the judgement: at ≥0.82 the Lord discards its whole plan
    // and every attacker goes face without finishing the fractured
    // being first. Below that it takes the kill too (see the loop), it
    // just tidies the board on the way.
    if (lethalNow && (brain?.skill ?? 0) >= SKILL_LETHAL_FOCUS) {
      lordPlan = { soloKill: [], comboPlan: [], myAttackers, faceAll: true, denialIdx: -1 };
    } else {

    // ── PROPHECY DENIAL (DEPTHS BRAIN, skill ≥ 0.95) ──
    // Sort the denial target to the front of the planning queue so the
    // soloKill / comboPlan assignments claim it first.
    const denialIdx = (brain?.skill ?? 0) >= SKILL_DENIAL ? findDenialTarget(player) : -1;

    const oppBeings = (liveField.esclings || [])
      .map((s, i) => ({ s, i, lyf: effLyfPure(s, liveField) }))
      .filter(({ s }) => s?.escendant && !s.fallen);
    if (denialIdx >= 0) {
      oppBeings.sort((a, b) => (b.i === denialIdx ? 1 : 0) - (a.i === denialIdx ? 1 : 0));
    }

    const comboPlan = [];
    const soloKill  = [];

    oppBeings.forEach(opp => {
      if (opp.lyf === 0) {
        const attacker = myAttackers.find(a => !soloKill.find(p => p.attackerIdx === a.i));
        if (attacker) soloKill.push({ targetIdx: opp.i, attackerIdx: attacker.i });
        return;
      }
      const defCard = getCard(opp.s.escendant);
      myAttackers.forEach(atk => {
        if (soloKill.find(p => p.attackerIdx === atk.i)) return;
        if (atk.lyt >= opp.lyf && defCard?.level === 1) {
          soloKill.push({ targetIdx: opp.i, attackerIdx: atk.i, twoHit: true });
        }
      });
      for (let a = 0; a < myAttackers.length; a++) {
        for (let b = 0; b < myAttackers.length; b++) {
          if (a === b) continue;
          if (comboPlan.find(p => p.fracturerIdx === myAttackers[a].i || p.ruinerIdx === myAttackers[b].i)) continue;
          if (soloKill.find(p => p.attackerIdx === myAttackers[a].i || p.attackerIdx === myAttackers[b].i)) continue;
          const fractures = myAttackers[a].lyt >= opp.lyf;
          // ⭐ `ruins` used to read `myAttackers[b].lyt >= 0`, which is
          // true for every card in the game — so the combo planner
          // booked second attackers that could not finish the job and
          // the guard at the execution site silently no-opped the
          // swing. A fractured being sits at 0 Lyf, so ANY positive Lyt
          // ruins it; zero Lyt does not, and neither does an attacker
          // the planner already spent.
          const ruins     = (myAttackers[b].lyt || 0) > 0;
          if (fractures && ruins) {
            comboPlan.push({ targetIdx: opp.i, fracturerIdx: myAttackers[a].i, ruinerIdx: myAttackers[b].i });
            break;
          }
        }
        if (comboPlan.find(p => p.targetIdx === opp.i)) break;
      }
    });

    lordPlan = { soloKill, comboPlan, myAttackers, faceAll: false, denialIdx };
    }
  }

  for (let i = 0; i < esclings.length; i++) {
    const slot = esclings[i];
    if (!slot?.escendant || slot.attackedThisTurn || slot.fallen) continue;
    // R121 — THE LAST DOOR, and the one that actually holds. The
    // planners above avoid booking a silenced Mind; this refuses to
    // swing it even if a plan, a soul flag or a future scorer hands it
    // one anyway. Re-asked per attacker because the trio can break
    // mid-phase and a Mind that woke up two swings ago is owed its
    // attack. Delete this and the brain declares a Lyt Attack the gate
    // must refuse, which is a stalled turn rather than a lost point.
    if (!npcMaySwing(i)) continue;
    const attacker = getCard(slot.escendant);
    if (!attacker || !attacker.lyt) continue;

    // ── R30 + R124 · HOW MANY SWINGS THIS BEING HAS ──────────────
    // ⚠ THIS WAS `hasExtra ? 2 : 1`, WHICH IS TWO RULES SHORT.
    // R124 separates A GRANT from A PASSIVE ADD, and the old expression
    // only knew the grants:
    //   · the GRANTS — `extraAttack` / `extraAttackAnimaOnly`, written
    //     by Relentless, Catastrophe, Apocalypse, Restless and Sword of
    //     Shadows. One-shots, consumed by the swing they paid for.
    //   · the PASSIVE — King of the Anthill: *"This being can make an
    //     additional Lyt Attack, for every other Antdroid being in
    //     combat."* Not a flag at all — it is recomputed from the board
    //     every time it is asked, counts Antdroids on BOTH sides, and
    //     grows as more of them arrive. A being wearing it beside two
    //     other Antdroids has THREE swings, and the brain gave it one.
    // `rules.lytAttacksAllowed` is the single predicate that folds both,
    // and the board has asked it since R124 landed. Same call, same
    // arguments, same order — `esclings` is my live draft and
    // `liveField.esclings` is the adversary's, which is what the
    // Antdroid count needs.
    const animaOnlySecond = !!slot.extraAttackAnimaOnly && !slot.extraAttack;
    const swings          = lytAttacksAllowed(slot, esclings, liveField?.esclings || [], getCard, i);
    const canRedirect     = !!(slot.canDirectAnima || slot.animaRedirectAll);

    let acted = false;
    let attackerRuined = false;
    // How many swings this being actually LANDED, for the R124 stamp
    // below. `acted` is a boolean and cannot tell one swing from three.
    let swingsTaken = 0;
    // SEVENTEEN branches in this loop declare a swing, and patching
    // seventeen call sites is a discipline rather than a rule — the
    // eighteenth gets written next month by somebody who never read this
    // comment. So the count is taken through ONE idempotent mark: the
    // first branch of an iteration to declare a swing counts it, any
    // later one in the same iteration is a no-op, and the flag resets at
    // the top of every iteration. Exact however the branches are
    // rearranged, and it cannot double-count one swing.
    let _countedThisSwing = false;
    const markSwing = () => {
      if (!_countedThisSwing) { _countedThisSwing = true; swingsTaken++; }
      return true;
    };

    for (let swing = 0; swing < swings; swing++) {
      if (killed || npcDied) break;
      if (swing > 0 && !acted) break; // the first swing found nothing — no more
      // Wave 4 (C12 a): the attacker may have been ruined by a response
      // mid-window (the re-based draft shows it) — remaining swings fizzle.
      if (!esclings[i]?.escendant) break;
      _countedThisSwing = false;
      // BUG #2 FIX: effective attack value includes the Gauntlets +2 bond.
      const aLyt = effLyt(i);
      // Restless (p_antdroid_1) buys a swing that may ONLY dim Anima.
      // ⚠ WHICH swing that is, when the allowance is now more than two,
      // is left exactly as it was: index 1. A being can wear Restless AND
      // King of the Anthill (both are Antdroid Impera), so 1 base + 1
      // grant + 2 passive = four swings, and no card says which of them
      // is the restricted one. Behaviour is unchanged for the two-swing
      // case, which is every case that existed before R124 landed here.
      // Flagged as a question rather than decided in code.
      const animaOnly = swing === 1 && animaOnlySecond;

      // ── Must-target resolution — being MRF first, then tome (canon) ──
      const req = getRequiredTargetNpc(liveField);
      if (req?.kind === "tome") {
        await attackRuinFirstTome(aLyt);
        acted = markSwing();
        await afterAttackRiders(i, false);
        continue;
      }
      if (req?.kind === "escling") {
        if (animaOnly) break; // anima-only swing has no legal target under a taunt
        const r = await strikeBeing(req.slotIdx, aLyt);
        if (r === "npcDied") break;
        acted = markSwing();
        await afterAttackRiders(i, false);
        continue;
      }

      // ── None More Than I — the PLAYER's Chaos Lord aura (July 2026) ──
      // Symmetric with the player side: an NPC attacker with Lyt > 10 that
      // Lyt-attacks while the player has Chaos Dragon Lord + p_chaos_dragon_3
      // is instantly ruined (universal semantics — descends if a lower level
      // waits in its soul, otherwise full ruin with soul to Necropolis).
      const playerHasNoneMoreThanI = (liveField.esclings || []).some(s => {
        const c = getCard(s?.escendant);
        return c?.id === "chaos_dragon_lord" && (s?.soul || []).some(id => id === "p_chaos_dragon_3");
      });
      if (playerHasNoneMoreThanI && aLyt > 10) {
        const npcMatchNow = await getMatch(code);
        const npcNec = [...((npcMatchNow?.players?.[NPC_IDX]?.necropolis) || [])];
        // Mutate the caller's OWN field object (npcOwnField.esclings IS the
        // shared `esclings` array) so the descent + any wielded-Treasure
        // cascade survive the caller's full-field write at turn end.
        const npcFieldDraft = npcOwnField || { esclings, tomes: [] };
        descendPlayerSlot(npcFieldDraft, npcNec, i, matchTurn);
        await updatePlayer(code, NPC_IDX, { field: npcFieldDraft, necropolis: npcNec });
        await sleep(POST_RUIN_DELAY);
        attackerRuined = true;
        break;
      }

      // ── Anima-only second swing (Restless) ──
      if (animaOnly) {
        if (animaClear() || canRedirect) {
          await strikePlayerAnima(aLyt);
          acted = markSwing();
          await afterAttackRiders(i, true);
        }
        break;
      }

      const { vuln, standing } = liveTargets();
      const allTargets = [...vuln, ...standing];

      // ── FINISH THE FRACTURED (caP'n canon, July 2026) ──
      // Every brain, every level: a Fractured (vulnerable) player being is
      // ALWAYS finished off before any other target is considered — no
      // more fracturing one being and wandering off to chip another while
      // it stands at 0 Lyf. Highest level first (biggest tempo denial).
      // Sole exception: a Lord in LETHAL FOCUS — if going face wins the
      // duel THIS turn, the fractured being's fate is irrelevant.
      // Universal now — a Beb and a Champion holding lethal go face too.
      // The skill gate no longer decides WHETHER to take the kill (that
      // was the blunder); it decides whether the fractured being on the
      // board gets tidied up on the way to the face.
      const lethalFace = (lethalNow && (brain?.skill ?? 0) >= SKILL_LETHAL_FOCUS)
        || (difficulty === "Lord" && lordPlan?.faceAll);

      // ── THE LANE PLAN RUNS FIRST (see the banner above the plan). It
      // is the only plan in this loop that was built by asking what the
      // WHOLE board costs rather than what one being is worth, so it
      // outranks Finish-the-Fractured and every tier's own plan. Its
      // spare attackers fall through to the face below, which is the
      // point of the whole exercise. Plans are dropped for the swing
      // once the board has moved out from under them (a response window
      // can ruin the target mid-plan).
      if (lanePlan && swing === 0) {
        const order = lanePlan.byAttacker.get(i);
        if (order) {
          const t = liveField.esclings[order.targetIdx];
          if (t?.escendant && !t.fallen) {
            const r = await strikeBeing(order.targetIdx, aLyt);
            if (r === "npcDied") break;
            if (r !== "none") { acted = markSwing(); await afterAttackRiders(i, false); continue; }
          }
          // Target already gone — this swing is free, fall through.
        } else if (lanePlan.spare.has(i) && (animaClear() || canRedirect)) {
          await strikePlayerAnima(aLyt);
          acted = markSwing();
          await afterAttackRiders(i, true);
          if (killed) break;
          continue;
        }
      }

      if (!lethalFace && vuln.length > 0) {
        const finishTarget = [...vuln].sort((a, b) =>
          (getCard(b.s.escendant)?.level || 0) - (getCard(a.s.escendant)?.level || 0))[0];
        const r = await strikeBeing(finishTarget.i, aLyt);
        if (r === "npcDied") break;
        acted = markSwing();
        await afterAttackRiders(i, false);
        continue;
      }

      if (difficulty === "Lord" && lordPlan) {
        // ── LETHAL FOCUS — the plan IS the face. Nothing else exists.
        // `lethalNow` is here too: a sub-0.82 Lord keeps its plan, but
        // it does NOT decline a kill that is sitting on the table.
        if (lordPlan.faceAll || lethalNow) {
          if (animaClear() || canRedirect) {
            await strikePlayerAnima(aLyt);
            acted = markSwing();
            await afterAttackRiders(i, true);
            if (killed) break;
          }
          continue;
        }

        const soloTarget = swing === 0 ? lordPlan.soloKill.find(p => p.attackerIdx === i) : null;
        const fracTarget = swing === 0 ? lordPlan.comboPlan.find(p => p.fracturerIdx === i) : null;
        const ruinTarget = swing === 0 ? lordPlan.comboPlan.find(p => p.ruinerIdx === i) : null;

        // ⭐ ALL THREE PLANNED BRANCHES USED TO SET `acted = true`
        // OUTSIDE THEIR OWN GUARD. So when the plan's precondition was
        // not met — most often a `ruinerIdx` sitting on a LOWER slot
        // index than its `fracturerIdx`, swinging first, at a being
        // that is not vulnerable YET — the attack was consumed and
        // nothing happened. A plan that mis-orders itself is a plan
        // that throws attacks away, and the loop order is not something
        // the planner controls. Now each branch consumes the swing only
        // when its strike actually resolved, and otherwise falls
        // through to the unplanned logic below (face, fracture, chip),
        // which always does something.
        if (soloTarget) {
          const t = liveField.esclings[soloTarget.targetIdx];
          if (t?.escendant && (isVulnerable(t, liveField) || (soloTarget.twoHit && aLyt >= effLyfPure(t, liveField)))) {
            const r = await strikeBeing(soloTarget.targetIdx, aLyt);
            if (r === "npcDied") break;
            // twoHit — fracture then ruin in the same attack (pre-existing
            // Lord behavior, preserved; defenses above intercept first).
            if (r === "fractured" && soloTarget.twoHit) {
              descendPlayerSlot(liveField, liveNec, soloTarget.targetIdx, matchTurn);
              await updatePlayer(code, 0, { field: liveField, necropolis: liveNec });
              await sleep(POST_RUIN_DELAY);
            }
            await afterAttackRiders(i, false);
            acted = markSwing();
            continue;
          }
        }

        if (fracTarget) {
          const t = liveField.esclings[fracTarget.targetIdx];
          if (t?.escendant && !isVulnerable(t, liveField)) {
            const r = await strikeBeing(fracTarget.targetIdx, aLyt);
            if (r === "npcDied") break;
            await afterAttackRiders(i, false);
            acted = markSwing();
            continue;
          }
        }

        if (ruinTarget) {
          const t = liveField.esclings[ruinTarget.targetIdx];
          if (t?.escendant && isVulnerable(t, liveField) && !t.fallen) {
            const r = await strikeBeing(ruinTarget.targetIdx, aLyt);
            if (r === "npcDied") break;
            await afterAttackRiders(i, false);
            acted = markSwing();
            continue;
          }
        }

        // No planned target — Lord strikes Anima if clear OR via redirect
        if (animaClear() || canRedirect) {
          await strikePlayerAnima(aLyt);
          acted = markSwing();
          await afterAttackRiders(i, true);
          if (killed) break;
        } else if (standing.length > 0) {
          // ── AGGRESSION BUMP ──
          // Lord previously held back if no kill plan. Now it fractures the
          // closest-to-fracture standing being to set up future combos.
          // PROPHECY DENIAL: if the player's prophecy piece is standing
          // and fracturable, it eats the swing instead.
          let target = standing.sort((a, b) => effLyfPure(a.s, liveField) - effLyfPure(b.s, liveField))[0];
          if (lordPlan.denialIdx >= 0) {
            const den = standing.find(x => x.i === lordPlan.denialIdx);
            if (den && aLyt >= effLyfPure(den.s, liveField)) target = den;
          }
          // ⭐ THE NO-OP SWING IS GONE. This block used to run
          // `if (aLyt >= lyf) strike;` and then set `acted = true`
          // UNCONDITIONALLY — so a Lord that could not fracture anything
          // burned its attack, marked the being attackedThisTurn, and
          // changed no number on the board. Combined with R28 (Lyf
          // resets every Turn) and R31 (Anima unreachable behind a
          // standing wall), that is the exact shape of the mutual
          // board-lock the balancer measured: a turn that legally
          // "attacked" and moved nothing.
          //
          // Two rules replace it, in this order:
          //   1. If the swing fractures, take it — as before.
          //   2. If it cannot, CHIP the biggest threat anyway (this is
          //      what the Champion has always done, and R27 only says
          //      chip is wasteful, never that it is illegal). It forces
          //      the defender to spend a heal, and it makes the turn
          //      observable instead of silent.
          // An attack is only ever SPENT for a strike that resolved.
          if (aLyt >= effLyfPure(target.s, liveField)) {
            const r = await strikeBeing(target.i, aLyt);
            if (r === "npcDied") break;
            acted = markSwing();
            await afterAttackRiders(i, false);
          } else {
            const chipTarget = standing.sort((a, b) =>
              (getCard(b.s.escendant)?.lyt || 0) - (getCard(a.s.escendant)?.lyt || 0))[0];
            if (chipTarget) {
              const r = await strikeBeing(chipTarget.i, aLyt);
              if (r === "npcDied") break;
              if (r !== "none") { acted = markSwing(); await afterAttackRiders(i, false); }
            }
          }
        }

      } else if (lethalNow && (animaClear() || canRedirect)) {
        // ── BEB / CHAMPION LETHAL — the lane wins the duel this swing.
        await strikePlayerAnima(aLyt);
        acted = markSwing();
        await afterAttackRiders(i, true);
        if (killed) break;

      } else if (allTargets.length > 0) {
        // ── BEB / CHAMPION ──
        let target = null;

        if (difficulty === "Beb") {
          // Beb: chaotic — sometimes goes for the throat when redirect is
          // live, otherwise a legal target. DEPTHS BRAIN: the throat-lust
          // runs through the temperament gate (Reckless +, Patient −,
          // Cold always).
          //
          // ⭐ AND THE TARGET IS NO LONGER A COIN TOSS AT EVERY SKILL.
          // `pickRandom(allTargets)` at every rung of the ladder is why
          // Champion-vs-Beb averaged 93 turns while Lord-vs-Beb averaged
          // 30: spraying one swing at each of three beings fractures all
          // three and ruins none, R28 erases the lot at end of Turn, and
          // the duel cannot end. Chaos is a low-skill trait, not a Beb
          // trait — an Escling-rank Beb still dices, a top-of-band one
          // concentrates its fire like anything else that wants to win.
          if (canRedirect && chance(0.5, brain)) target = null;
          else if (chance(1 - (brain?.skill ?? 0.5), brain)) target = pickRandom(allTargets);
          else if (vuln.length > 0) target = vuln[0];
          else {
            const canFracture = standing.filter(({ s }) => aLyt >= effLyfPure(s, liveField));
            target = canFracture.length > 0
              ? canFracture.sort((a, b) => effLyfPure(b.s, liveField) - effLyfPure(a.s, liveField))[0]
              : pickRandom(allTargets);
          }
        } else {
          // Champion: ruin vuln first, then fracture if possible, then
          // redirect if trained for it, then chip the biggest threat.
          // DEPTHS BRAIN: animaHunger temperaments (Reckless/Cold) take
          // a trained redirect BEFORE hunting fractures — pressure over
          // patience.
          if (vuln.length > 0) {
            target = vuln[0];
          } else if (canRedirect && brain?.t?.animaHunger) {
            target = null;
          } else {
            const canFracture = standing.filter(({ s }) => aLyt >= effLyfPure(s, liveField));
            if (canFracture.length > 0) {
              target = canFracture.sort((a, b) => effLyfPure(a.s, liveField) - effLyfPure(b.s, liveField))[0];
            } else if (canRedirect) {
              target = null;
            } else if (standing.length > 0) {
              // ── AGGRESSION BUMP ──
              // Champion previously held back if it couldn't fracture. Now it
              // chips the highest-Lyt threat to apply pressure.
              target = standing.sort((a, b) =>
                (getCard(b.s.escendant)?.lyt || 0) - (getCard(a.s.escendant)?.lyt || 0))[0];
            }
          }
        }

        if (target) {
          const r = await strikeBeing(target.i, aLyt);
          if (r === "npcDied") break;
          acted = markSwing();
          await afterAttackRiders(i, false);
        } else if (animaClear() || canRedirect) {
          await strikePlayerAnima(aLyt);
          acted = markSwing();
          await afterAttackRiders(i, true);
          if (killed) break;
        }

      } else if (animaClear() || canRedirect) {
        await strikePlayerAnima(aLyt);
        acted = markSwing();
        await afterAttackRiders(i, true);
        if (killed) break;
      }
    }

    // Attacker was ruined mid-phase (None More Than I) — slot already
    // rewritten by descendPlayerSlot; never re-mark it.
    if (attackerRuined) { if (killed || npcDied) break; continue; }

    if (acted) {
      // ── R124 · THE COUNT IS STAMPED, NOT ONLY THE LATCH ──────────
      // `attacksThisTurn` is reset to 0 by resetEndOfTurn and was never
      // incremented anywhere in this file, so it read 0 for the brain's
      // whole duel. Three readers care: the board's gate context counts
      // occurrences off it (Shatterfield.gateCtx), GameStateValidator
      // audits it, and the transition referee diffs it — so a brain that
      // swung three times looked like a brain that had swung none, and
      // `attackedThisTurn` alone could not tell an allowance of one from
      // an allowance of three. Counted here, exactly as the board counts
      // it at the end of its own attack resolver.
      //
      // `attackedThisTurn` stays the LATCH and goes true only when the
      // allowance is SPENT — which for a lone being is the first swing
      // and for an Anthill trio is the third.
      const takenNow = (Number(esclings[i]?.attacksThisTurn) || 0) + swingsTaken;
      esclings[i] = {
        ...esclings[i],
        attacksThisTurn: takenNow,
        attackedThisTurn: takenNow >= swings,
        // A GRANTED one-shot is spent by the swing it paid for. The
        // PASSIVE half (King of the Anthill) is recomputed from the
        // board every time and must never be cleared here.
        extraAttack: false,          // consumed (whether or not the second swing landed)
        extraAttackAnimaOnly: false, // consumed
      };
    }
    if (killed || npcDied) break;
  }

  // ── RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27 — ESCLINGS CAN LYT ATTACK ──────────────
  // Bare Esclings (no Escendant transcended on top) may Lyt Attack with
  // their PRINTED Lyt — Escendants ONLY, NEVER Anima. That prohibition
  // is absolute: even with the player's board fully Fallen or empty, an
  // Escling has no path to the Anima. Mirrors the player-side
  // handleStartAttackEscling / handleAttackTargetEscling pair.
  // Archetype appetite: Beb swings whenever able (chaos with a
  // rulebook); Champion/Lord swing only when it accomplishes something
  // (ruin a vulnerable being or land a fracture).
  for (let i = 0; i < esclings.length; i++) {
    if (killed || npcDied) break;
    const slot = esclings[i];
    if (!slot?.escling || slot?.escendant || slot.attackedThisTurn) continue;
    const eLyt = getCard(slot.escling)?.lyt || 0;
    if (eLyt <= 0) continue;

    const { vuln, standing } = liveTargets();
    if (vuln.length === 0 && standing.length === 0) continue; // no beings — Anima is off-limits to Esclings

    // Taunt tome — Escling attacks are directed at it, same as any Lyt Attack.
    const req = getRequiredTargetNpc(liveField);
    if (req?.kind === "tome") {
      await attackRuinFirstTome(eLyt);
      esclings[i] = { ...slot, attackedThisTurn: true };
      await afterAttackRiders(i, false);
      continue;
    }

    let target = null;
    if (req?.kind === "escling") {
      target = { i: req.slotIdx };
    } else if (difficulty === "Lord" || difficulty === "Champion") {
      if (vuln.length > 0) target = vuln[0];
      else {
        const canFracture = standing.filter(({ s }) => eLyt >= effLyfPure(s, liveField));
        if (canFracture.length > 0) {
          target = canFracture.sort((a, b) => effLyfPure(a.s, liveField) - effLyfPure(b.s, liveField))[0];
        } else if (standing.length > 0) {
          // ⭐ An Escling that can neither finish nor fracture used to
          // `continue` — so on a locked board every Escling on a
          // Champion's or a Lord's side stood still for the entire
          // duel. R30 says each being attacks once per Turn; nothing
          // says the attack has to be efficient. Chip the biggest
          // threat: free pressure, and one more turn that is not silent.
          target = standing.sort((a, b) =>
            (getCard(b.s.escendant)?.lyt || 0) - (getCard(a.s.escendant)?.lyt || 0))[0];
        }
      }
    } else {
      // Beb (and any unknown archetype) — a random legal target, every time.
      target = pickRandom([...vuln, ...standing]);
    }
    if (!target) continue;

    const r = await strikeBeing(target.i, eLyt);
    if (r === "npcDied") break;
    if (r !== "none") {
      esclings[i] = { ...esclings[i], attackedThisTurn: true };
      await afterAttackRiders(i, false);
    }
    if (killed || npcDied) break;
  }

  // Every being this phase ruined pays whoever is wearing Akula.
  await flushAkula(code);
  return { liveField, liveAnima, killed, npcDied };
}

// ─────────────────────────────────────────────────────────────
// R10 (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27): NPC HAND CAP — draw-then-discard parity
// ─────────────────────────────────────────────────────────────
// Runs at end of turn before the final hand write. If the turn draw or
// effect draws pushed the hand over 8, discard down by value tier:
// dead Tomes first (lowest level), then excess duplicates, then random.
// The NPC is never stuck above the cap.
const NPC_HAND_LIMIT = HAND_LIMIT; // R10 — one catalogue, one number
// ⭐ IS THERE A MOVE IN THIS HAND AT ALL?
// The lock probe's second finding: in the duels that reach the clock,
// the hand sits at 7.9 of 8 at turn 100 and 39% of Turns move nothing.
// Those two facts are one fact. Champion and Lord both refuse to draw
// at the cap (`hand.length < NPC_HAND_LIMIT`), so a hand of eight cards
// that cannot be played is a hand the brain keeps forever: no draw, no
// placement, no ascend, nothing to activate — a Turn that legally
// happened and moved nothing.
//
// R10 is explicit that the cap is not a wall: "Drawing at the cap still
// resolves — then discard 1 to the Necropolis (draw-then-discard)."
// The Beb has always done it. This is what lets the other two, but only
// when the hand really is dead, so a brain holding answers still holds
// them.
function handHasPlay(hand, field, grimoire) {
  const esclings = field?.esclings || [];
  const tomes = riftArray(field?.tomes, 3);
  const rift = tomes.some(t => t === null);
  const emptySlot = esclings.some(sl => sl?.escling && !sl?.escendant);
  return (hand || []).some(id => {
    const c = getCard(id);
    if (!c) return false;
    if (c.type === "Escendant") {
      if (c.level === 1 && emptySlot) {
        return esclings.some(sl => {
          if (sl?.escendant || !sl?.escling) return false;
          const e = getCard(sl.escling);
          return !e || e.school === c.school;           // R16
        });
      }
      return esclings.some(sl => canAscendNatural(getCard(sl?.escendant), c));
    }
    if (c.type === "Impera") {
      return esclings.some(sl => {
        const b = getCard(sl?.escendant);
        if (!b || c.school !== b.school || c.ilk !== b.ilk) return false;
        if ((c.level || 1) > (b.level || 1)) return false;
        return !(sl.soul || []).some(sid => {
          const sc = getCard(sid);
          return sc?.type === "Impera" && sc?.level === c.level;   // R71
        });
      });
    }
    if (c.type === "U'hlti" || c.type === "Affinity") {
      return esclings.some(sl => {
        const b = getCard(sl?.escendant);
        return b && (b.level || 1) >= 3 && !sl.deviant;             // R64/R70
      });
    }
    if (c.type === "Tome") return rift && tomeIsPlaceable(c.id);
    return false;
  });
}

function npcPickDiscardIdx(hand) {
  // ⚠ MEASURED AND REJECTED: preferring to discard the Tomes the
  // dispatcher cannot fire (the old NPC_CANNOT_USE_TOMES blacklist /
  // !npcTomeIsWorthPaying)
  // reads like an obvious improvement and costs FIVE POINTS of clock-call
  // rate (7.5% → 12.5%, seed 7). Keeping the good Tomes means firing more
  // of them, which feeds both walls faster than it breaks either. The
  // cheapest Tome in the hand goes, whatever it is.
  const tomes = hand.map((id, i) => ({ c: getCard(id), i })).filter(x => x.c?.type === "Tome");
  if (tomes.length > 0) return tomes.sort((a, b) => (a.c.level || 1) - (b.c.level || 1))[0].i;
  const seen = {};
  for (let i = 0; i < hand.length; i++) {
    if (seen[hand[i]] !== undefined) return i; // second copy = excess dupe
    seen[hand[i]] = i;
  }
  return Math.floor(Math.random() * hand.length);
}
// ⭐ R10 ON THE WAY OUT — EVERY WAY OUT, not just the kill shot.
// Three sites in each runner return the instant the duel ends mid-turn
// (a soul effect, a stamp, or a Fate Maker reflection), and every one
// of them skipped the cap. The referee caught it as H02 the moment the
// Phantom stamp gave the brain a fourth way to win in the middle of its
// own turn: the duel is over, nothing can be played from that hand, but
// the final snapshot is what a replay renders and what the end screen
// reads — and a referee that reports a rule break it has decided to
// forgive is a referee people stop reading. (That paragraph is quoted
// from the kill-shot path, which got this right first.)
// npcEnforceHandCap returns the trimmed hand AND writes both halves of
// the discard itself now — `{ hand, necropolis }` in one payload (see the
// banner on it for the two-zones bug that change closes). The write below
// is therefore belt to those braces rather than the only one; it is kept
// because this path trims what FIREBASE holds rather than the local draft
// (by the time the duel ended mid-turn the reactive brain may have spent
// from that hand, and the stored snapshot is what the referee reads).
async function npcAbortTurn(code, hand) {
  try {
    const m = await getMatch(code);
    const live = m?.players?.[NPC_IDX]?.hand;
    const src = Array.isArray(live) ? [...live] : [...(hand || [])];
    const trimmed = await npcEnforceHandCap(code, src);
    if (trimmed.length !== src.length) await updatePlayer(code, NPC_IDX, { hand: trimmed });
  } catch (e) { /* the duel is already over — never throw on the way out */ }
}

// ⚠ IT WRITES BOTH HALVES OF THE DISCARD, IN ONE PAYLOAD, AND THAT IS
// THE FIX. It used to write ONLY `{ necropolis: nec }` and hand the
// trimmed array back for the caller to persist — which left a window in
// which Firebase held the card in the HAND and in the NECROPOLIS at the
// same time. npcRoleArts is where that window is real: the Healer Art
// puts a being in the hand (roleArts.performHealerArt), the cap trims it,
// and the runner does not write the hand until the END of the turn — so
// any reactor move in between sets npcReactorDirty, syncNpcHand re-reads
// the UNTRIMMED nine-card hand from Firebase, and the buried card is back
// in hand with its copy already in the Necropolis. That is a card in two
// zones, which is the multiset half of trap 4.3.
//
// One payload, both keys: `hand` and `necropolis` move together or not at
// all, exactly as the player's forced-discard modal writes them
// (Shatterfield · the R10 modal writes `{ hand, necropolis }` in one go).
// Every caller still receives the trimmed hand and still writes it — that
// write is now idempotent rather than load-bearing.
async function npcEnforceHandCap(code, hand) {
  return npcSettleHandCapFor(code, NPC_IDX, hand);
}
// ── R10 · THE HOLDER SETTLES, WHOEVER PUSHED IT OVER (2026-09-15) ────
// RULEZ_V2 ch.4: "the draw still resolves — then send cards to the
// Necropolis until you are at 8. You choose which." The CHOOSER is the
// seat that holds the cards. A human holder chooses in the forced-
// discard modal, which their client raises the moment ANY source pushes
// them over (Shatterfield's R10 watcher). A brain holder chooses HERE,
// with npcPickDiscardIdx — and, since the one-resolver collapse, it has
// to be reachable for a seat other than NPC_IDX: when the brain plays
// Devil's Advocate against ANOTHER brain (the balancer, self-play, a
// boss table), the adversary's ninth card has no client to settle it.
// The old executor "fixed" that by having the CASTER discard for the
// adversary — a human's discard chosen by the brain, which the book
// forbids. Now the caster settles only a BRAIN adversary (the door below
// asks rules.seatIsBrain), and a human's hand is theirs to trim.
// `hand` may be a draft ahead of Firebase; absent, the live hand is read.
async function npcSettleHandCapFor(code, seatIdx, hand = null) {
  const m = await getMatch(code);
  const live = m?.players?.[seatIdx] || {};
  const h = [...(Array.isArray(hand) ? hand : (live.hand || []))];
  if (h.length <= NPC_HAND_LIMIT) return h;
  const nec = [...(live.necropolis || [])];
  while (h.length > NPC_HAND_LIMIT) {
    const [gone] = h.splice(npcPickDiscardIdx(h), 1);
    nec.push(gone);
  }
  await updatePlayer(code, seatIdx, { hand: h, necropolis: nec });
  return h;
}
// The brain's twin of the board's R10 watcher: the host's match listener
// (App.js) calls this on EVERY snapshot, so a human's Devil's Advocate
// that lands a ninth card on the brain is settled at once — during the
// human's half-turn, exactly when the human's own modal would settle
// theirs — instead of riding into the brain's next Turn as a ninth card
// it could play from. Never runs while the brain's own runner holds the
// chair (the runner carries hand drafts ahead of Firebase and trims at
// its own bottom); re-entrance is guarded per match.
const _settling = new Set();
export async function npcWatchHandCap(code, match) {
  if (!match || match.status === "finished") return false;
  if (match.activePlayer === NPC_IDX) return false;
  const hand = match.players?.[NPC_IDX]?.hand;
  if (!Array.isArray(hand) || hand.length <= NPC_HAND_LIMIT) return false;
  if (_settling.has(code)) return false;
  _settling.add(code);
  try { await npcSettleHandCapFor(code, NPC_IDX); return true; }
  finally { _settling.delete(code); }
}

// ─────────────────────────────────────────────────────────────
// END OF TURN: reset attacked flags, restore Lyf
// ─────────────────────────────────────────────────────────────
// ── FALLEN HALF-TURN (canon, caP'n July 2026) ──
// Fallen clears the moment its OWNER's turn begins — it lasts only the
// adversary's half-turn, never a full cycle. Called at every turn
// handoff for the INCOMING side: the player's fallen clears when the NPC
// ends its turn (before each activePlayer: 0 write), and the NPC's own
// fallen clears at NPC turn start (runNPCTurn entry). Reads the field
// FRESH from Firebase — never a turn-start snapshot.
// (clearFallenFor removed 2026-09-15 — the incoming seat's Fallen rise (R26) is done by turnEngine.handOffHalfTurn for every driver.)

// (resetEndOfTurn removed 2026-09-15 — Phase 5 lives ONCE in turnEngine.handOffHalfTurn: R28's Lyf reset and the latch clear are done there for every driver, inside the chair transaction. The brain writes its field PRE-reset so the seam can read attackedThisTurn for Restraining Ordeal.)

// ═════════════════════════════════════════════════════════════
// REACTIVE BRAIN (Wave 2, July 2026) — the mirror of the window.
// ═════════════════════════════════════════════════════════════
// Wave 1 taught the NPC to DECLARE (requestWindow before every
// response-worthy action). Wave 2 teaches it to ANSWER: App.js
// registers makeNpcReactor on the ResponseBridge's reactor lane, and
// Shatterfield declares every response-worthy PLAYER action through
// requestReaction. This brain reads fresh Firebase state, gates the
// decision by skill + temperament (same Depths Brain scalars as the
// turn engine — decision QUALITY only, never rule cheats), pays its
// own fares, moves its own cards, and returns the same verdict shape
// the player's window returns. No React, no closures, no staleness.
//
// WHAT IT CAN ANSWER (Wave 2 scope — honest, not aspirational):
//   • ACTIVATION  — true counters: L3 Nihil/Omnia from hand (the true
//     instants), face-down L2/L3 Nihil/Omnia from the field (the
//     ambushes). Counter Hierarchy honored: a L2 field counter vs a
//     L3+ activation is LATE — and LATE-COUNTER WISDOM applies (see
//     below).
//   • REBUTTAL    — chain law: a true counter is itself an activation
//     and may be answered, same-or-higher level, never late — and the
//     answer may be answered, LIFO, until a side has none.
//     ⭐ LIVE IN BOTH DIRECTIONS since C12 (August 2026). The block on
//     the NPC-rebuts-player direction was never about the brain: it
//     fires mid-NPC-turn, where runNPCTurn held stale hand/field
//     drafts that would resurrect the card this reactor just spent.
//     THE REBASE LAW (see npcReactorDirty, up top) closed that hole,
//     so Shatterfield now sends REBUTTAL on both lanes. The branch
//     below did not change — it was correct all along, just parked.
//   • ANIMA_ATTACK — survival flips: when the declared strike drops
//     the NPC's Anima into its danger zone, it may flip a face-down
//     Anima tome (Bhodi Tree / Anima House / Stars and Skies) whose
//     effect the engine can already apply. The declaring attack then
//     lands on the BOOSTED total (the declarer re-reads fresh state —
//     the same law the player-side generic interrupts live by).
//   • LYT_ATTACK / LYT_ATTACK_TOME — sockets are WIRED (Shatterfield
//     declares them; this brain is consulted) but Wave 2 has no
//     defensive play the engine can correctly apply here yet. When
//     the NPC effect dispatcher grows (see applyNpcTomeFireEffects
//     header), reactions slot in HERE with zero plumbing changes.
//
// LATE-COUNTER WISDOM: a late counter cannot stop the effect — it
// only evicts the card afterward. Eviction is worth khao ONLY against
// persistent cards (a one-shot tome ruins itself anyway), and only
// high-skill brains (≥ 0.6) see this line at all. The reactor never
// late-counters a soul-card activation (Impera/U'hlti) — the
// eviction bookkeeping lives player-side and souls get full-counter
// treatment only.
// ─────────────────────────────────────────────────────────────

// The set of face-down tomes the reactive brain can flip as an Anima
// survival play — each maps to the flat gain applyNpcTomeFireEffects
// already knows how to grant. Grow this map as the dispatcher grows.
const REACTIVE_ANIMA_TOMES = {
  tome_bhodi_tree: 1,
  tome_anima_house: 2,
  tome_stars_and_skies: 5,
};

/**
 * Build the reactor App.js registers on the ResponseBridge. Bound to
 * one match code + difficulty; NEVER throws (the bridge has its own
 * failsafe, but a clean {responded:false} beats a raced timeout).
 */
export function makeNpcReactor(code, difficulty) {
  return async (action) => {
    try {
      const verdict = await npcReact(code, difficulty, action);
      // ── THE REBASE LAW (C12) ──────────────────────────────────
      // Any answer the brain actually PLAYS is a card move on the
      // NPC's own node made from outside the turn runner. One mark
      // here covers every reactor there is or will be — counters,
      // rebuttals, survival flips, wards — instead of a mark that
      // has to be remembered at each new one's write site.
      if (verdict?.responded) markNpcReactorMove();
      return verdict;
    } catch (e) {
      console.warn("[NPC reactor] error — resolving clean:", e?.message);
      return { responded: false };
    }
  };
}

async function npcReact(code, difficulty, action) {
  const m = await getMatch(code);
  if (!m || m.status === "finished") return { responded: false };
  const npc = m.players?.[NPC_IDX];
  if (!npc) return { responded: false };
  const brain = resolveBrain(npc, difficulty);
  const kind = action?.kind;

  if (kind === "ACTIVATION" || kind === "REBUTTAL") {
    return npcConsiderCounter(code, npc, brain, action);
  }
  if (kind === "ANIMA_ATTACK") {
    return npcConsiderAnimaSurvival(code, npc, brain, action);
  }
  // C13 (July 10 2026): the sockets Wave 2 left empty got their first
  // real teeth — the shield tomes. The brain may flip a face-down ward
  // onto the exact card the player just declared an attack against.
  if (kind === "LYT_ATTACK") {
    return npcConsiderBeingWard(code, npc, brain, action);
  }
  if (kind === "LYT_ATTACK_TOME") {
    return npcConsiderTomeWard(code, npc, brain, action);
  }
  return { responded: false };
}

// ── TRUE COUNTERS vs a declared player ACTIVATION (or REBUTTAL) ──
async function npcConsiderCounter(code, npc, brain, action) {
  const declared = getCard(action?.cardId);
  if (!declared) return { responded: false };
  const isRebuttal = action?.kind === "REBUTTAL";
  // A chain link declares the level it must be answered AT; an
  // activation answers to its own level. (The chain sends `level`
  // because the card it names is the ANSWER, not the original play.)
  const declaredLevel = (isRebuttal && action?.level) || declared.level || 1;
  const declaredIsSoul = declared.type === "Impera" || declared.type === "U'hlti";
  const khao = npc.khao ?? 0;

  // Legal, affordable answers RIGHT NOW — read from ChainLaw, the same
  // function the player's two windows read. The mirror is no longer a
  // near-copy that agrees by luck; it is literally the same law, and
  // the rebuttal filter (same-or-higher level, never late) is applied
  // inside it.
  const pool0 = trueCounterOptions({
    hand: npc.hand || [],
    tomes: riftArray(npc.field?.tomes, 3),
    khao, getCard, declaredLevel, isRebuttal,
  }).map(o => ({ ...o, card: getCard(o.cardId) }));
  if (pool0.length === 0) return { responded: false };

  // ── LAWS OF THE ANSWER ──
  let pool = pool0;
  // Late-counter wisdom: eviction-only value → persistent tome
  // targets only, high-skill brains only, never vs soul cards.
  pool = pool.filter(o => !o.late ||
    (!declaredIsSoul && isTomePersistent(declared) && brain.skill >= 0.6));
  if (pool.length === 0) return { responded: false };

  // ── SKILL GATE — will it fire at all? ──
  // Stakes scale with the declared card's level; temperament flavors
  // the trigger finger; chains are advanced play (skill-scaled twice,
  // hard floor at 0.5 — Bebs don't see rebuttal lines).
  const stakes = clamp01(declaredLevel / 3);
  let p = lerp(0.08, 0.92, brain.skill) * lerp(0.5, 1, stakes);
  if (brain.temperament === "Patient")  p += 0.12; // holds answers for this exact moment
  if (brain.temperament === "Cold")     p += 0.08; // never lets value pass
  if (brain.temperament === "Reckless") p -= 0.15; // counters are for cowards
  if (brain.temperament === "Greedy")   p -= 0.10; // hoards its khao
  if (isRebuttal) {
    if (brain.skill < 0.5) return { responded: false };
    p *= lerp(0.3, 0.9, brain.skill);
  }
  if (!chance(p, brain)) return { responded: false };

  // ── CHOICE — which answer? ──
  // Omnia (ruin) outvalues Nihil (void) vs L3 or persistent
  // declarations; otherwise the cheapest clean answer wins. Late
  // options always sort last.
  const preferOmnia = declaredLevel >= 3 || isTomePersistent(declared);
  pool.sort((a, b) => {
    const score = (o) =>
      (o.late ? 100 : 0) +
      (((o.card.ilk === "Omnia") === preferOmnia) ? 0 : 10) +
      (o.card.khaoCost || 0);
    return score(a) - score(b);
  });
  const pick = pool[0];

  // A beat of visible deliberation — a Lord's answer is swift and sure.
  await sleep(Math.round(lerp(1600, 700, brain.skill)));

  // ── PAY & MOVE — fresh reads, stale-guards, mirror of the player's
  // fireCounterFromWindow lifecycle: fare paid, then the counter ruins
  // after use unless persistent (OPT/OP2/OP3). ──
  const m2 = await getMatch(code);
  if (!m2 || m2.status === "finished") return { responded: false };
  const npc2 = m2.players?.[NPC_IDX] || {};
  const persistent = pick.card.effectTag === "OPT" ||
    pick.card.effectTag === "OP2" || pick.card.effectTag === "OP3";

  if (pick.source === "hand") {
    const hand = [...(npc2.hand || [])];
    if (hand[pick.idx] !== pick.card.id) return { responded: false }; // stale — abort clean
    if (!(await npcSpendKhao(code, pick.card.khaoCost || 0))) return { responded: false };
    hand.splice(pick.idx, 1);
    if (persistent) {
      const t2 = riftArray(npc2.field?.tomes, 3);
      const slot = t2.findIndex(t => t === null);
      if (slot >= 0) {
        t2[slot] = { id: pick.card.id, faceDown: false, persistent: true };
        await updatePlayer(code, NPC_IDX, { hand, field: { ...(npc2.field || {}), tomes: t2 } });
      } else {
        // No rift for it to live on — fires straight to the Necropolis.
        await updatePlayer(code, NPC_IDX, { hand, necropolis: [...(npc2.necropolis || []), pick.card.id] });
      }
    } else {
      // One-shot counter — fires straight from hand to the Necropolis.
      await updatePlayer(code, NPC_IDX, { hand, necropolis: [...(npc2.necropolis || []), pick.card.id] });
    }
  } else {
    const t2 = riftArray(npc2.field?.tomes, 3);
    const t = t2[pick.idx];
    if (!t || t.id !== pick.card.id || !t.faceDown) return { responded: false }; // stale — abort clean
    if (!(await npcSpendKhao(code, pick.card.khaoCost || 0))) return { responded: false };
    if (persistent) {
      t2[pick.idx] = { ...t, faceDown: false, persistent: true };
      await updatePlayer(code, NPC_IDX, { field: { ...(npc2.field || {}), tomes: t2 } });
    } else {
      t2[pick.idx] = null;
      await updatePlayer(code, NPC_IDX, {
        field: { ...(npc2.field || {}), tomes: t2 },
        necropolis: [...(npc2.necropolis || []), pick.card.id],
      });
    }
  }

  return {
    responded: true,
    counter: pick.card.ilk === "Nihil" ? "nihil" : "omnia",
    late: !!pick.late,
    counterName: pick.card.name,
    counterCardId: pick.card.id,
    // C12 — the chain gates the NEXT answer on this level (same-or-
    // higher). Sending it saves the other side a card lookup and, more
    // importantly, makes the level travel with the link over PvP too.
    counterLevel: pick.card.level || 3,
  };
}

// ── C13 SHIELD TOMES (canon ruling, caP'n, July 10 2026) ──
// The wards the reactive brain can flip when the player declares an
// attack. Being wards stamp the EXISTING `nullifyNextAttack` flag on
// the being under attack; card wards stamp the EXISTING
// `nullifyFirstRuin` flag on the tome under attack — the declarer's
// fresh-state re-read then hits the ward through consumers that were
// already live on every strike path. The spent shield tome rides
// straight to the Necropolis (Necropolis law: activated one-shots
// never squat the rift). Attacker-ruin clause dormant — see the
// fireShieldTome header in Shatterfield for the ruling.
const REACTIVE_SHIELD_TOMES = {
  tome_lyt_footed:         { beings: true,  cards: false },
  tome_radiant_aura:       { beings: true,  cards: true  },
  tome_sword_shield_nihil: { beings: true,  cards: true  },
};

// ── BEING WARD — flip a shield onto the being under attack ──
async function npcConsiderBeingWard(code, npc, brain, action) {
  const targetIdx = action?.targetIdx;
  const incoming = Number(action?.lyt) || 0;
  if (targetIdx === undefined || targetIdx === null || incoming <= 0) return { responded: false };
  const slot = npc.field?.esclings?.[targetIdx];
  if (!slot?.escendant || slot.fallen || slot.nullifyNextAttack) return { responded: false };
  // Wards are for KILLING BLOWS, not chip damage — a skilled brain
  // never burns a one-attack shield on a hit the being survives.
  // (Low-skill brains occasionally panic-flip; that's flavor.)
  const beingCard = getCard(slot.escendant);
  const lyf = slot.currentLyf ?? beingCard?.lyf ?? 0;
  const lethal = incoming >= lyf;
  if (!lethal && brain.skill >= 0.35) return { responded: false };

  const khao = npc.khao ?? 0;
  const tomes = riftArray(npc.field?.tomes, 3);
  let pick = null;
  tomes.forEach((t, i) => {
    if (!t || typeof t !== "object" || !t.faceDown) return;
    if (!REACTIVE_SHIELD_TOMES[t.id]?.beings) return;
    const c = getCard(t.id);
    if (!c || (c.khaoCost || 0) > khao) return;
    if (!pick || (c.khaoCost || 0) < (pick.card.khaoCost || 0)) pick = { idx: i, card: c };
  });
  if (!pick) return { responded: false };

  // Worth the fare? Stakes scale with the being's level.
  const stakes = clamp01((beingCard?.level || 1) / 3);
  let p = lerp(0.12, 0.9, brain.skill) * lerp(0.55, 1, stakes);
  if (brain.temperament === "Patient")  p += 0.10;
  if (brain.temperament === "Reckless") p -= 0.15;
  if (!chance(p, brain)) return { responded: false };

  await sleep(Math.round(lerp(1500, 700, brain.skill)));

  // Fresh validate → pay → stamp the ward → shield to Necropolis.
  const m2 = await getMatch(code);
  if (!m2 || m2.status === "finished") return { responded: false };
  const npc2 = m2.players?.[NPC_IDX] || {};
  const t2 = riftArray(npc2.field?.tomes, 3);
  const t = t2[pick.idx];
  const s2 = npc2.field?.esclings?.[targetIdx];
  if (!t || t.id !== pick.card.id || !t.faceDown || !s2?.escendant || s2.nullifyNextAttack) return { responded: false };
  if (!(await npcSpendKhao(code, pick.card.khaoCost || 0))) return { responded: false };
  const esc2 = [...(npc2.field?.esclings || [])];
  esc2[targetIdx] = { ...s2, nullifyNextAttack: true };
  t2[pick.idx] = null;
  await updatePlayer(code, NPC_IDX, {
    field: { ...(npc2.field || {}), esclings: esc2, tomes: t2 },
    necropolis: [...(npc2.necropolis || []), pick.card.id],
  });
  return { responded: true, counter: null, reactionName: pick.card.name };
}

// ── TOME WARD — flip a shield onto the tome under attack ──
async function npcConsiderTomeWard(code, npc, brain, action) {
  const targetIdx = action?.targetIdx;
  if (targetIdx === undefined || targetIdx === null) return { responded: false };
  const tomes0 = riftArray(npc.field?.tomes, 3);
  const target = tomes0[targetIdx];
  if (!target || typeof target !== "object" || !target.id || target.nullifyFirstRuin) return { responded: false };
  // Only ward tomes worth the fare: persistent engines or L2+ pieces.
  const targetCard = getCard(target.id);
  const worthIt = target.persistent || (targetCard?.level || 1) >= 2;
  if (!worthIt) return { responded: false };

  const khao = npc.khao ?? 0;
  let pick = null;
  tomes0.forEach((t, i) => {
    if (i === targetIdx) return; // a ward can't ward itself mid-flip
    if (!t || typeof t !== "object" || !t.faceDown) return;
    if (!REACTIVE_SHIELD_TOMES[t.id]?.cards) return;
    const c = getCard(t.id);
    if (!c || (c.khaoCost || 0) > khao) return;
    if (!pick || (c.khaoCost || 0) < (pick.card.khaoCost || 0)) pick = { idx: i, card: c };
  });
  if (!pick) return { responded: false };

  let p = lerp(0.08, 0.75, brain.skill);
  if (brain.temperament === "Patient")  p += 0.10;
  if (brain.temperament === "Reckless") p -= 0.15;
  if (!chance(p, brain)) return { responded: false };

  await sleep(Math.round(lerp(1500, 700, brain.skill)));

  const m2 = await getMatch(code);
  if (!m2 || m2.status === "finished") return { responded: false };
  const npc2 = m2.players?.[NPC_IDX] || {};
  const t2 = riftArray(npc2.field?.tomes, 3);
  const t = t2[pick.idx];
  const tgt2 = t2[targetIdx];
  if (!t || t.id !== pick.card.id || !t.faceDown) return { responded: false };
  if (!tgt2 || typeof tgt2 !== "object" || tgt2.id !== target.id || tgt2.nullifyFirstRuin) return { responded: false };
  if (!(await npcSpendKhao(code, pick.card.khaoCost || 0))) return { responded: false };
  t2[targetIdx] = { ...tgt2, nullifyFirstRuin: true };
  t2[pick.idx] = null;
  await updatePlayer(code, NPC_IDX, {
    field: { ...(npc2.field || {}), tomes: t2 },
    necropolis: [...(npc2.necropolis || []), pick.card.id],
  });
  return { responded: true, counter: null, reactionName: pick.card.name };
}

// ── ANIMA SURVIVAL — flip a face-down Anima tome before the strike ──
async function npcConsiderAnimaSurvival(code, npc, brain, action) {
  const incoming = Number(action?.lyt) || 0;
  if (incoming <= 0) return { responded: false };
  const anima = npc.anima ?? 30;
  const after = anima - incoming;
  // Only stir when the strike genuinely threatens — skill widens the
  // danger sense (a Beb feels it at 2 Anima, a Lord at 8).
  const dangerLine = Math.round(lerp(2, 8, brain.skill));
  if (after > dangerLine) return { responded: false };

  const khao = npc.khao ?? 0;
  const tomes = riftArray(npc.field?.tomes, 3);
  let pick = null;
  tomes.forEach((t, i) => {
    if (!t || typeof t !== "object" || !t.faceDown) return;
    const gain = REACTIVE_ANIMA_TOMES[t.id];
    if (!gain) return;
    const c = getCard(t.id);
    if (!c || (c.khaoCost || 0) > khao) return;
    if (!pick || gain > pick.gain) pick = { idx: i, card: c, gain };
  });
  if (!pick) return { responded: false };

  const lethal = after <= 0;
  // A survival flip that still dies is theater — high-skill brains skip it.
  if (lethal && anima + pick.gain - incoming <= 0 && brain.skill >= 0.5) {
    return { responded: false };
  }
  let p = lethal ? lerp(0.5, 1, brain.skill) : lerp(0.15, 0.7, brain.skill);
  if (brain.temperament === "Patient")  p += 0.10;
  if (brain.temperament === "Reckless") p -= 0.15;
  if (!chance(p, brain)) return { responded: false };

  await sleep(Math.round(lerp(1500, 700, brain.skill)));

  // Fresh re-validate → pay → flip → grant → lifecycle. Every write
  // individually visible; anima gain is a delta on a fresh read,
  // capped at 50 (mirrors applyNpcTomeFireEffects).
  const m2 = await getMatch(code);
  if (!m2 || m2.status === "finished") return { responded: false };
  const npc2 = m2.players?.[NPC_IDX] || {};
  const t2 = riftArray(npc2.field?.tomes, 3);
  const t = t2[pick.idx];
  if (!t || t.id !== pick.card.id || !t.faceDown) return { responded: false };
  if (!(await npcSpendKhao(code, pick.card.khaoCost || 0))) return { responded: false };

  t2[pick.idx] = {
    ...t, faceDown: false,
    lastUsedTurn: m2.turn || 0,
    persistent: isTomePersistent(pick.card),
  };
  await updatePlayer(code, NPC_IDX, { field: { ...(npc2.field || {}), tomes: t2 } });

  const m3 = await getMatch(code);
  const npc3 = m3?.players?.[NPC_IDX] || {};
  // ── R32 · THE 29TH SITE ──────────────────────────────────────────
  // This was `Math.min(50, (npc3.anima || 30) + pick.gain)` — the one
  // gain in the brain still doing the ceiling by hand, with the number
  // 50 typed in rather than MAX_ANIMA. The seal's own banner in
  // ruleGate.js names the failure mode exactly: "28 authors each
  // remembering the same arithmetic, and the 29th forgetting". Here is
  // the 29th. It never entered `__animaSeal.asked`, so the balancer's
  // "the seal is wired" counter under-reported every batch by however
  // often the brain flipped a survival Tome.
  await updatePlayer(code, NPC_IDX, {
    anima: sealAnimaGain((npc3.anima ?? STARTING_ANIMA), pick.gain, "NPC.npcConsiderAnimaSurvival"),
    ...buildAnimaGainUpdate(npc3, pick.gain),
  });

  if (!isTomePersistent(pick.card)) {
    await ruinNpcTome(code, pick.idx, pick.card.id);
  }

  // Generic-interrupt verdict: the attack STILL RESOLVES, against the
  // boosted total — the declarer re-reads fresh state before striking.
  return { responded: true, counter: null, reactionName: pick.card.name };
}

// ─────────────────────────────────────────────────────────────
// BUILD NPC PLAYER DATA
// ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════
// THE VANGUARD — the board an opponent is already holding
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-08-17: "the enemies in Story Mode will be a bit
// different, they will slowly start having more and better cards
// already on the Shatterfield as soon as The Solrend begins,
// whereas you have to begin a brand new Solrend every time drawing
// fresh with only Esclings on field etc — nothing changes for
// player, but the opponent's progressively keep having more and
// better cards immediately spawned in on the table (properly of
// course can't just have a lvl 2 champion without a lvl 1 beneath
// it, never Deviant training for now, just normal)."
//
// ── THE THREE LAWS IN THAT PARAGRAPH ─────────────────────────
// 1. NOTHING CHANGES FOR THE PLAYER. This function is only ever
//    called on seat 1. There is no symmetric version and there
//    should not be one.
// 2. THE LINE MUST BE REAL. A Champion sits on its own Beb, a Lord
//    on its own Champion and Beb, and the whole stack comes from
//    ONE natural line — same School, same Ilk — so `deviant` is
//    false for a reason rather than by assertion. getEscendantLine
//    is the same helper the board uses, so a vanguard cannot be
//    built out of a lineage the game does not have.
// 3. THE CARDS COME FROM THE DECK. Everything placed is DEPLETED
//    out of the grimoire. Otherwise a late-region opponent opens
//    with a Lord AND still has that Lord's whole line in a deck
//    that never shrank, and the difficulty curve stops being about
//    the board and starts being about free cards. A vanguard is a
//    head start, not extra material.
//
// ── THE LADDER IS A DIAL ─────────────────────────────────────
// Tier is the region index, 0-based. Region I is deliberately
// EMPTY: the first thing a player meets in Story Mode has to be a
// clean Solrend or they never learn what a clean Solrend looks
// like. Everything past it escalates. These seven rows are tuning
// numbers and are meant to be moved in playtest, not argued about.
// ⚠ SIX ROWS FOR NINE ZONES, until 2026-08-21. `regionTier` is
// `REGIONS.findIndex(...)` and caP'n's re-trace took REGIONS from six
// to NINE — and `vanguardFor` clamps to the last row. So Boneyard
// North, the Crystal Mausoleum, the Eyeland AND the Crimson Dais all
// resolved to the same top row, and the pressure curve went flat for
// the final four zones of a nine-zone world, endgame included.
//
// The old row comments named the six regions that were deleted, which
// is how the mismatch stayed invisible: the table looked authored and
// complete and was describing a world that no longer existed. It is
// keyed by ZONE now, in REGIONS order, and the rule is one line —
// A ROW PER REGION. Add a region, add a row.
const VANGUARD_LADDER = [
  [],              // A  Boneyard South — a fair fight, on purpose
  [1],             // B  Harsh Marsh West — one Beb waiting
  [1, 1],          // C  SHV's Mausoleum — two
  [2, 1],          // D  Harsh Marsh East — a Champion on its own Beb
  [2, 1, 1],       // E  Deserted Desert
  [2, 2, 1],       // F  Boneyard North — the cold does not open its hand
  [3, 2, 1],       // G  SHV's Crystal Mausoleum — a Lord is already there
  [3, 2, 2],       // S  The Eyeland — six keepers, and none of them alone
  [3, 3, 2],       // Z  The Crimson Dais — the end of it
];

export const vanguardFor = (tier) =>
  VANGUARD_LADDER[Math.min(Math.max(Number(tier) || 0, 0), VANGUARD_LADDER.length - 1)];

// Build one rift's contents at `level` out of `pool`, consuming what
// it uses. Returns null when the deck simply cannot field it — which
// is a normal outcome, not an error: a mono-Beb starter deck asked
// for a Lord has no Lord, and the correct answer is an empty rift
// rather than an invented card. THE FLOOR LAW, applied to a board.
function carveVanguardSlot(pool, level, i) {
  // ⚠ A "LINE" IS NOT A SPECIES. getEscendantLine returns every
  // Escendant sharing a School and an Ilk, and in the real card
  // database that is routinely THREE species deep — H-01 Blue
  // Giant's line holds h01, h09 and xulan at all three levels. The
  // first cut of this function read that list as one species' chain
  // and demanded exactly level-1 cards below the top, which every
  // real deck fails: it silently fielded nothing from Region IV
  // onward and the whole feature would have shipped looking like it
  // was working. Caught by running it against all twelve starter
  // decks before it ever reached a browser.
  //
  // The rule the GAME uses is R19: a Natural Ascend needs a matching
  // School and Ilk, not a matching name. So h09_beb standing under
  // h01_champion is not a compromise, it is a legal ascension the
  // player can perform themselves — and it is what makes a vanguard
  // buildable out of the cards a deck actually holds.
  const candidates = [];
  for (const id of Object.keys(pool)) {
    const card = ESCENDANTS.find(e => e.id === id);
    if (!card || card.level !== level) continue;

    // One card per level below, drawn from the same School+Ilk and
    // held by the deck. Same-species first, purely so a vanguard
    // reads as one creature's history when the deck allows it.
    const line = getEscendantLine(id);
    const stem = id.split("_")[0];
    const below = [];
    const reserved = {};
    for (let lv = 1; lv < level; lv++) {
      const atLv = line
        .filter(c => c.level === lv && (pool[c.id] || 0) > (reserved[c.id] || 0))
        .sort((x, y) => (y.id.startsWith(stem) ? 1 : 0) - (x.id.startsWith(stem) ? 1 : 0));
      if (!atLv.length) { below.length = 0; break; }
      below.push(atLv[0]);
      reserved[atLv[0].id] = (reserved[atLv[0].id] || 0) + 1;
    }
    if (below.length !== level - 1) continue;   // the deck cannot stand this one up
    candidates.push({ card, below });
  }
  if (!candidates.length) return null;

  // Deterministic per rift rather than random: two launches of the
  // same node with the same deck must produce the same board,
  // because a story fight that plays differently on a retry for
  // reasons the player cannot see is indistinguishable from a bug.
  const pick = candidates[i % candidates.length];

  const take = (id) => {
    if (pool[id] > 1) pool[id]--; else delete pool[id];
  };
  pick.below.forEach(c => take(c.id));
  take(pick.card.id);

  return {
    escendant: pick.card.id,
    // The Soul is the lineage stack, lowest first — the same order
    // descendBeing walks when this being falls, which is what makes
    // a vanguard Lord descend into its own Champion instead of
    // dropping straight to the Necropolis.
    soul: pick.below.map(c => c.id),
    ilk: pick.card.ilk,
    lyf: pick.card.lyf ?? null,
  };
}

// The whole vanguard. Mutates nothing it was handed — takes a copy of
// the grimoire, returns the depleted copy alongside the rifts.
export function buildVanguard(grimoire, tier) {
  const plan = vanguardFor(tier);
  const pool = { ...(grimoire || {}) };
  const slots = [];
  if (!plan.length) return { slots: [], grimoire: pool, placed: 0 };

  // ⚠ R110 — THE CLAMP WAS A LITERAL 3 AND IS NOW THE CONSTANT. Left at
  // three, a fourth number added to any ladder row below would have been
  // read, planned for, and then silently dropped on the floor — the row
  // would look authored and do nothing, which is exactly the failure this
  // table's own banner records happening once already when the world grew
  // from three regions to nine and the rows did not.
  //
  // ⚠ AND THE ROWS THEMSELVES ARE DELIBERATELY UNCHANGED, so this edit is
  // BEHAVIOUR-NEUTRAL. No row is four long, so no NPC starts with a being
  // in its Mind rift today, exactly as none did yesterday. The seat still
  // gets its fourth ESCLING at setup and can Transcend into it in play, so
  // there is no gap in the board — only in the head start. Giving the late
  // zones a fourth opening being is a real difficulty increase and it is a
  // DIAL, not a coherency fix: add a fourth number to a row and it works.
  for (let i = 0; i < Math.min(plan.length, ESCLING_SLOTS); i++) {
    slots.push(carveVanguardSlot(pool, plan[i], i));
  }
  return { slots, grimoire: pool, placed: slots.filter(Boolean).length };
}

export function buildNPCPlayerData(difficulty, starterDeck, vanguardTier = 0) {
  // ── DEPTHS BRAIN: consume the one-shot handoff (if a launcher set
  // one) and stamp it onto the seat-1 object. Firebase note for the
  // future rules-hardening session: `npcSkill` and `temperament` are
  // two new fields on the match player object — whitelist them if the
  // permissive closed-beta match rules ever grow an $extra:false.
  const pendingBrain = consumePendingNpcBrain();
  const brainStamp = {
    npcSkill: clamp01(
      (typeof pendingBrain?.skill === "number") ? pendingBrain.skill
        : (TIER_BASELINE_SKILL[difficulty] ?? 0.5)
    ),
    ...(NPC_TEMPERAMENTS.includes(pendingBrain?.temperament)
      ? { temperament: pendingBrain.temperament } : {}),
  };

  // THE VANGUARD comes off the top of the deck BEFORE the opening
  // hand is drawn, in that order and deliberately: a card standing
  // on the table cannot also be a card in hand, and drawing first
  // would let the same copy be both.
  const vanguard = buildVanguard(starterDeck.grimoire, vanguardTier);
  const grimoire = { ...vanguard.grimoire };
  const luxmoire = { ...starterDeck.luxmoire };

  // ── H15 DEAL PARITY (2026-08-18): the hand DEPLETES the grimoire,
  // exactly as the player's deal does (drawAndDepleteCards: "hand +
  // grimoire = full deck"). The old code drew from a temp copy and
  // returned the UN-depleted map — the NPC's five hand cards stayed
  // in its deck as ghosts, against this very file's vanguard-carve
  // law above: a card in hand cannot also be a card in the deck.
  const hand = [];
  const tempGrimoire = { ...grimoire };
  for (let i = 0; i < 5; i++) {
    const drawn = drawFromPool(tempGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (tempGrimoire[drawn] > 1) tempGrimoire[drawn]--;
      else delete tempGrimoire[drawn];
    }
  }

  const deckCards = Object.keys(grimoire);
  const firstEscendant = ESCENDANTS.find(e => deckCards.includes(e.id));
  const matchingEscling = firstEscendant ? ESCLINGS.find(e => e.ilk === firstEscendant.ilk) : null;
  const defaultEscling = matchingEscling || ESCLINGS[0];

  // R110 — the NPC seat is born here, and it is born ESCLING_SLOTS wide.
  // This is the twin of gameManager.seatEsclings and the two must stay
  // the same width or an NPC duel is structurally invalid from turn 0.
  const esclings = Array.from({ length: ESCLING_SLOTS }, (_, i) => {
    const v = vanguard.slots[i] || null;
    // A rift holding a vanguard being wears the Escling of that
    // being's own Ilk. Not cosmetic: R16 says the L1 must match the
    // Escling's School, so a rift whose Escling disagreed with the
    // being standing in it would be a board state the rules engine
    // says cannot exist — and the Debugger would be right.
    const escling = v
      ? (ESCLINGS.find(e => e.ilk === v.ilk) || defaultEscling).id
      : defaultEscling.id;
    return {
      id: `npc_slot${i}`,
      escling,
      escendant: v ? v.escendant : null,
      soul: v ? v.soul : [],
      fallen: false,
      currentLyf: v ? v.lyf : null,
      attackedThisTurn: false,
      // NEVER DEVIANT, by caP'n's decree and by construction: every
      // stack came out of one natural line, so this is a statement
      // of what was built rather than a flag hopefully set right.
      deviant: false, natural: true,
    };
  });

  // (The old silent build-time redraw for L1-less hands lived here.
  // Retired 2026-08-18 with H15: the NPC now holds the same public,
  // once-only mulligan right the player does — exercised at its own
  // first turn in runNPCTurn, where the table can see it happen.)

  return {
    name: `${difficulty} NPC`,
    anima: STARTING_ANIMA, maxAnima: MAX_ANIMA, khao: STARTING_KHAO, maxKhao: STARTING_KHAO,
    grimoire: tempGrimoire, luxmoire,
    prophecy: starterDeck.prophecy || null,
    hand,
    field: {
      esclings,
      tomes: [null, null, null],
      curios: starterDeck.curios || [null, null],
      prophecy: starterDeck.prophecy || null,
    },
    necropolis: [],
    // R112/R113 — the Exile rift and its mark, seeded explicitly and kept
    // byte-parallel with gameManager.buildSeat for the same reason the
    // Anima ledger below is: a field that is sometimes absent is a field
    // every consumer must defend against, and one of them always forgets.
    exile: [],
    exileReturned: [],
    shadowGamblingStreak: 0,
    // ── THE ANIMA LEDGER, SEEDED EXPLICITLY ──────────────────────
    // These four match gameManager's buildSeat exactly, and they are
    // written out rather than left to every reader's `|| 0` because an
    // NPC seat is a SEAT: the harness audits it, the transition referee
    // diffs it, and turnEngine's hand-off rotates it. A seat whose
    // ledger springs into existence on first use is a seat whose first
    // half-turn cannot be told apart from a seat that gained nothing.
    // Harmless today; free to make honest.
    totalAnimaLost: 0,
    animaLostThisTurn: 0,
    // R100 — the self-dim ledger, seeded for the same reason.
    totalAnimaSelfDimmed: 0,
    animaSelfDimmedThisTurn: 0,
    animaGainedThisTurn: 0,
    animaGainedLastTurn: 0,
    isNPC: true,
    difficulty,
    ...brainStamp,
    // NOTE: resurrectedCards (R44 record) is intentionally NOT seeded —
    // Firebase strips empty arrays; every reader defaults `|| []`.
  };
}

// ─────────────────────────────────────────────────────────────
// H15 · THE MULLIGAN, NPC SIDE (2026-08-18)
// The right is symmetric by decree: your own first Turn only, only
// while the dealt five are untouched, one full-hand redraw, once.
// The NPC's trigger is one labeled dial: a hand with no L1
// Escendant AND no Escendant already standing is a hand that
// cannot develop — redraw it. (This replaces the old silent
// build-time redraw in buildNPCPlayerData: same protection, now
// canonical, visible, and rules-legal.)
// `mulliganUsed` on the seat object is doing two jobs: the
// crash-recovery guard (runNPCTurn can re-enter) and the public
// announcement — the player's HUD toasts when the flag flips.
// ─────────────────────────────────────────────────────────────
async function maybeNpcMulligan(code, npc, turnNo, seatCount = 2) {
  // ⚠ R90's WINDOW IS THE OPENING CYCLE, WHICH IS AS LONG AS THE TABLE.
  // This was the literal `>= 2` — a two-seat Cycle typed into a rule
  // predicate, the same shape as the hardcoded 2 at the R08 search
  // helpers that had to be fixed. At a three- or four-seat table the
  // brain's mulligan window was a half-turn or two too narrow, so a
  // no-L1 hand it was entitled to redraw it simply kept.
  if (!isOpeningTurnIndex(turnNo || 0, seatCount)) return false;  // own first Turn only
  if (npc?.mulliganUsed) return false;           // once, ever
  const hand = npc?.hand || [];
  if (hand.length !== 5) return false;           // the dealt hand, untouched
  const hasL1 = hand.some(id => { const c = ESCENDANTS.find(e => e.id === id); return c && c.level === 1; });
  const standing = (npc?.field?.esclings || []).some(s => s?.escendant);
  // MULLIGAN DIAL: redraw iff nothing to play and nothing on board.
  if (hasL1 || standing) return false;

  // The redraw — the player's own math (handleMulligan), seat 1.
  // Deal parity holds (hand was depleted from the grimoire at build),
  // so hand + grimoire is exactly the original deck; no cap needed.
  const grimoire = { ...(npc.grimoire || {}) };
  hand.forEach(id => { grimoire[id] = (grimoire[id] || 0) + 1; });
  // ⚠ THE DECK LIMIT, WHICH THE BOARD CLAMPS AND THIS DID NOT. The old
  // note here read "deal parity holds ... so hand + grimoire is exactly
  // the original deck; no cap needed" — true of a clean deal, and a
  // reason rather than a guard. The player's handleMulligan clamps every
  // key to the card's own `deckLimit` as the hand folds back, so a hand
  // that is NOT the clean dealt five (a seeded fixture, a hand-built
  // board, a future effect that swaps a card in) cannot mint a copy past
  // the limit. Same clamp, same place in the sequence.
  Object.keys(grimoire).forEach(id => {
    const limit = getCard(id)?.deckLimit || 3;
    if (grimoire[id] > limit) grimoire[id] = limit;
  });
  const pool = [];
  Object.entries(grimoire).forEach(([id, qty]) => { for (let i = 0; i < qty; i++) pool.push(id); });
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  const newHand = shuffled.slice(0, 5);
  const newGrimoire = { ...grimoire };
  newHand.forEach(id => {
    if (newGrimoire[id] > 1) newGrimoire[id]--;
    else delete newGrimoire[id];
  });
  await updatePlayer(code, NPC_IDX, { hand: newHand, grimoire: newGrimoire, mulliganUsed: true });
  return true;
}

// ══════════════════════════════════════════════════════════════
// 🏆 A PROPHECY MAY BE CASHED THE INSTANT IT COMES TRUE
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-09-14, verbatim: *"yes once its fulfilled and the
// conditions met etc you can activate immediately and interrupt the
// entire duel."*
//
// ⚠ SO THE HUMAN WAS RIGHT AND THE BRAIN WAS THE HALF THAT WAS WRONG.
// Shatterfield has a ProphecyButton that ends the Solrend the moment the
// condition and the fare are both there. The brain had no such door: its
// only route was `turnEngine.handOffHalfTurn` → `checkProphecies`, at the
// BOUNDARY. So a board that became Prophecy-complete in the middle of a
// Turn was an instant win for a person and a maybe for the NPC — and
// turnEngine's own banner records the consequence, that R33 wins the race
// when a seat is zeroed in the same window. The brain could complete a
// Supreme trio, get knocked out before the hand-off, and lose a duel it
// had already won.
//
// ⚠ ONE PREDICATE, ONE RESOLVER, NEITHER OF THEM WRITTEN HERE.
// `prophecyConditionMet` is pure and free; `checkProphecies` is the
// resolver every other win goes through and it owns the fare (it skips a
// seat that cannot pay), the `prophecySpent` stamp (R99), R97's Toll
// outside 1v1 and the ending itself. This function is only the TIMING.
//
// ⚠ AND IT IS CHEAP WHEN THERE IS NOTHING TO DO. `prophecyId` comes off
// the runner's own entry snapshot — a seat's Prophecy is pre-placed and
// never changes during a Solrend — so a brain with no Prophecy pays one
// falsy check and makes no read at all.
//
// Returns true when the duel is over, so a runner can stop rather than
// playing on into a finished match.
async function npcCashProphecyIfReady(code, prophecyId) {
  if (!prophecyId) return false;
  const m = await getMatch(code);
  if (!m || m.status === "finished") return !!m && m.status === "finished";
  const me = m.players?.[NPC_IDX];
  const opp = m.players?.[0];
  if (!me || !opp) return false;
  if (me.prophecySpent === prophecyId) return false;      // R99 — spent when it resolved
  // The cheap question first. Everything expensive is behind it.
  if (!prophecyConditionMet(prophecyId, me, opp)) return false;
  try {
    const fired = await checkProphecies(code, m);
    if (fired) {
      tomeProbe("prophecy", prophecyId);
      return !!fired.ended;
    }
  } catch (e) { console.error("[Prophecy] in-turn check failed:", e && e.message); }
  return false;
}

// ─────────────────────────────────────────────────────────────
// MAIN ROUTER
// ─────────────────────────────────────────────────────────────
// ── R93 · THE HAND-OFF, AND THE SECOND HAND ─────────────────────────
// Every NPC turn used to end with a bare `activePlayer: 0, turn+1`, in
// three places, and NOTHING anywhere asked whether the duel had gone on
// too long. R87's twenty minutes cannot elapse in a headless run, so a
// mutual board-lock (R31 + R28 + R44, each correct alone) produced duels
// that genuinely never ended — 13% of a 200-game batch. R93 gives the
// clock a second hand measured in half-turns, and this is where it is
// read: at the only moment a turn actually changes hands.
//
// It is deliberately NOT a harness flag. A rule the simulator obeys and
// the shipping game does not is not a rule, it is a workaround.
// ═════════════════════════════════════════════════════════════
// 🔬 THE LOCK PROBE — diagnostic only, off unless TERROR_LOCK is set
// ═════════════════════════════════════════════════════════════
// "A lock you have not looked at is a lock you are guessing about."
// This records, per match: every Primera press by word, every Turn the
// brain took that moved nothing observable, and a board photograph at
// turn 100. At process exit it prints ONLY the matches that were
// actually called on the clock (R93), because those are the duels the
// question is about — a summary over all 120 games hides them.
//
// Zero cost when the flag is unset: `LOCK` is null and every call site
// is a single truthy test. Never wire this into gameplay.
const LOCK = (typeof process !== "undefined" && process?.env?.TERROR_LOCK)
  ? { m: new Map(), get(c) { let r = this.m.get(c); if (!r) { r = { stamps: {}, turns: 0, dead: 0, snap: null, clock: false, ruins: 0, revives: 0 }; this.m.set(c, r); } return r; } }
  : null;
if (LOCK && typeof process !== "undefined" && process?.on) {
  process.on("exit", () => {
    const rows = [...LOCK.m.values()].filter((r) => r.clock);
    const all  = [...LOCK.m.values()];
    const sum = (rs, f) => rs.reduce((n, r) => n + f(r), 0);
    const words = {};
    rows.forEach((r) => Object.entries(r.stamps).forEach(([w, n]) => { words[w] = (words[w] || 0) + n; }));
    const wordsAll = {};
    all.forEach((r) => Object.entries(r.stamps).forEach(([w, n]) => { wordsAll[w] = (wordsAll[w] || 0) + n; }));
    console.error(`\n🔬 LOCK PROBE — ${rows.length} clock-called of ${all.length} matches`);
    console.error(`   clock-called: turns ${sum(rows, r => r.turns)}, dead turns ${sum(rows, r => r.dead)} ` +
      `(${rows.length ? Math.round(100 * sum(rows, r => r.dead) / Math.max(1, sum(rows, r => r.turns))) : 0}%), ` +
      `ruins ${sum(rows, r => r.ruins)}, revives ${sum(rows, r => r.revives)}`);
    console.error(`   stamps in clock-called: ${JSON.stringify(words)}`);
    console.error(`   stamps in ALL matches : ${JSON.stringify(wordsAll)}`);
    const g = (k) => all.reduce((n, r) => n + (r[k] || 0), 0);
    console.error(`   P04 raw-state at the win: slot OCCUPIED ${g("p04ok")} (of which SEALED id ${g("p04sealed")}) · slot genuinely EMPTY ${g("p04real")}`);
    const snaps = rows.map((r) => r.snap).filter(Boolean);
    if (snaps.length) {
      const avg = (f) => (snaps.reduce((n, x) => n + f(x), 0) / snaps.length).toFixed(1);
      console.error(`   @turn100 (n=${snaps.length}): standing me ${avg(x => x.myStand)} / opp ${avg(x => x.oppStand)} · ` +
        `anima me ${avg(x => x.myAnima)} / opp ${avg(x => x.oppAnima)} · hand ${avg(x => x.hand)} · grim ${avg(x => x.grim)} · ` +
        `nec ${avg(x => x.nec)} · myBestLyt ${avg(x => x.myBestLyt)} · oppMinLyf ${avg(x => x.oppMinLyf)} · ` +
        `canRuin ${(100 * snaps.filter(x => x.canRuin).length / snaps.length).toFixed(0)}% · ` +
        `hasPhantom ${(100 * snaps.filter(x => x.hasPhantom).length / snaps.length).toFixed(0)}%`);
    }
  });
}

// One board photograph, in the same currency the balancer projects:
// what a DECISION can move.
//
// ⚠ THIS COMMENT USED TO CLAIM AN AGREEMENT THAT DID NOT EXIST. It read
// "the same currency the balancer projects — Lyf is excluded on purpose
// (R28 resets it)", and `_lockShot` duly left `currentLyf` out, while
// tools/balance.mjs's projectBoard PUT IT IN. Two probes disagreeing is
// survivable; a comment asserting they agree is not, because with no
// version control in this directory the comments are the only changelog
// and the next reader would have trusted it.
//
// The harness has the better of the argument and this now matches it.
// R28 resets the ACTING seat's Lyf at the end of the acting seat's Turn
// — a being chipped on the ADVERSARY's board stays chipped until its
// own Turn comes round, which is precisely the window a turn-boundary
// diff straddles. Excluding it filed "swung at their board and took two
// Lyf off it" as a Turn where nothing happened, and at seed 7 / 40 games
// that was 56 dead turns against the harness's 34 — the Lord's whole
// 22-turn contribution was chip pressure counted as inaction, so this
// probe was over-reporting by ~40% against §1d.
//
// KEEP THE TWO IN STEP. If projectBoard's field list changes again,
// change this one in the same edit or write down why they differ.
function _lockMeasure(m) {
  return {
    proj: _lockShot(m),
    nec: ((m?.players?.[0]?.necropolis) || []).length + ((m?.players?.[1]?.necropolis) || []).length,
    res: ((m?.players?.[1]?.resurrectedCards) || []).length,
  };
}

function _lockShot(m) {
  const one = (p) => [
    p?.anima ?? 0, (p?.hand || []).length,
    Object.values(p?.grimoire || {}).reduce((n, c) => n + (Number(c) || 0), 0),
    (p?.necropolis || []).length,
    // currentLyf is IN, matching projectBoard — see the banner above.
    ((p?.field?.esclings) || []).map(s => s?.escendant ? `${s.escendant}${s.fallen ? "F" : ""}:${s.currentLyf}:${(s.soul || []).join("+")}` : "-").join("|"),
    ((p?.field?.tomes) || []).map(t => (t && t.id) || "-").join("|"),
  ].join(",");
  return one(m?.players?.[0]) + "//" + one(m?.players?.[1]);
}

// ── THE NPC SEAT'S ONE HAND-OFF ─────────────────────────────────────
// Everything this function used to do by hand — increment the turn, ask
// R93, write the hand-off — now lives in src/turnEngine.js, shared with
// Shatterfield's two transitions, along with the two things this
// function never did at all: rotating the outgoing seat's per-half-turn
// Anima ledger, and asking whether a Prophecy has come true. Both of
// those were, until August 2026, the renderer's private business, which
// is why the NPC seat's `animaGainedThisTurn` accumulated for the whole
// duel and why a headless duel had no Prophecies in it.
//
// THE RETURN CONTRACT IS PRESERVED AND SLIGHTLY WIDENED: `true` still
// means "this hand-off ENDED the duel", which used to be reachable only
// through the clock and is now also reachable through a Prophecy.
// (No caller reads it today — all five discard it — but a boolean that
// means one thing on Tuesday is worse than one that means the honest
// thing every day.)
async function npcHandOffTurn(code, match) {
  // ⚠ `activePlayer: 0` was hardcoded here, and passing it through as
  // `nextIdx` keeps this write byte-identical rather than quietly
  // adopting getNextActiveSeat's answer. The two agree on every 2-seat
  // match — the only shape an NPC seat is ever dealt into today — and
  // differ only in the window where seat 0 is already down but the
  // match has not been marked finished yet. Preserving the old write is
  // the conservative half of that choice; drop the override the day an
  // NPC takes a seat in an FFA.
  // ⭐ THE DAY CAME (2026-09-06): the brain now sits at seat 1 of a
  // three-seat table in Gauntlet — Duo and Story Co-op. With the
  // override, the partner at seat 2 never received a Turn — the boss
  // handed straight back to the host every time. Two seats keep the
  // byte-identical write; three or more adopt getNextActiveSeat.
  const seatCount = Number(match?.seatCount) || 2;
  const res = await handOffHalfTurn(code, match, NPC_IDX, seatCount > 2 ? {} : { nextIdx: 0 });
  const verdict = res.verdict;
  const nextTurn = res.nextTurn;
  if (LOCK) {
    const r = LOCK.get(code);
    if (verdict) r.clock = true;
    // The LOCK probe measures the BOARD — anima, hands, Grimoire depth,
    // Necropolis, per-rift Lyf, tomes — and the hand-off above touches
    // none of those: it writes `turn`, `activePlayer`, three Anima
    // ledger counters, the INCOMING seat's Khao (`khao` always,
    // `maxKhao` on a climb, and `khaoSpentThisCycle` — R92b/R132), and
    // `status`/`winner` only when the duel ends. So reading `post` after
    // it, rather than before, measures the same thing it always did.
    // ⚠ THIS ENUMERATION WAS ALREADY WRONG WHEN R132 FOUND IT: it filed
    // `khao` under "only when the duel ends", which stopped being true
    // the day the refill moved into handOffHalfTurn. The probe's
    // CONCLUSION survived both changes, because no Khao field is in
    // _lockShot — the list did not. If the hand-off ever learns to write
    // something _lockShot DOES read, this probe has to move back above
    // it, and a stale list is how that gets missed.
    const post = await getMatch(code);
    const now = _lockMeasure(post);
    if (r.pre) {
      r.turns++;
      if (now.proj === r.pre.proj) r.dead++;
      if (now.nec > r.pre.nec) r.ruins += now.nec - r.pre.nec;
      if (now.res > r.pre.res) r.revives += now.res - r.pre.res;
    }
    if (nextTurn === 100 && !r.snap) {
      const me = post?.players?.[NPC_IDX] || {}, op = post?.players?.[0] || {};
      const stand = (pl) => ((pl?.field?.esclings) || []).filter(x => x?.escendant && !x.fallen);
      const myS = stand(me), opS = stand(op);
      const myBestLyt = myS.reduce((mx, sl) => Math.max(mx, effLytPure(sl, 0, me.field, op.field)), 0);
      const oppMinLyf = opS.length ? Math.min(...opS.map(sl => effLyfPure(sl, op.field))) : 0;
      r.snap = {
        myStand: myS.length, oppStand: opS.length,
        myAnima: me.anima ?? 0, oppAnima: op.anima ?? 0,
        hand: (me.hand || []).length,
        grim: Object.values(me.grimoire || {}).reduce((n, c) => n + (Number(c) || 0), 0),
        nec: (me.necropolis || []).length,
        myBestLyt, oppMinLyf,
        canRuin: opS.some(sl => effLyfPure(sl, op.field) === 0) || (myS.length >= 2 && myBestLyt >= oppMinLyf),
        hasPhantom: myS.some(sl => stampWordFor(getCard(sl.escendant), "school") === "Phantom"),
      };
    }
  }
  return !!res.ended;
}

export async function runNPCTurn(matchCode, match, difficulty) {
  const freshMatch = await getMatch(matchCode);
  if (!freshMatch || freshMatch.status === "finished") return;
  if (freshMatch.activePlayer !== NPC_IDX) return;

  const npc = freshMatch?.players?.[NPC_IDX];
  const player = freshMatch?.players?.[0];
  if (!npc || !player) return;

  // ── FALLEN HALF-TURN: the NPC's turn is starting — its own Fallen
  // beings rise NOW (before the fresh snapshot below, so the turn
  // runners see them as standing and they can attack this turn).

  const nd = difficulty
    ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase()
    : "Champion";

  // 🔬 LOCK PROBE — photograph the board before the brain touches it,
  // and again after the turn, so "did this Turn move anything" is
  // measured the same way the balancer's zero-delta measure does it.
  if (LOCK) LOCK.get(matchCode).pre = _lockMeasure(freshMatch);

  // ── R75 — the pre-placed Curios pay out ONCE, at the top of the
  // brain's first Turn. Idempotent via `curiosActivated`, so a live
  // Shatterfield listener that already fired them is a no-op here.
  await activateNpcCurios(matchCode);

  // ── H15 — the NPC's mulligan window: decided NOW, at the top of
  // its own first Turn, before it draws or acts. The think-time
  // sleep below gives the player's toast room to land, and the
  // currentMatch snapshot after it picks up the new hand naturally.
  await maybeNpcMulligan(matchCode, npc, freshMatch.turn, freshMatch.seatCount || 2);

  // ── ATV CONTRACTS — the adversary's standing demands are settled at
  // the top of MY turn, exactly where the player's devotion modal opens.
  await npcHonourDevotions(matchCode);
  {
    const afterDevote = await getMatch(matchCode);
    if (!afterDevote || afterDevote.status === "finished") return;
  }

  // ── DEPTHS BRAIN: resolved off the seat-1 object, stamped at build
  // time — the deeper the mind, the longer it visibly considers you.
  // Purely dramatic; pacing only, never legality.
  const brain = resolveBrain(npc, nd);
  const thinkTime = Math.round(
    (THINK_TIME[nd] || THINK_TIME.Champion) * (1 + 0.35 * brain.skill)
  );
  await sleep(thinkTime);

  const currentMatch = await getMatch(matchCode);
  if (!currentMatch || currentMatch.status === "finished") return;
  if (currentMatch.activePlayer !== NPC_IDX) return;
  const freshNpc    = currentMatch?.players?.[NPC_IDX];
  const freshPlayer = currentMatch?.players?.[0];
  if (!freshNpc || !freshPlayer) return;

  try {
    if (nd === NPC_DIFFICULTIES.BEB)           await runBebTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
    else if (nd === NPC_DIFFICULTIES.CHAMPION) await runChampionTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
    else if (nd === NPC_DIFFICULTIES.LORD)     await runLordTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
    else                                       await runBebTurn(matchCode, currentMatch, freshNpc, freshPlayer, brain);
  } catch (e) {
    console.error("[NPC] CRASH:", e.message, e.stack);
    // ── BUG #1 FIX (NPC soft-lock): crash recovery ──
    // If a turn function throws, the old code only logged it — the match
    // stayed on activePlayer: NPC_IDX forever, so the NPC "stopped playing"
    // and the human was locked out (not their turn). ALWAYS hand the turn
    // back to the player so the game can continue, even on an AI error.
    // Best-effort: reset attacked flags, then pass.
    // ⚠ THIS LINE USED TO READ "reset attacked flags + clear the cycle
    // flag", AND THE CLEAR IS GONE (R132). R12's window runs from one
    // sit-down to the next and `khaoSpentThisCycle` is now cleared in
    // exactly one place — handOffHalfTurn, onto the seat SITTING DOWN.
    // Clearing it here was worse than redundant: a brain that crashed a
    // Turn it had already spent Khao on would have had that spend
    // forgiven and climbed anyway. A crash must lose the Turn, not
    // launder it.
    try {
      const recover = await getMatch(matchCode);
      if (recover && recover.status !== "finished" && recover.activePlayer === NPC_IDX) {
        const npcRec = recover?.players?.[NPC_IDX];
        if (npcRec?.field?.esclings) {
          await updatePlayer(matchCode, NPC_IDX, {
            field: { ...npcRec.field, esclings: npcRec.field.esclings },
          });
        }
        // ⭐ R93 IS ASKED ON EVERY HAND-OFF, INCLUDING THIS ONE.
        // This path used to write `{ activePlayer: 0, turn: turn + 1 }`
        // by hand — the only hand-off in the file that advanced the
        // clock without consulting it. Every other one goes through
        // npcHandOffTurn (and Shatterfield's two through its own
        // equivalents), so the honest answer to "is the clock enforced
        // on every path a Turn can change hands" was NO, in this
        // driver, on the one path that only runs when something has
        // already gone wrong. A duel that crashes its way past half-turn
        // 150 is exactly the duel that most needs calling.
        await npcHandOffTurn(matchCode, recover);
      }
    } catch (recoverErr) {
      console.error("[NPC] CRASH RECOVERY FAILED:", recoverErr.message);
    }
  }
}

// ═════════════════════════════════════════════════════════════
// BEB — Chaotic. Always acts. Random (but LEGAL) choices. ALWAYS
// aggressive. AWAKENING: Beb now legally Deviant-Transcends, sometimes
// Deviant-Ascends on a whim, occasionally trains a random Impera, and
// occasionally fires a soul card — chaos with a rulebook. No searches,
// no resurrection strategy: Beb doesn't plan, Beb DOES.
// DEPTHS BRAIN: skill ramps every appetite — an Escling-rank Beb barely
// remembers its own Necropolis; a top-of-band Beb is chaos that lands.
// Temperament shifts the same gates (Reckless +, Patient −, Greedy
// hoards Luxmoire, Cold stops rolling dice entirely).
// ═════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// ⭐ GRAVESHIFT — FOR ALL THREE BRAINS, NOT JUST THE LORD
// ═══════════════════════════════════════════════════════════════
// R43: "Cannot lose by running out of cards (Graveshift exists)." For
// two of the three brains it did not exist. `sealGraveshift` was called
// from exactly one place in this file — inside runLordTurn — so when a
// Beb's or a Champion's Grimoire emptied, drawAndDeplete returned
// nothing and that seat DREW FOR THE REST OF THE DUEL while the player
// kept refuelling. A rule that says you cannot lose this way applied to
// one difficulty.
//
// The Lord's own gate was wrong too. It read
// `grimoireEmpty && handEmpty && planAnima > 12`, and only the first
// third of that is a rule:
//   · R40 — Graveshift ONLY when the Grimoire is empty.  ✔
//   · handEmpty — NOT A RULE. R42 folds HAND + NECROPOLIS into the new
//     Grimoire, so a full hand is fuel, not a blocker. The Lord was
//     refusing to refuel precisely when it had the most to refuel with.
//   · planAnima > 12 — a made-up number. R41 is exactly 10.
// And there must be something to fold: a Graveshift with an empty hand
// AND an empty Necropolis pays 10 Anima for a Grimoire of nothing.
//
// One implementation, three callers, placed BEFORE the draw so the
// refuel can actually feed the draw it exists to enable.
async function maybeGraveshift(code, turn, hand, grimoire, field) {
  if (Object.keys(grimoire || {}).length > 0) return { hand, grimoire, field, did: false };

  const m = await getMatch(code);
  const live = m?.players?.[NPC_IDX] || {};
  const fuel = (hand?.length || 0) + ((live.necropolis || []).length);
  if (fuel === 0) return { hand, grimoire, field, did: false };

  // R41 + R96 — the Graveshift fare is the ONE exemption to the Till
  // Law, because you may not lose a Solrend by running out of cards, so
  // the remedy for running out must always be payable. `<=` made the
  // NPC immortal where a human on exactly 10 shifts, lands on 0 and
  // loses on the spot. exempt:true is what the player's till passes.
  if (!canPayVoluntary(live.anima ?? 0, GRAVESHIFT_COST, { exempt: true })) return { hand, grimoire, field, did: false };
  if (!(await npcSpendAnima(code, GRAVESHIFT_COST))) return { hand, grimoire, field, did: false };

  // R42 + R85 parity, identical to handleGraveshift: stamp the field
  // survivors at the outgoing generation, strip seals as cards fold
  // back into the Grimoire map, tick gshift. The brain loses its
  // passive Resurrect on the shifted cards exactly as the player does —
  // R-UNIVERSAL. `hand` is the local working copy and may be ahead of
  // the last write, so it is passed explicitly.
  const gs = sealGraveshift({ ...live, hand }, isEscendantId);
  await updatePlayer(code, NPC_IDX, {
    grimoire: gs.grimoire,
    field: gs.field,
    gshift: gs.gshift,
    necropolis: [],
    hand: [],
  });
  await sleep(ACTION_DELAY);
  return { hand: [], grimoire: gs.grimoire, field: gs.field, did: true };
}

async function runBebTurn(code, match, npc, player, brain = null) {
  const turn = match.turn || 0;
  // ── R08's WINDOW IS AS LONG AS THE TABLE ────────────────────
  // ⚠ THIS USED TO BE THE LITERAL `2`, TYPED AT THE R08 GATE
  // BELOW AND DEFAULTED AT EVERY SEARCH HELPER. The opening Cycle
  // is `openingCycleLength(seatCount)` half-turns long — FOUR on a
  // four-seat table — so a hardcoded 2 declared the Cycle over at
  // half-turn 2 and let the brain Attack, search its Grimoire and
  // search its Luxmoire inside a window the rule still holds open.
  // Latent only because an NPC seat has never been dealt into a
  // 3-4p table; the instant one is, R08 is wrong IN THE BRAIN,
  // which is the one place the backstop in Shatterfield cannot see.
  const seatCount = match.seatCount || 2;
  let hand     = [...(npc.hand || [])];
  let field    = JSON.parse(JSON.stringify(npc.field || {}));
  let bebGrimoire = { ...npc.grimoire };
  let bebLuxmoire = { ...npc.luxmoire };
  const usedSouls = new Set();
  const stampTurn = newStampTurn(); // R83 — one per Turn, discarded at end of Turn
  await npcOpenMainPhase(code);     // R108 — a new Turn opens in Main
  // Universal cost rule: no local khao/anima mirror. Every spend goes
  // through npcSpendKhao / npcSpendAnima, which read fresh from Firebase.

  // 0. GRAVESHIFT — R43, before the draw so the refuel can feed it.
  {
    const gs = await maybeGraveshift(code, turn, hand, bebGrimoire, field);
    hand = gs.hand; bebGrimoire = gs.grimoire; field = gs.field;
  }

  // 1. DRAW — always tries to draw something (was Math.random() > 0.6 gate, now removed)
  // R10 (Rulez v2): cap is 8, draw-then-discard. Beb happily draws AT the
  // cap — the end-of-turn enforcer churns the worst card to the Necropolis.
  if (hand.length <= NPC_HAND_LIMIT) {
    const fromLux = Object.keys(bebLuxmoire).length > 0
      && chance(0.5 + (brain?.t?.luxBias || 0), brain);
    const { card: drawn, newPool } = drawAndDeplete(fromLux ? bebLuxmoire : bebGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (fromLux) bebLuxmoire = newPool; else bebGrimoire = newPool;
      // Persist draw + brief pause so player sees it
      await updatePlayer(code, NPC_IDX, {
        hand,
        grimoire: bebGrimoire,
        luxmoire: bebLuxmoire,
      });
      await sleep(ACTION_DELAY);
    }
  }

  // 1b. RESURRECT — v2 (caP'n ruling): a standard once-per-turn play
  // for EVERY duelist. Beb remembers the Necropolis exists sometimes —
  // rules-true when it fires, forgotten when it doesn't. DEPTHS BRAIN:
  // the memory sharpens with skill (30% at the bottom of the ladder,
  // 65% at the top of the Beb band).
  // DEPTHS BRAIN: sharpened from 0.30-0.65 to 0.55-0.95. Resurrection
  // is never a bad play when resurrectOne has already self-gated on a
  // legal slot, the R44 tally and the fare — the old floor threw away
  // up to 70% of the Beb's legal revivals for flavour.
  if (turn >= 3 && chance(lerp(0.55, 0.95, brain?.skill ?? 0.5), brain)) {
    const r = await resurrectOne(code, field, turn);
    field = r.field;
  }

  // 2. TRANSCEND — fill all empty slots, ONE at a time (legal picks only)
  // DEPTHS BRAIN: the placement pick used to be `pickRandom` at every
  // skill — a Beb that draws its best body and plays its worst one.
  // Natural preference (and the highest-Lyt pick that rides with it) is
  // now what the top of the band does and the bottom still doesn't.
  const bebPicksWell = chance(lerp(0.1, 0.9, brain?.skill ?? 0.5), brain);
  while (true) {
    const r = await placeOneEscendant(code, hand, field, bebPicksWell);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // 3. FLIP TOMES — one at a time. Cost paid here per Rulebook line 300.
  while (true) {
    const r = await flipOneTome(code, field, turn, hand, bebGrimoire);
    field = r.field; hand = r.hand; bebGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }   // the Tome ended the duel (R33 / a Prophecy) — stop, at the cap
    if (!r.flipped) break;
  }

  // 4. PLACE TOME — one face-down (free)
  {
    const r = await placeOneTome(code, hand, field, turn, "Beb");
    hand = r.hand; field = r.field;
  }

  // 5. ACTIVATE TOMES — re-activate persistent face-up tomes only
  while (true) {
    const r = await activateOneTome(code, field, turn, "Beb", hand, bebGrimoire);
    field = r.field; hand = r.hand; bebGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
    if (!r.activated) break;
  }

  // 6. ASCEND — once per turn TOTAL (Rulebook line 643).
  // No loop: even if multiple beings could ascend, the rule allows only
  // one. AWAKENING: sometimes Beb is willing to pay a Deviant fare on a
  // whim — chaos, but the rulebook's chaos. DEPTHS BRAIN: the whim
  // scales with skill (25% → 55% across the Beb band).
  {
    const r = await ascendOne(code, hand, field, bebPicksWell,
      chance(lerp(0.25, 0.55, brain?.skill ?? 0.5), brain));
    hand = r.hand; field = r.field;
  }

  // 7. TRAIN — AWAKENING: sometimes Beb slaps one legal Impera onto
  // something. No plan. Just vibes. DEPTHS BRAIN: 35% → 70% with skill.
  if (chance(lerp(0.35, 0.7, brain?.skill ?? 0.5), brain)) {
    const r = await trainOneImpera(code, hand, field);
    hand = r.hand; field = r.field;
  }

  // ── 7b. U'HLTI / AFFINITY — the Beb dresses its Natural L3 too.
  // attachOneSoulCard had exactly two call sites, Champion and Lord, so
  // a Beb holding a complete Supreme kit sat on it: no U'hlti, so no
  // Affinity (R70), so never a Supreme Being (R69) and never a Supreme
  // prophecy. Attaching is FREE and legality is enforced inside the
  // helper (Natural, L3, one each — R64/R70/R72/R73), so there is
  // nothing here for chaos to get wrong. Beb is reckless, not blind.
  while (true) {
    const r = await attachOneSoulCard(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.attached) break;
  }

  // 8. SOUL ACTIVATION — AWAKENING: sometimes Beb fires one soul card
  // if it's useful and affordable (includes the Dragon Eyes gamble —
  // Beb is the only tier that rolls those dice). DEPTHS BRAIN:
  // 20% → 50% with skill.
  // DEPTHS BRAIN: one coin toss became a skill-scaled budget (1 → 3).
  // A Beb's soul cards are the only Anima-movers it owns on a walled
  // board, and it was firing them on two turns in five.
  const bebSoulCap = Math.round(lerp(1, 3, brain?.skill ?? 0.5));
  for (let sa = 0; sa < bebSoulCap; sa++) {
    if (!chance(lerp(0.45, 0.9, brain?.skill ?? 0.5), brain)) break;
    const r = await activateOneSoulCard(code, hand, field, bebGrimoire, turn, usedSouls, "Beb");
    hand = r.hand; field = r.field; bebGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
    if (!r.activated) break;
  }

  // ── ⭐ 8b. PRIMERA STAMPS (R78–R84) — pressed BEFORE the attack
  // phase, because that is where every one of them pays: Phantom is
  // the only path to the Anima through a standing wall, Ilk-Dragon
  // converts a swing that would do nothing into a fracture, Beast arms
  // the Anima rider, and Machine/Space/Time/Humanoid all put a body or
  // a piece on the board in time for it to matter this Turn.
  {
    const fpS = (await getMatch(code))?.players?.[0] || player;
    const r = await pressPrimeras(code, hand, field, bebGrimoire, bebLuxmoire, stampTurn,
      { playerField: fpS.field || {}, difficulty: "Beb", brain });
    hand = r.hand; field = r.field; bebGrimoire = r.grimoire; bebLuxmoire = r.luxmoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
  }

  // ── R108 · DECLARE THE ATTACK PHASE ─────────────────────────
  // Everything above this line is the Main phase; nothing below it
  // plays a card. The declaration is written to the seat BEFORE the
  // first swing so the board, the gate and an adversary's client all
  // agree the brain has committed. Written unconditionally — even on
  // the opening Cycle, where R08 bars the attacks themselves: the
  // phase advanced whether or not anything came of it, and a phase
  // that only exists on turns you happen to swing is not a phase.
  // ── A PROPHECY MAY BE CASHED THE INSTANT IT COMES TRUE ───────
  // caP'n, 2026-09-14. BEAT ONE: everything above this line is the Main
  // phase, so every Supreme-being condition — two Supremes of a School
  // plus a Divine title, the two Tablets — has just had its chance to
  // complete through an Ascend, a Training, a U'hlti, an Affinity or a
  // Resurrection. See npcCashProphecyIfReady.
  if (await npcCashProphecyIfReady(code, npc?.prophecy)) { await npcAbortTurn(code, hand); return; }   // R10 on the way out — a duel WON by a Prophecy is archived at the cap too
  await npcDeclareAttackPhase(code);
  // 9. ATTACK — ALWAYS
  let esclings = field.esclings;
  if (!isOpeningTurnIndex(turn, seatCount)) {   // R08 — no Attacks of any kind on the opening Cycle
    const fp = (await getMatch(code))?.players?.[0] || player;
    const { killed, npcDied } = await doAttacks(code, esclings, fp, "Beb", field, turn, brain);
    if (npcDied) { await npcAbortTurn(code, hand); return; } // Fate Maker / Sweet Dreams ended the match
    field.esclings = esclings;
    if (killed) {
      // R10 ON THE WAY OUT. The kill-shot path returns early and used
      // to skip npcEnforceHandCap entirely, so a duel WON on a turn
      // that also drew at the cap was archived with a nine-card hand.
      // The duel is over, so nothing can be played from it -- but the
      // final snapshot is what a replay renders and what the end
      // screen reads, and a referee that reports a real rule break it
      // has decided to forgive is a referee people stop reading.
      // ⚠ THE REBASE LAW APPLIES ON THE WAY OUT TOO. This write is the
      // LAST word on the duel — it is what the replay renders and what
      // the referee audits — and until now it was the only hand/luxmoire
      // write in the runner that skipped both gates. A winning Turn is
      // exactly the Turn most likely to need them: doAttacks opens a
      // response window on every swing (the reactor may have spent a
      // counter out of this very hand), and sections 7 and 9 can tutor
      // an Impera out of the Luxmoire (npcLuxDirty). Writing the turn-
      // start drafts over the top puts the spent counter back in hand
      // and walks the tutored Impera back into the Luxmoire while the
      // hand still holds it. Same two lines as section 12, same reason.
      hand = await syncNpcHand(code, hand);      // THE REBASE LAW
      hand = await npcEnforceHandCap(code, hand);
      field.esclings = esclings;
      bebLuxmoire = await syncNpcLuxmoire(code, bebLuxmoire);   // see npcLuxDirty
      // ⚠ NO `khao:` — R14/R92b. The pool refills at the START of a
      // Turn, onto the seat SITTING DOWN, inside handOffHalfTurn's chair
      // transaction. This line refilled the brain as it STOOD UP: in a
      // three-seat table (Gauntlet — Duo, Story Co-op) a kill-shot Turn
      // does not end the duel, so the brain crossed into the surviving
      // player's half-turn on a full pool to answer with. In 1v1 it only
      // poisoned the archived snapshot a replay renders — which is still
      // a stored state that contradicts the rule.
      await updatePlayer(code, NPC_IDX, { hand, field, grimoire: bebGrimoire, luxmoire: bebLuxmoire });
      await npcFinish(code, NPC_IDX, 0);
      return;
    }
  }

  // BEAT TWO: the swings have landed, so Bloody Hell (45 Anima lost,
  // on EITHER side) and Ace of Shades (a seven-streak of Shadow
  // Gamblings) can only have completed here. Asked before the
  // hand-off, which is the whole point of the ruling.
  if (await npcCashProphecyIfReady(code, npc?.prophecy)) { await npcAbortTurn(code, hand); return; }   // R10 on the way out — a duel WON by a Prophecy is archived at the cap too
  // END TURN
  hand = await syncNpcHand(code, hand);      // THE REBASE LAW — the chain may have spent from it
  hand = await npcEnforceHandCap(code, hand); // R10 — never end a turn above the cap
  field.esclings = esclings;
  const npcFresh   = (await getMatch(code))?.players?.[NPC_IDX] || npc;
  // ── R132 · THE MAX KHAO CLIMB IS NOT DECIDED HERE ANY MORE ────
  // caP'n, 2026-09-15: *"after 1 full turn of not using Khao, so if you
  // didn't use any turn 1, then the beginning of turn 2 you get +1 to
  // your Max Khao"* — the +1 lands at the BEGINNING of a Turn, which is
  // the chair transaction, so it now lives in ONE place:
  // turnEngine.handOffHalfTurn, granted onto the seat SITTING DOWN.
  //
  // ⚠ WHAT THIS LINE USED TO DO, AND WHY IT WAS A BUG. It granted at
  // END TURN — `(!spent && turn >= 2 && (maxKhao||3) < 10) ? +1 : same`
  // — in the same payload that cleared the spend flag. `match.turn`
  // counts half-turns, so this brain evaluated as it STOOD UP from
  // half-turn 3 and first PLAYED on the higher ceiling at half-turn 5,
  // while the human seat evaluated as it sat DOWN into half-turn 2 and
  // played half-turn 2 on it. The brain was a full Turn behind the
  // player on Max Khao for the whole duel — not a decision anybody
  // made, just drift between four copies of one rule.
  //
  // ⚠ AND THE `khaoSpentThisCycle: false` CLEAR WENT WITH IT, WHICH IS
  // THE OTHER HALF OF THE FIX. R12's window is one sit-down to the next
  // — this seat's own half-turn PLUS its adversary's. Clearing it here,
  // as the brain stands up, pointed the window at the wrong half of the
  // Turn and let a counter the brain fires during the PLAYER's half-turn
  // slip past the rule caP'n wrote to catch it. The flag is now cleared
  // in the one place the window opens: the hand-off, onto the incoming
  // seat. DO NOT RE-ADD A CLEAR HERE.
  //
  // `maxKhao` is still written, and is now an echo of the ceiling
  // Firebase already holds: it is this seat's own ceiling rather than
  // its pool, and the payload keeps the key for the same reason it keeps
  // the deliberate ABSENCE of `khao:` below.
  //
  // ⚠ R13 IS STILL LOAD-BEARING WHERE THE CLIMB WENT, AND THIS IS WHERE
  // THE BUG LIVED. The climb that used to occupy the line below clamped
  // with `Math.min(10, …)` on EVERY end of turn, so a brain sitting at
  // maxKhao 12 after flipping a Khao Arcadia was written back DOWN to 10
  // the next time it ended a turn without spending — the card's whole
  // effect, deleted by a bookkeeping line. The climb's new home guards
  // on `current < MAX_KHAO_NAT` and never writes downward, which is the
  // shape the player's path always had. Do not reintroduce a Math.min
  // against the NATURAL ceiling, here or anywhere.
  const newMaxKhao = npcFresh.maxKhao || STARTING_KHAO;
  bebLuxmoire = await syncNpcLuxmoire(code, bebLuxmoire);   // see npcLuxDirty
  await updatePlayer(code, NPC_IDX, {
    hand, field,
    // ⚠ NO `khao:` — the pool refills at the START of a Turn (caP'n,
    // 2026-09-13) and handOffHalfTurn writes it onto the seat sitting
    // down. This line used to refill the brain as it STOOD UP, which
    // gave it a full pool to answer the player's whole half-turn with.
    maxKhao: newMaxKhao,
    grimoire: bebGrimoire, luxmoire: bebLuxmoire,
  });
  // FALLEN HALF-TURN: the player's turn starts now — their Fallen rise.
  await npcHandOffTurn(code, match);
}

// ═════════════════════════════════════════════════════════════
// CHAMPION — Plays correctly. Attacks every turn. Manages Khao.
// AWAKENING: Champion now runs the fundamentals a good club player
// would — trains Impera every turn, dresses its Natural L3 in its
// U'hlti and Affinity, fires the simple soul effects, Grimoire-
// searches an L1 when its board is empty-handed, and resurrects when
// its board has been wiped. Natural ascensions only — Deviancy is
// the Lord's (and Beb's) vice.
// DEPTHS BRAIN: the top of the Champion band studies harder — at
// skill ≥ 0.38 it tutors its ascension lines from the Grimoire (a move
// that was Lord-only), and at ≥ 0.40 it fires a third soul effect per
// turn. Greedy Champions raid the Luxmoire early; Reckless/Cold ones
// go for the throat when trained to redirect.
// ═════════════════════════════════════════════════════════════
async function runChampionTurn(code, match, npc, player, brain = null) {
  const turn = match.turn || 0;
  // ── R08's WINDOW IS AS LONG AS THE TABLE ────────────────────
  // ⚠ THIS USED TO BE THE LITERAL `2`, TYPED AT THE R08 GATE
  // BELOW AND DEFAULTED AT EVERY SEARCH HELPER. The opening Cycle
  // is `openingCycleLength(seatCount)` half-turns long — FOUR on a
  // four-seat table — so a hardcoded 2 declared the Cycle over at
  // half-turn 2 and let the brain Attack, search its Grimoire and
  // search its Luxmoire inside a window the rule still holds open.
  // Latent only because an NPC seat has never been dealt into a
  // 3-4p table; the instant one is, R08 is wrong IN THE BRAIN,
  // which is the one place the backstop in Shatterfield cannot see.
  const seatCount = match.seatCount || 2;
  let hand        = [...(npc.hand || [])];
  let field       = JSON.parse(JSON.stringify(npc.field || {}));
  let champGrimoire = { ...npc.grimoire };
  let champLuxmoire = { ...npc.luxmoire };
  const usedSouls = new Set();
  const stampTurn = newStampTurn(); // R83 — one per Turn, discarded at end of Turn
  await npcOpenMainPhase(code);     // R108 — a new Turn opens in Main
  let drewThisTurn = false;
  // Universal cost rule: no local mirror — npcSpendKhao reads fresh.

  // 0. GRAVESHIFT — R43, before anything that wants to draw or tutor.
  {
    const gs = await maybeGraveshift(code, turn, hand, champGrimoire, field);
    hand = gs.hand; champGrimoire = gs.grimoire; field = gs.field;
  }

  // 1a. EMERGENCY GRIMOIRE SEARCH — board empty + no L1 in hand: pay 2
  // to tutor a Level 1 rather than praying at the top of the Grimoire.
  // Consumes the turn's draw, exactly like the player (AWAKENING).
  {
    const boardEmpty = (field.esclings || []).every(s => !s?.escendant);
    const l1InHand = hand.some(id => {
      const c = getCard(id);
      return c?.type === "Escendant" && c.level === 1;
    });
    if (boardEmpty && !l1InHand && turn >= 1) {
      const r = await grimoireSearchOne(code, hand, champGrimoire, 1, null, turn, seatCount, SEARCH_BY_PLAYER);
      hand = r.hand; champGrimoire = r.grimoire;
      if (r.searched) drewThisTurn = true;
    }
  }

  // 1a-ii. ASCENSION-LINE SEARCH — DEPTHS BRAIN (skill ≥ 0.38): the
  // studied Champion tutors a standing being's natural next level from
  // the Grimoire instead of praying at the top of it. Same fare, same
  // draw-consumption, same helper the Lord uses — a move earned at the
  // top of the Champion band and by every gatekeeper Champion.
  if (!drewThisTurn && (brain?.skill ?? 0) >= SKILL_CHAMP_LINE_SEARCH) {
    const npcNow = (await getMatch(code))?.players?.[NPC_IDX] || {};
    // The old floor was a flat 6 for a 4-fare — two Khao of pure
    // conservatism on top of every tutor (R88 + R96 are the only gates
    // the rules ask for; the +1 keeps one point for a response).
    if ((npcNow.khao ?? 0) >= grimSearchFare(2) + 1) {
      for (const s of (field.esclings || [])) {
        const cur = getCard(s?.escendant);
        if (!cur || cur.level >= 3 || s?.fallen) continue;
        const handHasNext = hand.some(id => canAscendNatural(cur, getCard(id)));
        if (handHasNext) continue;
        const wantIds = Object.keys(champGrimoire).filter(gid =>
          (champGrimoire[gid] || 0) > 0 && canAscendNatural(cur, getCard(gid)));
        if (wantIds.length === 0) continue;
        const r = await grimoireSearchOne(code, hand, champGrimoire, cur.level + 1, wantIds, turn, seatCount, SEARCH_BY_PLAYER);
        hand = r.hand; champGrimoire = r.grimoire;
        if (r.searched) drewThisTurn = true;
        break;
      }
    }
  }

  // 1b. SMART DRAW — Luxmoire if L2+ ready (gate removed; always evaluates)
  // R10 (Rulez v2): cap 8. Champion won't draw INTO a forced discard.
  // DEPTHS BRAIN: a Greedy Champion raids the Luxmoire as soon as it
  // holds cards — Impera hunger over curve discipline.
  if (!drewThisTurn && (hand.length < NPC_HAND_LIMIT
        || !handHasPlay(hand, field, champGrimoire))) {   // R10 — churn a dead hand
    const esclings = field.esclings || [];
    const hasL2 = esclings.some(s => getCard(s?.escendant)?.level >= 2);
    const luxHasCards = Object.keys(champLuxmoire).length > 0;
    const fromLux = (hasL2 || (brain?.t?.luxBias || 0) > 0) && luxHasCards; // ── AGGRESSION BUMP ── (was random>0.4)
    const { card: drawn, newPool } = drawAndDeplete(fromLux ? champLuxmoire : champGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (fromLux) champLuxmoire = newPool; else champGrimoire = newPool;
      await updatePlayer(code, NPC_IDX, {
        hand, grimoire: champGrimoire, luxmoire: champLuxmoire,
      });
      await sleep(ACTION_DELAY);
    }
  }

  // 2. RESURRECT — AWAKENING v2 (caP'n ruling, July 2026): resurrection
  // is a standard once-per-turn play, not a last resort. Champion
  // attempts one legal revival every turn (resurrectOne self-gates on
  // candidates, the R44 record, chain order, and cost).
  if (turn >= 3) {
    const r = await resurrectOne(code, field, turn);
    field = r.field;
  }

  // 3. TRANSCEND — natural preferred, one at a time
  while (true) {
    const r = await placeOneEscendant(code, hand, field, true);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // 4. ASCEND — once per turn TOTAL (Rulebook line 643). Natural only.
  {
    const r = await ascendOne(code, hand, field, true, false);
    hand = r.hand; field = r.field;
  }

  // 5. TRAIN — AWAKENING: attach every legal Impera in hand, one write
  // at a time. Champion plays its support cards instead of hoarding them.
  while (true) {
    const r = await trainOneImpera(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.trained) break;
  }

  // 6. U'HLTI / AFFINITY — AWAKENING: dress the Natural L3.
  while (true) {
    const r = await attachOneSoulCard(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.attached) break;
  }

  // 7. FLIP WAITING TOMES — cost paid here per Rulebook
  while (true) {
    const r = await flipOneTome(code, field, turn, hand, champGrimoire);
    field = r.field; hand = r.hand; champGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }   // the Tome ended the duel (R33 / a Prophecy) — stop, at the cap
    if (!r.flipped) break;
  }

  // 8. PLACE TOME — placement is FREE (R34) and a face-down tome is a
  // threat the adversary has to play around. The old `hand.length >= 4`
  // meant a Champion holding three cards left all three rifts empty for
  // the rest of the duel — hoarding a resource that costs nothing to
  // deploy. Two is enough to keep a card in reserve.
  // DEPTHS BRAIN: a studied Champion deploys its rifts on a thinner
  // hand than a raw one (4 cards → 2 across the band).
  if (hand.length >= Math.max(2, Math.round(lerp(4, 2, brain?.skill ?? 0.35)))) {
    const r = await placeOneTome(code, hand, field, turn, "Champion");
    hand = r.hand; field = r.field;
  }

  // 9. ACTIVATE TOMES — re-activate persistent face-up tomes only
  while (true) {
    const r = await activateOneTome(code, field, turn, "Champion", hand, champGrimoire);
    field = r.field; hand = r.hand; champGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
    if (!r.activated) break;
  }

  // 10. SOUL ACTIVATIONS — AWAKENING: the simple whitelist (Intangible,
  // Nullify, dims, cheap draws). Two firings per turn; three at the top
  // of the band (DEPTHS BRAIN, skill ≥ 0.40).
  // DEPTHS BRAIN — continuous, not a single step. SKILL_CHAMP_SOUL3
  // stays as the guaranteed floor it always was; above it the cap keeps
  // climbing so the dial is not flat across a whole ELO band.
  const champSoulCap = Math.max(
    (brain?.skill ?? 0) >= SKILL_CHAMP_SOUL3 ? 3 : 2,
    Math.round(lerp(2, 4, brain?.skill ?? 0.35)));
  for (let sa = 0; sa < champSoulCap; sa++) {
    const r = await activateOneSoulCard(code, hand, field, champGrimoire, turn, usedSouls, "Champion");
    hand = r.hand; field = r.field; champGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
    if (!r.activated) break;
  }

  // ── ⭐ 10b. PRIMERA STAMPS (R78–R84) — pressed BEFORE the attack
  // phase, because that is where every one of them pays: Phantom is
  // the only path to the Anima through a standing wall, Ilk-Dragon
  // converts a swing that would do nothing into a fracture, Beast arms
  // the Anima rider, and Machine/Space/Time/Humanoid all put a body or
  // a piece on the board in time for it to matter this Turn.
  {
    const fpS = (await getMatch(code))?.players?.[0] || player;
    const r = await pressPrimeras(code, hand, field, champGrimoire, champLuxmoire, stampTurn,
      { playerField: fpS.field || {}, difficulty: "Champion", brain });
    hand = r.hand; field = r.field; champGrimoire = r.grimoire; champLuxmoire = r.luxmoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
  }

  // ── R108 · DECLARE THE ATTACK PHASE ─────────────────────────
  // Everything above this line is the Main phase; nothing below it
  // plays a card. The declaration is written to the seat BEFORE the
  // first swing so the board, the gate and an adversary's client all
  // agree the brain has committed. Written unconditionally — even on
  // the opening Cycle, where R08 bars the attacks themselves: the
  // phase advanced whether or not anything came of it, and a phase
  // that only exists on turns you happen to swing is not a phase.
  // ── THE ROLE ARTS AND EXILIATION (R111-R119) ──────────────────
  // The Champion knows them too; only the Beb does not. Same resolver,
  // same gate, same judgement — see npcRoleArts. Before the declaration,
  // because declaring shuts the hand and the Warden Art spends from it.
  {
    const reb = await npcRoleArts(code, turn, seatCount, "Champion",
      { hand, grimoire: champGrimoire, luxmoire: champLuxmoire });
    hand = reb.hand; champGrimoire = reb.grimoire; champLuxmoire = reb.luxmoire;
  }

  // ── A PROPHECY MAY BE CASHED THE INSTANT IT COMES TRUE ───────
  // caP'n, 2026-09-14. BEAT ONE: everything above this line is the Main
  // phase, so every Supreme-being condition — two Supremes of a School
  // plus a Divine title, the two Tablets — has just had its chance to
  // complete through an Ascend, a Training, a U'hlti, an Affinity or a
  // Resurrection. See npcCashProphecyIfReady.
  if (await npcCashProphecyIfReady(code, npc?.prophecy)) { await npcAbortTurn(code, hand); return; }   // R10 on the way out — a duel WON by a Prophecy is archived at the cap too
  await npcDeclareAttackPhase(code);
  // 11. ATTACK
  let esclings = field.esclings;
  if (!isOpeningTurnIndex(turn, seatCount)) {   // R08 — no Attacks of any kind on the opening Cycle
    const fp = (await getMatch(code))?.players?.[0] || player;
    const { killed, npcDied } = await doAttacks(code, esclings, fp, "Champion", field, turn, brain);
    if (npcDied) { await npcAbortTurn(code, hand); return; } // Fate Maker / Sweet Dreams ended the match
    field.esclings = esclings;
    if (killed) {
      // R10 ON THE WAY OUT. The kill-shot path returns early and used
      // to skip npcEnforceHandCap entirely, so a duel WON on a turn
      // that also drew at the cap was archived with a nine-card hand.
      // The duel is over, so nothing can be played from it -- but the
      // final snapshot is what a replay renders and what the end
      // screen reads, and a referee that reports a real rule break it
      // has decided to forgive is a referee people stop reading.
      // ⚠ THE REBASE LAW APPLIES ON THE WAY OUT TOO. This write is the
      // LAST word on the duel — it is what the replay renders and what
      // the referee audits — and until now it was the only hand/luxmoire
      // write in the runner that skipped both gates. A winning Turn is
      // exactly the Turn most likely to need them: doAttacks opens a
      // response window on every swing (the reactor may have spent a
      // counter out of this very hand), and sections 7 and 9 can tutor
      // an Impera out of the Luxmoire (npcLuxDirty). Writing the turn-
      // start drafts over the top puts the spent counter back in hand
      // and walks the tutored Impera back into the Luxmoire while the
      // hand still holds it. Same two lines as section 12, same reason.
      hand = await syncNpcHand(code, hand);      // THE REBASE LAW
      hand = await npcEnforceHandCap(code, hand);
      field.esclings = esclings;
      champLuxmoire = await syncNpcLuxmoire(code, champLuxmoire);   // see npcLuxDirty
      // ⚠ NO `khao:` — R14/R92b. The pool refills at the START of a
      // Turn, onto the seat SITTING DOWN, inside handOffHalfTurn's chair
      // transaction. This line refilled the brain as it STOOD UP: in a
      // three-seat table (Gauntlet — Duo, Story Co-op) a kill-shot Turn
      // does not end the duel, so the brain crossed into the surviving
      // player's half-turn on a full pool to answer with. In 1v1 it only
      // poisoned the archived snapshot a replay renders — which is still
      // a stored state that contradicts the rule.
      await updatePlayer(code, NPC_IDX, { hand, field, grimoire: champGrimoire, luxmoire: champLuxmoire });
      await npcFinish(code, NPC_IDX, 0);
      return;
    }
  }

  // BEAT TWO: the swings have landed, so Bloody Hell (45 Anima lost,
  // on EITHER side) and Ace of Shades (a seven-streak of Shadow
  // Gamblings) can only have completed here. Asked before the
  // hand-off, which is the whole point of the ruling.
  if (await npcCashProphecyIfReady(code, npc?.prophecy)) { await npcAbortTurn(code, hand); return; }   // R10 on the way out — a duel WON by a Prophecy is archived at the cap too
  // END TURN
  hand = await syncNpcHand(code, hand);      // THE REBASE LAW — the chain may have spent from it
  hand = await npcEnforceHandCap(code, hand); // R10 — never end a turn above the cap
  field.esclings = esclings;
  const npcFresh    = (await getMatch(code))?.players?.[NPC_IDX] || npc;
  // ── R132 · THE MAX KHAO CLIMB IS NOT DECIDED HERE ANY MORE ────
  // caP'n, 2026-09-15: *"after 1 full turn of not using Khao, so if you
  // didn't use any turn 1, then the beginning of turn 2 you get +1 to
  // your Max Khao"* — the +1 lands at the BEGINNING of a Turn, which is
  // the chair transaction, so it now lives in ONE place:
  // turnEngine.handOffHalfTurn, granted onto the seat SITTING DOWN.
  //
  // ⚠ WHAT THIS LINE USED TO DO, AND WHY IT WAS A BUG. It granted at
  // END TURN — `(!spent && turn >= 2 && (maxKhao||3) < 10) ? +1 : same`
  // — in the same payload that cleared the spend flag. `match.turn`
  // counts half-turns, so this brain evaluated as it STOOD UP from
  // half-turn 3 and first PLAYED on the higher ceiling at half-turn 5,
  // while the human seat evaluated as it sat DOWN into half-turn 2 and
  // played half-turn 2 on it. The brain was a full Turn behind the
  // player on Max Khao for the whole duel — not a decision anybody
  // made, just drift between four copies of one rule.
  //
  // ⚠ AND THE `khaoSpentThisCycle: false` CLEAR WENT WITH IT, WHICH IS
  // THE OTHER HALF OF THE FIX. R12's window is one sit-down to the next
  // — this seat's own half-turn PLUS its adversary's. Clearing it here,
  // as the brain stands up, pointed the window at the wrong half of the
  // Turn and let a counter the brain fires during the PLAYER's half-turn
  // slip past the rule caP'n wrote to catch it. The flag is now cleared
  // in the one place the window opens: the hand-off, onto the incoming
  // seat. DO NOT RE-ADD A CLEAR HERE.
  //
  // `maxKhao` is still written, and is now an echo of the ceiling
  // Firebase already holds: it is this seat's own ceiling rather than
  // its pool, and the payload keeps the key for the same reason it keeps
  // the deliberate ABSENCE of `khao:` below.
  // See runBebTurn's copy of this note for the R13 clamp bug that used
  // to live on the line below, and turnEngine's R132 banner for the fix.
  const newMaxKhao  = npcFresh.maxKhao || STARTING_KHAO;
  champLuxmoire = await syncNpcLuxmoire(code, champLuxmoire);   // see npcLuxDirty
  await updatePlayer(code, NPC_IDX, {
    hand, field,
    // ⚠ NO `khao:` — the pool refills at the START of a Turn (caP'n,
    // 2026-09-13) and handOffHalfTurn writes it onto the seat sitting
    // down. This line used to refill the brain as it STOOD UP, which
    // gave it a full pool to answer the player's whole half-turn with.
    maxKhao: newMaxKhao,
    grimoire: champGrimoire, luxmoire: champLuxmoire,
  });
  // FALLEN HALF-TURN: the player's turn starts now — their Fallen rise.
  await npcHandOffTurn(code, match);
}

// ─────────────────────────────────────────────────────────────
// DEPTHS BRAIN: PROPHECY HELPERS — compact mirrors of
// prophecyEngine's checkers (isSupreme, divine titles, Ilk sweeps).
// The engine itself watches BOTH seats on every state update and ends
// the match the moment condition + fare are met — the NPC never
// "declares" anything. These helpers only let the Lord PLAY TOWARD
// that moment instead of stumbling into it.
// ─────────────────────────────────────────────────────────────
const DIVINE_AFFS = ["aff_evil", "aff_holy"]; // A'oeternis / A'aeternis
const ILK_PROPHECIES = {
  proph_space_race:      "Space",
  proph_phantom_planet:  "Phantom",
  proph_dragonforce:     "Dragon",
  proph_time_to_pretend: "Time",
};
// Prophecy fare is 15 Khao (getProphecyKhaoCost default; Ace's 13 is
// Shadow-Gambling-only — never Lord-actionable). The engine checks the
// pool itself — the Lord just has to let the pool GROW.

function soulPieces(slot) {
  const soul = (slot?.soul || []).map(id => getCard(id)).filter(Boolean);
  return {
    imperaLevels: new Set(soul.filter(c => c.type === "Impera").map(c => c.level)),
    hasUhlti: soul.some(c => c.type === "U'hlti"),
    affinityId: (soul.find(c => c.type === "Affinity") || {}).id || null,
  };
}

// Mirror of prophecyEngine.isSupreme: Natural L3 + Impera L1/L2/L3 +
// U'hlti + Affinity, standing (not Fallen).
function isSupremeSlot(slot) {
  if (!slot?.escendant || slot.fallen || !slot.natural) return false;
  const c = getCard(slot.escendant);
  if (!c || c.level !== 3) return false;
  const p = soulPieces(slot);
  return p.imperaLevels.has(1) && p.imperaLevels.has(2)
    && p.imperaLevels.has(3) && p.hasUhlti && !!p.affinityId;
}

// Assess the NPC's OWN prophecy: is it worth playing toward, is the
// board condition already met (→ hold Khao and let it regrow to the
// fare), and which Luxmoire pieces would advance it?
// Returns null when the prophecy is not Lord-actionable:
//   • tablet_nullus — the OPPONENT builds it; nothing to push
//   • ace_of_shades — Shadow Gambling streaks; Beb's dice, not chess
//   • supreme-based prophecies with < 2 distinct U'hltis or no divine
//     Affinity anywhere in the NPC's world (every 2026 starter deck
//     ships exactly ONE U'hlti — those go dormant here by design, and
//     the check future-proofs richer NPC decks for free)
function assessProphecyPush(npcLive, playerSnap, field, hand, grimoire, luxmoire) {
  const pid = npcLive?.prophecy || field?.prophecy || null;
  if (!pid) return null;

  // Bloody Hell — 45+ total Anima lost by EITHER duelist, fare 15.
  // The Lord's most reachable prophecy: it deals the blood itself.
  //
  // ⚠ R100 (caP'n, 2026-08-26): *"dimming your own Anima in any way at
  // all does not satisfy Prophecy requirements."* `totalAnimaLost` is
  // now the ADVERSARIAL ledger and only that, so this planner reads the
  // right number without changing — but it must NEVER learn the name
  // `totalAnimaSelfDimmed`. Feeding the brain its own self-dims here
  // would have it plan toward a Prophecy that can never fire, which is
  // a worse failure than not planning at all.
  if (pid === "proph_bloody_hell") {
    const worst = Math.max(playerSnap?.totalAnimaLost || 0, npcLive?.totalAnimaLost || 0);
    return { active: true, kind: "blood", boardMet: worst >= 45, near: worst >= 28, wantSoulPieceIds: [] };
  }

  const ilk = ILK_PROPHECIES[pid] ?? (pid === "proph_tablet_omnis" ? null : undefined);
  if (ilk === undefined) return null;

  const everywhere = [
    ...hand,
    ...Object.keys(grimoire || {}),
    ...Object.keys(luxmoire || {}),
    ...((field.esclings || []).flatMap(s => s?.soul || [])),
  ];
  const uhltiIds = new Set(everywhere.filter(id => getCard(id)?.type === "U'hlti"));
  const hasDivine = everywhere.some(id => DIVINE_AFFS.includes(id));
  if (uhltiIds.size < 2 || !hasDivine) return null; // unreachable — dormant

  const cands = (field.esclings || []).filter(s => {
    if (!s?.escendant || s.fallen || !s.natural) return false;
    const c = getCard(s.escendant);
    return c && (ilk === null || c.ilk === ilk);
  });
  const l3cands  = cands.filter(s => getCard(s.escendant)?.level === 3);
  const supremes = cands.filter(isSupremeSlot);
  const divineWorn = supremes.some(s => DIVINE_AFFS.includes(soulPieces(s).affinityId));
  const boardMet = supremes.length >= 2 && divineWorn;

  // Missing Supreme pieces the Luxmoire could supply right now.
  const want = [];
  l3cands.forEach(s => {
    const p = soulPieces(s);
    if (!p.hasUhlti) {
      Object.keys(luxmoire || {}).forEach(id => {
        const c = getCard(id);
        if (c?.type === "U'hlti" && c.owner === baseId(s.escendant) && (luxmoire[id] || 0) > 0) want.push(id);
      });
    } else if (!p.affinityId) {
      DIVINE_AFFS.forEach(id => { if ((luxmoire || {})[id] > 0) want.push(id); });
    }
  });

  return { active: true, kind: "supreme", boardMet, near: l3cands.length >= 2, wantSoulPieceIds: want };
}

// PROPHECY DENIAL (skill ≥ 0.95): find the PLAYER being whose ruin
// most sets back THEIR prophecy — the deepest-souled Natural L3 of
// their prophecy's Ilk, once their Khao pool says the fare is coming.
// Returns a player slot index, or -1 when there's nothing to deny.
function findDenialTarget(playerFresh) {
  const pid = playerFresh?.prophecy;
  const ilk = ILK_PROPHECIES[pid] ?? (pid === "proph_tablet_omnis" ? null : undefined);
  if (ilk === undefined) return -1;
  if ((playerFresh?.khao || 0) < 8) return -1; // no fare in sight — no panic
  let best = -1, bestScore = 2; // require real progress: ≥ 3 soul pieces
  ((playerFresh.field?.esclings) || []).forEach((s, i) => {
    if (!s?.escendant || s.fallen || !s.natural) return;
    const c = getCard(s.escendant);
    if (!c || c.level !== 3) return;
    if (ilk !== null && c.ilk !== ilk) return;
    const p = soulPieces(s);
    const score = p.imperaLevels.size + (p.hasUhlti ? 1 : 0) + (p.affinityId ? 1 : 0);
    if (score > bestScore) { best = i; bestScore = score; }
  });
  return best;
}

// ═════════════════════════════════════════════════════════════
// LORD — Chess-level: reads board, calculates kill turns, pivots.
// AWAKENING: the Lord now wields the full arsenal —
//   • Grimoire Search to complete ascension lines and to refill an
//     empty board (never top-decking when it can tutor).
//   • Luxmoire Search to pull the exact trainable Impera it wants.
//   • Deviant Ascends when no natural line exists and the fare is
//     worth the tempo.
//   • Full training + U'hlti + Affinity — the Lord will assemble a
//     Supreme Being if the duel runs long enough.
//   • Soul activations across the whole dispatcher, value-gated.
//   • Rules-true resurrection — including the L3-over-standing-L2
//     chain revive, the single biggest tempo swing it owns.
// The old sections 11/12 (Fallen bypass + kill-shot tunnel vision)
// collapsed INTO doAttacks: animaClear() already encodes the R26
// Fallen bypass, the Lord's no-plan branch already strikes Anima when
// clear, and collapsing them means defensive states (Fate Maker,
// Sweet Dreams, Intangible) apply during kill shots too — they used
// to be bypassed by the raw writes. One attack phase, one rulebook.
// DEPTHS BRAIN — the Lord's skill sub-bands:
//   ≥ 0.70 PROPHECY: the Lord races its own win condition. Blood
//          prophecies escalate it to AGGRO early, and a met condition
//          coils it around its Khao pool — no optional spend, natural
//          +1 growth carries it to the fare, the prophecyEngine ends
//          the duel. A second win-axis the player has to watch.
//   ≥ 0.82 LETHAL FOCUS: a clear, lethal Anima lane is never traded
//          for a being kill — see doAttacks.
//   ≥ 0.95 DENIAL: hunts the being that completes the PLAYER's
//          prophecy — see doAttacks.
// ═════════════════════════════════════════════════════════════
async function runLordTurn(code, match, npc, player, brain = null) {
  const turn = match.turn || 0;
  // ── R08's WINDOW IS AS LONG AS THE TABLE ────────────────────
  // ⚠ THIS USED TO BE THE LITERAL `2`, TYPED AT THE R08 GATE
  // BELOW AND DEFAULTED AT EVERY SEARCH HELPER. The opening Cycle
  // is `openingCycleLength(seatCount)` half-turns long — FOUR on a
  // four-seat table — so a hardcoded 2 declared the Cycle over at
  // half-turn 2 and let the brain Attack, search its Grimoire and
  // search its Luxmoire inside a window the rule still holds open.
  // Latent only because an NPC seat has never been dealt into a
  // 3-4p table; the instant one is, R08 is wrong IN THE BRAIN,
  // which is the one place the backstop in Shatterfield cannot see.
  const seatCount = match.seatCount || 2;
  let hand        = [...(npc.hand || [])];
  let field       = JSON.parse(JSON.stringify(npc.field || {}));
  let lordGrimoire = { ...npc.grimoire };
  let lordLuxmoire = { ...npc.luxmoire };
  const usedSouls = new Set();
  const stampTurn = newStampTurn(); // R83 — one per Turn, discarded at end of Turn
  await npcOpenMainPhase(code);     // R108 — a new Turn opens in Main
  let drewThisTurn = false;
  // Universal cost rule: no local khao/anima mirror. Every spend goes
  // through npcSpendKhao / npcSpendAnima — fresh Firebase reads each
  // time. The PLANNING values below (npc.khao, npc.anima) are the
  // turn-start snapshot, fine for planning since no spends have
  // happened yet at that point.
  const planKhao  = npc.khao ?? (npc.maxKhao || 3);
  const planAnima = npc.anima ?? 30;

  // ── DEEP BOARD ASSESSMENT (AWAKENING: effective values) ──
  const myStanding      = standingBeings(field);
  const myTotalLyt      = totalAvailableLyt(field, player.field);
  const playerStanding  = standingBeings(player.field || {});
  const playerFallen    = ((player.field?.esclings) || []).filter(s => s?.escendant && s.fallen);
  const playerThreat    = threatScore(player, player.field);
  const turnsToMyDeath  = turnsUntilDeath(npc.anima || 30, player.field, field, match?.turn ?? 0, seatCount);
  const playerAnima     = player.anima || 30;
  const playerAnimaClearNow = playerAnimaClear(player.field, match?.turn ?? 0, seatCount);

  const canKillNow = playerAnimaClearNow && myTotalLyt >= playerAnima;
  const canKillNextTurn = myTotalLyt >= playerAnima && playerStanding.length <= 1;

  // Resurrection intel (planning snapshot; resurrectOne re-reads fresh)
  // R44 TALLY (July 25 2026 wave) — count occurrences, not membership
  const revivedSnap = npc.resurrectedCards || [];
  const necUnrevived = (npc.necropolis || []).filter(id => {
    const c = getCard(id);
    if (c?.type !== "Escendant") return false;
    return resTallyNpc(revivedSnap, id) < (RESURRECT_LIMIT[`L${c.level}`] ?? 1);
  });
  const shouldResurrect = necUnrevived.length > 0
    && myStanding.length === 0
    && turn >= 3;

  const grimoireEmpty = Object.keys(lordGrimoire).length === 0;
  // ⚠ `shouldGraveshift` is now only a MODE HINT for the plan below —
  // the act itself moved to maybeGraveshift, which all three brains
  // share and which reads R40/R41/R42 rather than the old
  // `handEmpty && planAnima > 12` (see the banner on that function:
  // handEmpty was never a rule and 12 was never the fare).
  const shouldGraveshift = grimoireEmpty;

  const baseMode = canKillNow     ? "KILL"
    : turnsToMyDeath <= 1         ? "SURVIVAL"
    : canKillNextTurn             ? "AGGRO"
    : playerThreat >= 40          ? "AGGRO"
    : shouldResurrect             ? "RESURRECT"
    : "BALANCE";

  // ── PROPHECY BRAIN (DEPTHS BRAIN, skill ≥ 0.70) ──
  const prophecyPush = (brain?.skill ?? 0) >= SKILL_PROPHECY
    ? assessProphecyPush(npc, player, field, hand, lordGrimoire, lordLuxmoire)
    : null;
  // A met condition makes every optional Khao spend a delay: Khao grows
  // +1 per no-spend cycle, and the prophecyEngine ends the duel the
  // moment the pool reaches the fare (15). KILL outranks it (dimming
  // the Anima to 0 this turn is faster than any prophecy) and SURVIVAL
  // outranks it (a dead Lord collects no fare).
  // ⚠ AND IT IS NOW BOUNDED. `holdKhao` silences NINE steps of this
  // runner at once — resurrect, both searches, tome flips, tome
  // re-activation, every soul activation, offensive tome destruction,
  // Deviant ascends and the stamps. Unbounded, that is a Lord that
  // voluntarily stops playing Terror for as many Turns as the +1 Khao
  // climb needs, on a board the adversary is free to rebuild. Three
  // consecutive Turns is the whole hold the fare can honestly need
  // (R12 grants +1 a cycle), and a threat that has grown past the AGGRO
  // line breaks it immediately: a prophecy collected from a corpse is
  // collected by nobody.
  const HOLD_KHAO_MAX_TURNS = 3;
  const prevHoldStreak = npc.holdKhaoStreak || 0;
  const holdKhao = !!(prophecyPush?.boardMet)
    && baseMode !== "KILL" && baseMode !== "SURVIVAL"
    && prevHoldStreak < HOLD_KHAO_MAX_TURNS
    && playerThreat < 40;
  await updatePlayer(code, NPC_IDX, { holdKhaoStreak: holdKhao ? prevHoldStreak + 1 : 0 });
  // Nearness turns the screws — a BALANCE Lord with blood in the water
  // (or two L3 candidates standing) escalates to AGGRO on its own.
  const mode = (prophecyPush?.near && baseMode === "BALANCE") ? "AGGRO" : baseMode;

  // ── 1. TURN 1 OPTIMAL SETUP — placement only, still sequential ──
  if (turn === 0) {
    while (true) {
      const r = await placeOneEscendant(code, hand, field, true);
      hand = r.hand; field = r.field;
      if (!r.placed) break;
    }
    // No tome on turn 0 (rule)
    field.esclings = field.esclings;
    await updatePlayer(code, NPC_IDX, {
      hand, field,
      // ⚠ NO `khao:` — R14/R92b, same as the kill-shot writes above.
      // This is a hand-off too (npcHandOffTurn below), so the pool it
      // refilled belonged to the seat LEAVING. maxKhao stays: that is
      // this seat's own ceiling, not its pool.
      // ⚠ AND NO `khaoSpentThisCycle:` EITHER, SINCE R132. The flag is
      // cleared in one place — handOffHalfTurn, onto the seat sitting
      // DOWN — for exactly the reason the pool is. Nothing in this
      // branch can have spent Khao (placement only; no Tome on turn 0),
      // so the clear was already a no-op; it is gone so the file has ONE
      // answer to "where does a seat's spend window open" instead of
      // three.
      maxKhao: npc.maxKhao || 3,
      grimoire: lordGrimoire, luxmoire: lordLuxmoire,
    });
    // The Lord's turn-0 setup is a hand-off too. R93 can never fire at
    // half-turn 1 — that is the whole point of routing it anyway: the
    // invariant is "EVERY path that changes hands asks the clock", and
    // an invariant with a documented exception is an invariant nobody
    // can grep for. This one and the crash-recovery path above were the
    // last two bare writes in the file.
    await npcHandOffTurn(code, { ...match, turn: 0 });
    return;
  }

  // ── 2. GRAVESHIFT — R40/R41/R42, through the one shared helper.
  // The Lord's bespoke copy lived here and was the only Graveshift in
  // the file; see the banner on maybeGraveshift for what it got wrong
  // and why all three brains now use the same one.
  {
    const gs = await maybeGraveshift(code, turn, hand, lordGrimoire, field);
    hand = gs.hand; lordGrimoire = gs.grimoire; field = gs.field;
  }

  // ── 3. RESURRECTION — rules-true (R44-R48), reads fresh state ──
  // v2 (caP'n ruling, July 2026): a standard once-per-turn play. The
  // Lord attempts one revival every turn — chain revives (an L3 back
  // over its standing L2, full soul intact) are the jackpot, and
  // resurrectOne's highest-level-first ordering already grabs them.
  // PROPHECY BRAIN: the fare is Khao-else-Anima — skipped while the
  // Lord is holding its pool for the prophecy fare.
  if (turn >= 3 && !holdKhao) {
    const r = await resurrectOne(code, field, turn);
    field = r.field;
  }

  // ── 4. SMART DRAW (with hand cap discard) ──
  // ═══════════════════════════════════════════════════════════
  // ⭐ THE LORD USED TO DELETE A CARD OUT OF THE GAME HERE.
  // ═══════════════════════════════════════════════════════════
  // This block fired at `hand.length >= 7` and did `hand.splice(
  // worstIdx, 1)` — straight out of the local array, with nothing
  // pushing it to the Necropolis and no necropolis write. The next
  // updatePlayer persisted the shortened hand and the card ceased to
  // exist. Compare the CORRECT path fourteen hundred lines up:
  //     const [gone] = h.splice(npcPickDiscardIdx(h), 1); nec.push(gone);
  // followed by `{ necropolis: nec }`.
  //
  // Three things follow from a card that is deleted rather than
  // discarded: it can never be Resurrected (R44), it never folds back
  // into a Graveshift (R42), and the Necropolis count the player reads
  // off the board is wrong for the rest of the duel. It is the only
  // place in the engine where a card leaves the game entirely.
  //
  // ⚠ AND THE NUMBER WAS WRONG TOO. It trimmed at 7 — the pre-Rulez-v2
  // hand limit. R10 has been 8 since July, draw-then-discard, and
  // npcEnforceHandCap already enforces exactly that at the correct
  // number, at the end of every turn, into the Necropolis.
  //
  // So the block is not repaired, it is DELETED, and the one function
  // that already does this job correctly does it. Keeping a second
  // implementation of a discard is how the first one drifted.
  hand = await npcEnforceHandCap(code, hand);

  // ── 4a. EMERGENCY BEING SEARCH (AWAKENING) — empty slots, no L1 in
  // hand, L1 in Grimoire: pay 2 to tutor rather than pray. Consumes
  // the turn's draw, exactly like the player's Grimoire Search.
  // PROPHECY BRAIN: skipped while holding (2 Khao resets the growth).
  if (!holdKhao) {
    const emptySlot = (field.esclings || []).some(s => !s?.escendant);
    const l1InHand = hand.some(id => {
      const c = getCard(id);
      return c?.type === "Escendant" && c.level === 1;
    });
    if (emptySlot && !l1InHand) {
      const r = await grimoireSearchOne(code, hand, lordGrimoire, 1, null, turn, seatCount, SEARCH_BY_PLAYER);
      hand = r.hand; lordGrimoire = r.grimoire;
      if (r.searched) drewThisTurn = true;
    }
  }

  // ── 4b. ASCENSION-LINE SEARCH (AWAKENING) — a standing being's
  // natural next level sits in the Grimoire but not in hand: pay 4 to
  // complete the line. Khao-gated so tomes still get fed; skipped in
  // SURVIVAL/RESURRECT where every point matters elsewhere, and while
  // holding the pool for a prophecy fare.
  if (!drewThisTurn && mode !== "SURVIVAL" && mode !== "RESURRECT" && !holdKhao) {
    const npcNow = (await getMatch(code))?.players?.[NPC_IDX] || {};
    // The old floor was a flat 6 for a 4-fare — two Khao of pure
    // conservatism on top of every tutor (R88 + R96 are the only gates
    // the rules ask for; the +1 keeps one point for a response).
    if ((npcNow.khao ?? 0) >= grimSearchFare(2) + 1) {
      for (const s of (field.esclings || [])) {
        const cur = getCard(s?.escendant);
        if (!cur || cur.level >= 3 || s?.fallen) continue;
        const handHasNext = hand.some(id => canAscendNatural(cur, getCard(id)));
        if (handHasNext) continue;
        const wantIds = Object.keys(lordGrimoire).filter(gid =>
          (lordGrimoire[gid] || 0) > 0 && canAscendNatural(cur, getCard(gid)));
        if (wantIds.length === 0) continue;
        const r = await grimoireSearchOne(code, hand, lordGrimoire, cur.level + 1, wantIds, turn, seatCount, SEARCH_BY_PLAYER);
        hand = r.hand; lordGrimoire = r.grimoire;
        if (r.searched) drewThisTurn = true;
        break;
      }
    }
  }

  // ── 4c. LUXMOIRE SEARCH (AWAKENING v3) — 5 Khao to tutor the exact
  // trainable Impera it wants. CANON (July 4): the search CONSUMES the
  // turn's draw, same as Grimoire Search — so it now sits BEFORE the
  // normal draw and competes with it. Only when Khao-rich, nothing
  // trainable already in hand, and the duel isn't in crisis.
  if (!drewThisTurn && mode !== "SURVIVAL" && mode !== "RESURRECT" && !holdKhao) {
    // PROPHECY BRAIN: while a supreme push is live and unmet, the
    // search also hunts the missing Supreme pieces (the exact owner-
    // bound U'hlti for a standing L3, or a divine Affinity once the
    // U'hlti is worn). Legal parity: the PLAYER's Luxmoire Search
    // modal offers EVERY Luxmoire card, not just Impera — the old
    // Impera-only restriction here was NPC conservatism, not a rule.
    const wantPieces = (prophecyPush?.kind === "supreme" && !prophecyPush.boardMet)
      ? (prophecyPush.wantSoulPieceIds || []).filter(id => !hand.includes(id))
      : [];
    const handTrainable = hand.some(id => {
      const card = getCard(id);
      if (card?.type !== "Impera") return false;
      return (field.esclings || []).some(slot => {
        if (!slot?.escendant) return false;
        const being = getCard(slot.escendant);
        if (!being) return false;
        if (card.school !== being.school || card.ilk !== being.ilk) return false;
        if ((card.level || 1) > (being.level || 1)) return false;
        return !(slot.soul || []).some(sid => {
          const sc = getCard(sid);
          return sc?.type === "Impera" && sc?.level === card.level;
        });
      });
    });
    if (!handTrainable || wantPieces.length > 0) {
      const npcNow = (await getMatch(code))?.players?.[NPC_IDX] || {};
      // Was a flat 7 for a 5-fare. Same reasoning as the Grimoire floor,
      // and the cushion itself is now a skill dial: a cautious brain
      // keeps three points back for a response, a sharp one keeps one.
      if ((npcNow.khao ?? 0) >= LUX_SEARCH_COST + Math.round(lerp(3, 1, brain?.skill ?? 0.7))) {
        const r = await luxmoireSearchOne(code, hand, field, lordLuxmoire, wantPieces, turn, seatCount, SEARCH_BY_PLAYER);
        hand = r.hand; lordLuxmoire = r.luxmoire;
        if (r.searched) drewThisTurn = true; // CANON: search = the draw
      }
    }
  }

  // ── 4d. NORMAL DRAW — only if no search consumed it ──
  // R10 (Rulez v2): cap 8. The Lord won't draw INTO a forced discard.
  if (!drewThisTurn && (hand.length < NPC_HAND_LIMIT
        || !handHasPlay(hand, field, lordGrimoire))) {    // R10 — churn a dead hand
    const esclings = field.esclings || [];
    const hasL2Plus = esclings.some(s => getCard(s?.escendant)?.level >= 2);
    const luxHasCards = Object.keys(lordLuxmoire).length > 0;
    const needsAscend = esclings.some(s => canAscendFromPool(s, lordGrimoire));
    const needsImpera = hasL2Plus && luxHasCards;
    const fromLux = needsImpera && !needsAscend && mode !== "SURVIVAL" && mode !== "RESURRECT";
    const { card: drawn, newPool } = drawAndDeplete(fromLux ? lordLuxmoire : lordGrimoire);
    if (drawn) {
      hand.push(drawn);
      if (fromLux) lordLuxmoire = newPool; else lordGrimoire = newPool;
      await updatePlayer(code, NPC_IDX, {
        hand, grimoire: lordGrimoire, luxmoire: lordLuxmoire,
      });
      await sleep(ACTION_DELAY);
    }
  }

  // ── 5. TRANSCEND — natural, one at a time ──
  while (true) {
    const r = await placeOneEscendant(code, hand, field, true);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // ── 6. ASCEND — once per turn TOTAL (Rulebook line 643). ──
  // AWAKENING: Deviancy unlocked — when no natural line exists, the
  // Lord pays the Deviant fare (rules.deviantFare, R101) to keep climbing.
  // PROPHECY BRAIN: a supreme push forbids Deviant ascends entirely —
  // Deviants can never wear a U'hlti (R64), so tainting a line would
  // sabotage the very win the Lord is building. A Khao hold forbids
  // them too (the fare resets the growth).
  {
    const allowDeviant = !holdKhao && prophecyPush?.kind !== "supreme";
    const r = await ascendOne(code, hand, field, true, allowDeviant);
    hand = r.hand; field = r.field;
  }

  // ── 6a. TRAIN — every legal Impera in hand, one write at a time ──
  while (true) {
    const r = await trainOneImpera(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.trained) break;
  }

  // ── 6b. U'HLTI / AFFINITY — dress the Natural L3 (Supreme road) ──
  while (true) {
    const r = await attachOneSoulCard(code, hand, field);
    hand = r.hand; field = r.field;
    if (!r.attached) break;
  }

  // ── 7. FLIP WAITING TOMES — cost paid here per Rulebook ──
  // PROPHECY BRAIN: flips pay Khao — held back while holding. The
  // tomes wait one turn; the win doesn't.
  while (!holdKhao) {
    const r = await flipOneTome(code, field, turn, hand, lordGrimoire);
    field = r.field; hand = r.hand; lordGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }   // the Tome ended the duel (R33 / a Prophecy) — stop, at the cap
    if (!r.flipped) break;
  }

  // ── 8. TOME PLACEMENT — strategic, ONE per call. Loop fills empties. ──
  const lordChooserMode =
    mode === "SURVIVAL" ? "Lord-survival"
    : (mode === "AGGRO" || mode === "KILL") ? "Lord-aggro"
    : "Lord-balance";
  while (true) {
    const r = await placeOneTome(code, hand, field, turn, lordChooserMode);
    hand = r.hand; field = r.field;
    if (!r.placed) break;
  }

  // ── 9. TOME ACTIVATION — re-activate persistent face-up tomes ──
  // PROPHECY BRAIN: activations pay fares — silenced while holding.
  while (!holdKhao) {
    const r = await activateOneTome(code, field, turn, "Lord", hand, lordGrimoire);
    field = r.field; hand = r.hand; lordGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
    if (!r.activated) break;
  }

  // ── 9a. SOUL ACTIVATIONS (AWAKENING) — the full dispatcher, value-
  // gated per effect. Loops until nothing useful/affordable remains
  // (capped at 4 firings a turn so the Khao pool still feeds combat).
  // PROPHECY BRAIN: soul fares are optional spends — held back too.
  // DEPTHS BRAIN: the cap was a flat 4 for every Lord from 0.70 to
  // 1.00 — the single widest flat stretch on the ladder. 3 → 6.
  const lordSoulCap = holdKhao ? 0 : Math.round(lerp(3, 6, brain?.skill ?? 0.7));
  for (let sa = 0; sa < lordSoulCap; sa++) {
    const r = await activateOneSoulCard(code, hand, field, lordGrimoire, turn, usedSouls, "Lord");
    hand = r.hand; field = r.field; lordGrimoire = r.grimoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
    if (!r.activated) break;
  }

  // ── 10. OFFENSIVE TOME DESTRUCTION ──
  // ── AGGRESSION BUMP ── removed `mode !== "SURVIVAL"` gate.
  // PROPHECY BRAIN: pays a fare — skipped while holding.
  if (!holdKhao) {
    const fp = (await getMatch(code))?.players?.[0] || player;
    const fpTomes = riftArray(fp.field?.tomes, 3);
    const playerTomes = fpTomes.filter(t => t?.id && !t.faceDown);
    if (playerTomes.length > 0) {
      // THE REBASE LAW — sections 9 and 9a each open a response window,
      // and the reactor answers out of THIS hand. Both the decision
      // below and the `{ hand }` write at the end of the block are
      // taken from this draft, so it re-bases before either.
      hand = await syncNpcHand(code, hand);
      const destructTome = hand.find(id => ["tome_nothing_left", "tome_regnant", "tome_lyt_hearted"].includes(id));
      if (destructTome) {
        const destructCard = getCard(destructTome);
        const cost      = destructCard?.khaoCost  || 3;
        const animaCost = destructCard?.animaCost || 0;

        // Pre-check both costs against fresh Firebase before spending either
        const m = await getMatch(code);
        const npcLive = m?.players?.[NPC_IDX] || {};
        const canAfford = (npcLive.khao ?? 0) >= cost
                       && (npcLive.anima ?? 0) >= animaCost;

        // Brain guard before the Khao leaves the till — see npcSpendAnima.
        const animaSafe = animaCost <= 0 || (npcLive.anima ?? 0) > animaCost;
        if (canAfford && animaSafe && (await npcSpendKhao(code, cost)) && (await npcSpendAnima(code, animaCost))) {
          hand.splice(hand.indexOf(destructTome), 1);
          // Priority target: the D'uhmdumn taunt if one stands (must-ruin-
          // first applies to tome effects too), else the first face-up tome.
          const mrf = getRuinFirstTome(fp.field);
          const targetTomeIdx = mrf ? mrf.slotIdx : fpTomes.findIndex(t => t?.id && !t.faceDown);
          if (targetTomeIdx !== -1) {
            // AWAKENING: routed through the shared soak-aware helper
            // (identical semantics — nullifyFirstRuin consumed, ruined
            // tome to the player's Necropolis, never vanished).
            await ruinPlayerTomeSlot(code, targetTomeIdx);
          }
          // ⚠ THE FIRED TOME HAS TO LAND SOMEWHERE. This block spliced
          // the destruct Tome out of the hand and wrote the hand, and
          // that was the whole of it: the card was in no pile, on no
          // rift, in no Soul, in no hand and in no deck afterwards. It
          // had left the game. Harmless-looking for eighty turns and
          // then not: Seal of Omnis tutors `tome_nothing_left` OUT of
          // the Necropolis in section 9, section 10 finds it in hand
          // one line later and burns it, and the copy the tutor spent
          // is never repaid. That is X09 (R44/R77 — "a copy left the
          // Necropolis and is nowhere at all"), and it is the reason
          // the Necropolis count reads 28 → 28 while a card is missing:
          // one id left by the tutor and a different id arrived from a
          // ruin, so the tally balances over a hole.
          //
          // A Tome played from hand goes to its owner's Necropolis when
          // it resolves — every one of these three is non-persistent
          // (effectTag null), the player side ruins them from
          // handlePlayTome, and ruinNpcTome does the same for the ones
          // the NPC fires off its own rifts. Fresh read for the pile,
          // because ruinPlayerTomeSlot ran between here and the last
          // time this turn looked at it.
          const mDone = await getMatch(code);
          const necSelf = [...((mDone?.players?.[NPC_IDX]?.necropolis) || []), destructTome];
          await updatePlayer(code, NPC_IDX, { hand, necropolis: necSelf });
          await sleep(TOME_INTERRUPT);
        }
      }
    }
  }

  // ── ⭐ 10b. PRIMERA STAMPS (R78–R84) — pressed BEFORE the attack
  // phase, because that is where every one of them pays: Phantom is
  // the only path to the Anima through a standing wall, Ilk-Dragon
  // converts a swing that would do nothing into a fracture, Beast arms
  // the Anima rider, and Machine/Space/Time/Humanoid all put a body or
  // a piece on the board in time for it to matter this Turn.
  if (!holdKhao) {
    const fpS = (await getMatch(code))?.players?.[0] || player;
    const r = await pressPrimeras(code, hand, field, lordGrimoire, lordLuxmoire, stampTurn,
      { playerField: fpS.field || {}, difficulty: "Lord", brain });
    hand = r.hand; field = r.field; lordGrimoire = r.grimoire; lordLuxmoire = r.luxmoire;
    if (r.matchOver) { await npcAbortTurn(code, r.hand ?? hand); return; }
  }

  // ── R108 · DECLARE THE ATTACK PHASE ─────────────────────────
  // Everything above this line is the Main phase; nothing below it
  // plays a card. The declaration is written to the seat BEFORE the
  // first swing so the board, the gate and an adversary's client all
  // agree the brain has committed. Written unconditionally — even on
  // the opening Cycle, where R08 bars the attacks themselves: the
  // phase advanced whether or not anything came of it, and a phase
  // that only exists on turns you happen to swing is not a phase.
  // ── 10b. THE ROLE ARTS (R115-R119) AND EXILIATION (R111) ──────
  // caP'n, 2026-09-13: the Exile Wave is playable at release. The
  // PLAYER got four rift wedges, an Exiliation door and the Ritual the
  // same day — and a mechanic one side can perform and the other cannot
  // is the six brain/player asymmetries this codebase already paid for,
  // arriving again in a new coat.
  //
  // ⚠ THE BRAIN CALLS THE SAME RESOLVER THE BOARD CALLS. Not a copy,
  // not a simplified twin: `src/roleArts.js`, through the same gate,
  // charging the same fares. What is the brain's own is only the
  // JUDGEMENT — which Art is worth buying — and it is deliberately
  // narrow (see below), because a brain that spends its whole pool on
  // Arts stops playing the card game.
  //
  // ⚠ AND IT RUNS BEFORE THE ATTACK PHASE IS DECLARED, which is R108
  // rather than an ordering preference: declaring shuts the hand, and
  // the Warden Art sends a card FROM the hand.
  // ⚠ "Lord" IS A LITERAL HERE AND IT IS NOT A SHORTCUT. Each brain is
  // its own function and there is no `difficulty` variable inside one —
  // the function IS the difficulty. Reading one crashed the Lord's whole
  // turn the first time this was written, and the brain's crash handler
  // swallowed it into a duel that simply never ended.
  {
    const reb = await npcRoleArts(code, turn, seatCount, "Lord",
      { hand, grimoire: lordGrimoire, luxmoire: lordLuxmoire });
    hand = reb.hand; lordGrimoire = reb.grimoire; lordLuxmoire = reb.luxmoire;
  }

  // ── A PROPHECY MAY BE CASHED THE INSTANT IT COMES TRUE ───────
  // caP'n, 2026-09-14. BEAT ONE: everything above this line is the Main
  // phase, so every Supreme-being condition — two Supremes of a School
  // plus a Divine title, the two Tablets — has just had its chance to
  // complete through an Ascend, a Training, a U'hlti, an Affinity or a
  // Resurrection. See npcCashProphecyIfReady.
  if (await npcCashProphecyIfReady(code, npc?.prophecy)) { await npcAbortTurn(code, hand); return; }   // R10 on the way out — a duel WON by a Prophecy is archived at the cap too
  await npcDeclareAttackPhase(code);
  // ── 11. ATTACK — one phase, one rulebook (AWAKENING) ──
  // The old Fallen-bypass (11) and kill-shot (12) sections collapsed
  // into doAttacks: animaClear() encodes the R26 bypass, the Lord
  // branch strikes Anima whenever the lane is clear (that IS the kill
  // shot), and every strike now resolves defensive states — Fate
  // Maker, Sweet Dreams, and Intangible apply during lethal pushes
  // too, exactly as they do against a human.
  let esclings = field.esclings || [];
  if (!isOpeningTurnIndex(turn, seatCount)) {   // R08 — no Attacks of any kind on the opening Cycle
    const fp = (await getMatch(code))?.players?.[0] || player;
    const { killed, npcDied } = await doAttacks(code, esclings, fp, "Lord", field, turn, brain);
    if (npcDied) { await npcAbortTurn(code, hand); return; } // the player's defenses won the duel mid-phase
    field.esclings = esclings;
    if (killed) {
      // R10 ON THE WAY OUT. The kill-shot path returns early and used
      // to skip npcEnforceHandCap entirely, so a duel WON on a turn
      // that also drew at the cap was archived with a nine-card hand.
      // The duel is over, so nothing can be played from it -- but the
      // final snapshot is what a replay renders and what the end
      // screen reads, and a referee that reports a real rule break it
      // has decided to forgive is a referee people stop reading.
      // ⚠ THE REBASE LAW APPLIES ON THE WAY OUT TOO. This write is the
      // LAST word on the duel — it is what the replay renders and what
      // the referee audits — and until now it was the only hand/luxmoire
      // write in the runner that skipped both gates. A winning Turn is
      // exactly the Turn most likely to need them: doAttacks opens a
      // response window on every swing (the reactor may have spent a
      // counter out of this very hand), and sections 7 and 9 can tutor
      // an Impera out of the Luxmoire (npcLuxDirty). Writing the turn-
      // start drafts over the top puts the spent counter back in hand
      // and walks the tutored Impera back into the Luxmoire while the
      // hand still holds it. Same two lines as section 12, same reason.
      hand = await syncNpcHand(code, hand);      // THE REBASE LAW
      hand = await npcEnforceHandCap(code, hand);
      field.esclings = esclings;
      lordLuxmoire = await syncNpcLuxmoire(code, lordLuxmoire);   // see npcLuxDirty
      // ⚠ NO `khao:` — R14/R92b. The pool refills at the START of a
      // Turn, onto the seat SITTING DOWN, inside handOffHalfTurn's chair
      // transaction. This line refilled the brain as it STOOD UP: in a
      // three-seat table (Gauntlet — Duo, Story Co-op) a kill-shot Turn
      // does not end the duel, so the brain crossed into the surviving
      // player's half-turn on a full pool to answer with. In 1v1 it only
      // poisoned the archived snapshot a replay renders — which is still
      // a stored state that contradicts the rule.
      await updatePlayer(code, NPC_IDX, { hand, field, grimoire: lordGrimoire, luxmoire: lordLuxmoire });
      await npcFinish(code, NPC_IDX, 0);
      return;
    }
  }

  // BEAT TWO: the swings have landed, so Bloody Hell (45 Anima lost,
  // on EITHER side) and Ace of Shades (a seven-streak of Shadow
  // Gamblings) can only have completed here. Asked before the
  // hand-off, which is the whole point of the ruling.
  if (await npcCashProphecyIfReady(code, npc?.prophecy)) { await npcAbortTurn(code, hand); return; }   // R10 on the way out — a duel WON by a Prophecy is archived at the cap too
  // ── 12. END TURN ──────────────────────────────────────────────
  // Note: anima is NOT written here. All anima changes during the turn
  // were committed via npcSpendAnima / dimNpcAnima — Firebase already
  // has the truth.
  hand = await syncNpcHand(code, hand);      // THE REBASE LAW — the chain may have spent from it
  hand = await npcEnforceHandCap(code, hand); // R10 — never end a turn above the cap
  field.esclings = esclings;
  const npcFresh   = (await getMatch(code))?.players?.[NPC_IDX] || npc;
  // ── R132 · THE MAX KHAO CLIMB IS NOT DECIDED HERE ANY MORE ────
  // caP'n, 2026-09-15: *"after 1 full turn of not using Khao, so if you
  // didn't use any turn 1, then the beginning of turn 2 you get +1 to
  // your Max Khao"* — the +1 lands at the BEGINNING of a Turn, which is
  // the chair transaction, so it now lives in ONE place:
  // turnEngine.handOffHalfTurn, granted onto the seat SITTING DOWN.
  //
  // ⚠ WHAT THIS LINE USED TO DO, AND WHY IT WAS A BUG. It granted at
  // END TURN — `(!spent && turn >= 2 && (maxKhao||3) < 10) ? +1 : same`
  // — in the same payload that cleared the spend flag. `match.turn`
  // counts half-turns, so this brain evaluated as it STOOD UP from
  // half-turn 3 and first PLAYED on the higher ceiling at half-turn 5,
  // while the human seat evaluated as it sat DOWN into half-turn 2 and
  // played half-turn 2 on it. The brain was a full Turn behind the
  // player on Max Khao for the whole duel — not a decision anybody
  // made, just drift between four copies of one rule.
  //
  // ⚠ AND THE `khaoSpentThisCycle: false` CLEAR WENT WITH IT, WHICH IS
  // THE OTHER HALF OF THE FIX. R12's window is one sit-down to the next
  // — this seat's own half-turn PLUS its adversary's. Clearing it here,
  // as the brain stands up, pointed the window at the wrong half of the
  // Turn and let a counter the brain fires during the PLAYER's half-turn
  // slip past the rule caP'n wrote to catch it. The flag is now cleared
  // in the one place the window opens: the hand-off, onto the incoming
  // seat. DO NOT RE-ADD A CLEAR HERE.
  //
  // `maxKhao` is still written, and is now an echo of the ceiling
  // Firebase already holds: it is this seat's own ceiling rather than
  // its pool, and the payload keeps the key for the same reason it keeps
  // the deliberate ABSENCE of `khao:` below.
  // See runBebTurn's copy of this note for the R13 clamp bug that used
  // to live on the line below, and turnEngine's R132 banner for the fix.
  const newMaxKhao = npcFresh.maxKhao || STARTING_KHAO;
  lordLuxmoire = await syncNpcLuxmoire(code, lordLuxmoire);   // see npcLuxDirty
  await updatePlayer(code, NPC_IDX, {
    hand, field,
    // ⚠ NO `khao:` — the pool refills at the START of a Turn (caP'n,
    // 2026-09-13) and handOffHalfTurn writes it onto the seat sitting
    // down. This line used to refill the brain as it STOOD UP, which
    // gave it a full pool to answer the player's whole half-turn with.
    maxKhao: newMaxKhao,
    grimoire: lordGrimoire, luxmoire: lordLuxmoire,
  });
  // FALLEN HALF-TURN: the player's turn starts now — their Fallen rise.
  await npcHandOffTurn(code, match);
}
